import { useState, useCallback, useEffect, useRef } from "react";

function useLS(key, def) {
  const [v, setV] = useState(() => {
    try { const s = localStorage.getItem(key); return s ? JSON.parse(s) : def; } catch { return def; }
  });
  const set = useCallback((u) => {
    setV(p => { const n = typeof u === "function" ? u(p) : u; try { localStorage.setItem(key, JSON.stringify(n)); } catch {} return n; });
  }, [key]);
  return [v, set];
}

const today = () => new Date().toISOString().split("T")[0];
const addDays = (dateStr, n) => {
  const d = new Date(dateStr + "T12:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().split("T")[0];
};
const fmtDateLabel = (dateStr) => {
  const td = today();
  if(dateStr === td) return "Hoy";
  if(dateStr === addDays(td, -1)) return "Ayer";
  return new Date(dateStr + "T12:00:00").toLocaleDateString("es-ES", {weekday:"short", day:"numeric", month:"short"});
};

const DARK = {
  bg:"#1C1C1E", surface:"#252528", surfaceHover:"#2C2C30",
  border:"#38383C", borderLight:"#464649",
  text:"#FAFAFA", textSecondary:"#A0A0A0", textTertiary:"#606060",
  accent:"#D4A843", accentMuted:"rgba(212,168,67,0.12)",
  green:"#22C55E", greenBg:"rgba(34,197,94,0.1)",
  red:"#EF4444", redBg:"rgba(239,68,68,0.1)",
  blue:"#3B82F6", blueBg:"rgba(59,130,246,0.1)",
  gold:"#F59E0B", goldBg:"rgba(245,158,11,0.1)",
  purple:"#A78BFA", purpleBg:"rgba(167,139,250,0.1)",
};
const LIGHT = {
  bg:"#FAFAFA", surface:"#FFFFFF", surfaceHover:"#F5F5F5",
  border:"#E8E8E8", borderLight:"#F0F0F0",
  text:"#0A0A0A", textSecondary:"#606060", textTertiary:"#A0A0A0",
  accent:"#000000", accentMuted:"rgba(0,0,0,0.06)",
  green:"#16A34A", greenBg:"rgba(22,163,74,0.08)",
  red:"#DC2626", redBg:"rgba(220,38,38,0.08)",
  blue:"#2563EB", blueBg:"rgba(37,99,235,0.08)",
  gold:"#D97706", goldBg:"rgba(217,119,6,0.08)",
  purple:"#7C3AED", purpleBg:"rgba(124,58,237,0.08)",
};

// ─── QUOTES ───────────────────────────────────────────────────────────────────
// BIBLICAL: versículos y proverbios bíblicos. Se muestra uno aleatorio en HoyTab.
// MOTIVATIONAL: citas de atletas, emprendedores y líderes. Se muestra en StatsTab.
// DAILY_TIPS: consejos diarios por categoría (gym, nutrición, dinero, MMA, sueño).
const BIBLICAL = [
  {text:"Confía en el Señor con todo tu corazón y no te apoyes en tu propio entendimiento.",author:"Proverbios 3:5"},
  {text:"El que trabaja su tierra tendrá abundante comida.",author:"Proverbios 12:11"},
  {text:"Los planes bien pensados dan buenas ganancias; los que se hacen de prisa llevan a la pobreza.",author:"Proverbios 21:5"},
  {text:"Hijo mío, no te olvides de mis enseñanzas; guarda en tu corazón mis mandamientos.",author:"Proverbios 3:1"},
  {text:"El alma del diligente quedará satisfecha.",author:"Proverbios 13:4"},
  {text:"Mejor es el fin de un asunto que su principio; mejor es el paciente que el altivo.",author:"Eclesiastés 7:8"},
  {text:"El que siembra con lágrimas, con regocijo segará.",author:"Salmos 126:5"},
  {text:"Todo lo puedo en Cristo que me fortalece.",author:"Filipenses 4:13"},
  {text:"Porque yo sé los planes que tengo para vosotros, planes de bienestar.",author:"Jeremías 29:11"},
  {text:"El que camina con sabios se hará sabio.",author:"Proverbios 13:20"},
  {text:"La sabiduría es lo primero. ¡Adquiere sabiduría!",author:"Proverbios 4:7"},
  {text:"El corazón alegre es una buena medicina.",author:"Proverbios 17:22"},
  {text:"El hombre diligente gobernará, pero el perezoso será sometido.",author:"Proverbios 12:24"},
  {text:"Antes del quebrantamiento está la soberbia.",author:"Proverbios 16:18"},
  {text:"Encomienda al Señor tus obras, y tus propósitos se afianzarán.",author:"Proverbios 16:3"},
  {text:"El que es lento para la ira vale más que el poderoso.",author:"Proverbios 16:32"},
  {text:"Hierro con hierro se afila; y así el hombre aguza el rostro de su amigo.",author:"Proverbios 27:17"},
  {text:"Buscad primeramente el reino de Dios y su justicia.",author:"Mateo 6:33"},
  {text:"El corazón tranquilo es vida para el cuerpo.",author:"Proverbios 14:30"},
  {text:"En la multitud de consejeros está la victoria.",author:"Proverbios 11:14"},
  {text:"Mira bien tus senderos y todos tus caminos serán establecidos.",author:"Proverbios 4:26"},
  {text:"El que es fiel en lo muy poco, también en lo más es fiel.",author:"Lucas 16:10"},
  {text:"Hay camino que parece derecho al hombre, pero su fin es camino de muerte.",author:"Proverbios 14:12"},
  {text:"El que refrena su boca guarda su alma.",author:"Proverbios 13:3"},
  {text:"Como ciudad derribada y sin muro es el hombre cuyo espíritu no tiene rienda.",author:"Proverbios 25:28"},
  {text:"El temor del Señor prolonga la vida.",author:"Proverbios 10:27"},
  {text:"Las riquezas no aprovechan en el día de ira.",author:"Proverbios 11:4"},
  {text:"No te jactes del día de mañana, porque no sabes lo que traerá.",author:"Proverbios 27:1"},
  {text:"La blanda respuesta aplaca la ira.",author:"Proverbios 15:1"},
  {text:"El hombre bueno deja herencia a los hijos de sus hijos.",author:"Proverbios 13:22"},
  {text:"Honra al Señor con tus riquezas.",author:"Proverbios 3:9"},
  {text:"La esperanza que se demora enferma el corazón, pero el deseo cumplido es árbol de vida.",author:"Proverbios 13:12"},
  {text:"No te sobrepongas al pobre porque es pobre.",author:"Proverbios 22:22"},
  {text:"Instruye al niño en el camino correcto.",author:"Proverbios 22:6"},
  {text:"El que perdona la ofensa cultiva el amor.",author:"Proverbios 17:9"},
  {text:"No digas: Me vengaré. Confía en el Señor.",author:"Proverbios 20:22"},
  {text:"El hombre de bien actúa con liberalidad y presta con justicia.",author:"Salmos 112:5"},
  {text:"Mejor es el pobre que camina en integridad.",author:"Proverbios 19:1"},
  {text:"El que confía en sus riquezas caerá.",author:"Proverbios 11:28"},
  {text:"La diligencia de los hombres es su riqueza preciosa.",author:"Proverbios 12:27"},
  {text:"Quien halla esposa halla el bien y alcanza el favor del Señor.",author:"Proverbios 18:22"},
  {text:"El hombre que reprime sus labios actúa con prudencia.",author:"Proverbios 10:19"},
  {text:"La palabra del Señor permanece para siempre.",author:"1 Pedro 1:25"},
  {text:"El rico se hace el pobre pero tiene mucha riqueza.",author:"Proverbios 13:7"},
  {text:"El perezoso no atrapa su presa, pero el diligente ya tiene su riqueza.",author:"Proverbios 12:27"},
  {text:"Toda la gloria del hombre es como la flor del campo que se seca.",author:"1 Pedro 1:24"},
  {text:"Obedece al Señor y tendrás una larga vida.",author:"Proverbios 3:2"},
  {text:"El camino del perezoso está plagado de espinas.",author:"Proverbios 15:19"},
  {text:"El hombre justo tiene cuidado de la vida de su animal.",author:"Proverbios 12:10"},
  {text:"Cada uno cosecha lo que siembra.",author:"Gálatas 6:7"},
  {text:"El que confía en el Señor prospera.",author:"Proverbios 28:25"},
  {text:"En toda labor hay fruto, pero las vanas palabras solo traen pobreza.",author:"Proverbios 14:23"},
  {text:"Aunque caiga siete veces, se levantará.",author:"Proverbios 24:16"},
  {text:"El hombre se alegra con la respuesta de su boca; y la palabra a su tiempo, ¡cuán buena es!",author:"Proverbios 15:23"},
  {text:"Sin visión el pueblo perece, pero el que guarda la ley es bienaventurado.",author:"Proverbios 29:18"},
  {text:"El principio de la sabiduría es el temor del Señor.",author:"Proverbios 9:10"},
  {text:"No se cansen de hacer el bien, porque a su debido tiempo cosecharán si no se dan por vencidos.",author:"Gálatas 6:9"},
  {text:"El valiente no es el que no siente miedo, sino el que lo conquista.",author:"1 Macabeos 3:19"},
  {text:"Pon en manos del Señor todas tus obras y tus planes tendrán éxito.",author:"Proverbios 16:3"},
  {text:"El hierro se afila con el hierro; así el hombre forja su carácter en contacto con otro.",author:"Proverbios 27:17"},
  {text:"Fía tu suerte al Señor, confía en él, y él actuará.",author:"Salmos 37:5"},
  {text:"Mucho produce la fuerza del brazo, pero más el hombre que confía en Dios.",author:"Proverbios 21:31"},
  {text:"Todo lo que tu mano encuentre para hacer, hazlo con toda tu fuerza.",author:"Eclesiastés 9:10"},
  {text:"No temas, porque yo estoy contigo. No te angusties, porque yo soy tu Dios.",author:"Isaías 41:10"},
  {text:"El que refrena su lengua protege su vida.",author:"Proverbios 13:3"},
  {text:"Fuertes son tus brazos; poderosa es tu mano.",author:"Salmos 89:13"},
  {text:"El que busca el bien buscará el favor del Señor.",author:"Proverbios 11:27"},
  {text:"Esfuérzate y sé valiente; no temas ni desmayes.",author:"Josué 1:9"},
  {text:"El que da al pobre no le faltará nada.",author:"Proverbios 28:27"},
  {text:"La integridad de los rectos los encaminará.",author:"Proverbios 11:3"},
  {text:"Confía en el Señor de todo corazón y él enderezará tus veredas.",author:"Proverbios 3:6"},
  {text:"No te venzas del mal, sino vence el mal con el bien.",author:"Romanos 12:21"},
  {text:"El justo cae siete veces y vuelve a levantarse.",author:"Proverbios 24:16"},
  {text:"Al hombre de bien la misericordia no le abandona.",author:"Proverbios 3:3"},
  {text:"El trabajo del hombre trabajará para él.",author:"Proverbios 12:14"},
  // ── 10 nuevas entradas bíblicas ──
  {text:"Fortaleceos en el Señor y en el poder de su fuerza.",author:"Efesios 6:10"},
  {text:"El Señor es mi luz y mi salvación; ¿a quién temeré?",author:"Salmos 27:1"},
  {text:"Porque somos hechura suya, creados en Cristo Jesús para buenas obras.",author:"Efesios 2:10"},
  {text:"Mejor es adquirir sabiduría que oro, y adquirir inteligencia vale más que la plata.",author:"Proverbios 16:16"},
  {text:"No os conforméis a este siglo, sino transformaos por medio de la renovación de vuestro entendimiento.",author:"Romanos 12:2"},
  {text:"El que cuida su boca y su lengua, guarda su alma de angustias.",author:"Proverbios 21:23"},
  {text:"Sean fuertes y valientes. No teman ni se asusten ante ellas.",author:"Deuteronomio 31:6"},
  {text:"El hombre perezoso desea, pero no tiene nada; en cambio, los diligentes son bien recompensados.",author:"Proverbios 13:4"},
  {text:"Pedid y se os dará; buscad y hallaréis; llamad y se os abrirá.",author:"Mateo 7:7"},
  {text:"Todo lo que es verdadero, todo lo honesto, todo lo justo, pensad en estas cosas.",author:"Filipenses 4:8"},
  // ── nuevas citas bíblicas ──
  {text:"¿No sabéis que sois templo de Dios y que el Espíritu de Dios mora en vosotros?",author:"1 Corintios 3:16"},
  {text:"Así que mi Dios suplirá todo lo que necesitéis conforme a sus riquezas en gloria en Cristo Jesús.",author:"Filipenses 4:19"},
  {text:"El que labra su tierra se saciará de pan; el que sigue a los holgazanes carece de juicio.",author:"Proverbios 12:11"},
  {text:"Mas buscad primeramente el reino de Dios y su justicia, y todas estas cosas os serán añadidas.",author:"Mateo 6:33"},
  {text:"No os afanéis por el mañana, porque el mañana traerá su propio afán. Basta al día su propio mal.",author:"Mateo 6:34"},
  {text:"Seréis libres de verdad.",author:"Juan 8:32"},
  {text:"El que es fiel en las cosas pequeñas, también lo es en las grandes.",author:"Lucas 16:10"},
  {text:"Vestíos de toda la armadura de Dios para que podáis estar firmes contra las asechanzas del diablo.",author:"Efesios 6:11"},
];

const MOTIVATIONAL = [
  {text:"Stay hard. You have to do what you hate to be great.",author:"David Goggins"},
  {text:"Don't stop when you're tired. Stop when you're done.",author:"David Goggins"},
  {text:"The only way to grow is to go beyond what's comfortable.",author:"David Goggins"},
  {text:"There's no talent here, this is hard work. This is an obsession.",author:"Conor McGregor"},
  {text:"I am not talented, I am obsessed.",author:"Conor McGregor"},
  {text:"Work like there is someone working 24 hours a day to take it all away.",author:"Mark Cuban"},
  {text:"The man who has no imagination has no wings.",author:"Muhammad Ali"},
  {text:"Impossible is just a word thrown around by small men.",author:"Muhammad Ali"},
  {text:"Discipline is the bridge between goals and accomplishment.",author:"Jim Rohn"},
  {text:"You are the average of the five people you spend the most time with.",author:"Jim Rohn"},
  {text:"The mind is the limit.",author:"Arnold Schwarzenegger"},
  {text:"You can have results or excuses. Not both.",author:"Arnold Schwarzenegger"},
  {text:"Suffer the pain of discipline or suffer the pain of regret.",author:"Khabib Nurmagomedov"},
  {text:"The temporary comfort of giving up is not worth the permanent regret.",author:"Elon Musk"},
  {text:"Absorb what is useful, discard what is useless, add what is specifically your own.",author:"Bruce Lee"},
  {text:"The successful warrior is the average man with laser-like focus.",author:"Bruce Lee"},
  {text:"Pain unlocks a secret doorway in the mind that leads to peak performance.",author:"David Goggins"},
  {text:"Winners focus on winning. Losers focus on winners.",author:"Conor McGregor"},
  {text:"If you want to be the best, you have to do things others aren't willing to do.",author:"Khabib Nurmagomedov"},
  {text:"Time is the most valuable thing a man can spend.",author:"Theophrastus"},
  {text:"Entreno como si el título dependiera de cada sesión. Porque así es.",author:"Ilia Topuria"},
  {text:"Soy el mejor del mundo en lo que hago porque no paro cuando duele. Paro cuando termino.",author:"Ilia Topuria"},
  {text:"La mentalidad del campeón es simple: no hay excusas, solo resultados.",author:"Ilia Topuria"},
  {text:"Cuando todos duermen, yo trabajo. Esa es la diferencia.",author:"Ilia Topuria"},
  {text:"Nadie te regala nada. O lo construyes con sacrificio o simplemente no lo tienes.",author:"Ilia Topuria"},
  {text:"Champions aren't made in gyms. Champions are made from something deep inside.",author:"Muhammad Ali"},
  {text:"I fear not the man who has practiced 10,000 kicks once, but the man who has practiced one kick 10,000 times.",author:"Bruce Lee"},
  {text:"It's not the size of the dog in the fight, it's the size of the fight in the dog.",author:"Georges St-Pierre"},
  {text:"I never lost a fight. Time just ran out.",author:"Georges St-Pierre"},
  {text:"A lion doesn't concern himself with the opinions of sheep.",author:"Jon Jones"},
  {text:"The only way out is through. No shortcuts, no detours.",author:"Francis Ngannou"},
  {text:"Pain is just weakness leaving the body. Embrace it.",author:"Israel Adesanya"},
  {text:"The more you sweat in training, the less you bleed in battle.",author:"Cus D'Amato"},
  {text:"Everyone has a plan until they get punched in the face. Prepare for that punch every day.",author:"Mike Tyson"},
  {text:"I am not in this sport to be liked. I am here to be the best.",author:"Khabib Nurmagomedov"},
  {text:"El éxito no es el resultado de la suerte. Es el resultado de elegir el sacrificio cuando otros eligen el confort.",author:"Ilia Topuria"},
  {text:"Hard times create strong men. Strong men create good times.",author:"G. Michael Hopf"},
  {text:"Your body can stand almost anything. It's your mind you have to convince.",author:"David Goggins"},
  {text:"Wealth is not about having a lot of money; it's about having a lot of options.",author:"Chris Rock"},
  {text:"Si no estás dispuesto a sufrir, no estás dispuesto a ganar.",author:"Khabib Nurmagomedov"},
  {text:"El dinero no compra la felicidad, pero compra libertad. Y la libertad puede comprar felicidad.",author:"Naval Ravikant"},
  {text:"The man who has no imagination has no wings.",author:"Muhammad Ali"},
  {text:"Compound interest is the eighth wonder of the world. He who understands it, earns it.",author:"Albert Einstein"},
  {text:"Discipline equals freedom.",author:"Jocko Willink"},
  {text:"La mente es tu arma más poderosa. Entrénala antes que el cuerpo.",author:"Ilia Topuria"},
  // ── 10 nuevas citas motivacionales ──
  {text:"Every champion was once a contender who refused to give up.",author:"Rocky Balboa"},
  {text:"It's not about how hard you hit. It's about how hard you can get hit and keep moving forward.",author:"Rocky Balboa"},
  {text:"El éxito es la suma de pequeños esfuerzos repetidos día tras día.",author:"Robert Collier"},
  {text:"Don't count the days. Make the days count.",author:"Muhammad Ali"},
  {text:"The fight is won or lost far away from witnesses — behind the lines, in the gym, out there on the road.",author:"Muhammad Ali"},
  {text:"Si quieres ser el mejor, tienes que hacer cosas que la gente normal no está dispuesta a hacer.",author:"Khabib Nurmagomedov"},
  {text:"Money is a tool. Used properly it makes something beautiful; used wrong, it makes a mess.",author:"Bradley Vinson"},
  {text:"Rich people have small TVs and big libraries. Poor people have small libraries and big TVs.",author:"Zig Ziglar"},
  {text:"La consistencia es lo que transforma el talento promedio en resultados extraordinarios.",author:"Ilia Topuria"},
  {text:"Wake up determined. Go to bed satisfied.",author:"Georges St-Pierre"},
];

const DAILY_TIPS = [
  {cat:"💪 Gym", tip:"Pesa siempre la proteína ANTES de cocinar. El salmón y el pollo pierden hasta un 30% de peso al cocinarse."},
  {cat:"🧠 Mente", tip:"Los primeros 30 minutos del día deciden tu energía mental. Sin móvil al despertar, siempre."},
  {cat:"😴 Sueño", tip:"El 80% de la testosterona se produce en sueño profundo. La weed nocturna destruye esa fase."},
  {cat:"💰 Dinero", tip:"Págate a ti primero. Aparta el ahorro el día que cobras, no con lo que sobre."},
  {cat:"🥊 MMA", tip:"En los primeros 6 meses no pienses en ganar. Solo en guardia, distancia y timing."},
  {cat:"🍽️ Nutrición", tip:"Si no llegas a las calorías, añade crema de maní o aguacate. Son densas sin volumen."},
  {cat:"🧘 Recuperación", tip:"El descanso no es debilidad. Es donde el músculo crece. Entrenar sin recuperar es retroceder."},
  {cat:"⚡ Energía", tip:"Deshidratación del 2% reduce rendimiento físico y cognitivo un 15%. Bebe antes de tener sed."},
  {cat:"🧠 Dopamina", tip:"Cada vez que resistes abrir el móvil sin razón, reconstruyes tu sistema de recompensa."},
  {cat:"💰 Deudas", tip:"Ataca la deuda con mayor interés primero. Es imposible enriquecerse pagando 12% anual."},
  {cat:"🏃 Cardio", tip:"8 sprints de 40m generan más hormona de crecimiento que 45 minutos de cardio continuo."},
  {cat:"🍗 Proteína", tip:"Distribuye la proteína en 4-5 tomas. La síntesis proteica dura ~4h. Si pasas más sin proteína, paras de construir."},
  {cat:"💊 Zinc", tip:"El zinc regula la testosterona. Los deportistas que sudan mucho suelen tener deficiencia."},
  {cat:"🏋️ Técnica", tip:"Técnica perfecta con poco peso construye más músculo que mucho peso con mala técnica."},
  {cat:"🌙 Sueño", tip:"18-20°C es la temperatura óptima para dormir y producir hormona de crecimiento."},
  {cat:"💰 Inversión", tip:"El interés compuesto es la octava maravilla del mundo. El que lo entiende, lo gana."},
  {cat:"🥊 Posición", tip:"La base del MMA es la posición. No importa cuánto sepas si te sacan de posición."},
  {cat:"🧠 Hábitos", tip:"Un hábito tiene tres partes: señal, rutina y recompensa. Para cambiar uno, cambia la rutina."},
  {cat:"💪 Core", tip:"La plancha y el Pallof son superiores a los crunches para un core funcional en combate."},
  {cat:"🥑 Grasas", tip:"Las grasas saludables son esenciales para la testosterona. No las elimines nunca."},
  {cat:"🥊 Muay Thai", tip:"El teep (patada frontal) es tu arma más importante para controlar la distancia. Entrénalo 100 veces por sesión."},
  {cat:"💪 Volumen", tip:"Para ganar músculo necesitas entre 10-20 series por grupo por semana. Menos de 10 es mantenimiento."},
  {cat:"🧠 Foco", tip:"Elimina decisiones por la mañana: la ropa, la comida y el entrenamiento ya deben estar decididos la noche anterior."},
  {cat:"😴 Recuperación", tip:"Una siesta de 20 minutos restaura el rendimiento cognitivo y físico casi al nivel de 8h de sueño. No más de 30min o entras en sueño profundo."},
  {cat:"⚡ Creatina", tip:"La creatina funciona mejor tomada cerca del entreno (pre o post). Con el tiempo los resultados son los mismos, pero la consistencia es lo único que importa."},
  {cat:"🍗 Timing", tip:"Consume carbohidratos ANTES y proteína DESPUÉS del entreno. Los carbos dan energía, la proteína inicia la síntesis."},
  {cat:"🥊 Clinch", tip:"En el clinch de Muay Thai, la posición del cuello manda. El que controla la cabeza controla el combate."},
  {cat:"💪 Progresión", tip:"Aumenta el peso solo cuando completes todas las series y repeticiones con buena técnica. La progresión forzada genera lesiones."},
  {cat:"🧠 Identidad", tip:"No digas 'quiero tener disciplina'. Di 'soy alguien disciplinado'. La identidad precede al comportamiento."},
  {cat:"💊 Vitamina D", tip:"El 80% de la población tiene déficit de vitamina D. Regula testosterona, humor e inmunidad. Toma 2000-4000 UI diarias."},
  {cat:"🔥 Metabolismo", tip:"Comer más veces NO acelera el metabolismo. Lo que acelera el metabolismo es el músculo. Más músculo = más calorías en reposo."},
  {cat:"🥊 Sparring", tip:"En el sparring, el objetivo es aprender, no ganar. El que intenta ganar en el sparring, pierde en el combate."},
  {cat:"💰 Mentalidad", tip:"Gasta menos de lo que ganas. Invierte la diferencia. Repite durante años. Así funciona la riqueza real."},
  {cat:"🏋️ Fuerza", tip:"Los 3 levantamientos más importantes para MMA son: peso muerto, sentadilla y press militar. Construyen fuerza funcional real."},
  {cat:"🧘 Cortisol", tip:"El estrés crónico eleva el cortisol que destruye músculo y almacena grasa. La meditación, el sol y el sueño lo bajan."},
  {cat:"💪 Isquios", tip:"Entrena peso muerto rumano 2 veces por semana. Los isquiotibiales son el músculo más lesionado en deportes de combate y el más olvidado en el gym."},
  {cat:"🧠 Dopamina", tip:"El scroll en redes sociales activa dopamina sin esfuerzo. Tu cerebro aprende que no necesita trabajar para sentirse bien. Limita el consumo a momentos fijos del día."},
  {cat:"💰 Activos", tip:"Invierte antes de gastar en lujos. Un activo te paga mientras duermes. Un lujo solo te hace sentir bien hoy."},
  {cat:"🥊 Guardia", tip:"Tu guardia en MMA define tu distancia. Sin guardia consistente, todo lo demás falla. Practica con foco en guardia aunque no golpees."},
  {cat:"😴 Pantalla", tip:"La luz azul del móvil retrasa la producción de melatonina hasta 2 horas. Modo nocturno o sin pantallas desde las 21h."},
  {cat:"🍗 Ayuno", tip:"Si entrenas en ayunas, toma proteína en los 30 minutos post-entreno. El catabolismo es real cuando no hay aminoácidos disponibles."},
  {cat:"💊 Magnesio", tip:"El magnesio glicinato antes de dormir mejora la calidad del sueño profundo y reduce el cortisol nocturno. Es el suplemento nocturno más infravalorado."},
  {cat:"🏋️ Descanso", tip:"Los músculos crecen en el descanso, no en el entreno. Respetar el descanso entre series (2-3 min en fuerza, 1 min en hipertrofia) marca la diferencia."},
  {cat:"💰 Mentalidad", tip:"Trata cada euro como un empleado: ¿está trabajando para ti o solo pasa por tus manos? El control del dinero empieza en el gasto consciente."},
  {cat:"🥊 Cardio MMA", tip:"El cardio más específico para MMA son los rounds de sombra de 5 minutos. Simula el ritmo real del combate mejor que cualquier máquina."},
  // ── 10 nuevos consejos diarios ──
  {cat:"💪 Gym", tip:"El calentamiento no es opcional. 10 minutos de movilidad articular y activación previenen el 60% de las lesiones más comunes en el gym."},
  {cat:"🥊 MMA", tip:"La defensa de derribos es la habilidad que más separa a los amateurs de los profesionales. Practica la postura de triple amenaza cada sesión."},
  {cat:"💰 Finanzas", tip:"Automatiza tus ahorros. Si tienes que decidir ahorrar cada mes, vas a fallar. Si el dinero desaparece solo hacia tu cuenta de inversión, nunca lo echarás de menos."},
  {cat:"😴 Sueño", tip:"Mantén el mismo horario de sueño los 7 días de la semana, incluso los fines de semana. El cambio de horario destruye el ritmo circadiano igual que un jet lag."},
  {cat:"🧠 Dopamina", tip:"Tras una sesión de gym o entreno duro, tienes una ventana de 4-6 horas con dopamina y norepinefrina elevadas. Usa ese tiempo para trabajo creativo o estudio, no para el móvil."},
  {cat:"🍗 Nutrición", tip:"Las legumbres (lentejas, garbanzos, habichuelas) combinan proteína vegetal con fibra y carbos complejos. Son el alimento más infravalorado para composición corporal."},
  {cat:"💊 Suplementos", tip:"La cafeína funciona mejor en ayunas y antes del entreno. Después de las 14h interfiere con el sueño hasta 12h. Un mal sueño borra todos los beneficios del entreno."},
  {cat:"🥊 Defensa", tip:"En MMA, el ángulo lo es todo. Nunca retrocedas en línea recta. Muévete en ángulo después de cada golpe para salir de la trayectoria del contra."},
  {cat:"💰 Mentalidad", tip:"No compres cosas que no necesitas con dinero que no tienes para impresionar a gente que no te importa. La libertad financiera empieza con esa frase."},
  {cat:"🏋️ Recuperación", tip:"El foam roller antes de dormir relaja la fascia muscular, reduce el DOMS al día siguiente y mejora la calidad del sueño. 5 minutos de rodillo es la inversión en recuperación más barata que existe."},
  // ── tips de mentalidad, confianza y peleador ──
  {cat:"🔥 Mentalidad", tip:"La confianza no viene de lo que dices. Viene de los compromisos que te has cumplido a ti mismo. Cada vez que te prometiste algo y lo hiciste, se acumula. Cada vez que no lo hiciste, también se acumula."},
  {cat:"🔥 Mentalidad", tip:"No expliques tus movimientos. Actúa, luego habla con resultados. El que anuncia constantemente lo que va a hacer, casi nunca lo hace. Los perros que ladran no muerden."},
  {cat:"🔥 Mentalidad", tip:"Tu círculo decide tu techo. Si los 5 más cercanos no te desafían, no te exigen y no te hacen mejor — o los elevas o te alejas. No hay término medio. El ambiente manda más que la voluntad."},
  {cat:"🔥 Mentalidad", tip:"Cuando entras a una habitación, entra. Postura, mirada al frente, sin prisa. El lenguaje corporal decide cómo te tratan antes de que abras la boca. Nunca entres encogido."},
  {cat:"🔥 Mentalidad", tip:"El hombre fuerte no reacciona: responde. La reacción es instinto sin control. La respuesta es inteligencia bajo presión. Esa diferencia se entrena igual que un jab. Rep a rep."},
  {cat:"🥊 Peleador", tip:"El hombre que sabe pelear no es libre porque pelea. Es libre porque no tiene miedo. Saber que puedes defenderte cambia cómo caminas, cómo hablas y cómo te paras frente a cualquiera."},
  {cat:"🥊 Peleador", tip:"En el sparring te conoces de verdad. El miedo, el orgullo, la cobardía, la calma — todo sale sin filtro. Úsalo como espejo para construirte, no como excusa para parar de entrenar."},
  {cat:"🥊 Peleador", tip:"Aprende a que te golpeen y a seguir. Un hombre que encaja y no se rompe tiene una ventaja psicológica real sobre cualquiera que nunca ha estado en ese lugar. El dolor enseña más que el éxito."},
  {cat:"🔥 Mentalidad", tip:"Los hombres débiles se quejan del mundo. Los hombres fuertes se adaptan y actúan. El mundo no te debe absolutamente nada. El mundo responde a lo que tú proyectas y lo que tú produces."},
  {cat:"💪 Gym", tip:"Haz 3 segundos de fase excéntrica (la bajada) en cada repetición. Esa velocidad controlada genera más daño muscular y más hipertrofia que subir y bajar rápido con el mismo peso."},
  {cat:"🥊 MMA", tip:"La clave del derribo no es la fuerza: es el timing. Entra justo después de que el rival golpee y su peso esté comprometido hacia delante. Nunca cuando está equilibrado y listo."},
  {cat:"😴 Sueño", tip:"Si tardas menos de 5 minutos en dormirte, estás crónicamente privado de sueño. El tiempo normal para conciliar es 10-20 minutos. Si caes más rápido que eso, acuéstate antes."},
  {cat:"💰 Dinero", tip:"La diferencia entre rico y clase media no es el salario: es que el rico compra activos con cada ingreso extra. La clase media compra comodidades. Cada euro extra, decide: ¿activo o comodidad?"},
];

// ─── HABITS DATA ─────────────────────────────────────────────────────────────
// DEFAULT_HABITS: lista completa de hábitos del sistema. Cada hábito tiene
// un estado (obl=obligatorio, opt=opcional) que el usuario puede cambiar
// en Settings. El campo "why" explica el porqué científico o práctico.
// priority: "critical" = esencial (oro), "high" = importante (azul), omitido = normal
const DEFAULT_HABITS = [
  // ── Obligatorios por defecto ── CRÍTICOS (la base de todo)
  {id:"aguaDes",   label:"Agua al despertar",            icon:"🥤", obl:true,  priority:"critical", why:"Reactiva metabolismo y cerebro tras 7-8h sin hidratación. Antes del móvil, siempre."},
  {id:"sinMovM",   label:"Sin móvil 30min al despertar", icon:"📵", obl:true,  priority:"critical", why:"El móvil activa dopamina inmediata y pone el cerebro en modo reactivo. Los primeros 30min deciden tu día."},
  {id:"tender",    label:"Tender la cama",               icon:"🛏️", obl:true,  priority:"critical", why:"Primera victoria del día. Completa una tarea pequeña al levantarte y activas el sistema de recompensa."},
  {id:"agua",      label:"Agua 3.7L",                    icon:"💧", obl:true,  priority:"critical", why:"Hidratación = +15% rendimiento. Sin agua la proteína no llega al músculo. Tu orina debe ser clara."},
  {id:"tupper",    label:"Tupper preparado",             icon:"🍱", obl:true,  priority:"critical", why:"Tu única pausa en 8h de trabajo físico. Sin tupper no llegas a calorías. Prepáralo la noche anterior."},
  {id:"creatina",  label:"Creatina 5g",                  icon:"⚡", obl:true,  priority:"critical", why:"El suplemento más estudiado del mundo. Más ATP disponible = más fuerza, más músculo, mejor recuperación."},
  {id:"supl_m",    label:"Suplementos mañana",           icon:"💊", obl:true,  priority:"critical", why:"D3 + Omega3 + Ashwagandha + Zinc con el desayuno. La grasa del desayuno mejora la absorción."},
  {id:"dormir",    label:"Dormir a la hora objetivo",    icon:"😴", obl:true,  priority:"critical", why:"22h turno mañana / 3h turno tarde. El 80% de testosterona y HGH se producen durmiendo. Sin sueño no hay músculo."},
  {id:"sinMovN",   label:"Sin móvil 30min antes dormir", icon:"📴", obl:true,  priority:"critical", why:"Luz azul bloquea melatonina. Sin esto el sueño es siempre subóptimo aunque duermas 8h."},
  {id:"sinWeed",   label:"Sin weed antes dormir",        icon:"🚫", obl:true,  priority:"critical", why:"Destruye la fase REM donde se repara el músculo. Reduce testosterona 30-40% con uso nocturno frecuente."},
  // ── Obligatorios ── IMPORTANTES
  {id:"supl_n",    label:"Suplementos noche",            icon:"🌙", obl:true,  priority:"high",     why:"Magnesio 300mg antes de dormir. Mejora sueño profundo y producción de testosterona nocturna."},
  {id:"lavar",     label:"Lavar el plato al momento",    icon:"🍽️", obl:true,  priority:"high",     why:"Regla de los 2 minutos (Atomic Habits): si tarda menos de 2 min, hazlo ahora. Elimina fricción mental."},
  // ── Opcionales ── MI SELECCIÓN: los que más impacto tienen en tu perfil ──
  // ★★★ TOP — Máximo impacto (haz estos antes que cualquier otro opcional):
  {id:"hora_fija",    label:"Misma hora al despertar",          icon:"⏰", obl:false, priority:"high", why:"El ritmo circadiano necesita consistencia. Levantarte siempre a la misma hora (incluso fines de semana) es el factor más potente para mejorar calidad del sueño y energía diurna. Si solo haces un cambio en el sueño, que sea este."},
  {id:"desayuno_p",   label:"Desayuno proteico ≥30g",          icon:"🍳", obl:false, priority:"high", why:"Rompe el ayuno con ≥30g de proteína: huevos, yogur griego, batido de whey. Activa la síntesis muscular por la mañana y reduce el hambre el resto del día hasta un 40%."},
  {id:"deepwork",     label:"Deep work (tarea difícil primero)",icon:"🧠", obl:false, priority:"high", why:"Las primeras 2-3h tienes los niveles más altos de dopamina y cortisol. Ataca la tarea más difícil primero. Sin emails, sin redes, solo trabajo profundo. Una hora así vale por cuatro horas dispersas."},
  {id:"ducha_f",      label:"Ducha fría 30seg",                icon:"🚿", obl:false, priority:"high", why:"Al final de la ducha, abre el agua fría totalmente 30seg mínimo. Activa dopamina y norepinefrina de forma duradera. Más efectivo que el café. Con el tiempo entrenas la tolerancia al malestar."},
  {id:"sol",          label:"Exposición al sol 15min",         icon:"☀️", obl:false, priority:"high", why:"La luz solar matutina regula el ritmo circadiano, activa serotonina, mejora el humor y es el único estímulo que sincroniza todos tus relojes biológicos. 15min antes de las 10h."},
  {id:"planif_noche", label:"Organizar el día siguiente",      icon:"📋", obl:false, priority:"high", why:"5min antes de dormir: escribe tus 3 tareas prioritarias de mañana. Reduce la ansiedad nocturna (el cerebro deja de procesar tareas pendientes) y elimina la toma de decisiones matutina."},
  // ★★ ALTOS — Muy recomendables, añádelos progresivamente:
  {id:"mover_c",    label:"5-10min movimiento al despertar", icon:"🏃", obl:false, priority:"normal", why:"Movilidad, estiramientos o caminar activan el sistema linfático y suben la temperatura corporal. Acelera el estado de alerta mejor que el café en los primeros 10 minutos. Ideal antes del desayuno."},
  {id:"escribir",   label:"Escribir qué harás hoy",         icon:"✍️", obl:false, priority:"normal", why:"¿Qué 3 cosas harás hoy sí o sí? ¿Cómo quieres sentirte? Lo que escribes, lo revisas. Lo que revisas, mejora."},
  {id:"nosnooze",   label:"Sin snooze",                     icon:"⏰", obl:false, priority:"normal", why:"Primera decisión del día: ¿cumplo lo que me propuse? El snooze entrena el cerebro a rendirse ante el malestar. Ponlo al otro lado de la habitación."},
  {id:"leer",       label:"Leer 10-20min",                  icon:"📖", obl:false, timer:true, priority:"normal", why:"10min/día son 60h al año. Lee algo que te haga mejor en lo que más importa. Reentrena la atención sostenida que el scroll destruye."},
  {id:"gratitud",   label:"3 cosas de gratitud",            icon:"🙏", obl:false, priority:"normal", why:"Cerrar el día con gratitud refuerza el bucle del hábito. Lo que celebras, lo repites. El cerebro en modo agradecimiento genera más dopamina basal."},
  {id:"revision",   label:"Revisión del día 2min",          icon:"🔍", obl:false, priority:"normal", why:"¿Qué fue bien? ¿Qué mejorar mañana? El bucle de retroalimentación es el motor del hábito compuesto."},
  {id:"orden",      label:"5min orden habitación",          icon:"🏠", obl:false, priority:"normal", why:"Ambiente ordenado = mente ordenada. Reduce ruido mental. Mejora la calidad del sueño. Hazlo mientras se calienta la ducha."},
  // ★ OPCIONALES — Buenos hábitos, actívalos cuando ya domines los anteriores:
  {id:"medit",      label:"Meditación",                     icon:"🧘", obl:false, timer:true, why:"4s inhala, 4s aguanta, 4s exhala. 10 ciclos. Reduce cortisol matutino hasta un 20%. Mejora el foco durante horas."},
  {id:"caminar",    label:"Caminar sin auriculares",         icon:"🚶", obl:false, timer:true, why:"El cerebro necesita tiempo sin estímulos para procesar, consolidar memoria y generar ideas. Al menos 15min/día sin pantallas ni auriculares."},
  {id:"aburrir",    label:"Tiempo sin estímulos (aburrirte)",icon:"💭", obl:false, why:"La red neuronal por defecto necesita 'modo reposo' para consolidar memoria y generar creatividad. Mínimo 15min/día sin pantallas ni auriculares. Ducharte, caminar, sentarte y punto."},
  {id:"respira",    label:"Respiración Wim Hof",            icon:"🌬️", obl:false, why:"Tumbado: 30 respiraciones profundas y rápidas, exhala la 30 y aguanta sin aire lo máximo, inhala fuerte y aguanta 15s. 3 rondas. Activa el sistema nervioso simpático y reduce inflamación."},
  {id:"sin_redes",  label:"Sin redes sociales",             icon:"📱", obl:false, why:"Cada scroll activa el mismo circuito de recompensa que el juego. Los días sin redes la productividad sube un 26% (estudio 2018). Limítalas a momentos conscientes con tiempo fijo."},
  {id:"omega3",     label:"Omega-3 extra dosis",            icon:"🐟", obl:false, why:"Reduce inflamación articular. Con trabajo físico intenso y artes marciales, las articulaciones necesitan protección extra. 2g EPA+DHA/día."},
  {id:"noper",      label:"Sin pornografía",                icon:"🧲", obl:false, why:"Desensibiliza el sistema dopaminérgico, reduciendo motivación y capacidad de concentración. 90 días sin ello reorganiza los circuitos de recompensa del cerebro."},
  {id:"proteina_check",label:"Proteína ≥ 150g",            icon:"🥩", obl:false, why:"Sin suficiente proteína el músculo no crece sin importar cuánto entrenes. 2g/kg mínimo. Usa el tab Dieta para comprobarlo."},
];

// ─── MEAL_SLOTS ───────────────────────────────────────────────────────────────
// Franjas horarias de comida. Se usan para organizar alimentos en DietaTab.
// Cada franja tiene un id y etiqueta visible para el usuario.
const MEAL_SLOTS = [
  {id:"m1",label:"Desayuno"},{id:"m2",label:"Comida (Tupper)"},
  {id:"m3",label:"Pre-gym"},{id:"m4",label:"Post-gym"},
  {id:"m5",label:"Cena"},{id:"m6",label:"Snack"},
];
// ─── FOOD_CATS ────────────────────────────────────────────────────────────────
// Categorías de alimentos disponibles en el catálogo de la DietaTab.
// "Custom" es la categoría para alimentos añadidos por el usuario.
// "Comida rápida" agrupa snacks, fast food y alimentos ultraprocesados.
const FOOD_CATS = ["Carbos","Proteína","Lácteos","Grasas","Frutas","Verduras","Comida rápida","Custom"];

// ─── FOODS ──────────────────────────────────────────────────────────────────
// Base de datos de alimentos: todos los alimentos disponibles en el catálogo.
// Cada alimento tiene: id único, nombre, icono, categoría, unidad (g/ml/ud/scoop),
// y valores nutricionales POR UNIDAD (por gramo, ml, o por unidad entera).
const FOODS = [
  {id:"arroz_b",name:"Arroz blanco cocido",icon:"🍚",cat:"Carbos",unit:"g",cal:1.30,p:0.027,c:0.280,f:0.003,note:"cocido"},
  {id:"arroz_g",name:"Arroz con guandules",icon:"🍚",cat:"Carbos",unit:"g",cal:1.50,p:0.040,c:0.290,f:0.020,note:"cocido"},
  {id:"arroz_hab",name:"Arroz con habichuelas",icon:"🍚",cat:"Carbos",unit:"g",cal:1.45,p:0.055,c:0.275,f:0.018,note:"cocido"},
  {id:"arroz_m",name:"Arroz con maíz",icon:"🌽",cat:"Carbos",unit:"g",cal:1.45,p:0.035,c:0.290,f:0.015,note:"cocido"},
  {id:"platano_v",name:"Plátano verde cocido",icon:"🍌",cat:"Carbos",unit:"g",cal:1.16,p:0.015,c:0.270,f:0.003,note:"cocido"},
  {id:"platano_m",name:"Plátano maduro",icon:"🍌",cat:"Carbos",unit:"g",cal:0.89,p:0.011,c:0.230,f:0.003,note:"cocido"},
  {id:"tostones",name:"Tostones",icon:"🍌",cat:"Carbos",unit:"g",cal:1.95,p:0.015,c:0.290,f:0.080,note:"cocido"},
  {id:"mangu",name:"Mangú",icon:"🍌",cat:"Carbos",unit:"g",cal:1.10,p:0.012,c:0.250,f:0.025,note:"cocido"},
  {id:"yuca",name:"Yuca cocida",icon:"🥔",cat:"Carbos",unit:"g",cal:1.60,p:0.014,c:0.380,f:0.003,note:"cocido"},
  {id:"batata",name:"Batata / Camote",icon:"🍠",cat:"Carbos",unit:"g",cal:0.86,p:0.016,c:0.200,f:0.001,note:"cocido"},
  {id:"patata",name:"Patata cocida",icon:"🥔",cat:"Carbos",unit:"g",cal:0.77,p:0.020,c:0.170,f:0.001,note:"cocido"},
  {id:"avena",name:"Avena",icon:"🌾",cat:"Carbos",unit:"g",cal:3.89,p:0.170,c:0.660,f:0.070,note:"crudo"},
  {id:"banana",name:"Banana",icon:"🍌",cat:"Carbos",unit:"g",cal:0.89,p:0.011,c:0.230,f:0.003},
  {id:"pasta",name:"Pasta cocida",icon:"🍝",cat:"Carbos",unit:"g",cal:1.31,p:0.050,c:0.250,f:0.011,note:"cocido"},
  {id:"espaguetis_s",name:"Espaguetis secos",icon:"🍝",cat:"Carbos",unit:"g",cal:3.71,p:0.125,c:0.742,f:0.015,note:"crudo/seco"},
  {id:"macarrones_s",name:"Macarrones secos",icon:"🍝",cat:"Carbos",unit:"g",cal:3.71,p:0.120,c:0.744,f:0.014,note:"crudo/seco"},
  {id:"habich",name:"Habichuelas rojas",icon:"🫘",cat:"Carbos",unit:"g",cal:1.27,p:0.087,c:0.228,f:0.005,note:"cocido"},
  {id:"pan",name:"Pan integral",icon:"🍞",cat:"Carbos",unit:"g",cal:2.47,p:0.130,c:0.410,f:0.033},
  {id:"pollo_p",name:"Pechuga de pollo",icon:"🍗",cat:"Proteína",unit:"g",cal:1.65,p:0.310,c:0,f:0.036,note:"crudo"},
  {id:"pollo_mu",name:"Muslo de pollo",icon:"🍗",cat:"Proteína",unit:"g",cal:1.79,p:0.260,c:0,f:0.083,note:"crudo"},
  {id:"salmon",name:"Salmón",icon:"🐟",cat:"Proteína",unit:"g",cal:2.08,p:0.200,c:0,f:0.130,note:"crudo"},
  {id:"atun",name:"Atún en agua",icon:"🐟",cat:"Proteína",unit:"g",cal:1.16,p:0.260,c:0,f:0.010},
  {id:"carne",name:"Carne molida magra",icon:"🥩",cat:"Proteína",unit:"g",cal:2.18,p:0.260,c:0,f:0.120,note:"crudo"},
  {id:"merluza",name:"Merluza",icon:"🐠",cat:"Proteína",unit:"g",cal:0.86,p:0.170,c:0,f:0.017,note:"crudo"},
  {id:"pavo",name:"Pechuga de pavo",icon:"🦃",cat:"Proteína",unit:"g",cal:1.35,p:0.290,c:0,f:0.010,note:"crudo"},
  {id:"sardinas",name:"Sardinas en lata",icon:"🐟",cat:"Proteína",unit:"g",cal:2.08,p:0.245,c:0,f:0.114},
  {id:"huevo",name:"Huevo entero",icon:"🥚",cat:"Proteína",unit:"ud",cal:70,p:6.0,c:0.6,f:4.8},
  {id:"clara",name:"Clara de huevo",icon:"🥚",cat:"Proteína",unit:"ud",cal:17,p:3.6,c:0.2,f:0},
  {id:"whey",name:"Whey (1 scoop)",icon:"💪",cat:"Proteína",unit:"scoop",cal:120,p:25,c:3,f:1.5},
  {id:"yogur",name:"Yogur griego natural",icon:"🥛",cat:"Lácteos",unit:"g",cal:0.97,p:0.090,c:0.038,f:0.050},
  {id:"yogur_az",name:"Yogur griego azucarado (Hacendado)",icon:"🥛",cat:"Lácteos",unit:"g",cal:1.29,p:0.025,c:0.088,f:0.093},
  {id:"leche",name:"Leche semidesnatada",icon:"🥛",cat:"Lácteos",unit:"ml",cal:0.46,p:0.032,c:0.048,f:0.016},
  {id:"leche_ent",name:"Leche entera",icon:"🥛",cat:"Lácteos",unit:"ml",cal:0.61,p:0.033,c:0.047,f:0.033},
  {id:"leche_des",name:"Leche desnatada",icon:"🥛",cat:"Lácteos",unit:"ml",cal:0.35,p:0.033,c:0.050,f:0.001},
  {id:"requeson",name:"Requesón",icon:"🧀",cat:"Lácteos",unit:"g",cal:0.74,p:0.110,c:0.040,f:0.020},
  {id:"queso_bl",name:"Queso blanco dominicano",icon:"🧀",cat:"Lácteos",unit:"g",cal:3.20,p:0.200,c:0.010,f:0.260},
  {id:"cottage",name:"Queso cottage",icon:"🧀",cat:"Lácteos",unit:"g",cal:0.98,p:0.118,c:0.033,f:0.043},
  {id:"kefir",name:"Kéfir",icon:"🥛",cat:"Lácteos",unit:"ml",cal:0.61,p:0.033,c:0.047,f:0.033},
  {id:"aguacate",name:"Aguacate",icon:"🥑",cat:"Grasas",unit:"g",cal:1.60,p:0.020,c:0.085,f:0.146},
  {id:"almendras",name:"Almendras",icon:"🌰",cat:"Grasas",unit:"g",cal:5.79,p:0.210,c:0.220,f:0.500},
  {id:"mani",name:"Crema de maní",icon:"🥜",cat:"Grasas",unit:"g",cal:5.88,p:0.250,c:0.200,f:0.500},
  {id:"nueces",name:"Nueces",icon:"🌰",cat:"Grasas",unit:"g",cal:6.54,p:0.153,c:0.138,f:0.654},
  {id:"aceite",name:"Aceite de oliva",icon:"🫒",cat:"Grasas",unit:"ml",cal:8.84,p:0,c:0,f:1},
  {id:"manzana",name:"Manzana",icon:"🍎",cat:"Frutas",unit:"g",cal:0.52,p:0.003,c:0.140,f:0.002},
  {id:"naranja",name:"Naranja",icon:"🍊",cat:"Frutas",unit:"g",cal:0.47,p:0.009,c:0.120,f:0.001},
  {id:"fresas",name:"Fresas",icon:"🍓",cat:"Frutas",unit:"g",cal:0.32,p:0.007,c:0.077,f:0.003},
  {id:"mango",name:"Mango",icon:"🥭",cat:"Frutas",unit:"g",cal:0.60,p:0.008,c:0.150,f:0.004},
  {id:"kiwi",name:"Kiwi",icon:"🥝",cat:"Frutas",unit:"g",cal:0.61,p:0.011,c:0.150,f:0.005},
  {id:"arandanos",name:"Arándanos",icon:"🫐",cat:"Frutas",unit:"g",cal:0.57,p:0.007,c:0.145,f:0.003},
  {id:"espinacas",name:"Espinacas",icon:"🥬",cat:"Verduras",unit:"g",cal:0.23,p:0.029,c:0.036,f:0.004},
  {id:"brocoli",name:"Brócoli",icon:"🥦",cat:"Verduras",unit:"g",cal:0.34,p:0.028,c:0.066,f:0.004},
  {id:"tomate",name:"Tomate",icon:"🍅",cat:"Verduras",unit:"g",cal:0.18,p:0.009,c:0.038,f:0.002},
  {id:"pepino",name:"Pepino",icon:"🥒",cat:"Verduras",unit:"g",cal:0.16,p:0.007,c:0.038,f:0.001},
  {id:"zanahoria",name:"Zanahoria",icon:"🥕",cat:"Verduras",unit:"g",cal:0.41,p:0.009,c:0.096,f:0.002},
  {id:"lechuga",name:"Lechuga",icon:"🥗",cat:"Verduras",unit:"g",cal:0.15,p:0.014,c:0.029,f:0.002},
  // ── Comida rápida ──
  {id:"pizza",name:"Pizza",icon:"🍕",cat:"Comida rápida",unit:"g",cal:2.66,p:0.110,c:0.290,f:0.100},
  {id:"burger",name:"Hamburguesa",icon:"🍔",cat:"Comida rápida",unit:"g",cal:2.95,p:0.130,c:0.240,f:0.150},
  {id:"hot_dog",name:"Hot dog",icon:"🌭",cat:"Comida rápida",unit:"g",cal:2.90,p:0.110,c:0.230,f:0.160},
  {id:"nuggets",name:"Nuggets de pollo",icon:"🍗",cat:"Comida rápida",unit:"g",cal:2.97,p:0.150,c:0.160,f:0.170},
  {id:"fries",name:"Patatas fritas",icon:"🍟",cat:"Comida rápida",unit:"g",cal:3.12,p:0.035,c:0.410,f:0.150},
  {id:"nachos",name:"Nachos",icon:"🌮",cat:"Comida rápida",unit:"g",cal:4.90,p:0.070,c:0.630,f:0.230},
  {id:"donuts",name:"Donut",icon:"🍩",cat:"Comida rápida",unit:"g",cal:4.00,p:0.050,c:0.500,f:0.190},
  {id:"choco_bar",name:"Chocolatina",icon:"🍫",cat:"Comida rápida",unit:"g",cal:5.46,p:0.060,c:0.594,f:0.300},
  {id:"cola",name:"Refresco cola",icon:"🥤",cat:"Comida rápida",unit:"ml",cal:0.42,p:0,c:0.105,f:0},
  {id:"icecream",name:"Helado (bola)",icon:"🍦",cat:"Comida rápida",unit:"g",cal:2.07,p:0.035,c:0.238,f:0.110},
  {id:"chips",name:"Patatas chips",icon:"🥔",cat:"Comida rápida",unit:"g",cal:5.36,p:0.069,c:0.526,f:0.349},
  {id:"croissant",name:"Croissant",icon:"🥐",cat:"Comida rápida",unit:"g",cal:4.06,p:0.082,c:0.457,f:0.210},
  // ── Embutidos y lácteos España ──
  {id:"lomo_emb",name:"Lomo Embuchado",icon:"🥩",cat:"Proteína",unit:"g",cal:2.00,p:0.380,c:0.005,f:0.050},
  {id:"jamon_pavo",name:"Jamón de Pavo (Hacendado)",icon:"🦃",cat:"Proteína",unit:"g",cal:0.80,p:0.170,c:0.010,f:0.010},
  {id:"queso_ligero",name:"Queso Ligero Blanco",icon:"🧀",cat:"Lácteos",unit:"g",cal:2.00,p:0.260,c:0.015,f:0.100},
  {id:"queso_cheddar",name:"Queso Cheddar (Hacendado)",icon:"🧀",cat:"Lácteos",unit:"g",cal:3.92,p:0.250,c:0.005,f:0.330},
  {id:"pan_bl",name:"Pan Blanco",icon:"🍞",cat:"Carbos",unit:"g",cal:2.60,p:0.080,c:0.520,f:0.015},
  {id:"baguette",name:"Baguette Vienesa",icon:"🥖",cat:"Carbos",unit:"g",cal:2.90,p:0.090,c:0.500,f:0.050},
  {id:"ternera_m",name:"Ternera Magra (guisar)",icon:"🥩",cat:"Proteína",unit:"g",cal:1.30,p:0.220,c:0,f:0.040,note:"crudo"},
  {id:"bistec",name:"Bistec de Ternera",icon:"🥩",cat:"Proteína",unit:"g",cal:1.60,p:0.260,c:0,f:0.060,note:"crudo"},
  {id:"queso_freir",name:"Queso Blanco Freír (Latino)",icon:"🧀",cat:"Lácteos",unit:"g",cal:3.20,p:0.220,c:0.020,f:0.250},
  {id:"pollo_emp",name:"Pollo Empanado",icon:"🍗",cat:"Comida rápida",unit:"g",cal:2.50,p:0.200,c:0.150,f:0.120},
  {id:"salami",name:"Salami Induveca",icon:"🥩",cat:"Proteína",unit:"g",cal:3.10,p:0.160,c:0.030,f:0.260},
  {id:"pistachos",name:"Pistachos",icon:"🌰",cat:"Grasas",unit:"g",cal:5.60,p:0.200,c:0.270,f:0.450},
  {id:"pina",name:"Piña",icon:"🍍",cat:"Frutas",unit:"g",cal:0.50,p:0.005,c:0.130,f:0.001},
  {id:"smash_burger",name:"Smash Burger (completo)",icon:"🍔",cat:"Comida rápida",unit:"ud",cal:750,p:35,c:40,f:45},
  {id:"guineo_v",name:"Guineo Verde (crudo)",icon:"🍌",cat:"Carbos",unit:"g",cal:0.95,p:0.012,c:0.230,f:0.002,note:"crudo"},
  // ── Árabe ──
  {id:"hummus",name:"Hummus",icon:"🫘",cat:"Carbos",unit:"g",cal:1.66,p:0.077,c:0.143,f:0.097},
  {id:"falafel",name:"Falafel",icon:"🧆",cat:"Carbos",unit:"g",cal:3.33,p:0.130,c:0.320,f:0.180},
  {id:"pita",name:"Pan pita",icon:"🫓",cat:"Carbos",unit:"g",cal:2.75,p:0.092,c:0.554,f:0.012},
  {id:"lentil",name:"Lentejas cocidas",icon:"🫘",cat:"Carbos",unit:"g",cal:1.16,p:0.090,c:0.200,f:0.004},
  {id:"dates",name:"Dátiles",icon:"🫙",cat:"Frutas",unit:"g",cal:2.77,p:0.019,c:0.750,f:0.004},
  {id:"lamb",name:"Cordero magro",icon:"🥩",cat:"Proteína",unit:"g",cal:2.58,p:0.260,c:0,f:0.160,note:"crudo"},
  {id:"shawarma_c",name:"Pollo shawarma",icon:"🍗",cat:"Proteína",unit:"g",cal:1.85,p:0.220,c:0.050,f:0.085},
  {id:"tahini",name:"Tahini",icon:"🫙",cat:"Grasas",unit:"g",cal:5.95,p:0.170,c:0.260,f:0.538},
  {id:"labneh",name:"Labneh",icon:"🧀",cat:"Lácteos",unit:"g",cal:0.97,p:0.095,c:0.038,f:0.055},
  // ── Dominicana (nuevos) ──
  {id:"chivo",name:"Chivo guisado",icon:"🐐",cat:"Proteína",unit:"g",cal:2.15,p:0.270,c:0.010,f:0.110,note:"cocido"},
  {id:"longaniza_d",name:"Longaniza dominicana",icon:"🌭",cat:"Proteína",unit:"g",cal:3.20,p:0.150,c:0.030,f:0.280},
  {id:"chicharron",name:"Chicharrón de pollo",icon:"🍗",cat:"Comida rápida",unit:"g",cal:3.40,p:0.180,c:0.100,f:0.250},
  {id:"habich_n",name:"Habichuelas negras",icon:"🫘",cat:"Carbos",unit:"g",cal:1.32,p:0.089,c:0.234,f:0.005,note:"cocido"},
  {id:"auyama",name:"Auyama (calabaza)",icon:"🎃",cat:"Verduras",unit:"g",cal:0.26,p:0.010,c:0.065,f:0.001,note:"cocida"},
  {id:"name",name:"Ñame cocido",icon:"🥔",cat:"Carbos",unit:"g",cal:1.18,p:0.015,c:0.278,f:0.002,note:"cocido"},
  {id:"maiz_g",name:"Maíz desgranado",icon:"🌽",cat:"Carbos",unit:"g",cal:0.86,p:0.032,c:0.187,f:0.013},
  {id:"bacalao_g",name:"Bacalao guisado",icon:"🐟",cat:"Proteína",unit:"g",cal:1.05,p:0.230,c:0.040,f:0.020,note:"cocido"},
  {id:"sancocho",name:"Sancocho (ración)",icon:"🍲",cat:"Comida rápida",unit:"ud",cal:350,p:22,c:32,f:8,note:"ración ~400g"},
  {id:"locrio_p",name:"Locrio de pollo",icon:"🍚",cat:"Carbos",unit:"g",cal:1.60,p:0.090,c:0.220,f:0.045,note:"cocido"},
  // ── España / Mercadona (nuevos) ──
  {id:"skyr",name:"Skyr natural (Hacendado)",icon:"🥛",cat:"Lácteos",unit:"g",cal:0.59,p:0.110,c:0.040,f:0.001},
  {id:"garbanzo",name:"Garbanzos cocidos",icon:"🫘",cat:"Carbos",unit:"g",cal:1.64,p:0.088,c:0.275,f:0.026,note:"cocido/bote"},
  {id:"jamon_serrano",name:"Jamón serrano",icon:"🥩",cat:"Proteína",unit:"g",cal:2.50,p:0.310,c:0.005,f:0.130},
  {id:"wrap_trigo",name:"Wrap de trigo",icon:"🫓",cat:"Carbos",unit:"g",cal:3.00,p:0.094,c:0.540,f:0.050},
  {id:"beb_avena",name:"Bebida de avena",icon:"🥛",cat:"Lácteos",unit:"ml",cal:0.45,p:0.010,c:0.090,f:0.015},
  {id:"tortita_arroz",name:"Tortitas de arroz",icon:"🫓",cat:"Carbos",unit:"g",cal:3.87,p:0.080,c:0.820,f:0.028},
  {id:"aceitunas",name:"Aceitunas verdes",icon:"🫒",cat:"Grasas",unit:"g",cal:1.45,p:0.010,c:0.040,f:0.150},
  {id:"sardina_tom",name:"Sardinillas en tomate",icon:"🐟",cat:"Proteína",unit:"g",cal:1.10,p:0.140,c:0.030,f:0.055},
  {id:"barra_prot",name:"Barrita proteica (Mercadona)",icon:"💪",cat:"Proteína",unit:"ud",cal:200,p:20,c:22,f:6},
  {id:"queso_manchego",name:"Queso Manchego curado",icon:"🧀",cat:"Lácteos",unit:"g",cal:3.95,p:0.270,c:0.005,f:0.330},
];

// ─── MULTI-USER: COUNTRY FOOD PRESETS ────────────────────────────────────────
// 20 alimentos representativos por cultura (IDs de la base FOODS).
// Se usan en el onboarding y en la pantalla de favoritos de cada usuario.
const COUNTRY_PRESETS = {
  dominicana: { label:"🇩🇴 Dominicana", foodIds:["arroz_b","habich","yuca","platano_v","platano_m","pollo_p","atun","huevo","queso_bl","aguacate","leche_ent","naranja","mango","tomate","espinacas","arroz_hab","tostones","carne","kefir","brocoli"] },
  espana:     { label:"🇪🇸 España",     foodIds:["arroz_b","pasta","pan","pollo_p","salmon","merluza","atun","sardinas","aceite","huevo","yogur","leche","manzana","naranja","brocoli","macarrones_s","espaguetis_s","tomate","almendras","cottage"] },
  italia:     { label:"🇮🇹 Italia",     foodIds:["pasta","espaguetis_s","arroz_b","pan","pollo_p","salmon","atun","aceite","huevo","cottage","yogur","tomate","brocoli","espinacas","fresas","macarrones_s","leche","naranja","almendras","kiwi"] },
  india:      { label:"🇮🇳 India",      foodIds:["arroz_b","avena","batata","habich","pollo_p","carne","huevo","clara","leche_ent","yogur","almendras","mani","espinacas","zanahoria","mango","lentil","pita","hummus","kiwi","fresas"] },
  colven:     { label:"🇨🇴 Col/Ven",   foodIds:["arroz_b","yuca","platano_v","batata","pollo_p","atun","carne","huevo","queso_bl","aguacate","leche_ent","naranja","mango","tomate","zanahoria","platano_m","habich","arroz_m","kefir","espinacas"] },
  arab:       { label:"🇦🇪 Árabe",      foodIds:["hummus","falafel","pita","lentil","dates","lamb","shawarma_c","tahini","labneh","arroz_b","huevo","yogur","espinacas","tomate","pepino","almendras","nueces","aceite","pollo_p","naranja"] },
};

// ─── INTERNACIONALIZACIÓN ─────────────────────────────────────────────────────
// Diccionario de traducción para los 4 idiomas soportados.
// Usar tr(key) para obtener el texto en el idioma activo (_LANG).
// Formato: tr("key") o tr("key", {name: "Cris"}) para interpolación.
const I18N = {
  es: {
    tab_hoy: "Hoy",
    tab_gym: "Gym",
    tab_diet: "Dieta",
    tab_stats: "Stats",
    tab_metas: "Metas",
    greeting_hoy: "Buenos días, {name}  👋",
    greeting_afternoon: "Buenas tardes, {name}  👋",
    greeting_evening: "Buenas noches, {name}  👋",
    greeting_gym: "Entrenamiento 💪",
    greeting_diet: "Nutrición 🍽️",
    greeting_stats: "Progreso 📈",
    greeting_metas: "Objetivos 🎯",
    btn_save: "Guardar",
    btn_add: "Añadir",
    btn_cancel: "Cancelar",
    btn_logout: "Salir",
    btn_add_set: "+ Set",
    btn_save_session: "Guardar sesión",
    btn_saved: "✓ Sesión guardada",
    lbl_duration: "Duración",
    lbl_exercises: "Ejercicios",
    lbl_session_type: "Tipo de sesión",
    lbl_body_weight: "Peso de hoy",
    lbl_dark_mode: "Modo oscuro",
    lbl_general: "General",
    lbl_habits: "Hábitos",
    lbl_goal: "Objetivo",
    lbl_users: "Usuarios",
    lbl_language: "Idioma",
    lbl_calories: "Calorías",
    lbl_protein: "Proteína",
    lbl_carbs: "Carbos",
    lbl_fat: "Grasas",
    lbl_kcal: "kcal",
    settings_title: "Configuración",
    settings_save_goal: "Guardar objetivo",
    settings_add_user: "Crear usuario",
    settings_users_desc: "Los usuarios pueden añadirse desde aquí. Cada uno tiene su propio PIN y datos.",
    onboard_step1_title: "¿Cuál es tu cocina base?",
    onboard_step1_subtitle: "Tendrás alimentos típicos de tu cultura listos para agregar gramos en tu dieta",
    onboard_step2_title: "Tu perfil físico",
    onboard_next: "Continuar →",
    onboard_finish: "Empezar 🚀",
    onboard_lang_title: "¿En qué idioma?",
    login_select: "Selecciona tu perfil",
    login_admin: "Administrador",
    login_user: "Usuario",
    login_refresh: "↻ No veo mi perfil",
    select_profile: "Selecciona tu perfil",
    auto_saved: "✓ Auto-guardado",
    session_saved: "✓ Sesión guardada",
    no_exercises: "Sin ejercicios — pulsa + Añadir",
    my_foods: "⭐ Mis alimentos habituales",
    food_catalog: "Catálogo de alimentos",
    add_custom_food: "+ Custom",
    header_kcal_habits: "{cal} kcal · {done}/{total} hábitos",
  },
  en: {
    tab_hoy: "Today",
    tab_gym: "Gym",
    tab_diet: "Diet",
    tab_stats: "Stats",
    tab_metas: "Goals",
    greeting_hoy: "Good morning, {name}  👋",
    greeting_afternoon: "Good afternoon, {name}  👋",
    greeting_evening: "Good evening, {name}  👋",
    greeting_gym: "Training 💪",
    greeting_diet: "Nutrition 🍽️",
    greeting_stats: "Progress 📈",
    greeting_metas: "Goals 🎯",
    btn_save: "Save",
    btn_add: "Add",
    btn_cancel: "Cancel",
    btn_logout: "Log out",
    btn_add_set: "+ Set",
    btn_save_session: "Save session",
    btn_saved: "✓ Session saved",
    lbl_duration: "Duration",
    lbl_exercises: "Exercises",
    lbl_session_type: "Session type",
    lbl_body_weight: "Today's weight",
    lbl_dark_mode: "Dark mode",
    lbl_general: "General",
    lbl_habits: "Habits",
    lbl_goal: "Goal",
    lbl_users: "Users",
    lbl_language: "Language",
    lbl_calories: "Calories",
    lbl_protein: "Protein",
    lbl_carbs: "Carbs",
    lbl_fat: "Fat",
    lbl_kcal: "kcal",
    settings_title: "Settings",
    settings_save_goal: "Save goal",
    settings_add_user: "Create user",
    settings_users_desc: "Users can be added from here. Each has their own PIN and data.",
    onboard_step1_title: "What is your base cuisine?",
    onboard_step1_subtitle: "You'll have typical foods from your culture ready to add grams to your diet",
    onboard_step2_title: "Your physical profile",
    onboard_next: "Continue →",
    onboard_finish: "Start 🚀",
    onboard_lang_title: "Which language?",
    login_select: "Select your profile",
    login_admin: "Administrator",
    login_user: "User",
    login_refresh: "↻ I don't see my profile",
    select_profile: "Select your profile",
    auto_saved: "✓ Auto-saved",
    session_saved: "✓ Session saved",
    no_exercises: "No exercises — press + Add",
    my_foods: "⭐ My usual foods",
    food_catalog: "Food catalog",
    add_custom_food: "+ Custom",
    header_kcal_habits: "{cal} kcal · {done}/{total} habits",
  },
  ar: {
    tab_hoy: "اليوم",
    tab_gym: "الجيم",
    tab_diet: "النظام الغذائي",
    tab_stats: "الإحصائيات",
    tab_metas: "الأهداف",
    greeting_hoy: "صباح الخير، {name}  👋",
    greeting_afternoon: "مساء الخير، {name}  👋",
    greeting_evening: "مساء النور، {name}  👋",
    greeting_gym: "التدريب 💪",
    greeting_diet: "التغذية 🍽️",
    greeting_stats: "التقدم 📈",
    greeting_metas: "الأهداف 🎯",
    btn_save: "حفظ",
    btn_add: "إضافة",
    btn_cancel: "إلغاء",
    btn_logout: "خروج",
    btn_add_set: "+ مجموعة",
    btn_save_session: "حفظ الجلسة",
    btn_saved: "✓ تم الحفظ",
    lbl_duration: "المدة",
    lbl_exercises: "التمارين",
    lbl_session_type: "نوع الجلسة",
    lbl_body_weight: "الوزن اليوم",
    lbl_dark_mode: "الوضع الداكن",
    lbl_general: "عام",
    lbl_habits: "العادات",
    lbl_goal: "الهدف",
    lbl_users: "المستخدمون",
    lbl_language: "اللغة",
    lbl_calories: "السعرات",
    lbl_protein: "البروتين",
    lbl_carbs: "الكربوهيدرات",
    lbl_fat: "الدهون",
    lbl_kcal: "سعرة",
    settings_title: "الإعدادات",
    settings_save_goal: "حفظ الهدف",
    settings_add_user: "إضافة مستخدم",
    settings_users_desc: "يمكن إضافة المستخدمين من هنا. لكل منهم رمز PIN وبياناته الخاصة.",
    onboard_step1_title: "ما هو مطبخك الأساسي؟",
    onboard_step1_subtitle: "ستجد أطعمة من ثقافتك جاهزة لإضافة الجرامات إلى نظامك الغذائي",
    onboard_step2_title: "ملفك الجسدي",
    onboard_next: "متابعة →",
    onboard_finish: "ابدأ 🚀",
    onboard_lang_title: "أي لغة تفضل؟",
    login_select: "اختر ملفك الشخصي",
    login_admin: "مدير",
    login_user: "مستخدم",
    login_refresh: "↻ لا أرى ملفي الشخصي",
    select_profile: "اختر ملفك الشخصي",
    auto_saved: "✓ حُفظ تلقائياً",
    session_saved: "✓ تم حفظ الجلسة",
    no_exercises: "لا تمارين — اضغط + إضافة",
    my_foods: "⭐ أطعمتي المعتادة",
    food_catalog: "كتالوج الطعام",
    add_custom_food: "+ مخصص",
    header_kcal_habits: "{cal} سعرة · {done}/{total} عادات",
  },
  de: {
    tab_hoy: "Heute",
    tab_gym: "Gym",
    tab_diet: "Ernährung",
    tab_stats: "Statistik",
    tab_metas: "Ziele",
    greeting_hoy: "Guten Morgen, {name}  👋",
    greeting_afternoon: "Guten Nachmittag, {name}  👋",
    greeting_evening: "Guten Abend, {name}  👋",
    greeting_gym: "Training 💪",
    greeting_diet: "Ernährung 🍽️",
    greeting_stats: "Fortschritt 📈",
    greeting_metas: "Ziele 🎯",
    btn_save: "Speichern",
    btn_add: "Hinzufügen",
    btn_cancel: "Abbrechen",
    btn_logout: "Abmelden",
    btn_add_set: "+ Satz",
    btn_save_session: "Einheit speichern",
    btn_saved: "✓ Einheit gespeichert",
    lbl_duration: "Dauer",
    lbl_exercises: "Übungen",
    lbl_session_type: "Trainingsart",
    lbl_body_weight: "Gewicht heute",
    lbl_dark_mode: "Dunkelmodus",
    lbl_general: "Allgemein",
    lbl_habits: "Gewohnheiten",
    lbl_goal: "Ziel",
    lbl_users: "Benutzer",
    lbl_language: "Sprache",
    lbl_calories: "Kalorien",
    lbl_protein: "Protein",
    lbl_carbs: "Kohlenhydrate",
    lbl_fat: "Fett",
    lbl_kcal: "kcal",
    settings_title: "Einstellungen",
    settings_save_goal: "Ziel speichern",
    settings_add_user: "Benutzer erstellen",
    settings_users_desc: "Benutzer können hier hinzugefügt werden. Jeder hat seine eigene PIN und Daten.",
    onboard_step1_title: "Was ist deine Basisküche?",
    onboard_step1_subtitle: "Du erhältst typische Lebensmittel deiner Kultur, die du deiner Ernährung hinzufügen kannst",
    onboard_step2_title: "Dein körperliches Profil",
    onboard_next: "Weiter →",
    onboard_finish: "Loslegen 🚀",
    onboard_lang_title: "Welche Sprache?",
    login_select: "Wähle dein Profil",
    login_admin: "Administrator",
    login_user: "Benutzer",
    login_refresh: "↻ Ich sehe mein Profil nicht",
    select_profile: "Wähle dein Profil",
    auto_saved: "✓ Automatisch gespeichert",
    session_saved: "✓ Einheit gespeichert",
    no_exercises: "Keine Übungen — drücke + Hinzufügen",
    my_foods: "⭐ Meine üblichen Lebensmittel",
    food_catalog: "Lebensmittelkatalog",
    add_custom_food: "+ Eigenes",
    header_kcal_habits: "{cal} kcal · {done}/{total} Gewohnheiten",
  },
};

// Variable de idioma activo (módulo) y función de traducción
let _LANG = "es";
function tr(key, vars={}) {
  let s = (I18N[_LANG]||I18N.es)[key] ?? I18N.es[key] ?? key;
  Object.entries(vars).forEach(([k,v])=>{ s=s.replace(`{${k}}`,v); });
  return s;
}

// ─── EXERCISES ────────────────────────────────────────────────────────────────
// Base de datos de ejercicios agrupados por músculo. Se usan en GymTab para
// el selector de ejercicios y en MuscleBody para las recomendaciones por zona.
const EXERCISES = {
  Pecho:[{id:"press_inc",name:"Press inclinado"},{id:"press_ban",name:"Press banca"},{id:"apert",name:"Apertura máquina"},{id:"flex_plio",name:"Flexiones pliométricas"}],
  Espalda:[{id:"domin",name:"Dominadas"},{id:"jalon",name:"Jalón al pecho"},{id:"remo_t",name:"Remo barra T"},{id:"face_pull",name:"Face pull"},{id:"shrugs",name:"Shrugs"},{id:"remo_maq",name:"Remo máquina neutro"},{id:"pullover",name:"Pullover"},{id:"peso_m",name:"Peso muerto"}],
  Hombros:[{id:"press_mil",name:"Press militar"},{id:"elev_lat",name:"Elevación lateral"},{id:"rev_fly",name:"Reverse fly"},{id:"push_pr",name:"Push press"}],
  Tríceps:[{id:"press_fr",name:"Press francés"},{id:"ext_pol",name:"Extensión polea cuerda"},{id:"fondos",name:"Fondos paralelas"},{id:"ext_uni",name:"Extensión unilateral"}],
  Bíceps:[{id:"curl_ara",name:"Curl araña"},{id:"curl_mart",name:"Curl martillo"},{id:"pred_maq",name:"Predicador máquina"},{id:"curl_z",name:"Curl barra Z"}],
  Piernas:[{id:"sentad",name:"Sentadilla"},{id:"bulgar",name:"Búlgara"},{id:"hip_th",name:"Hip thrust"},{id:"rum",name:"Peso muerto rumano"},{id:"prensa",name:"Prensa"},{id:"ext_cuad",name:"Extensión cuádricep"}],
  Core:[{id:"pallof",name:"Press Pallof"},{id:"elev_col",name:"Elevación piernas colgado"},{id:"plancha",name:"Plancha"},{id:"russian",name:"Russian twist"},{id:"rueda",name:"Rueda abdominal"},{id:"landmine",name:"Landmine rotation"}],
  Pliometría:[{id:"box_j",name:"Box jump"},{id:"broad_j",name:"Broad jump"},{id:"hang_cl",name:"Hang clean"},{id:"med_slam",name:"Medicine ball slam"},{id:"sprint",name:"Sprints 40m"},{id:"burpee",name:"Burpee con salto"}],
};

const OPT_TARGETS = {Pecho:14,Espalda:20,Hombros:16,Tríceps:12,Bíceps:12,Piernas:18,Core:12,Pliometría:8};
// eslint-disable-next-line no-unused-vars
const MET = {muay_thai:10,bjj:8,grappling:8,lucha:7,mma:9,default:5};

const ACTIVITY_LEVELS = [
  { v:"1.2",  l:"Sedentario",  desc:"Escritorio todo el día, sin deporte" },
  { v:"1.4",  l:"Ligero",      desc:"Oficina + 1-2 entrenos/semana" },
  { v:"1.6",  l:"Moderado",    desc:"3-4 entrenos/sem o trabajo de pie (dependiente, camarero)" },
  { v:"1.75", l:"Activo",      desc:"Almacén, reparto, obra o gym 5 días/sem" },
  { v:"1.9",  l:"Muy activo",  desc:"Trabajo físico intenso + gym diario" },
  { v:"2.1",  l:"Atleta",      desc:"Dobles sesiones diarias o competición" },
];

// Rangos de proteína (g/kg) por nivel de actividad
const PROTEIN_RANGES = {
  "1.2":  { min:0.8, good:1.2, opt:1.6 },
  "1.4":  { min:1.0, good:1.4, opt:1.8 },
  "1.6":  { min:1.2, good:1.6, opt:2.0 },
  "1.75": { min:1.5, good:1.9, opt:2.3 },
  "1.9":  { min:1.6, good:2.0, opt:2.5 },
  "2.1":  { min:1.8, good:2.2, opt:2.8 },
};
const PROT_MAX_SLIDER = 3.5;

function proteinRating(gPerKg, activityFactor) {
  const r = PROTEIN_RANGES[String(activityFactor)] || PROTEIN_RANGES["1.9"];
  const v = parseFloat(gPerKg);
  if(v >= r.opt)  return { label:"Óptimo 🔥", color:"#22c55e", pct:Math.min(100, 60 + ((v-r.opt)/(PROT_MAX_SLIDER-r.opt))*40) };
  if(v >= r.good) return { label:"Bueno ✅",  color:"#3b82f6", pct: 40 + ((v-r.good)/(r.opt-r.good))*20 };
  if(v >= r.min)  return { label:"Mínimo ⚠️", color:"#f59e0b", pct: 15 + ((v-r.min)/(r.good-r.min))*25 };
  return                  { label:"Bajo ❌",   color:"#ef4444", pct: Math.max(2,(v/r.min)*15) };
}

function calcMacros(g) {
  const w=parseFloat(g.weight)||69,h=parseFloat(g.height)||175,a=parseFloat(g.age)||22;
  const bmr=(10*w)+(6.25*h)-(5*a)+5;
  const af=parseFloat(g.activityFactor)||1.9;
  const tdee=Math.round(bmr*af);
  const gw=parseFloat(g.goalWeight)||75;
  const surplus=gw>w?(g.pace==="fast"?400:250):(g.pace==="fast"?-600:-400);
  const cal=Math.max(1400,tdee+surplus);
  const pkgVal=parseFloat(g.proteinPerKg)||2.5;
  const protein=Math.round(w*pkgVal);
  const fat=Math.round(cal*0.27/9);
  const carbs=Math.round((cal-protein*4-fat*9)/4);
  return {cal,protein,carbs,fat,tdee};
}

// ─── RESPONSIVE HOOK ──────────────────────────────────────────────────────────
function useWindowWidth() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return w;
}

// Reloj interno — se actualiza cada 30s; `day` cambia en medianoche
function useClock() {
  const snap = () => {
    const n = new Date();
    return {
      clock: `${String(n.getHours()).padStart(2,"0")}:${String(n.getMinutes()).padStart(2,"0")}`,
      day: n.toISOString().split("T")[0],
    };
  };
  const [state, setState] = useState(snap);
  useEffect(() => {
    const id = setInterval(() => setState(snap()), 30000);
    return () => clearInterval(id);
  }, []);
  return state;
}

// ─── UI ───────────────────────────────────────────────────────────────────────
const Card = ({children,t,style={}}) => <div style={{background:t.surface,border:`1px solid ${t.border}`,borderRadius:"16px",padding:"1.1rem",...style}}>{children}</div>;
const Lbl = ({children,t}) => <div style={{color:t.textTertiary,fontSize:"0.6rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:"0.4rem"}}>{children}</div>;
const Inp = ({value,onChange,placeholder,type="text",t,style={}}) => (
  <input value={value} onChange={onChange} placeholder={placeholder} type={type}
    style={{background:t.surfaceHover,border:`1px solid ${t.border}`,borderRadius:"10px",color:t.text,padding:"0.55rem 0.8rem",fontSize:"0.83rem",outline:"none",fontFamily:"inherit",boxSizing:"border-box",...style}}/>
);

function Ring({value,max,size=60,stroke=5,color,bg,children}) {
  const r=(size-stroke*2)/2,circ=2*Math.PI*r,pct=Math.min(1,value/max);
  return (
    <div style={{position:"relative",width:size,height:size,flexShrink:0}}>
      <svg width={size} height={size} style={{transform:"rotate(-90deg)"}}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={bg} strokeWidth={stroke}/>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeDasharray={circ} strokeDashoffset={circ*(1-pct)} strokeLinecap="round"/>
      </svg>
      <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center"}}>{children}</div>
    </div>
  );
}

// ─── MUSCLE BODY VISUALIZATION ────────────────────────────────────────────────
function MuscleBody({weekVol, todaySets, t, onAddExercise, countdown}) {
  const[selected,setSelected]=useState(null);

  const ZONES = {
    "pecho_alto":{label:"Pecho superior",muscle:"Pecho",subLabel:"Pectoral clavicular",desc:"Parte alta del pecho. Se activa con movimientos inclinados y press con agarre ancho.",exercises:[{id:"press_inc",name:"Press inclinado",note:"Principal activador del pecho superior"},{id:"flex_plio",name:"Flexiones pies elevados",note:"Pecho alto con peso corporal"}]},
    "pecho_bajo":{label:"Pecho inferior",muscle:"Pecho",subLabel:"Pectoral esternal",desc:"Parte baja del pecho. Movimientos planos o declinados. Da grosor y profundidad.",exercises:[{id:"press_ban",name:"Press banca plano",note:"Base del pecho completo"},{id:"apert",name:"Apertura máquina",note:"Aislamiento pecho bajo"},{id:"fondos",name:"Fondos paralelas",note:"Torso inclinado = más pecho"}]},
    "hombro_ant":{label:"Hombro anterior",muscle:"Hombros",subLabel:"Deltoides anterior",desc:"Frente del hombro. Se activa en todos los press. Suele estar sobreentrenado sin ni saberlo.",exercises:[{id:"press_mil",name:"Press militar",note:"Activa los tres deltoides"},{id:"push_pr",name:"Push press",note:"Versión explosiva con piernas"}]},
    "hombro_lat":{label:"Hombro lateral",muscle:"Hombros",subLabel:"Deltoides medial",desc:"Da amplitud visual al cuerpo — el más importante para tener la forma en V. Necesita trabajo aislado.",exercises:[{id:"elev_lat",name:"Elevación lateral",note:"El ejercicio rey del deltoides medial"},{id:"push_pr",name:"Push press",note:"Explosividad con deltoides"}]},
    "biceps_larga":{label:"Bíceps cabeza larga",muscle:"Bíceps",subLabel:"Cabeza larga (exterior)",desc:"Da el pico visual del bíceps. Actívala con codos hacia atrás respecto al torso.",exercises:[{id:"curl_mart",name:"Curl martillo",note:"Cabeza larga + braquial"},{id:"curl_z",name:"Curl barra Z",note:"Masa general de bíceps"}]},
    "biceps_corta":{label:"Bíceps cabeza corta",muscle:"Bíceps",subLabel:"Cabeza corta (interior)",desc:"Da anchura al bíceps. Mejor activación con codos hacia delante (curl predicador).",exercises:[{id:"pred_maq",name:"Predicador máquina",note:"Codos fijos = cabeza corta"},{id:"curl_ara",name:"Curl araña",note:"Máximo aislamiento"}]},
    "antebrazo_f":{label:"Antebrazo",muscle:"Bíceps",subLabel:"Flexores y braquiorradial",desc:"Crucial en MMA para el agarre. Entrena con curls con agarre neutro y trabajo de agarre específico.",exercises:[{id:"curl_mart",name:"Curl martillo",note:"Activa braquiorradial directamente"},{id:"curl_z",name:"Curl barra Z agarre estrecho",note:"Más antebrazo"}]},
    "oblicuos":{label:"Oblicuos",muscle:"Core",subLabel:"Oblicuo externo e interno",desc:"Rotación de tronco y golpeo. En Muay Thai, el giro de cadera en el teep y el uppercut viene de aquí.",exercises:[{id:"russian",name:"Russian twist",note:"Oblicuos con rotación"},{id:"landmine",name:"Landmine rotation",note:"Simula rotación real de combate"},{id:"pallof",name:"Press Pallof",note:"Anti-rotación = core funcional"}]},
    "core_ant":{label:"Core — Recto abdominal",muscle:"Core",subLabel:"Recto abdominal (6-pack)",desc:"Estabilización y flexión. En MMA la función es proteger el cuerpo y generar tensión en cada golpe.",exercises:[{id:"elev_col",name:"Elevación piernas colgado",note:"Recto abdominal con carga"},{id:"rueda",name:"Rueda abdominal",note:"Core completo"},{id:"russian",name:"Russian twist",note:"Oblicuos + recto"}]},
    "core_est":{label:"Core profundo",muscle:"Core",subLabel:"Transverso y estabilizadores",desc:"El core real para lucha. Protege columna y transfiere fuerza entre piernas y brazos en cada derribo.",exercises:[{id:"pallof",name:"Press Pallof",note:"Anti-rotación = core funcional"},{id:"plancha",name:"Plancha",note:"Estabilidad isométrica"},{id:"landmine",name:"Landmine rotation",note:"Simula el golpe real"}]},
    "cuadriceps":{label:"Cuádriceps",muscle:"Piernas",subLabel:"4 cabezas del cuádricep",desc:"Motor de la explosividad. Cada patada, salto y cambio de dirección en el octógono depende de aquí.",exercises:[{id:"sentad",name:"Sentadilla",note:"Más completo para piernas"},{id:"prensa",name:"Prensa",note:"Mayor carga sin lumbar"},{id:"ext_cuad",name:"Extensión cuádricep",note:"Aislamiento"},{id:"box_j",name:"Box jump",note:"Explosividad"}]},
    "gluteos_f":{label:"Glúteos",muscle:"Piernas",subLabel:"Glúteo mayor y medio",desc:"El músculo más potente del cuerpo. Decide tu potencia de golpe de cadera, derribos y cambios de nivel.",exercises:[{id:"hip_th",name:"Hip thrust",note:"Máxima activación"},{id:"bulgar",name:"Búlgara",note:"Glúteo + cuádricep"},{id:"sentad",name:"Sentadilla profunda",note:"Glúteo en fondo"}]},
    "pantorrilla_f":{label:"Pantorrillas",muscle:"Piernas",subLabel:"Gastrocnemio y sóleo",desc:"Absorción de impacto y movilidad sobre el pie. En Muay Thai, el trabajo en puntas es constante.",exercises:[{id:"sprint",name:"Sprints 40m",note:"Activa pantorrilla funcionalmente"},{id:"box_j",name:"Box jump",note:"Explosividad y pantorrilla"}]},
    "dorsal":{label:"Dorsal ancho",muscle:"Espalda",subLabel:"Latissimus dorsi",desc:"El más grande de la espalda. Da la forma en V y es clave en derribos, clinch y cada movimiento de tracción.",exercises:[{id:"domin",name:"Dominadas",note:"El mejor para dorsales"},{id:"jalon",name:"Jalón al pecho",note:"Alternativa asistida"},{id:"pullover",name:"Pullover",note:"Aislamiento máximo"}]},
    "espalda_med":{label:"Espalda media",muscle:"Espalda",subLabel:"Romboides y trapecio medio",desc:"Crucial para postura y salud del hombro. Muy descuidada — sin ella el hombro sufre a largo plazo.",exercises:[{id:"remo_t",name:"Remo barra T",note:"Masa espalda media"},{id:"remo_maq",name:"Remo máquina neutro",note:"Agarre neutro = más espalda"},{id:"face_pull",name:"Face pull",note:"Romboides + deltoides post."}]},
    "trap":{label:"Trapecios",muscle:"Espalda",subLabel:"Trapecio superior",desc:"Potencia en el clinch, los derribos y la defensa de cabeza. Dan protección al cuello en el combate.",exercises:[{id:"shrugs",name:"Shrugs",note:"Específico de trapecios"},{id:"peso_m",name:"Peso muerto",note:"Carga enorme en trapecio"}]},
    "lumbar":{label:"Lumbar / Erector",muscle:"Espalda",subLabel:"Erector spinae",desc:"Estabilidad de la columna en cada posición de combate. El punto débil más común en luchadores.",exercises:[{id:"peso_m",name:"Peso muerto",note:"El mejor para lumbar"},{id:"rum",name:"Peso muerto rumano",note:"Isquio + lumbar"},{id:"hip_th",name:"Hip thrust",note:"Glúteo + lumbar"}]},
    "hombro_post":{label:"Hombro posterior",muscle:"Hombros",subLabel:"Deltoides posterior",desc:"Parte trasera del hombro. Crucial para salud articular y postura. El más descuidado y el que más lesiones previene.",exercises:[{id:"rev_fly",name:"Reverse fly",note:"Aislamiento del deltoides post."},{id:"face_pull",name:"Face pull",note:"El mejor para salud del hombro"}]},
    "triceps_long":{label:"Tríceps cabeza larga",muscle:"Tríceps",subLabel:"Cabeza larga (posterior)",desc:"La más grande del tríceps. Solo se activa completamente con el brazo sobre la cabeza. Frecuentemente infraentrenada.",exercises:[{id:"press_fr",name:"Press francés",note:"Rey de la cabeza larga"},{id:"ext_uni",name:"Extensión sobre cabeza",note:"Brazo elevado = máxima activación"}]},
    "triceps_lat":{label:"Tríceps cabezas lateral/medial",muscle:"Tríceps",subLabel:"Cabeza lateral y medial",desc:"Dan la forma en herradura. Se activan en todos los movimientos de empuje con codo extendido.",exercises:[{id:"ext_pol",name:"Extensión polea cuerda",note:"Las dos cabezas laterales"},{id:"fondos",name:"Fondos paralelas",note:"Torso recto = más tríceps"}]},
    "isquios":{label:"Isquiotibiales",muscle:"Piernas",subLabel:"Bíceps femoral",desc:"Parte posterior del muslo. Previene lesiones de rodilla y potencia las patadas traseras y circulares.",exercises:[{id:"rum",name:"Peso muerto rumano",note:"El rey del isquiotibial"},{id:"bulgar",name:"Búlgara",note:"Isquio + glúteo"}]},
    "gluteos_b":{label:"Glúteos (posterior)",muscle:"Piernas",subLabel:"Glúteo mayor y medio",desc:"Vista posterior. Estabilidad en cada posición de guardia y potencia de cadera en el golpeo.",exercises:[{id:"hip_th",name:"Hip thrust",note:"Máxima activación glúteo"},{id:"sentad",name:"Sentadilla profunda",note:"Glúteo en fondo del movimiento"}]},
    "pantorrilla_b":{label:"Gemelos",muscle:"Piernas",subLabel:"Gastrocnemio medial y lateral",desc:"Absorción en cada golpe sobre el tatami. En Muay Thai el trabajo de pies constante los destruye si no los entrenas.",exercises:[{id:"sprint",name:"Sprints 40m",note:"Explosividad y gemelos"},{id:"box_j",name:"Box jump",note:"Salto + pantorrilla"}]},
  };

  const getC = (muscle) => {
    const sets=weekVol[muscle]||0;
    const tgt={Pecho:14,Espalda:20,Hombros:16,Bíceps:12,Tríceps:12,Core:12,Piernas:18}[muscle]||14;
    const p=Math.min(1,sets/tgt);
    // Verde brillante = sin entrenar (señal: dale caña), rojo = bien trabajado
    if(p===0) return {f:"rgba(34,197,94,0.80)",s:"#22C55E"};  // verde = falta entrenar
    if(p<0.4) return {f:"rgba(132,204,22,0.78)",s:"#84CC16"};  // lima = poco volumen
    if(p<0.7) return {f:"rgba(245,158,11,0.78)",s:"#F59E0B"};  // naranja = progresando
    if(p<1)   return {f:"rgba(239,68,68,0.75)",s:"#EF4444"};  // rojo = bien entrenado
    return {f:"rgba(185,28,28,0.82)",s:"#DC2626"};             // rojo oscuro = al límite
  };

  const tap=(id)=>setSelected(p=>p===id?null:id);
  const zc=(id,muscle)=>{
    const base=getC(muscle);
    if(selected===id) return {f:"rgba(255,255,255,0.55)",s:"#FFFFFF",sw:"2"};
    return {f:base.f,s:base.s,sw:"1.2"};
  };

  const selZone = selected ? ZONES[selected] : null;
  const so=t.border; const dl="rgba(180,160,140,0.18)";

  return (
    <div style={{display:"flex",flexDirection:"column",gap:"1rem"}}>
      <div style={{display:"flex",justifyContent:"center",gap:"2px"}}>
        {/* ===== FRONT ===== */}
        <svg viewBox="0 0 200 395" style={{width:"49%",display:"block",cursor:"pointer"}} preserveAspectRatio="xMidYMid meet">
          {/* ── HEAD ── */}
          <ellipse cx="100" cy="26" rx="22" ry="26" fill={t.surface} stroke={so} strokeWidth="1.2"/>
          <ellipse cx="78" cy="26" rx="3" ry="5.5" fill={t.surface} stroke={so} strokeWidth="0.8"/>
          <ellipse cx="122" cy="26" rx="3" ry="5.5" fill={t.surface} stroke={so} strokeWidth="0.8"/>
          {/* ── NECK ── */}
          <path d="M92,50 Q90,58 89,68 Q94,72 100,72 Q106,72 111,68 Q110,58 108,50" fill={t.surface} stroke={so} strokeWidth="1.1"/>
          <line x1="100" y1="50" x2="100" y2="68" stroke={dl} strokeWidth="0.8" opacity="0.5"/>
          {/* ── CLAVICLE LINE ── */}
          <path d="M89,68 Q94,64 100,64 Q106,64 111,68" fill="none" stroke={so} strokeWidth="0.8" opacity="0.45"/>
          {/* ── LEFT ARM BASE ── */}
          <path d="M58,68 Q42,74 28,90 Q18,104 18,124 Q18,144 22,162 Q18,178 16,198 Q14,214 16,228 Q20,234 28,232" fill={t.surface} stroke={so} strokeWidth="1.2"/>
          {/* ── RIGHT ARM BASE ── */}
          <path d="M142,68 Q158,74 172,90 Q182,104 182,124 Q182,144 178,162 Q182,178 184,198 Q186,214 184,228 Q180,234 172,232" fill={t.surface} stroke={so} strokeWidth="1.2"/>
          {/* ── TORSO BASE ── */}
          <path d="M56,68 Q88,82 100,82 Q112,82 144,68 L144,210 Q120,220 100,220 Q80,220 56,210 Z" fill={t.surface} stroke={so} strokeWidth="1.2"/>
          {/* arm-torso gap fillers */}
          <path d="M18,104 Q30,96 48,88 Q44,108 38,128 Q28,146 22,164" fill={t.surface} stroke="none"/>
          <path d="M182,104 Q170,96 152,88 Q156,108 162,128 Q172,146 178,164" fill={t.surface} stroke="none"/>
          {/* ── WRISTS ── */}
          <ellipse cx="20" cy="232" rx="9" ry="5" fill={t.surface} stroke={so} strokeWidth="0.9"/>
          <ellipse cx="180" cy="232" rx="9" ry="5" fill={t.surface} stroke={so} strokeWidth="0.9"/>
          {/* ── LEFT LEG ── */}
          <path d="M56,210 Q50,228 48,258 Q46,286 48,310 Q46,326 44,344 Q42,360 46,374 Q52,382 64,382 Q74,380 76,370 Q76,356 74,342 Q76,328 78,314" fill={t.surface} stroke={so} strokeWidth="1.2"/>
          <path d="M78,314 Q84,320 92,320 Q92,344 90,366 Q88,378 80,384 Q68,382 66,370 Q66,354 68,340 Q70,326 74,312 Z" fill={t.surface} stroke={so} strokeWidth="1.2"/>
          <ellipse cx="68" cy="385" rx="16" ry="6" fill={t.surface} stroke={so} strokeWidth="0.9"/>
          {/* ── RIGHT LEG ── */}
          <path d="M144,210 Q150,228 152,258 Q154,286 152,310 Q154,326 156,344 Q158,360 154,374 Q148,382 136,382 Q126,380 124,370 Q124,356 126,342 Q124,328 122,314" fill={t.surface} stroke={so} strokeWidth="1.2"/>
          <path d="M122,314 Q116,320 108,320 Q108,344 110,366 Q112,378 120,384 Q132,382 134,370 Q134,354 132,340 Q130,326 126,312 Z" fill={t.surface} stroke={so} strokeWidth="1.2"/>
          <ellipse cx="132" cy="385" rx="16" ry="6" fill={t.surface} stroke={so} strokeWidth="0.9"/>
          {/* ── CENTER LEG FILL ── */}
          <path d="M92,214 L108,214 L108,320 Q104,322 100,322 Q96,322 92,320 Z" fill={t.surface} stroke="none"/>
          <path d="M92,320 Q90,378 100,385 Q110,378 108,320" fill={t.surface} stroke={so} strokeWidth="1.1"/>
          {/* ── KNEE CAPS ── */}
          <ellipse cx="76" cy="314" rx="10" ry="7" fill={t.surface} stroke={so} strokeWidth="1"/>
          <ellipse cx="124" cy="314" rx="10" ry="7" fill={t.surface} stroke={so} strokeWidth="1"/>

          {/* ════ MUSCLES FRONT ════ */}

          {/* Trap — wedge at neck-shoulder junction */}
          {(()=>{const c=zc("trap","Espalda");return(<g onClick={()=>tap("trap")} style={{cursor:"pointer"}}>
            <path d="M89,58 Q80,60 60,72 Q68,78 80,80 Q88,74 89,64Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            <path d="M111,58 Q120,60 140,72 Q132,78 120,80 Q112,74 111,64Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
          </g>);})()}

          {/* Anterior deltoid — round dome front of shoulder */}
          {(()=>{const c=zc("hombro_ant","Hombros");return(<g onClick={()=>tap("hombro_ant")} style={{cursor:"pointer"}}>
            <path d="M58,70 Q44,74 32,90 Q26,106 36,120 Q48,126 62,118 Q70,106 68,86Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            <path d="M142,70 Q156,74 168,90 Q174,106 164,120 Q152,126 138,118 Q130,106 132,86Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
          </g>);})()}

          {/* Lateral deltoid — outer shoulder cap */}
          {(()=>{const c=zc("hombro_lat","Hombros");return(<g onClick={()=>tap("hombro_lat")} style={{cursor:"pointer"}}>
            <path d="M28,90 Q18,108 18,130 Q22,144 34,146 Q46,140 48,124 Q48,106 40,92Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            <path d="M172,90 Q182,108 182,130 Q178,144 166,146 Q154,140 152,124 Q152,106 160,92Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
          </g>);})()}

          {/* Upper pec — clavicular fan */}
          {(()=>{const c=zc("pecho_alto","Pecho");return(<g onClick={()=>tap("pecho_alto")} style={{cursor:"pointer"}}>
            <path d="M88,70 Q74,74 60,90 Q54,104 64,118 Q78,126 92,122 Q100,114 100,98 Q100,82 88,70Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            <path d="M112,70 Q126,74 140,90 Q146,104 136,118 Q122,126 108,122 Q100,114 100,98 Q100,82 112,70Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
          </g>);})()}

          {/* Lower pec — sternal, rounded drop */}
          {(()=>{const c=zc("pecho_bajo","Pecho");return(<g onClick={()=>tap("pecho_bajo")} style={{cursor:"pointer"}}>
            <path d="M62,116 Q52,128 54,144 Q62,156 78,158 Q92,156 100,144 Q100,130 100,120 Q96,116 90,122 Q78,126 62,116Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            <path d="M138,116 Q148,128 146,144 Q138,156 122,158 Q108,156 100,144 Q100,130 100,120 Q104,116 110,122 Q122,126 138,116Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            <path d="M62,140 Q80,152 100,146" fill="none" stroke={dl} strokeWidth="0.8" opacity="0.45"/>
            <path d="M138,140 Q120,152 100,146" fill="none" stroke={dl} strokeWidth="0.8" opacity="0.45"/>
          </g>);})()}

          {/* Serratus anterior — 3 finger ribs each side */}
          {(()=>{const c=zc("oblicuos","Core");return(<g onClick={()=>tap("oblicuos")} style={{cursor:"pointer"}}>
            <path d="M60,122 Q48,130 46,140 Q52,144 60,142 Q66,138 66,128Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            <path d="M62,140 Q48,148 46,158 Q52,162 60,160 Q66,156 66,146Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            <path d="M64,158 Q50,166 48,176 Q54,180 62,178 Q68,174 68,164Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            <path d="M140,122 Q152,130 154,140 Q148,144 140,142 Q134,138 134,128Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            <path d="M138,140 Q152,148 154,158 Q148,162 140,160 Q134,156 134,146Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            <path d="M136,158 Q150,166 152,176 Q146,180 138,178 Q132,174 132,164Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            {/* External oblique body */}
            <path d="M60,158 Q50,174 50,192 Q52,208 62,212 Q76,216 88,212 Q94,196 92,176 Q90,158 80,152Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            <path d="M140,158 Q150,174 150,192 Q148,208 138,212 Q124,216 112,212 Q106,196 108,176 Q110,158 120,152Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
          </g>);})()}

          {/* Rectus abdominis — 6 rounded blocks */}
          {(()=>{const c=zc("core_ant","Core");return(<g onClick={()=>tap("core_ant")} style={{cursor:"pointer"}}>
            {[[0,0],[1,0],[0,1],[1,1],[0,2],[1,2]].map(([col,row])=>(
              <rect key={`${col}${row}`} x={col===0?86:101} y={124+row*18} width={13} height={14} rx="5" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            ))}
            <line x1="100" y1="122" x2="100" y2="178" stroke={dl} strokeWidth="0.9" opacity="0.45"/>
          </g>);})()}

          {/* Lower abs / inguinal V */}
          {(()=>{const c=zc("core_est","Core");return(<g onClick={()=>tap("core_est")} style={{cursor:"pointer"}}>
            <path d="M88,178 Q86,194 94,202 Q97,206 100,206 Q103,206 106,202 Q114,194 112,178 Q106,174 100,174 Q94,174 88,178Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
          </g>);})()}

          {/* Biceps long head — peaked dome */}
          {(()=>{const c=zc("biceps_larga","Bíceps");return(<g onClick={()=>tap("biceps_larga")} style={{cursor:"pointer"}}>
            <path d="M24,112 Q16,128 16,150 Q18,168 28,176 Q40,180 52,172 Q62,158 60,136 Q56,116 44,108 Q32,106 24,112Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            <path d="M176,112 Q184,128 184,150 Q182,168 172,176 Q160,180 148,172 Q138,158 140,136 Q144,116 156,108 Q168,106 176,112Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            <path d="M20,144 Q34,136 52,144" fill="none" stroke={dl} strokeWidth="0.9" opacity="0.5"/>
            <path d="M180,144 Q166,136 148,144" fill="none" stroke={dl} strokeWidth="0.9" opacity="0.5"/>
          </g>);})()}

          {/* Brachialis — inner lower biceps */}
          {(()=>{const c=zc("biceps_corta","Bíceps");return(<g onClick={()=>tap("biceps_corta")} style={{cursor:"pointer"}}>
            <path d="M52,116 Q62,128 64,148 Q62,164 52,170 Q44,170 38,162 Q42,144 46,128Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            <path d="M148,116 Q138,128 136,148 Q138,164 148,170 Q156,170 162,162 Q158,144 154,128Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
          </g>);})()}

          {/* Forearms — tapered flex belly */}
          {(()=>{const c=zc("antebrazo_f","Bíceps");return(<g onClick={()=>tap("antebrazo_f")} style={{cursor:"pointer"}}>
            <path d="M18,168 Q10,186 10,208 Q10,224 16,234 Q26,240 40,236 Q48,222 48,202 Q48,182 42,170 Q30,162 18,168Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            <path d="M182,168 Q190,186 190,208 Q190,224 184,234 Q174,240 160,236 Q152,222 152,202 Q152,182 158,170 Q170,162 182,168Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            <path d="M12,194 Q26,198 42,196" fill="none" stroke={dl} strokeWidth="0.7" opacity="0.4"/>
            <path d="M188,194 Q174,198 158,196" fill="none" stroke={dl} strokeWidth="0.7" opacity="0.4"/>
            <path d="M12,214 Q26,218 42,216" fill="none" stroke={dl} strokeWidth="0.7" opacity="0.4"/>
            <path d="M188,214 Q174,218 158,216" fill="none" stroke={dl} strokeWidth="0.7" opacity="0.4"/>
          </g>);})()}

          {/* Hip flexors / inguinal V */}
          {(()=>{const c=zc("gluteos_f","Piernas");return(<g onClick={()=>tap("gluteos_f")} style={{cursor:"pointer"}}>
            <path d="M64,206 Q58,218 66,230 Q78,236 90,232 Q96,222 94,212 Q90,204 78,202Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            <path d="M136,206 Q142,218 134,230 Q122,236 110,232 Q104,222 106,212 Q110,204 122,202Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            <path d="M100,204 Q86,214 76,232" fill="none" stroke={dl} strokeWidth="0.9" opacity="0.5"/>
            <path d="M100,204 Q114,214 124,232" fill="none" stroke={dl} strokeWidth="0.9" opacity="0.5"/>
          </g>);})()}

          {/* Quadriceps */}
          {(()=>{const c=zc("cuadriceps","Piernas");return(<g onClick={()=>tap("cuadriceps")} style={{cursor:"pointer"}}>
            {/* Vastus lateralis — outer mass full thigh */}
            <path d="M58,230 Q50,256 48,284 Q48,310 58,324 Q70,330 78,324 Q84,310 82,284 Q80,256 70,236Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            {/* Rectus femoris — central column */}
            <path d="M74,226 Q72,252 74,280 Q76,308 74,324 Q82,328 90,324 Q96,312 94,288 Q92,260 90,236 Q84,224 74,226Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            {/* Vastus medialis — teardrop above inner knee */}
            <path d="M92,284 Q88,308 84,322 Q82,330 78,330 Q72,326 74,316 Q76,298 82,284 Q88,270 92,284Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            {/* Right mirror */}
            <path d="M142,230 Q150,256 152,284 Q152,310 142,324 Q130,330 122,324 Q116,310 118,284 Q120,256 130,236Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            <path d="M126,226 Q128,252 126,280 Q124,308 126,324 Q118,328 110,324 Q104,312 106,288 Q108,260 110,236 Q116,224 126,226Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            <path d="M108,284 Q112,308 116,322 Q118,330 122,330 Q128,326 126,316 Q124,298 118,284 Q112,270 108,284Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            <line x1="72" y1="234" x2="72" y2="322" stroke={dl} strokeWidth="0.6" opacity="0.35"/>
            <line x1="90" y1="232" x2="90" y2="320" stroke={dl} strokeWidth="0.6" opacity="0.35"/>
            <line x1="128" y1="234" x2="128" y2="322" stroke={dl} strokeWidth="0.6" opacity="0.35"/>
            <line x1="110" y1="232" x2="110" y2="320" stroke={dl} strokeWidth="0.6" opacity="0.35"/>
          </g>);})()}

          {/* Tibialis anterior + medial gastroc front peek */}
          {(()=>{const c=zc("pantorrilla_f","Piernas");return(<g onClick={()=>tap("pantorrilla_f")} style={{cursor:"pointer"}}>
            {/* Tibialis anterior — lateral to shin */}
            <path d="M78,326 Q72,346 72,370 Q74,384 82,388 Q90,386 92,376 Q92,354 88,338 Q84,326 78,326Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            {/* Medial gastroc peeking inner side */}
            <path d="M64,324 Q58,344 58,368 Q60,380 68,386 Q76,382 76,370 Q74,350 68,334 Q66,326 64,324Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            {/* Right */}
            <path d="M122,326 Q128,346 128,370 Q126,384 118,388 Q110,386 108,376 Q108,354 112,338 Q116,326 122,326Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            <path d="M136,324 Q142,344 142,368 Q140,380 132,386 Q124,382 124,370 Q126,350 132,334 Q134,326 136,324Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
          </g>);})()}

          {!selected&&<text x="100" y="393" textAnchor="middle" fill={t.textTertiary} fontSize="8" fontFamily="-apple-system,sans-serif">Frontal</text>}
        </svg>

        {/* ===== BACK ===== */}
        <svg viewBox="0 0 200 395" style={{width:"49%",display:"block",cursor:"pointer"}} preserveAspectRatio="xMidYMid meet">
          {/* ── HEAD ── */}
          <ellipse cx="100" cy="26" rx="22" ry="26" fill={t.surface} stroke={so} strokeWidth="1.2"/>
          <ellipse cx="78" cy="26" rx="3" ry="5.5" fill={t.surface} stroke={so} strokeWidth="0.8"/>
          <ellipse cx="122" cy="26" rx="3" ry="5.5" fill={t.surface} stroke={so} strokeWidth="0.8"/>
          {/* ── NECK ── */}
          <path d="M92,50 Q90,58 89,68 Q94,72 100,72 Q106,72 111,68 Q110,58 108,50" fill={t.surface} stroke={so} strokeWidth="1.1"/>
          {/* ── ARMS BASE ── */}
          <path d="M58,68 Q42,74 28,90 Q18,104 18,124 Q18,144 22,162 Q18,178 16,198 Q14,214 16,228 Q20,234 28,232" fill={t.surface} stroke={so} strokeWidth="1.2"/>
          <path d="M142,68 Q158,74 172,90 Q182,104 182,124 Q182,144 178,162 Q182,178 184,198 Q186,214 184,228 Q180,234 172,232" fill={t.surface} stroke={so} strokeWidth="1.2"/>
          {/* ── TORSO BACK BASE ── */}
          <path d="M56,68 Q88,82 100,82 Q112,82 144,68 L144,210 Q120,220 100,220 Q80,220 56,210 Z" fill={t.surface} stroke={so} strokeWidth="1.2"/>
          <path d="M18,104 Q30,96 48,88 Q44,108 38,128 Q28,146 22,164" fill={t.surface} stroke="none"/>
          <path d="M182,104 Q170,96 152,88 Q156,108 162,128 Q172,146 178,164" fill={t.surface} stroke="none"/>
          {/* ── SPINE ── */}
          <line x1="100" y1="68" x2="100" y2="210" stroke={so} strokeWidth="0.8" opacity="0.35"/>
          {/* ── WRISTS ── */}
          <ellipse cx="20" cy="232" rx="9" ry="5" fill={t.surface} stroke={so} strokeWidth="0.9"/>
          <ellipse cx="180" cy="232" rx="9" ry="5" fill={t.surface} stroke={so} strokeWidth="0.9"/>
          {/* ── LEGS BACK ── */}
          <path d="M56,210 Q50,228 48,258 Q46,286 48,310 Q46,326 44,344 Q42,360 46,374 Q52,382 64,382 Q74,380 76,370 Q76,356 74,342 Q76,328 78,314" fill={t.surface} stroke={so} strokeWidth="1.2"/>
          <path d="M78,314 Q84,320 92,320 Q92,344 90,366 Q88,378 80,384 Q68,382 66,370 Q66,354 68,340 Q70,326 74,312 Z" fill={t.surface} stroke={so} strokeWidth="1.2"/>
          <ellipse cx="68" cy="385" rx="16" ry="6" fill={t.surface} stroke={so} strokeWidth="0.9"/>
          <path d="M144,210 Q150,228 152,258 Q154,286 152,310 Q154,326 156,344 Q158,360 154,374 Q148,382 136,382 Q126,380 124,370 Q124,356 126,342 Q124,328 122,314" fill={t.surface} stroke={so} strokeWidth="1.2"/>
          <path d="M122,314 Q116,320 108,320 Q108,344 110,366 Q112,378 120,384 Q132,382 134,370 Q134,354 132,340 Q130,326 126,312 Z" fill={t.surface} stroke={so} strokeWidth="1.2"/>
          <ellipse cx="132" cy="385" rx="16" ry="6" fill={t.surface} stroke={so} strokeWidth="0.9"/>
          <path d="M92,214 L108,214 L108,320 Q104,322 100,322 Q96,322 92,320 Z" fill={t.surface} stroke="none"/>
          <path d="M92,320 Q90,378 100,385 Q110,378 108,320" fill={t.surface} stroke={so} strokeWidth="1.1"/>

          {/* ════ MUSCLES BACK ════ */}

          {/* Trapezius — diamond from neck to mid-back */}
          {(()=>{const c=zc("trap","Espalda");return(<g onClick={()=>tap("trap")} style={{cursor:"pointer"}}>
            {/* Upper fibers: neck → acromions */}
            <path d="M89,58 Q76,64 56,76 Q72,92 100,94 Q128,92 144,76 Q124,64 111,58 Q100,54 89,58Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            {/* Middle fibers: descend to T8 */}
            <path d="M84,94 Q82,112 84,132 Q88,142 92,144 Q95,130 96,106 Q100,100 84,94Z" fill={c.f} stroke={c.s} strokeWidth={c.sw} opacity="0.85"/>
            <path d="M116,94 Q118,112 116,132 Q112,142 108,144 Q105,130 104,106 Q100,100 116,94Z" fill={c.f} stroke={c.s} strokeWidth={c.sw} opacity="0.85"/>
          </g>);})()}

          {/* Posterior deltoid — rear shoulder cap */}
          {(()=>{const c=zc("hombro_post","Hombros");return(<g onClick={()=>tap("hombro_post")} style={{cursor:"pointer"}}>
            <path d="M58,70 Q44,74 30,92 Q26,108 36,124 Q48,130 62,122 Q70,110 68,88Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            <path d="M142,70 Q156,74 170,92 Q174,108 164,124 Q152,130 138,122 Q130,110 132,88Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
          </g>);})()}

          {/* Infraspinatus + teres — fills infraspinous fossa */}
          {(()=>{const c=zc("espalda_med","Espalda");return(<g onClick={()=>tap("espalda_med")} style={{cursor:"pointer"}}>
            <path d="M58,96 Q46,118 50,140 Q62,152 80,150 Q90,138 88,118 Q84,100 70,94Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            <path d="M142,96 Q154,118 150,140 Q138,152 120,150 Q110,138 112,118 Q116,100 130,94Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            <path d="M56,114 Q68,120 84,118" fill="none" stroke={dl} strokeWidth="0.8" opacity="0.4"/>
            <path d="M144,114 Q132,120 116,118" fill="none" stroke={dl} strokeWidth="0.8" opacity="0.4"/>
            <path d="M58,130 Q70,136 86,134" fill="none" stroke={dl} strokeWidth="0.7" opacity="0.35"/>
            <path d="M142,130 Q130,136 114,134" fill="none" stroke={dl} strokeWidth="0.7" opacity="0.35"/>
          </g>);})()}

          {/* Latissimus dorsi — wide V-taper sweep */}
          {(()=>{const c=zc("dorsal","Espalda");return(<g onClick={()=>tap("dorsal")} style={{cursor:"pointer"}}>
            <path d="M54,102 Q38,130 36,162 Q36,188 48,204 Q64,212 80,206 Q92,190 92,166 Q90,142 82,120 Q72,104 54,102Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            <path d="M146,102 Q162,130 164,162 Q164,188 152,204 Q136,212 120,206 Q108,190 108,166 Q110,142 118,120 Q128,104 146,102Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            <path d="M44,122 Q38,152 40,180" fill="none" stroke={dl} strokeWidth="0.8" opacity="0.4"/>
            <path d="M156,122 Q162,152 160,180" fill="none" stroke={dl} strokeWidth="0.8" opacity="0.4"/>
          </g>);})()}

          {/* Erector spinae — bilateral columns */}
          {(()=>{const c=zc("lumbar","Espalda");return(<g onClick={()=>tap("lumbar")} style={{cursor:"pointer"}}>
            <path d="M86,136 Q90,144 92,166 Q92,188 90,202 Q86,206 82,202 Q80,184 80,166 Q82,148 86,136Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            <path d="M114,136 Q110,144 108,166 Q108,188 110,202 Q114,206 118,202 Q120,184 120,166 Q118,148 114,136Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
          </g>);})()}

          {/* Triceps long head — medial posterior upper arm */}
          {(()=>{const c=zc("triceps_long","Tríceps");return(<g onClick={()=>tap("triceps_long")} style={{cursor:"pointer"}}>
            <path d="M56,104 Q44,124 44,150 Q44,170 54,178 Q68,182 78,176 Q86,160 82,136 Q78,114 66,102Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            <path d="M144,104 Q156,124 156,150 Q156,170 146,178 Q132,182 122,176 Q114,160 118,136 Q122,114 134,102Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
          </g>);})()}

          {/* Triceps lateral head — outer horseshoe */}
          {(()=>{const c=zc("triceps_lat","Tríceps");return(<g onClick={()=>tap("triceps_lat")} style={{cursor:"pointer"}}>
            <path d="M30,110 Q22,130 22,156 Q24,170 36,174 Q50,172 56,158 Q58,134 50,112Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            <path d="M170,110 Q178,130 178,156 Q176,170 164,174 Q150,172 144,158 Q142,134 150,112Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
          </g>);})()}

          {/* Forearm extensors — back of arm */}
          {(()=>{const c=zc("antebrazo_f","Bíceps");return(<g onClick={()=>tap("antebrazo_f")} style={{cursor:"pointer"}}>
            <path d="M18,168 Q10,186 10,208 Q10,224 16,234 Q26,240 40,236 Q48,222 48,202 Q48,182 42,170 Q30,162 18,168Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            <path d="M182,168 Q190,186 190,208 Q190,224 184,234 Q174,240 160,236 Q152,222 152,202 Q152,182 158,170 Q170,162 182,168Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
          </g>);})()}

          {/* Gluteus maximus — large rounded prominent */}
          {(()=>{const c=zc("gluteos_b","Piernas");return(<g onClick={()=>tap("gluteos_b")} style={{cursor:"pointer"}}>
            <path d="M56,212 Q46,234 46,260 Q50,280 68,284 Q88,282 96,264 Q100,244 92,226 Q82,210 56,212Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            <path d="M144,212 Q154,234 154,260 Q150,280 132,284 Q112,282 104,264 Q100,244 108,226 Q118,210 144,212Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            <path d="M54,238 Q70,260 96,262" fill="none" stroke={dl} strokeWidth="0.9" opacity="0.4"/>
            <path d="M146,238 Q130,260 104,262" fill="none" stroke={dl} strokeWidth="0.9" opacity="0.4"/>
          </g>);})()}

          {/* Hamstrings — biceps femoris outer + semitendinosus inner */}
          {(()=>{const c=zc("isquios","Piernas");return(<g onClick={()=>tap("isquios")} style={{cursor:"pointer"}}>
            {/* Biceps femoris — outer/lateral */}
            <path d="M50,270 Q44,296 46,322 Q48,336 60,340 Q70,338 74,324 Q76,300 72,274 Q64,262 50,270Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            {/* Semitendinosus — inner/medial */}
            <path d="M72,268 Q76,292 78,320 Q78,336 72,340 Q82,344 92,340 Q98,326 96,304 Q94,278 88,260 Q80,252 72,268Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            {/* Right biceps femoris */}
            <path d="M150,270 Q156,296 154,322 Q152,336 140,340 Q130,338 126,324 Q124,300 128,274 Q136,262 150,270Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            {/* Right semitendinosus */}
            <path d="M128,268 Q124,292 122,320 Q122,336 128,340 Q118,344 108,340 Q102,326 104,304 Q106,278 112,260 Q120,252 128,268Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            <line x1="70" y1="272" x2="72" y2="336" stroke={dl} strokeWidth="0.7" opacity="0.4"/>
            <line x1="130" y1="272" x2="128" y2="336" stroke={dl} strokeWidth="0.7" opacity="0.4"/>
          </g>);})()}

          {/* Gastrocnemius — twin diamond heads */}
          {(()=>{const c=zc("pantorrilla_b","Piernas");return(<g onClick={()=>tap("pantorrilla_b")} style={{cursor:"pointer"}}>
            {/* Left medial head */}
            <path d="M72,332 Q68,354 68,378 Q70,390 80,392 Q90,390 90,378 Q90,356 84,340 Q80,330 72,332Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            {/* Left lateral head */}
            <path d="M60,332 Q54,354 52,378 Q54,390 64,392 Q72,388 72,376 Q70,354 64,338 Q62,328 60,332Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            <path d="M66,334 Q66,364 68,388" fill="none" stroke={dl} strokeWidth="0.9" opacity="0.5"/>
            {/* Right medial head */}
            <path d="M128,332 Q132,354 132,378 Q130,390 120,392 Q110,390 110,378 Q110,356 116,340 Q120,330 128,332Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            {/* Right lateral head */}
            <path d="M140,332 Q146,354 148,378 Q146,390 136,392 Q128,388 128,376 Q130,354 136,338 Q138,328 140,332Z" fill={c.f} stroke={c.s} strokeWidth={c.sw}/>
            <path d="M134,334 Q134,364 132,388" fill="none" stroke={dl} strokeWidth="0.9" opacity="0.5"/>
          </g>);})()}

          {!selected&&<text x="100" y="393" textAnchor="middle" fill={t.textTertiary} fontSize="8" fontFamily="-apple-system,sans-serif">Posterior</text>}
        </svg>
      </div>

      {/* Info panel below both bodies */}
      {selZone?(
        <div style={{display:"flex",flexDirection:"column",gap:"0.42rem"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
            <div>
              <div style={{color:t.text,fontSize:"0.86rem",fontWeight:800,lineHeight:1.2}}>{selZone.label}</div>
              <div style={{color:t.textTertiary,fontSize:"0.58rem",fontStyle:"italic",marginTop:"0.1rem"}}>{selZone.subLabel}</div>
            </div>
            <button onClick={()=>setSelected(null)} style={{background:"none",border:"none",color:t.textTertiary,cursor:"pointer",fontSize:"0.9rem",padding:"0.1rem 0.3rem"}}>✕</button>
          </div>
          <div style={{background:t.accentMuted,borderRadius:"8px",padding:"0.4rem 0.5rem"}}>
            <div style={{color:t.textSecondary,fontSize:"0.68rem",lineHeight:1.5}}>{selZone.desc}</div>
          </div>
          <div style={{color:t.textTertiary,fontSize:"0.55rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:"0.05rem"}}>Ejercicios</div>
          {selZone.exercises.map(ex=>(
            <div key={ex.id} style={{padding:"0.28rem 0",borderBottom:`1px solid ${t.border}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:"0.3rem"}}>
                <span style={{color:t.text,fontSize:"0.74rem",fontWeight:600}}>{ex.name}</span>
                {onAddExercise&&<button onClick={()=>{onAddExercise({id:ex.id,name:ex.name},selZone.muscle);setSelected(null);}} style={{background:t.accent,color:t.bg,border:"none",borderRadius:"6px",padding:"0.12rem 0.44rem",fontSize:"0.6rem",fontWeight:700,cursor:"pointer",flexShrink:0}}>+ Añadir</button>}
              </div>
              <div style={{color:t.textTertiary,fontSize:"0.58rem",marginTop:"0.03rem"}}>{ex.note}</div>
            </div>
          ))}
        </div>
      ):(
        <div style={{color:t.textTertiary,fontSize:"0.65rem",textAlign:"center",lineHeight:1.5,padding:"0.25rem 0"}}>
          Pulsa cualquier músculo · ver función y ejercicios
        </div>
      )}

      {/* Legend */}
      <div style={{display:"flex",justifyContent:"center",gap:"0.5rem",flexWrap:"wrap"}}>
        {[{l:"Dale caña",c:"#22C55E"},{l:"Poco vol.",c:"#84CC16"},{l:"Progresando",c:"#F59E0B"},{l:"Bien trab.",c:"#EF4444"},{l:"Al límite",c:"#DC2626"}].map(item=>(
          <div key={item.l} style={{display:"flex",alignItems:"center",gap:"0.2rem"}}>
            <div style={{width:8,height:8,borderRadius:2,background:item.c,border:item.c==="#FFFFFF"?`1px solid ${t.border}`:""}}/>
            <span style={{color:t.textTertiary,fontSize:"0.55rem"}}>{item.l}</span>
          </div>
        ))}
      </div>

      {/* Volume bars */}
      {countdown&&<div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"0.1rem"}}>
        <span style={{color:t.textTertiary,fontSize:"0.58rem",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em"}}>Semana actual</span>
        <span style={{color:t.gold,fontSize:"0.6rem",fontWeight:700}}>🔄 Reset en {countdown}</span>
      </div>}
      <div style={{display:"flex",flexDirection:"column",gap:"0.32rem"}}>
        {Object.entries(OPT_TARGETS).map(([m,target])=>{
          const sets=weekVol[m]||0;
          const todayN=todaySets?.[m]||0;
          const pct=Math.min(100,sets/target*100);
          const col=pct>=100?t.green:pct>=70?t.blue:pct>=40?t.gold:pct>0?t.red:t.border;
          const highlight=todayN>0;
          return (
            <div key={m} style={{display:"flex",alignItems:"center",gap:"0.5rem"}}>
              <span style={{color:highlight?t.text:t.textSecondary,fontSize:"0.65rem",minWidth:"70px",fontWeight:highlight?700:400}}>{m}</span>
              <div style={{flex:1,background:t.surfaceHover,borderRadius:"4px",height:highlight?6:4,position:"relative",overflow:"hidden"}}>
                <div style={{width:`${pct}%`,height:"100%",background:col,borderRadius:"4px",transition:"width 0.4s"}}/>
              </div>
              <span style={{color:col,fontSize:"0.65rem",fontWeight:700,minWidth:"30px",textAlign:"right"}}>{sets}/{target}</span>
              {highlight&&<span style={{color:t.green,fontSize:"0.58rem",fontWeight:700,minWidth:"32px",whiteSpace:"nowrap"}}>+{todayN}hoy</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}



// ─── SLEEP TRACKER ───────────────────────────────────────────────────────────
// REM estimate: 20-25% of total sleep; cycles = total / 90 min
function calcSleepStats(segments) {
  // segments: [{beddate:"2024-01-15", bedtime:"23:00", wakedate:"2024-01-16", wake:"06:30", interruptions:[...]}]
  // beddate/wakedate are optional (legacy entries only have times)
  if(!segments||segments.length===0) return null;
  let totalMins=0;
  let interruptionMins=0;
  const toMins=s=>{const[h,m]=s.split(":").map(Number);return h*60+m;};
  segments.forEach(seg=>{
    if(!seg.bedtime||!seg.wake) return;
    let dur;
    if(seg.beddate&&seg.wakedate) {
      const bedDt=new Date(`${seg.beddate}T${seg.bedtime}:00`);
      const wakeDt=new Date(`${seg.wakedate}T${seg.wake}:00`);
      dur=Math.round((wakeDt-bedDt)/60000);
    } else {
      let bed=toMins(seg.bedtime),wake=toMins(seg.wake);
      if(wake<=bed) wake+=24*60; // cruzó medianoche (datos legacy)
      dur=wake-bed;
    }
    if(dur>0) totalMins+=dur;
    (seg.interruptions||[]).forEach(({from,to})=>{
      if(!from||!to) return;
      let f=toMins(from),tt=toMins(to);
      if(tt<f) tt+=24*60;
      interruptionMins+=tt-f;
    });
  });
  const effectiveMins=Math.max(0,totalMins-interruptionMins);
  const cycles=Math.floor(effectiveMins/90);
  const remMins=Math.round(effectiveMins*0.22);
  const deepMins=Math.round(effectiveMins*0.20);
  const lightMins=effectiveMins-remMins-deepMins;
  const score=cycles>=5?100:cycles===4?85:cycles===3?65:cycles===2?40:20;
  return {totalMins,effectiveMins,interruptionMins,cycles,remMins,deepMins,lightMins,score,totalH:(effectiveMins/60).toFixed(1)};
}

function SleepCalculator({t, onApply}) {
  const[targetWake,setTargetWake]=useState("07:00");
  const[targetBed,setTargetBed]=useState("23:00");
  const[calcMode,setCalcMode]=useState("wake"); // "wake" | "bed"
  const[saved,setSaved]=useState(null);

  const toMins=s=>{const[h,m]=s.split(":").map(Number);return h*60+m;};
  const toHHMM=m=>{const hh=Math.floor(((m%1440)+1440)%1440/60),mm=((m%1440)+1440)%60;return`${String(hh).padStart(2,"0")}:${String(mm).padStart(2,"0")}`;};

  // Ideal wake times based on bedtime (cycles of 90min + 15min to fall asleep)
  const wakeOptions = calcMode==="wake"
    ? [3,4,5,6].map(c=>toHHMM(toMins(targetBed)+15+c*90))
    : [3,4,5,6].map(c=>toHHMM(toMins(targetWake)-15-c*90));

  const labels=["3 ciclos","4 ciclos","5 ciclos","6 ciclos"];
  const quality=["⚠️ Mínimo","😐 Regular","✅ Bueno","🔥 Óptimo"];
  const hours=[3,4,5,6].map(c=>((c*90)/60).toFixed(1));

  const handleUsar=(time,i)=>{
    if(onApply) onApply(time, calcMode);
    setSaved(i);
    // Reset para el próximo ciclo
    setTimeout(()=>{
      setTargetWake("07:00");
      setTargetBed("23:00");
      setSaved(null);
    }, 1200);
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:"0.65rem"}}>
      <div style={{display:"flex",gap:"0.3rem"}}>
        {[["wake","¿A qué hora me duermo?"],["bed","¿A qué hora me despierto?"]].map(([m,l])=>(
          <button key={m} onClick={()=>setCalcMode(m)} style={{flex:1,padding:"0.38rem 0.3rem",borderRadius:"8px",border:"none",cursor:"pointer",fontSize:"0.63rem",fontWeight:700,background:calcMode===m?t.accent:t.surfaceHover,color:calcMode===m?t.bg:t.textSecondary}}>{l}</button>
        ))}
      </div>
      <div style={{display:"flex",gap:"0.5rem",alignItems:"center"}}>
        <span style={{color:t.textTertiary,fontSize:"0.7rem",minWidth:"55px"}}>{calcMode==="wake"?"Me duermo:":"Me despierto:"}</span>
        <input type="time" value={calcMode==="wake"?targetBed:targetWake}
          onChange={e=>calcMode==="wake"?setTargetBed(e.target.value):setTargetWake(e.target.value)}
          style={{background:t.surfaceHover,border:`1px solid ${t.border}`,borderRadius:"8px",color:t.text,padding:"0.38rem 0.6rem",fontSize:"0.82rem",outline:"none",fontFamily:"inherit"}}/>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:"0.3rem"}}>
        {wakeOptions.map((time,i)=>(
          <div key={i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"0.45rem 0.7rem",borderRadius:"10px",background:saved===i?t.greenBg:i===2||i===3?t.greenBg:t.surfaceHover,border:`1px solid ${saved===i?t.green:i===2||i===3?t.green+"30":t.border}`}}>
            <div>
              <span style={{color:t.text,fontWeight:700,fontSize:"0.85rem"}}>{time}</span>
              <span style={{color:t.textTertiary,fontSize:"0.62rem",marginLeft:"0.5rem"}}>{hours[i]}h · {labels[i]}</span>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:"0.4rem"}}>
              <span style={{fontSize:"0.7rem"}}>{saved===i?"✅ Guardado":quality[i]}</span>
              {onApply&&<button onClick={()=>handleUsar(time,i)} style={{background:t.accent,border:"none",borderRadius:"7px",padding:"0.18rem 0.5rem",color:t.bg,fontSize:"0.6rem",cursor:"pointer",fontWeight:700,whiteSpace:"nowrap"}}>Usar</button>}
            </div>
          </div>
        ))}
      </div>
      <div style={{color:t.textTertiary,fontSize:"0.6rem",lineHeight:1.5,marginTop:"0.1rem"}}>
        Cada ciclo de sueño dura ~90 min. Se añaden 15 min para quedarse dormido. Despertarse entre ciclos = más descansado que en medio de uno.
      </div>
    </div>
  );
}

function SleepTracker({sleep, setSleep, t}) {
  const[viewDate,setViewDate]=useState(today);
  const[showCalc,setShowCalc]=useState(false);
  const[savedMsg,setSavedMsg]=useState(false);
  const emptyDraft=(date)=>({segments:[{beddate:date,bedtime:"",wake:"",interruptions:[]}]});
  const[draft,setDraft]=useState(()=>emptyDraft(today()));

  // Reset draft when navigating dates
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(()=>{setDraft(emptyDraft(viewDate));},[viewDate]);

  // Saved entries for viewDate
  const savedSegs=(sleep[viewDate]?.segments||[]).filter(s=>s.bedtime||s.wake);
  const stats=calcSleepStats(savedSegs.length?savedSegs:null);

  // Draft helpers
  const updateSeg=(i,field,val)=>setDraft(p=>{const segs=[...p.segments];segs[i]={...segs[i],[field]:val};return{...p,segments:segs};});
  const addSeg=()=>setDraft(p=>({...p,segments:[...p.segments,{beddate:p.segments[0]?.beddate||viewDate,bedtime:"",wake:"",interruptions:[]}]}));
  const removeSeg=i=>setDraft(p=>{const segs=p.segments.filter((_,idx)=>idx!==i);return{...p,segments:segs.length?segs:[{beddate:viewDate,bedtime:"",wake:"",interruptions:[]}]};});
  const addInterruption=i=>setDraft(p=>{const segs=[...p.segments];segs[i]={...segs[i],interruptions:[...(segs[i].interruptions||[]),{from:"",to:""}]};return{...p,segments:segs};});
  const updateIntr=(si,ii,field,val)=>setDraft(p=>{const segs=[...p.segments];const intrs=[...(segs[si].interruptions||[])];intrs[ii]={...intrs[ii],[field]:val};segs[si]={...segs[si],interruptions:intrs};return{...p,segments:segs};});
  const removeIntr=(si,ii)=>setDraft(p=>{const segs=[...p.segments];segs[si]={...segs[si],interruptions:(segs[si].interruptions||[]).filter((_,idx)=>idx!==ii)};return{...p,segments:segs};});

  const draftHasData=draft.segments.some(s=>s.bedtime||s.wake);

  const toMinsLocal=s=>{const[h,m]=s.split(":").map(Number);return h*60+m;};
  const getWakedate=(seg)=>{
    const bd=seg.beddate||viewDate;
    if(seg.bedtime&&seg.wake&&toMinsLocal(seg.wake)<=toMinsLocal(seg.bedtime)) return addDays(bd,1);
    return bd;
  };

  const handleGuardar=()=>{
    if(!draftHasData) return;
    const newSegs=draft.segments.filter(s=>s.bedtime||s.wake).map(seg=>({
      ...seg,
      beddate:seg.beddate||viewDate,
      wakedate:getWakedate(seg),
    }));
    // Group by beddate so each goes to the right day's key
    const byDate={};
    newSegs.forEach(seg=>{
      const bd=seg.beddate;
      if(!byDate[bd]) byDate[bd]=[];
      byDate[bd].push(seg);
    });
    setSleep(p=>{
      let next={...p};
      Object.entries(byDate).forEach(([date,segs])=>{
        const existing=(next[date]?.segments||[]).filter(s=>s.bedtime||s.wake);
        next[date]={segments:[...existing,...segs]};
      });
      return next;
    });
    setDraft(emptyDraft(viewDate));
    setSavedMsg(true);
    setTimeout(()=>setSavedMsg(false),2500);
  };

  const handleDeshacer=()=>setDraft(emptyDraft(viewDate));

  const removeSavedSeg=i=>setSleep(p=>{
    const segs=(p[viewDate]?.segments||[]).filter(s=>s.bedtime||s.wake).filter((_,idx)=>idx!==i);
    if(!segs.length){const n={...p};delete n[viewDate];return n;}
    return {...p,[viewDate]:{segments:segs}};
  });

  const scoreColor=s=>s>=85?t.green:s>=60?t.blue:s>=40?t.gold:t.red;
  const ratingLabel=s=>s>=85?"Óptimo 🔥":s>=60?"Bueno ✅":s>=40?"Regular 😐":"Insuficiente ⚠️";

  return (
    <div style={{display:"flex",flexDirection:"column",gap:"0.6rem"}}>
      {/* Header: título + navegador de fecha + calculadora */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div style={{color:t.textTertiary,fontSize:"0.58rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase"}}>😴 Sueño</div>
        <button onClick={()=>setShowCalc(p=>!p)} style={{background:t.surfaceHover,border:`1px solid ${t.border}`,borderRadius:"8px",padding:"0.2rem 0.55rem",color:t.textSecondary,fontSize:"0.62rem",cursor:"pointer",fontWeight:600}}>
          {showCalc?"✕ Calculadora":"🧮 Calculadora"}
        </button>
      </div>
      {/* Navegador de fecha */}
      <div style={{display:"flex",alignItems:"center",gap:"0.3rem",background:t.surfaceHover,borderRadius:"10px",padding:"0.3rem 0.5rem",border:`1px solid ${t.border}`}}>
        <button onClick={()=>setViewDate(p=>addDays(p,-1))} style={{background:"transparent",border:"none",color:t.textSecondary,cursor:"pointer",fontSize:"1rem",lineHeight:1,padding:"0 0.2rem"}}>‹</button>
        <div style={{flex:1,textAlign:"center",color:t.text,fontSize:"0.72rem",fontWeight:700}}>{fmtDateLabel(viewDate)}</div>
        <button onClick={()=>setViewDate(p=>addDays(p,1))} disabled={viewDate>=today()} style={{background:"transparent",border:"none",color:viewDate>=today()?t.textTertiary:t.textSecondary,cursor:viewDate>=today()?"not-allowed":"pointer",fontSize:"1rem",lineHeight:1,padding:"0 0.2rem"}}>›</button>
      </div>

      {/* Saved stats */}
      {stats&&(
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:"0.35rem"}}>
          {[
            {l:"Total",v:`${stats.totalH}h`,c:scoreColor(stats.score)},
            {l:"Ciclos",v:stats.cycles,c:stats.cycles>=5?t.green:stats.cycles>=4?t.blue:t.gold},
            {l:"REM est.",v:`${Math.round(stats.remMins/60*10)/10}h`,c:t.purple},
            {l:"Calidad",v:stats.score,c:scoreColor(stats.score)},
          ].map(s=>(
            <div key={s.l} style={{background:t.surfaceHover,borderRadius:"10px",padding:"0.45rem 0.3rem",textAlign:"center"}}>
              <div style={{color:s.c,fontSize:"0.9rem",fontWeight:800,lineHeight:1}}>{s.v}</div>
              <div style={{color:t.textTertiary,fontSize:"0.48rem",fontWeight:700,textTransform:"uppercase",marginTop:"0.15rem"}}>{s.l}</div>
            </div>
          ))}
        </div>
      )}

      {/* Saved entries (read-only) */}
      {savedSegs.map((seg,i)=>{
        const wkd=seg.wakedate&&seg.wakedate!==seg.beddate;
        return (
          <div key={i} style={{background:t.greenBg,borderRadius:"12px",padding:"0.55rem 0.85rem",border:`1px solid ${t.green}30`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <span style={{color:t.green,fontSize:"0.6rem",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em"}}>✅ Guardado · {i===0?"Bloque principal":`Bloque #${i+1}`}</span>
              <div style={{color:t.text,fontSize:"0.82rem",fontWeight:700,marginTop:"0.1rem"}}>
                {seg.bedtime||"--:--"} → {seg.wake||"--:--"}{wkd&&<span style={{color:t.gold,fontSize:"0.6rem",fontWeight:600,marginLeft:"0.3rem"}}>(+1d)</span>}
                {(seg.interruptions||[]).filter(x=>x.from).length>0&&<span style={{color:t.textTertiary,fontSize:"0.6rem",fontWeight:400,marginLeft:"0.4rem"}}>{(seg.interruptions||[]).filter(x=>x.from).length} desvelos</span>}
              </div>
            </div>
            <button onClick={()=>removeSavedSeg(i)} style={{background:"transparent",border:"none",color:t.red,cursor:"pointer",fontSize:"0.7rem",padding:"0.2rem"}}>✕</button>
          </div>
        );
      })}

      {/* Draft form */}
      {draft.segments.map((seg,i)=>{
        const isNextDay=seg.bedtime&&seg.wake&&toMinsLocal(seg.wake)<=toMinsLocal(seg.bedtime);
        const wakeDisplayDate=isNextDay?addDays(seg.beddate||viewDate,1):(seg.beddate||viewDate);
        return (
        <div key={i} style={{background:t.surfaceHover,borderRadius:"12px",padding:"0.7rem 0.85rem",border:`1px solid ${t.border}`}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"0.45rem"}}>
            <span style={{color:t.textSecondary,fontSize:"0.72rem",fontWeight:600}}>
              {savedSegs.length===0&&i===0?"Bloque principal":i===0?"Nuevo bloque":`Bloque extra #${i+1}`}
            </span>
            {i>0&&<button onClick={()=>removeSeg(i)} style={{background:"transparent",border:"none",color:t.textTertiary,cursor:"pointer",fontSize:"0.72rem"}}>✕</button>}
          </div>
          {/* Fecha de inicio */}
          <div style={{marginBottom:"0.45rem"}}>
            <div style={{color:t.textTertiary,fontSize:"0.55rem",fontWeight:600,marginBottom:"0.2rem"}}>Fecha (me dormí el)</div>
            <input type="date" value={seg.beddate||viewDate} max={today()} onChange={e=>updateSeg(i,"beddate",e.target.value)}
              style={{width:"100%",background:t.surface,border:`1px solid ${t.border}`,borderRadius:"8px",color:t.text,padding:"0.38rem 0.5rem",fontSize:"0.78rem",outline:"none",fontFamily:"inherit",boxSizing:"border-box"}}/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0.45rem",marginBottom:"0.35rem"}}>
            <div>
              <div style={{color:t.textTertiary,fontSize:"0.55rem",fontWeight:600,marginBottom:"0.2rem"}}>Me dormí</div>
              <input type="time" value={seg.bedtime||""} onChange={e=>updateSeg(i,"bedtime",e.target.value)}
                style={{width:"100%",background:t.surface,border:`1px solid ${t.border}`,borderRadius:"8px",color:t.text,padding:"0.42rem 0.5rem",fontSize:"0.82rem",outline:"none",fontFamily:"inherit",boxSizing:"border-box"}}/>
            </div>
            <div>
              <div style={{color:t.textTertiary,fontSize:"0.55rem",fontWeight:600,marginBottom:"0.2rem"}}>
                Me desperté{isNextDay&&<span style={{color:t.gold,marginLeft:"0.3rem"}}>+1d ({fmtDateLabel(wakeDisplayDate)})</span>}
              </div>
              <input type="time" value={seg.wake||""} onChange={e=>updateSeg(i,"wake",e.target.value)}
                style={{width:"100%",background:t.surface,border:`1px solid ${t.border}`,borderRadius:"8px",color:t.text,padding:"0.42rem 0.5rem",fontSize:"0.82rem",outline:"none",fontFamily:"inherit",boxSizing:"border-box"}}/>
            </div>
          </div>
          {(seg.interruptions||[]).map((intr,ii)=>(
            <div key={ii} style={{display:"flex",gap:"0.35rem",alignItems:"center",marginBottom:"0.3rem"}}>
              <span style={{color:t.textTertiary,fontSize:"0.6rem",minWidth:"24px"}}>📱</span>
              <input type="time" value={intr.from||""} onChange={e=>updateIntr(i,ii,"from",e.target.value)}
                style={{flex:1,background:t.surface,border:`1px solid ${t.border}`,borderRadius:"6px",color:t.text,padding:"0.3rem 0.4rem",fontSize:"0.72rem",outline:"none",fontFamily:"inherit"}}/>
              <span style={{color:t.textTertiary,fontSize:"0.6rem"}}>→</span>
              <input type="time" value={intr.to||""} onChange={e=>updateIntr(i,ii,"to",e.target.value)}
                style={{flex:1,background:t.surface,border:`1px solid ${t.border}`,borderRadius:"6px",color:t.text,padding:"0.3rem 0.4rem",fontSize:"0.72rem",outline:"none",fontFamily:"inherit"}}/>
              <button onClick={()=>removeIntr(i,ii)} style={{background:"transparent",border:"none",color:t.textTertiary,cursor:"pointer",fontSize:"0.7rem",flexShrink:0}}>✕</button>
            </div>
          ))}
          <button onClick={()=>addInterruption(i)} style={{background:"transparent",border:`1px dashed ${t.border}`,borderRadius:"7px",padding:"0.2rem 0.5rem",color:t.textTertiary,fontSize:"0.6rem",cursor:"pointer",width:"100%",marginTop:"0.15rem"}}>
            + Desvelo / miré el móvil
          </button>
        </div>
        );
      })}

      <button onClick={addSeg} style={{background:"transparent",border:`1px dashed ${t.border}`,borderRadius:"10px",padding:"0.45rem",color:t.textTertiary,fontSize:"0.68rem",cursor:"pointer"}}>
        + Añadir siesta o bloque extra
      </button>

      {/* Guardar / Deshacer */}
      <div style={{display:"flex",gap:"0.4rem"}}>
        <button onClick={handleGuardar} disabled={!draftHasData} style={{flex:1,padding:"0.55rem",borderRadius:"10px",border:"none",cursor:draftHasData?"pointer":"not-allowed",fontSize:"0.75rem",fontWeight:700,background:draftHasData?t.green:t.surfaceHover,color:draftHasData?t.bg:t.textTertiary,transition:"all 0.2s"}}>
          {savedMsg?"✅ Guardado":"💾 Guardar sueño"}
        </button>
        {draftHasData&&<button onClick={handleDeshacer} style={{padding:"0.55rem 0.8rem",borderRadius:"10px",border:`1px solid ${t.border}`,cursor:"pointer",fontSize:"0.75rem",fontWeight:700,background:"transparent",color:t.textSecondary}}>
          Deshacer
        </button>}
      </div>

      {/* Rating */}
      {stats&&(
        <div style={{background:stats.score>=85?t.greenBg:stats.score>=60?t.blueBg:stats.score>=40?t.goldBg:t.redBg,border:`1px solid ${scoreColor(stats.score)}30`,borderRadius:"12px",padding:"0.6rem 0.9rem",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <div style={{color:scoreColor(stats.score),fontSize:"0.72rem",fontWeight:700}}>{ratingLabel(stats.score)}</div>
            <div style={{color:t.textTertiary,fontSize:"0.6rem",marginTop:"0.1rem"}}>
              {stats.interruptionMins>0?`${stats.interruptionMins}min interrupciones · `:""}
              REM ~{stats.remMins}min · Deep ~{stats.deepMins}min
            </div>
          </div>
          <div style={{color:scoreColor(stats.score),fontSize:"1.4rem",fontWeight:800,lineHeight:1}}>{stats.cycles}<span style={{fontSize:"0.65rem",fontWeight:400,color:t.textTertiary,marginLeft:"0.1rem"}}>ciclos</span></div>
        </div>
      )}

      {/* Calculator */}
      {showCalc&&(
        <div style={{background:t.surfaceHover,border:`1px solid ${t.border}`,borderRadius:"14px",padding:"0.85rem 0.9rem"}}>
          <div style={{color:t.textTertiary,fontSize:"0.58rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:"0.6rem"}}>🧮 Calculadora de ciclos</div>
          <SleepCalculator t={t} onApply={(time,mode)=>{
            const field=mode==="wake"?"wake":"bedtime";
            updateSeg(0,field,time);
          }}/>
        </div>
      )}
    </div>
  );
}

// ─── WEEK COUNTDOWN ──────────────────────────────────────────────────────────
function WeekCountdown({t}) {
  const [rem, setRem] = useState(0);
  useEffect(()=>{
    const tick=()=>{
      const now=new Date();
      const nextMon=new Date(now);
      const dow=now.getDay(); // 0=dom,1=lun...6=sab
      const daysLeft=(dow===0?1:8-dow); // días hasta lunes
      nextMon.setDate(now.getDate()+daysLeft);
      nextMon.setHours(0,0,0,0);
      setRem(nextMon-now);
    };
    tick();
    const id=setInterval(tick,60000);
    return()=>clearInterval(id);
  },[]);
  const totalMins=Math.floor(rem/60000);
  const days=Math.floor(totalMins/(60*24));
  const hours=Math.floor((totalMins%(60*24))/60);
  const mins=totalMins%60;
  const urgency=days===0?t.red:days<=1?t.gold:t.textSecondary;
  return (
    <div style={{background:t.surfaceHover,borderRadius:"12px",padding:"0.6rem 0.9rem",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
      <div>
        <div style={{color:t.textTertiary,fontSize:"0.52rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:"0.15rem"}}>📅 Reinicio semana</div>
        <div style={{color:t.text,fontSize:"1.05rem",fontWeight:800,letterSpacing:"-0.02em",lineHeight:1}}>
          <span style={{color:urgency}}>{days}d </span>
          <span style={{color:urgency}}>{hours}h </span>
          <span style={{color:t.textTertiary,fontSize:"0.75rem",fontWeight:500}}>{mins}m</span>
        </div>
      </div>
      <div style={{textAlign:"right"}}>
        <div style={{color:t.textTertiary,fontSize:"0.58rem"}}>hasta el lunes</div>
        <div style={{color:t.textTertiary,fontSize:"0.58rem"}}>00:00 · reset datos</div>
      </div>
    </div>
  );
}

// ─── HOY TAB ─────────────────────────────────────────────────────────────────
// Vista principal del día: resumen de hábitos, kcal, peso, cita bíblica,
// consejo diario y lista de hábitos obligatorios/opcionales con timers.
function HoyTab({habits,setHabits,habitConfig,setHabitConfig,bodyStats,setBodyStats,nutrition,workouts,goal,sleep,setSleep,currentDay,t}) {
  const[viewDate,setViewDate]=useState(today);
  const tk=viewDate;
  // Medianoche: avanza solo si el usuario estaba viendo "hoy"
  useEffect(()=>{setViewDate(p=>p===addDays(currentDay,-1)?currentDay:p);},[currentDay]);
  const todayH=habits[tk]||{};
  const items=Object.values((nutrition[tk]||{}).items||{});
  const totalCal=items.reduce((a,i)=>a+i.cal,0);
  const totalP=items.reduce((a,i)=>a+i.p,0);
  const macros=calcMacros(goal);
  const todayW=workouts[tk]||{};
  const burnEst=todayW.duration?Math.round(({Muay:10,BJJ:8,Lucha:7,MMA:9}[todayW.sessionType]||5)*(parseFloat(goal.weight)||69)*(parseFloat(todayW.duration)/60)):0;

  const activeHabits = DEFAULT_HABITS.filter(h => {
    const cfg = habitConfig[h.id];
    return cfg !== "hidden";
  });
  const oblHabits = activeHabits.filter(h => {
    const cfg = habitConfig[h.id];
    return cfg === "obl" || (cfg === undefined && h.obl);
  });
  const optHabits = activeHabits.filter(h => {
    const cfg = habitConfig[h.id];
    return cfg === "opt" || (cfg === undefined && !h.obl);
  });

  const oblDone = oblHabits.filter(h=>todayH[h.id]).length;
  const pct = oblHabits.length>0?Math.round(oblDone/oblHabits.length*100):0;
  const toggle = id => setHabits(p=>({...p,[tk]:{...p[tk],[id]:!p[tk]?.[id]}}));
  const setTimer = (id,v) => setHabits(p=>({...p,[tk]:{...p[tk],[`${id}_min`]:v}}));

  const [qi] = useState(() => Math.floor(Math.random() * BIBLICAL.length));
  const [ti] = useState(() => Math.floor(Math.random() * DAILY_TIPS.length));
  const [oblOpen, setOblOpen] = useState(false);
  const[weekOpen,setWeekOpen]=useState(false);

  // Resumen semanal (últimos 7 días)
  const last7=Array.from({length:7},(_,i)=>{const d=new Date();d.setDate(d.getDate()-6+i);return d.toISOString().split("T")[0];});
  const weekGym=last7.filter(k=>workouts[k]?.sessionType==="Gym").length;
  const weekMA=last7.filter(k=>workouts[k]?.sessionType&&workouts[k].sessionType!=="Gym").length;
  const weekHabPcts=last7.map(k=>{const dh=habits[k]||{};const done=oblHabits.filter(h=>dh[h.id]).length;return oblHabits.length>0?done/oblHabits.length:0;});
  const weekAvgHab=Math.round(weekHabPcts.reduce((a,b)=>a+b,0)/7*100);

  const silenceHabit = id => setHabitConfig(p=>({...p,[id]:"hidden"}));

  return (
    <div style={{display:"flex",flexDirection:"column",gap:"1.25rem"}}>
      <div style={{padding:"0.1rem 0"}}>
        <div style={{color:t.text,fontSize:"0.88rem",lineHeight:1.6,fontStyle:"italic"}}>"{BIBLICAL[qi].text}"</div>
        <div style={{color:t.textTertiary,fontSize:"0.68rem",marginTop:"0.25rem"}}>— {BIBLICAL[qi].author}</div>
      </div>

      {/* Navegador de fecha */}
      <div style={{display:"flex",alignItems:"center",gap:"0.3rem",background:t.surfaceHover,borderRadius:"10px",padding:"0.3rem 0.5rem",border:`1px solid ${t.border}`}}>
        <button onClick={()=>setViewDate(p=>addDays(p,-1))} style={{background:"transparent",border:"none",color:t.textSecondary,cursor:"pointer",fontSize:"1.1rem",lineHeight:1,padding:"0 0.25rem"}}>‹</button>
        <div style={{flex:1,textAlign:"center",color:t.text,fontSize:"0.78rem",fontWeight:700}}>{fmtDateLabel(viewDate)}</div>
        <button onClick={()=>setViewDate(p=>addDays(p,1))} disabled={viewDate>=today()} style={{background:"transparent",border:"none",color:viewDate>=today()?t.textTertiary:t.textSecondary,cursor:viewDate>=today()?"not-allowed":"pointer",fontSize:"1.1rem",lineHeight:1,padding:"0 0.25rem"}}>›</button>
      </div>

      <Card t={t}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
          <div>
            <div style={{color:t.textTertiary,fontSize:"0.63rem",fontWeight:600,letterSpacing:"0.06em",textTransform:"uppercase"}}>{new Date(viewDate+"T12:00:00").toLocaleDateString("es-ES",{weekday:"long",day:"numeric",month:"long"})}</div>
            <div style={{color:t.text,fontSize:"2.2rem",fontWeight:800,lineHeight:1,marginTop:"0.1rem",letterSpacing:"-0.03em"}}>{pct}%</div>
            <div style={{color:t.textSecondary,fontSize:"0.72rem",marginTop:"0.08rem"}}>{oblDone}/{oblHabits.length} obligatorios</div>
            {todayW.sessionType&&<div style={{marginTop:"0.4rem",display:"inline-flex",alignItems:"center",gap:"0.3rem",background:t.accentMuted,borderRadius:"20px",padding:"0.2rem 0.6rem"}}><span style={{color:t.text,fontSize:"0.7rem",fontWeight:600}}>{todayW.sessionType}{burnEst>0?` · ~${burnEst} kcal`:""}</span></div>}
          </div>
          <Ring value={pct} max={100} size={68} stroke={5} color={pct===100?t.green:t.accent} bg={t.border}>
            <span style={{fontSize:"0.9rem"}}>{pct===100?"✓":"○"}</span>
          </Ring>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:"0.4rem",marginTop:"1rem"}}>
          {[
            {l:"Kcal",v:Math.round(totalCal),sub:`/ ${macros.cal}`,c:t.gold},
            {l:"Proteína",v:`${Math.round(totalP)}g`,sub:`/ ${macros.protein}g`,c:t.blue},
            {l:"Quemado",v:burnEst?`~${burnEst}`:"-",sub:"kcal",c:t.green},
          ].map(s=>(
            <div key={s.l} style={{background:t.surfaceHover,borderRadius:"12px",padding:"0.6rem 0.4rem",textAlign:"center"}}>
              <div style={{color:s.c,fontSize:"1rem",fontWeight:800,letterSpacing:"-0.02em"}}>{s.v}</div>
              <div style={{color:t.textTertiary,fontSize:"0.5rem",fontWeight:600,textTransform:"uppercase"}}>{s.l}</div>
              <div style={{color:t.textTertiary,fontSize:"0.56rem"}}>{s.sub}</div>
            </div>
          ))}
        </div>
      </Card>

      <WeekCountdown t={t}/>

      <div style={{background:t.goldBg,border:`1px solid ${t.gold}30`,borderRadius:"14px",padding:"0.85rem 1rem"}}>
        <div style={{color:t.gold,fontSize:"0.58rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:"0.25rem"}}>{DAILY_TIPS[ti].cat} · Hoy recuerda</div>
        <div style={{color:t.text,fontSize:"0.8rem",lineHeight:1.55}}>{DAILY_TIPS[ti].tip}</div>
      </div>

      <Card t={t}>
        <Lbl t={t}>{tr("lbl_body_weight")}</Lbl>
        <div style={{display:"flex",alignItems:"center",gap:"0.75rem"}}>
          <Inp value={bodyStats[tk]?.weight||""} onChange={e=>setBodyStats(p=>({...p,[tk]:{...p[tk],weight:e.target.value}}))} placeholder="69.0" type="number" t={t} style={{width:"88px",fontSize:"1.1rem",fontWeight:700,textAlign:"center"}}/>
          <span style={{color:t.textSecondary,fontSize:"0.82rem"}}>kg</span>
          {bodyStats[tk]?.weight&&<span style={{color:t.textTertiary,fontSize:"0.75rem"}}>Faltan {Math.max(0,(parseFloat(goal.goalWeight||75)-parseFloat(bodyStats[tk].weight)).toFixed(1))}kg → {goal.goalWeight||75}kg</span>}
        </div>
      </Card>

      {/* Resumen semanal */}
      <div>
        <button onClick={()=>setWeekOpen(p=>!p)} style={{width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",background:t.surface,border:`1px solid ${t.border}`,borderRadius:"12px",padding:"0.7rem 0.9rem",cursor:"pointer",color:t.text}}>
          <span style={{fontSize:"0.82rem",fontWeight:600,color:t.textSecondary}}>📅 Resumen semanal</span>
          <span style={{color:t.textTertiary,fontSize:"0.72rem"}}>{weekOpen?"▲":"▼"}</span>
        </button>
        {weekOpen&&(
          <div style={{background:t.surface,border:`1px solid ${t.border}`,borderTop:"none",borderRadius:"0 0 12px 12px",padding:"0.75rem 0.9rem",display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:"0.5rem"}}>
            {[
              {icon:"🏋️",label:"Gym",value:`${weekGym}d`,color:weekGym>=3?t.green:weekGym>=1?t.gold:t.textTertiary},
              {icon:"🥊",label:"MA",value:`${weekMA}d`,color:weekMA>=3?t.green:weekMA>=1?t.gold:t.textTertiary},
              {icon:"✅",label:"Hábitos",value:`${weekAvgHab}%`,color:weekAvgHab>=80?t.green:weekAvgHab>=50?t.gold:t.red},
            ].map(s=>(
              <div key={s.label} style={{textAlign:"center",background:t.surfaceHover,borderRadius:"10px",padding:"0.55rem 0.3rem"}}>
                <div style={{fontSize:"1rem"}}>{s.icon}</div>
                <div style={{color:s.color,fontSize:"1rem",fontWeight:800,letterSpacing:"-0.02em"}}>{s.value}</div>
                <div style={{color:t.textTertiary,fontSize:"0.55rem",fontWeight:600,textTransform:"uppercase"}}>{s.label}</div>
              </div>
            ))}
            <div style={{gridColumn:"1/-1",display:"flex",gap:"3px",marginTop:"0.1rem"}}>
              {last7.map((k,i)=>{const dh=habits[k]||{};const done=oblHabits.filter(h=>dh[h.id]).length;const p=oblHabits.length>0?done/oblHabits.length:0;return(<div key={k} style={{flex:1,height:"5px",borderRadius:"3px",background:p>=1?t.green:p>=0.5?t.gold:p>0?t.red:t.border}}/>);})}
            </div>
          </div>
        )}
      </div>

      {/* Obligatory habits — collapsible */}
      <div>
        <button onClick={()=>setOblOpen(p=>!p)} style={{width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",background:oblDone===oblHabits.length&&oblHabits.length>0?t.greenBg:t.surface,border:`1px solid ${oblDone===oblHabits.length&&oblHabits.length>0?t.green+"40":t.border}`,borderRadius:"12px",padding:"0.7rem 0.9rem",cursor:"pointer",color:t.text}}>
          <div style={{display:"flex",alignItems:"center",gap:"0.6rem"}}>
            <span style={{fontSize:"0.82rem",fontWeight:600,color:oblDone===oblHabits.length&&oblHabits.length>0?t.green:t.text}}>
              {oblDone===oblHabits.length&&oblHabits.length>0?"✓ ":""}Obligatorios
            </span>
            <span style={{fontSize:"0.7rem",color:oblDone===oblHabits.length?t.green:t.textTertiary,fontWeight:600}}>{oblDone}/{oblHabits.length}</span>
          </div>
          <span style={{color:t.textTertiary,fontSize:"0.72rem"}}>{oblOpen?"▲":"▼"}</span>
        </button>
        {oblOpen&&(
          <div style={{display:"flex",flexDirection:"column",gap:"0.35rem",marginTop:"0.35rem"}}>
            {oblHabits.map(h=><HabitRow key={h.id} h={h} done={!!todayH[h.id]} onToggle={()=>toggle(h.id)} timerVal={todayH[`${h.id}_min`]} onTimer={v=>setTimer(h.id,v)} habitsLog={habits} t={t} onSilence={silenceHabit}/>)}
          </div>
        )}
      </div>

      {optHabits.length>0&&(
        <OptionalHabits habits={optHabits} todayH={todayH} toggle={toggle} setTimer={setTimer} habitsLog={habits} t={t} silenceHabit={silenceHabit}/>
      )}

      {/* Sleep tracker */}
      <SleepTracker sleep={sleep} setSleep={setSleep} t={t}/>
    </div>
  );
}

function OptionalHabits({habits,todayH,toggle,setTimer,habitsLog,t,silenceHabit}) {
  const[open,setOpen]=useState(false);
  const doneCnt=habits.filter(h=>todayH[h.id]).length;
  return (
    <div>
      <button onClick={()=>setOpen(!open)} style={{width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",background:t.surface,border:`1px solid ${t.border}`,borderRadius:"12px",padding:"0.7rem 0.9rem",cursor:"pointer",color:t.text}}>
        <span style={{fontSize:"0.82rem",fontWeight:500,color:t.textSecondary}}>Opcionales {doneCnt>0?`(${doneCnt}/${habits.length} hechos)`:""}</span>
        <span style={{color:t.textTertiary,fontSize:"0.75rem"}}>{open?"▲":"▼"}</span>
      </button>
      {open&&(
        <div style={{display:"flex",flexDirection:"column",gap:"0.35rem",marginTop:"0.35rem"}}>
          {habits.map(h=><HabitRow key={h.id} h={h} done={!!todayH[h.id]} onToggle={()=>toggle(h.id)} timerVal={todayH[`${h.id}_min`]} onTimer={v=>setTimer(h.id,v)} habitsLog={habitsLog} t={t} optional onSilence={silenceHabit}/>)}
        </div>
      )}
    </div>
  );
}

function HabitRow({h,done,onToggle,timerVal,onTimer,habitsLog,t,optional,onSilence}) {
  const[open,setOpen]=useState(false);
  const[showCal,setShowCal]=useState(false);
  const[showSilence,setShowSilence]=useState(false);
  const _silRef=useRef(null);

  const PRIORITY_COLOR={critical:t.gold,high:t.blue};
  const PRIORITY_LABEL={critical:"Esencial",high:"Importante"};
  const pc=h.priority?PRIORITY_COLOR[h.priority]:null;
  const pl=h.priority?PRIORITY_LABEL[h.priority]:null;

  // Últimos 30 días para el calendario de hábito
  const last30=Array.from({length:30},(_,i)=>{
    const d=new Date();d.setDate(d.getDate()-29+i);
    const k=d.toISOString().split("T")[0];
    const isDone=!!(habitsLog&&habitsLog[k]&&habitsLog[k][h.id]);
    return{k,isDone,day:d.getDate(),month:d.getMonth()+1};
  });

  // Streak: días consecutivos completados hasta hoy
  let streak=0;
  const _sd=new Date();
  const _todayK=_sd.toISOString().split("T")[0];
  if(!(habitsLog&&habitsLog[_todayK]&&habitsLog[_todayK][h.id])){_sd.setDate(_sd.getDate()-1);}
  for(let _i=0;_i<365;_i++){
    const _k=new Date(_sd);_k.setDate(_k.getDate()-_i);
    const _ks=_k.toISOString().split("T")[0];
    if(habitsLog&&habitsLog[_ks]&&habitsLog[_ks][h.id]){streak++;}else{break;}
  }

  // Máxima racha histórica
  let maxStreak=0,_cur=0;
  Object.keys(habitsLog||{}).sort().forEach(k=>{
    if(habitsLog[k]?.[h.id]){_cur++;if(_cur>maxStreak)maxStreak=_cur;}else{_cur=0;}
  });
  const isRecord=streak>0&&streak>=maxStreak&&maxStreak>=3;

  return (
    <div style={{borderRadius:"12px",overflow:"hidden",border:`1px solid ${done?t.green+"40":pc?pc+"33":t.border}`}}>
      <div onClick={onToggle}
        onPointerDown={()=>{if(onSilence)_silRef.current=setTimeout(()=>setShowSilence(true),600);}}
        onPointerUp={()=>clearTimeout(_silRef.current)}
        onPointerLeave={()=>clearTimeout(_silRef.current)}
        style={{display:"flex",alignItems:"center",gap:"0.7rem",padding:"0.65rem 0.9rem",cursor:"pointer",background:done?t.greenBg:t.surface,userSelect:"none"}}>
        {pc&&!done&&<div style={{width:"3px",height:"32px",background:pc,borderRadius:"2px",flexShrink:0,marginLeft:"-0.2rem"}}/>}
        <span style={{fontSize:"1rem",flexShrink:0}}>{h.icon}</span>
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:"flex",alignItems:"center",gap:"0.35rem",flexWrap:"wrap"}}>
            <span style={{color:done?t.green:optional?t.textSecondary:t.text,fontSize:"0.82rem",fontWeight:done?600:optional?400:500}}>{h.label}</span>
            {pl&&!done&&<span style={{fontSize:"0.55rem",fontWeight:700,color:pc,background:pc+"20",padding:"0.1rem 0.3rem",borderRadius:"4px",flexShrink:0}}>{pl}</span>}
            {streak>=2&&<span style={{fontSize:"0.55rem",fontWeight:700,color:streak>=30?"#f59e0b":streak>=7?"#22c55e":"#94a3b8",background:streak>=30?"rgba(245,158,11,0.15)":streak>=7?"rgba(34,197,94,0.15)":"rgba(148,163,184,0.12)",padding:"0.08rem 0.3rem",borderRadius:"20px",flexShrink:0}}>🔥{streak}</span>}
            {isRecord&&streak>=3&&<span style={{fontSize:"0.52rem",fontWeight:700,color:"#f59e0b",background:"rgba(245,158,11,0.15)",padding:"0.06rem 0.28rem",borderRadius:"20px",flexShrink:0}}>🏆récord</span>}
          </div>
        </div>
        {h.timer&&done&&(
          <div onClick={e=>e.stopPropagation()} style={{display:"flex",alignItems:"center",gap:"0.22rem"}}>
            <input type="number" min="0" max="180" placeholder="min" value={timerVal||""} onChange={e=>onTimer(e.target.value)}
              style={{width:"42px",background:t.surfaceHover,border:`1px solid ${t.border}`,borderRadius:"7px",color:t.text,padding:"0.18rem 0.3rem",fontSize:"0.7rem",outline:"none",textAlign:"center"}}/>
            <span style={{color:t.textTertiary,fontSize:"0.58rem"}}>m</span>
          </div>
        )}
        <span onClick={e=>{e.stopPropagation();setShowCal(!showCal);setOpen(false);}} style={{color:t.textTertiary,fontSize:"0.58rem",padding:"0.1rem 0.3rem",borderRadius:"5px",background:t.accentMuted,cursor:"pointer",flexShrink:0}}>📅</span>
        <span onClick={e=>{e.stopPropagation();setOpen(!open);setShowCal(false);}} style={{color:t.textTertiary,fontSize:"0.6rem",padding:"0.1rem 0.35rem",borderRadius:"5px",background:t.accentMuted,cursor:"pointer",flexShrink:0}}>?</span>
        <div style={{width:19,height:19,borderRadius:"50%",flexShrink:0,background:done?t.green:t.surface,border:`1.5px solid ${done?t.green:t.border}`,display:"flex",alignItems:"center",justifyContent:"center"}}>
          {done&&<span style={{color:"#fff",fontSize:"0.58rem",fontWeight:800}}>✓</span>}
        </div>
      </div>
      {showSilence&&onSilence&&(
        <div style={{padding:"0.55rem 0.9rem",background:t.surfaceHover,borderTop:`1px solid ${t.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",gap:"0.5rem"}}>
          <span style={{color:t.textSecondary,fontSize:"0.72rem"}}>¿Silenciar "{h.label}"?</span>
          <div style={{display:"flex",gap:"0.4rem"}}>
            <button onClick={e=>{e.stopPropagation();onSilence(h.id);setShowSilence(false);}} style={{background:t.red,color:"#fff",border:"none",borderRadius:"7px",padding:"0.2rem 0.6rem",fontSize:"0.65rem",fontWeight:700,cursor:"pointer"}}>Silenciar</button>
            <button onClick={e=>{e.stopPropagation();setShowSilence(false);}} style={{background:t.surfaceHover,border:`1px solid ${t.border}`,borderRadius:"7px",padding:"0.2rem 0.6rem",fontSize:"0.65rem",cursor:"pointer",color:t.textSecondary}}>No</button>
          </div>
        </div>
      )}
      {open&&<div style={{padding:"0.48rem 0.9rem",background:t.surfaceHover,borderTop:`1px solid ${t.border}`}}><span style={{color:t.textSecondary,fontSize:"0.72rem",lineHeight:1.5}}>{h.why}</span></div>}
      {showCal&&(
        <div style={{padding:"0.55rem 0.9rem",background:t.surfaceHover,borderTop:`1px solid ${t.border}`}}>
          <div style={{color:t.textTertiary,fontSize:"0.6rem",marginBottom:"0.35rem",fontWeight:600}}>Últimos 30 días</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:"3px"}}>
            {last30.map(({k,isDone,day,month})=>(
              <div key={k} title={`${day}/${month}`} style={{width:"18px",height:"18px",borderRadius:"3px",background:isDone?t.green:t.redBg,border:`1px solid ${isDone?t.green:t.red+"44"}`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                <span style={{fontSize:"0.45rem",color:isDone?"#fff":t.textTertiary,fontWeight:700}}>{day}</span>
              </div>
            ))}
          </div>
          <div style={{display:"flex",gap:"0.7rem",marginTop:"0.4rem"}}>
            <div style={{display:"flex",alignItems:"center",gap:"3px"}}><div style={{width:"10px",height:"10px",borderRadius:"2px",background:t.green}}/><span style={{color:t.textTertiary,fontSize:"0.58rem"}}>Hecho</span></div>
            <div style={{display:"flex",alignItems:"center",gap:"3px"}}><div style={{width:"10px",height:"10px",borderRadius:"2px",background:t.redBg,border:`1px solid ${t.red+"44"}`}}/><span style={{color:t.textTertiary,fontSize:"0.58rem"}}>Sin marcar</span></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── GYM TAB ─────────────────────────────────────────────────────────────────
// Registro de sesiones de entrenamiento: tipo, duración, ejercicios y series.
// Incluye visualización muscular semanal y récords personales (PRs).
function GymTab({workouts,setWorkouts,habits,setHabits,goal,customExercises,setCustomExercises,currentDay,t}) {
  const tk=today();
  const[selDay,setSelDay]=useState(tk);
  useEffect(()=>{setSelDay(p=>p===addDays(currentDay,-1)?currentDay:p);},[currentDay]);
  const[sessionType,setSessionType]=useState(null);
  const[duration,setDuration]=useState("");
  const[exercises,setExercises]=useState([]);
  const[showPicker,setShowPicker]=useState(false);
  const[showAddEx,setShowAddEx]=useState(false);
  const[newExName,setNewExName]=useState("");
  const[newExMuscle,setNewExMuscle]=useState("Pecho");
  const[temps,setTemps]=useState({});
  const[saved,setSaved]=useState(false);
  const[editMuscleIdx,setEditMuscleIdx]=useState(null);
  const[pendingEx,setPendingEx]=useState(null); // ejercicio esperando elección de músculo
  const bw=parseFloat(goal.weight)||69;

  const isMA = sessionType&&["Muay Thai","BJJ","Lucha","MMA"].includes(sessionType);
  const isGym = sessionType==="Gym";

  const existingW = workouts[selDay];

  // ISO week (Mon–Sun) — resets every Monday 00:00
  const getISOWeekStart=()=>{const now=new Date();const day=now.getDay();const diff=day===0?6:day-1;const mon=new Date(now);mon.setDate(now.getDate()-diff);mon.setHours(0,0,0,0);return mon;};
  const weekStart=getISOWeekStart();
  const period7Days=Array.from({length:7},(_,i)=>{const d=new Date(weekStart);d.setDate(d.getDate()+i);return d.toISOString().split("T")[0];});

  // Countdown to next Monday (reset)
  const[countdown,setCountdown]=useState("");
  useEffect(()=>{
    const tick=()=>{
      const next=getISOWeekStart();next.setDate(next.getDate()+7);
      const diff=next-new Date();
      if(diff<=0){setCountdown("¡Hoy se reinicia!");return;}
      const d=Math.floor(diff/86400000),h=Math.floor((diff%86400000)/3600000),m=Math.floor((diff%3600000)/60000);
      setCountdown(d>0?`${d}d ${h}h`:h>0?`${h}h ${m}m`:`${m}m`);
    };
    tick();const id=setInterval(tick,60000);return()=>clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  // weekVol: saved workouts for this ISO week (excluding today) + current local exercises for today (real-time)
  const weekVol={};
  Object.entries(workouts).forEach(([dk,wd])=>{
    if(period7Days.includes(dk)&&dk!==tk)(wd.exercises||[]).forEach(ex=>{weekVol[ex.muscle]=(weekVol[ex.muscle]||0)+(ex.sets||[]).length;});
  });
  if(period7Days.includes(tk)){
    exercises.forEach(ex=>{weekVol[ex.muscle]=(weekVol[ex.muscle]||0)+(ex.sets||[]).length;});
  }

  // Today's sets always from local exercises (real-time)
  const todaySets={};
  exercises.forEach(ex=>{todaySets[ex.muscle]=(todaySets[ex.muscle]||0)+(ex.sets||[]).length;});

  const burn = duration&&sessionType ? Math.round((isMA?({"Muay Thai":10,BJJ:8,Lucha:7,MMA:9}[sessionType]||8):5)*bw*(parseFloat(duration)/60)) : 0;

  const addEx=(ex,muscle)=>{if(exercises.find(e=>e.baseId===ex.id))return;setExercises(p=>[...p,{...ex,baseId:ex.id,id:ex.id+"_"+Date.now(),muscle,sets:[]}]);setShowPicker(false);setPendingEx(null);};
  const pickExercise=(ex,suggestedMuscle)=>{setPendingEx({ex,suggestedMuscle});setShowPicker(false);setShowAddEx(false);};
  const addSet=ei=>{const s=temps[ei]||{};if(!s.reps&&!s.weight)return;setExercises(p=>{const e=[...p];e[ei]={...e[ei],sets:[...(e[ei].sets||[]),{reps:s.reps||"",weight:s.weight||"",id:Date.now()}]};return e;});setTemps(p=>({...p,[ei]:{}}));};
  const removeSet=(ei,sid)=>setExercises(p=>{const e=[...p];e[ei]={...e[ei],sets:e[ei].sets.filter(s=>s.id!==sid)};return e;});
  const removeEx=ei=>setExercises(p=>{const e=[...p];e.splice(ei,1);return e;});

  const saveSession=()=>{
    if(!sessionType)return;
    setWorkouts(p=>({...p,[selDay]:{sessionType,duration,exercises,burn,savedAt:new Date().toISOString()}}));
    if(isMA&&duration){
      setHabits(p=>({...p,[selDay]:{...(p[selDay]||{}),ma_min:String(parseFloat(p[selDay]?.ma_min||0)+parseFloat(duration))}}));
    }
    setSaved(true);
    setTimeout(()=>setSaved(false),2000);
  };

  const loadExisting=()=>{
    if(!existingW)return;
    setSessionType(existingW.sessionType||null);
    setDuration(existingW.duration||"");
    setExercises(existingW.exercises||[]);
  };

  const allPRs={};
  Object.values(workouts).forEach(wd=>(wd.exercises||[]).forEach(ex=>(ex.sets||[]).forEach(set=>{
    const w2=parseFloat(set.weight)||0,r=parseInt(set.reps)||0;
    if(!allPRs[ex.name]||w2>allPRs[ex.name].weight||(w2===allPRs[ex.name].weight&&r>allPRs[ex.name].reps))allPRs[ex.name]={weight:w2,reps:r};
  })));

  const[showPRs,setShowPRs]=useState(false);

  const MA_TYPES=["Muay Thai","BJJ","Lucha","MMA"];
  const deleteWorkout=(day)=>{
    const wd=workouts[day];
    setWorkouts(p=>{const n={...p};delete n[day];return n;});
    // Limpiar ma_min de habits si era sesión de artes marciales
    if(wd?.sessionType&&MA_TYPES.includes(wd.sessionType)){
      setHabits(p=>{const dh={...(p[day]||{})};delete dh.ma_min;return{...p,[day]:dh};});
    }
    if(day===selDay){setSessionType(null);setDuration("");setExercises([]);}
  };

  // Auto-load when switching days
  useEffect(()=>{
    if(workouts[selDay]){loadExisting();}else{setSessionType(null);setDuration("");setExercises([]);}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[selDay]);

  // Auto-guardado: persiste el borrador en workouts cuando hay >= 2 sets en total
  const[autoSavedMsg,setAutoSavedMsg]=useState(false);
  const autoSaveRef=useRef(null);
  useEffect(()=>{
    if(!sessionType) return;
    const totalSets=exercises.reduce((a,ex)=>a+(ex.sets?.length||0),0);
    if(totalSets<2) return;
    clearTimeout(autoSaveRef.current);
    autoSaveRef.current=setTimeout(()=>{
      setWorkouts(p=>({...p,[selDay]:{sessionType,duration,exercises,burn,savedAt:new Date().toISOString()}}));
      setAutoSavedMsg(true);
      setTimeout(()=>setAutoSavedMsg(false),2000);
    },800);
    return()=>clearTimeout(autoSaveRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[exercises,duration,sessionType]);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:"1.25rem"}}>
      {/* Navegador de fecha */}
      <div style={{display:"flex",alignItems:"center",gap:"0.3rem",background:t.surfaceHover,borderRadius:"10px",padding:"0.3rem 0.5rem",border:`1px solid ${t.border}`}}>
        <button onClick={()=>setSelDay(p=>addDays(p,-1))} style={{background:"transparent",border:"none",color:t.textSecondary,cursor:"pointer",fontSize:"1.1rem",lineHeight:1,padding:"0 0.25rem"}}>‹</button>
        <div style={{flex:1,textAlign:"center",color:t.text,fontSize:"0.78rem",fontWeight:700}}>{fmtDateLabel(selDay)}</div>
        <button onClick={()=>setSelDay(p=>addDays(p,1))} disabled={selDay>=today()} style={{background:"transparent",border:"none",color:selDay>=today()?t.textTertiary:t.textSecondary,cursor:selDay>=today()?"not-allowed":"pointer",fontSize:"1.1rem",lineHeight:1,padding:"0 0.25rem"}}>›</button>
      </div>

      {/* Acciones del día seleccionado */}
      {workouts[selDay]?.sessionType&&(
        <div style={{display:"flex",gap:"0.3rem",justifyContent:"flex-end"}}>
          <button onClick={loadExisting} style={{background:t.accentMuted,border:"none",borderRadius:"7px",padding:"0.22rem 0.55rem",color:t.textSecondary,fontSize:"0.62rem",cursor:"pointer",fontWeight:600}}>Editar</button>
          <button onClick={()=>{if(window.confirm(`¿Eliminar entrenamiento del ${selDay}?`))deleteWorkout(selDay);}} style={{background:t.redBg,border:"none",borderRadius:"7px",padding:"0.22rem 0.55rem",color:t.red,fontSize:"0.62rem",cursor:"pointer",fontWeight:600}}>Eliminar</button>
        </div>
      )}

      {/* Session type */}
      <Card t={t}>
        <Lbl t={t}>{tr("lbl_session_type")}</Lbl>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr 1fr",gap:"0.4rem"}}>
          {[
            {id:"Gym",icon:"🏋️",label:"Gym"},
            {id:"Muay Thai",icon:"🥊",label:"Muay Thai"},
            {id:"BJJ",icon:"🥋",label:"BJJ"},
            {id:"Lucha",icon:"🤼",label:"Lucha"},
            {id:"MMA",icon:"⚡",label:"MMA"},
          ].map(s=>(
            <button key={s.id} onClick={()=>setSessionType(s.id)} style={{
              padding:"0.65rem 0.2rem",borderRadius:"12px",border:"none",cursor:"pointer",
              background:sessionType===s.id?t.accent:t.surfaceHover,
              display:"flex",flexDirection:"column",alignItems:"center",gap:"0.2rem",
            }}>
              <span style={{fontSize:"1.2rem"}}>{s.icon}</span>
              <span style={{fontSize:"0.6rem",fontWeight:600,color:sessionType===s.id?t.bg:t.textSecondary}}>{s.label}</span>
            </button>
          ))}
        </div>
      </Card>

      {sessionType&&(
        <>
          {/* Duration */}
          <Card t={t}>
            <Lbl t={t}>{tr("lbl_duration")}</Lbl>
            <div style={{display:"flex",alignItems:"center",gap:"0.75rem"}}>
              <Inp value={duration} onChange={e=>setDuration(e.target.value)} placeholder="60" type="number" t={t} style={{width:"72px",textAlign:"center",fontSize:"1.1rem",fontWeight:700}}/>
              <span style={{color:t.textSecondary,fontSize:"0.82rem"}}>minutos</span>
              {burn>0&&<div style={{marginLeft:"auto",background:t.goldBg,borderRadius:"8px",padding:"0.25rem 0.65rem"}}><span style={{color:t.gold,fontSize:"0.75rem",fontWeight:600}}>~{burn} kcal</span></div>}
            </div>
          </Card>

          {/* Exercises — only for Gym */}
          {isGym&&(
            <Card t={t}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"0.85rem"}}>
                <Lbl t={t}>{tr("lbl_exercises")}</Lbl>
                <div style={{display:"flex",gap:"0.32rem"}}>
                  <button onClick={()=>{setShowAddEx(!showAddEx);setShowPicker(false);}} style={{background:t.surfaceHover,border:`1px solid ${t.border}`,borderRadius:"8px",padding:"0.25rem 0.55rem",color:t.textSecondary,fontSize:"0.68rem",fontWeight:600,cursor:"pointer"}}>{tr("add_custom_food")}</button>
                  <button onClick={()=>{setShowPicker(!showPicker);setShowAddEx(false);}} style={{background:t.accent,border:"none",borderRadius:"8px",padding:"0.25rem 0.58rem",color:t.bg,fontSize:"0.68rem",fontWeight:700,cursor:"pointer"}}>+ {tr("btn_add")}</button>
                </div>
              </div>

              {showAddEx&&(
                <div style={{background:t.surfaceHover,borderRadius:"10px",padding:"0.7rem",marginBottom:"0.7rem",display:"flex",flexDirection:"column",gap:"0.38rem"}}>
                  <Inp value={newExName} onChange={e=>setNewExName(e.target.value)} placeholder="Nombre del ejercicio" t={t} style={{width:"100%"}}/>
                  <select value={newExMuscle} onChange={e=>setNewExMuscle(e.target.value)} style={{background:t.surfaceHover,border:`1px solid ${t.border}`,borderRadius:"8px",color:t.text,padding:"0.5rem 0.7rem",fontSize:"0.8rem",outline:"none"}}>
                    {Object.keys(EXERCISES).map(m=><option key={m}>{m}</option>)}
                  </select>
                  <button onClick={()=>{if(!newExName.trim())return;const cxId="cx_"+Date.now();const cxEx={id:cxId,name:newExName.trim(),muscle:newExMuscle};setCustomExercises(p=>[...(p||[]),cxEx]);pickExercise(cxEx,newExMuscle);setNewExName("");setShowAddEx(false);}} style={{background:t.accent,color:t.bg,border:"none",borderRadius:"8px",padding:"0.42rem",fontSize:"0.73rem",fontWeight:700,cursor:"pointer"}}>Añadir</button>
                </div>
              )}

              {/* Selector de músculo objetivo — aparece cuando se elige un ejercicio */}
              {pendingEx&&(
                <div style={{background:t.surfaceHover,borderRadius:"12px",padding:"0.85rem",marginBottom:"0.7rem",border:`1px solid ${t.blue}40`}}>
                  <div style={{color:t.text,fontSize:"0.8rem",fontWeight:700,marginBottom:"0.15rem"}}>{pendingEx.ex.name}</div>
                  <div style={{color:t.textTertiary,fontSize:"0.67rem",marginBottom:"0.6rem"}}>¿Qué músculo estás trabajando hoy?</div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:"0.3rem",marginBottom:"0.55rem"}}>
                    {Object.keys(EXERCISES).map(m=>(
                      <button key={m} onClick={()=>addEx(pendingEx.ex,m)}
                        style={{padding:"0.32rem 0.7rem",borderRadius:"10px",border:`1px solid ${m===pendingEx.suggestedMuscle?t.blue:t.border}`,background:m===pendingEx.suggestedMuscle?t.blueBg:t.surface,color:m===pendingEx.suggestedMuscle?t.blue:t.text,fontSize:"0.72rem",fontWeight:m===pendingEx.suggestedMuscle?700:500,cursor:"pointer"}}>
                        {m}
                      </button>
                    ))}
                  </div>
                  <button onClick={()=>setPendingEx(null)} style={{background:"transparent",border:"none",color:t.textTertiary,fontSize:"0.68rem",cursor:"pointer",padding:0}}>✕ Cancelar</button>
                </div>
              )}

              {showPicker&&(
                <div style={{background:t.surfaceHover,borderRadius:"10px",padding:"0.7rem",marginBottom:"0.7rem",maxHeight:"240px",overflowY:"auto",display:"flex",flexDirection:"column",gap:"0.5rem"}}>
                  {Object.entries(EXERCISES).map(([muscle,exs])=>(
                    <div key={muscle}>
                      <div style={{color:t.textTertiary,fontSize:"0.56rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:"0.25rem"}}>{muscle}</div>
                      <div style={{display:"flex",flexWrap:"wrap",gap:"0.2rem"}}>
                        {exs.map(ex=><button key={ex.id} onClick={()=>pickExercise(ex,muscle)} style={{padding:"0.22rem 0.5rem",borderRadius:"8px",border:`1px solid ${t.border}`,background:t.surface,color:t.text,fontSize:"0.7rem",cursor:"pointer"}}>{ex.name}</button>)}
                      </div>
                    </div>
                  ))}
                  {(customExercises||[]).length>0&&(
                    <div>
                      <div style={{color:t.blue,fontSize:"0.56rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:"0.25rem"}}>Mis ejercicios</div>
                      <div style={{display:"flex",flexWrap:"wrap",gap:"0.2rem"}}>
                        {(customExercises||[]).map(ex=><button key={ex.id} onClick={()=>pickExercise(ex,ex.muscle)} style={{padding:"0.22rem 0.5rem",borderRadius:"8px",border:`1px solid ${t.blue}40`,background:t.blueBg,color:t.blue,fontSize:"0.7rem",cursor:"pointer"}}>{ex.name} <span style={{fontSize:"0.55rem",opacity:0.7}}>({ex.muscle})</span></button>)}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {exercises.length===0&&!showPicker&&!showAddEx&&(
                <div style={{color:t.textTertiary,fontSize:"0.76rem",textAlign:"center",padding:"0.8rem 0"}}>{tr("no_exercises")}</div>
              )}

              {exercises.map((ex,ei)=>{
                // eslint-disable-next-line no-unused-vars
                const maxW=ex.sets?.length>0?Math.max(...ex.sets.map(s=>parseFloat(s.weight)||0)):0;
                const pr=allPRs[ex.name];
                return (
                  <div key={ex.id} style={{border:`1px solid ${t.border}`,borderRadius:"12px",padding:"0.75rem",marginBottom:"0.45rem"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"0.35rem"}}>
                      <div style={{display:"flex",flexDirection:"column",gap:"0.1rem"}}>
                        <span style={{color:t.text,fontWeight:600,fontSize:"0.85rem"}}>{ex.name}</span>
                        {editMuscleIdx===ei?(
                          <select value={ex.muscle} onChange={e=>{setExercises(p=>{const a=[...p];a[ei]={...a[ei],muscle:e.target.value};return a;});setEditMuscleIdx(null);}}
                            onBlur={()=>setEditMuscleIdx(null)}
                            autoFocus
                            style={{background:t.surfaceHover,border:`1px solid ${t.blue}`,borderRadius:"6px",color:t.text,padding:"0.2rem 0.4rem",fontSize:"0.7rem",outline:"none",marginTop:"0.1rem"}}>
                            {Object.keys(EXERCISES).map(m=><option key={m}>{m}</option>)}
                          </select>
                        ):(
                          <span onClick={()=>setEditMuscleIdx(ei)} style={{color:t.blue,fontSize:"0.62rem",cursor:"pointer",display:"inline-flex",alignItems:"center",gap:"0.2rem"}}>{ex.muscle} <span style={{opacity:0.7}}>✎</span></span>
                        )}
                      </div>
                      <div style={{display:"flex",gap:"0.4rem",alignItems:"center"}}>
                        {pr&&<span style={{color:t.gold,fontSize:"0.62rem"}}>PR {pr.weight}kg×{pr.reps}</span>}
                        <span onClick={()=>removeEx(ei)} style={{color:t.textTertiary,cursor:"pointer"}}>✕</span>
                      </div>
                    </div>
                    {(ex.sets||[]).map((set,si)=>(
                      <div key={set.id} style={{display:"flex",gap:"0.38rem",alignItems:"center",marginBottom:"0.18rem"}}>
                        <span style={{color:t.textTertiary,fontSize:"0.62rem",minWidth:"12px"}}>{si+1}</span>
                        <span style={{color:t.text,fontSize:"0.8rem",fontWeight:500}}>{set.weight}kg × {set.reps} reps</span>
                        <span onClick={()=>removeSet(ei,set.id)} style={{color:t.textTertiary,cursor:"pointer",fontSize:"0.75rem",marginLeft:"auto"}}>✕</span>
                      </div>
                    ))}
                    <div style={{display:"flex",gap:"0.38rem",marginTop:"0.4rem",alignItems:"center"}}>
                      <Inp value={temps[ei]?.weight||""} onChange={e=>setTemps(p=>({...p,[ei]:{...p[ei],weight:e.target.value}}))} placeholder="kg" type="number" t={t} style={{width:"54px",textAlign:"center"}}/>
                      <span style={{color:t.textTertiary,fontSize:"0.68rem"}}>×</span>
                      <Inp value={temps[ei]?.reps||""} onChange={e=>setTemps(p=>({...p,[ei]:{...p[ei],reps:e.target.value}}))} placeholder="reps" type="number" t={t} style={{width:"54px",textAlign:"center"}}/>
                      <button onClick={()=>addSet(ei)} style={{background:t.accent,color:t.bg,border:"none",borderRadius:"8px",padding:"0.35rem 0.6rem",fontSize:"0.7rem",fontWeight:700,cursor:"pointer"}}>{tr("btn_add_set")}</button>
                    </div>
                  </div>
                );
              })}
            </Card>
          )}

          {/* Auto-guardado indicator */}
          {autoSavedMsg&&<div style={{textAlign:"center",color:t.green,fontSize:"0.68rem",fontWeight:600}}>{tr("auto_saved")}</div>}

          {/* Save button */}
          <div style={{position:"sticky",bottom:"0.75rem",zIndex:10}}>
          <button onClick={saveSession} style={{
            width:"100%",padding:"0.95rem",borderRadius:"14px",border:"none",cursor:"pointer",
            background:saved?t.green:t.accent,color:saved?"#fff":t.bg,
            fontSize:"0.9rem",fontWeight:700,transition:"all 0.3s",
            boxShadow:"0 4px 20px rgba(0,0,0,0.3)",
          }}>
            {saved?tr("session_saved"):tr("btn_save_session")}
          </button>
          </div>

        </>
      )}

      {/* PRs */}
      <button onClick={()=>setShowPRs(!showPRs)} style={{background:t.surface,border:`1px solid ${t.border}`,borderRadius:"12px",padding:"0.8rem 1rem",display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer",color:t.text,width:"100%"}}>
        <span style={{fontSize:"0.8rem",fontWeight:600}}>🏆 Récords personales ({Object.keys(allPRs).length})</span>
        <span style={{color:t.textTertiary,fontSize:"0.72rem"}}>{showPRs?"Ocultar":"Ver"}</span>
      </button>
      {showPRs&&(
        <Card t={t}>
          {Object.keys(allPRs).length===0?<div style={{color:t.textTertiary,fontSize:"0.75rem",textAlign:"center",padding:"0.5rem"}}>Sin datos aún</div>:
            Object.entries(allPRs).sort(([,a],[,b])=>b.weight-a.weight).map(([name,pr])=>(
              <div key={name} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"0.4rem 0",borderBottom:`1px solid ${t.border}`}}>
                <span style={{color:t.textSecondary,fontSize:"0.8rem"}}>{name}</span>
                <div><span style={{color:t.text,fontWeight:700,fontSize:"0.82rem"}}>{pr.weight}kg</span><span style={{color:t.textTertiary,fontSize:"0.68rem",marginLeft:"0.3rem"}}>× {pr.reps} reps</span></div>
              </div>
            ))
          }
        </Card>
      )}

      {/* Muscle visualization */}
      <Card t={t}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"0.55rem"}}>
          <Lbl t={t} style={{margin:0}}>Series semanales por músculo</Lbl>
          <div style={{background:t.surfaceHover,borderRadius:"8px",padding:"0.18rem 0.55rem",display:"flex",gap:"0.35rem",alignItems:"center"}}>
            <span style={{color:t.textTertiary,fontSize:"0.55rem"}}>Periodo</span>
            <span style={{color:t.text,fontSize:"0.62rem",fontWeight:700}}>{period7Days[0].slice(5).replace("-","/")} → {period7Days[6].slice(5).replace("-","/")}</span>
            <span style={{color:t.green,fontSize:"0.55rem",fontWeight:700}}>7d</span>
          </div>
        </div>
        <MuscleBody weekVol={weekVol} todaySets={todaySets} t={t} countdown={countdown} onAddExercise={(ex,muscle)=>{pickExercise(ex,muscle);window.scrollTo(0,0);}}/>
      </Card>
    </div>
  );
}

// ─── DIETA TAB ────────────────────────────────────────────────────────────────
// Seguimiento nutricional: macros del día, alimentos por slot de comida,
// catálogo completo con favoritos y soporte para alimentos personalizados.
function DietaTab({nutrition,setNutrition,customFoods,setCustomFoods,goal,favoriteFoods,setFavoriteFoods,savedMeals,setSavedMeals,currentDay,t}) {
  const tk=today();
  const[selDay,setSelDay]=useState(tk);
  useEffect(()=>{setSelDay(p=>p===addDays(currentDay,-1)?currentDay:p);},[currentDay]);
  const[selSlot,setSelSlot]=useState("m1");
  const[showPicker,setShowPicker]=useState(false);
  const[showAdd,setShowAdd]=useState(false);
  const[selCat,setSelCat]=useState("Carbos");
  const[amounts,setAmounts]=useState({});
  const[nf,setNf]=useState({name:"",cal:"",p:"",c:"",f:"",unit:"g"});
  const[showMealSaver,setShowMealSaver]=useState(false);
  const[mealSaveName,setMealSaveName]=useState("");
  const[mealSaveIcon,setMealSaveIcon]=useState("🍽️");
  const[expandedMeal,setExpandedMeal]=useState(null);
  const[quickEditId,setQuickEditId]=useState(null);
  const _lpTimerRef=useRef(null);
  const[voiceActive,setVoiceActive]=useState(false);
  const[voiceTranscript,setVoiceTranscript]=useState("");
  const[voicePreview,setVoicePreview]=useState(null);
  const voiceRef=useRef(null);

  const macros=calcMacros(goal);
  const dayData=nutrition[selDay]||{items:{}};
  const allItems=Object.values(dayData.items||{});
  const totals=allItems.reduce((a,i)=>({cal:a.cal+i.cal,p:a.p+i.p,c:a.c+i.c,f:a.f+i.f}),{cal:0,p:0,c:0,f:0});
  const allFoods=[...FOODS,...(customFoods||[]).map(f=>({...f,cat:"Custom"}))];

  const addFood=food=>{
    const isU=food.unit==="ud"||food.unit==="scoop";
    const qty=parseFloat(amounts[food.id]||(isU?"1":"100"));
    if(!qty)return;
    const key=`${selSlot}_${food.id}_${Date.now()}`;
    const item={id:key,foodId:food.id,name:food.name,icon:food.icon||"🍽️",slot:selSlot,qty,unit:food.unit,cal:food.cal*qty,p:food.p*qty,c:food.c*qty,f:food.f*qty};
    setNutrition(p=>({...p,[selDay]:{...p[selDay],items:{...(p[selDay]?.items||{}),[key]:item}}}));
    setAmounts(p=>({...p,[food.id]:""}));
  };

  const saveCustom=()=>{
    if(!nf.name||!nf.cal)return;
    const isU=nf.unit==="ud"||nf.unit==="scoop";
    const div=isU?1:100;
    const food={id:"cf_"+Date.now(),name:nf.name,icon:"🍽️",cat:"Custom",unit:nf.unit,cal:parseFloat(nf.cal)/div,p:parseFloat(nf.p||0)/div,c:parseFloat(nf.c||0)/div,f:parseFloat(nf.f||0)/div};
    setCustomFoods(p=>[...(p||[]),food]);
    setNf({name:"",cal:"",p:"",c:"",f:"",unit:"g"});
    setShowAdd(false);
  };

  const toggleFav=id=>{if(!setFavoriteFoods)return;setFavoriteFoods(p=>p.includes(id)?p.filter(x=>x!==id):[...p,id]);};
  const favIds=favoriteFoods||[];
  const removeItem=key=>{const items={...(dayData.items||{})};delete items[key];setNutrition(p=>({...p,[selDay]:{...p[selDay],items}}));};
  const[editItemId,setEditItemId]=useState(null);
  const[editItemQty,setEditItemQty]=useState("");
  const updateItemQty=(item,newQty)=>{
    const q=parseFloat(newQty);
    if(!q||q<=0)return;
    const orig=item._origQty||item.qty;
    const ratio=q/orig;
    const updated={...item,qty:q,cal:item._origCal?item._origCal*ratio:item.cal*(q/item.qty),p:item._origP?item._origP*ratio:item.p*(q/item.qty),c:item._origC?item._origC*ratio:item.c*(q/item.qty),f:item._origF?item._origF*ratio:item.f*(q/item.qty),_origQty:orig,_origCal:item._origCal||item.cal,_origP:item._origP||item.p,_origC:item._origC||item.c,_origF:item._origF||item.f};
    setNutrition(p=>({...p,[selDay]:{...p[selDay],items:{...(p[selDay]?.items||{}),[item.id]:updated}}}));
    setEditItemId(null);
  };

  // ── Platos guardados ──────────────────────────────────────────────────────
  const slotItems=allItems.filter(i=>i.slot===selSlot);

  const saveSlotAsMeal=()=>{
    if(!mealSaveName.trim()||!slotItems.length)return;
    const meal={
      id:"meal_"+Date.now(),
      name:mealSaveName.trim(),
      icon:mealSaveIcon||"🍽️",
      items:slotItems.map(item=>({foodId:item.foodId,name:item.name,icon:item.icon,qty:item.qty,unit:item.unit,cal:item.cal,p:item.p,c:item.c,f:item.f}))
    };
    setSavedMeals(p=>[meal,...(p||[])]);
    setMealSaveName("");setMealSaveIcon("🍽️");setShowMealSaver(false);
  };

  const addMealToSlot=meal=>{
    const now=Date.now();
    const newItems={};
    meal.items.forEach((item,idx)=>{
      const key=`${selSlot}_${item.foodId}_${now+idx}`;
      newItems[key]={...item,id:key,slot:selSlot};
    });
    setNutrition(p=>({...p,[selDay]:{...p[selDay],items:{...(p[selDay]?.items||{}),...newItems}}}));
  };

  const deleteMeal=id=>{if(window.confirm("¿Eliminar este plato?"))setSavedMeals(p=>(p||[]).filter(m=>m.id!==id));};

  const parseVoice=(text)=>{
    const s=text.toLowerCase().trim();
    const norm=(x)=>x.normalize("NFD").replace(/[̀-ͯ]/g,"").toLowerCase().replace(/[^a-z0-9 ]/g,"").trim();
    const uMap={g:"g",gr:"g",gramo:"g",gramos:"g",ml:"ml",mililitro:"ml",mililitros:"ml",ud:"ud",unidad:"ud",unidades:"ud",scoop:"scoop",scoops:"scoop",kg:"g"};
    let qty=null,unit="g",foodStr=s;
    const m=s.match(/^(.*?)\s+([\d]+(?:[.,]\d+)?)\s*(g|gr|gramo[s]?|ml|mililitro[s]?|ud|unidad(?:es)?|scoop[s]?|kg)?\s*$/i);
    if(m&&m[2]){
      foodStr=m[1].trim();
      qty=parseFloat(m[2].replace(",","."));
      const u=(m[3]||"").toLowerCase();
      unit=uMap[u]||"g";
      if(u==="kg")qty*=1000;
    }
    const nf=norm(foodStr);
    let best=null,bs=0;
    for(const f of allFoods){
      const fn=norm(f.name);
      if(fn===nf){best=f;bs=1;break;}
      if(bs<1&&(fn.includes(nf)||nf.includes(fn))){
        const sc=Math.min(fn.length,nf.length)/Math.max(fn.length,nf.length);
        if(sc>bs){bs=sc;best=f;}
      }
    }
    const isU=best?.unit==="ud"||best?.unit==="scoop";
    return{food:best,qty:qty||(isU?1:100),unit:best?.unit||unit,raw:text};
  };

  const startVoice=()=>{
    const SR=window.SpeechRecognition||window.webkitSpeechRecognition;
    if(!SR){alert("Tu navegador no soporta reconocimiento de voz");return;}
    const r=new SR();
    r.lang="es-ES";r.interimResults=true;r.maxAlternatives=1;
    voiceRef.current=r;
    r.onstart=()=>{setVoiceActive(true);setVoiceTranscript("");};
    r.onresult=e=>{
      const txt=e.results[0][0].transcript;
      setVoiceTranscript(txt);
      if(e.results[0].isFinal){setVoicePreview(parseVoice(txt));setVoiceActive(false);}
    };
    r.onerror=()=>setVoiceActive(false);
    r.onend=()=>setVoiceActive(false);
    r.start();
  };

  const confirmVoice=()=>{
    if(!voicePreview?.food)return;
    const f=voicePreview.food,q=voicePreview.qty;
    const key=`${selSlot}_${f.id}_${Date.now()}`;
    const item={id:key,foodId:f.id,name:f.name,icon:f.icon||"🍽️",slot:selSlot,qty:q,unit:f.unit,cal:f.cal*q,p:f.p*q,c:f.c*q,f:f.f*q};
    setNutrition(p=>({...p,[selDay]:{...p[selDay],items:{...(p[selDay]?.items||{}),[key]:item}}}));
    setVoicePreview(null);setVoiceTranscript("");
  };

  const Bar=({val,max,color})=>{const pct=Math.min(100,(val/max)*100);return <div style={{background:t.surfaceHover,borderRadius:"4px",height:"4px",flex:1}}><div style={{width:`${pct}%`,height:"100%",background:pct>=100?t.green:color,borderRadius:"4px",transition:"width 0.4s"}}/></div>;};

  return (
    <div style={{display:"flex",flexDirection:"column",gap:"1.25rem"}}>
      {/* Navegador de fecha */}
      <div style={{display:"flex",alignItems:"center",gap:"0.3rem",background:t.surfaceHover,borderRadius:"10px",padding:"0.3rem 0.5rem",border:`1px solid ${t.border}`}}>
        <button onClick={()=>setSelDay(p=>addDays(p,-1))} style={{background:"transparent",border:"none",color:t.textSecondary,cursor:"pointer",fontSize:"1.1rem",lineHeight:1,padding:"0 0.25rem"}}>‹</button>
        <div style={{flex:1,textAlign:"center",color:t.text,fontSize:"0.78rem",fontWeight:700}}>{fmtDateLabel(selDay)}</div>
        <button onClick={()=>setSelDay(p=>addDays(p,1))} disabled={selDay>=today()} style={{background:"transparent",border:"none",color:selDay>=today()?t.textTertiary:t.textSecondary,cursor:selDay>=today()?"not-allowed":"pointer",fontSize:"1.1rem",lineHeight:1,padding:"0 0.25rem"}}>›</button>
      </div>

      {/* Resumen de macros */}
      <Card t={t}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:"0.85rem"}}>
          <div><span style={{color:t.text,fontSize:"2rem",fontWeight:800,letterSpacing:"-0.03em"}}>{Math.round(totals.cal)}</span><span style={{color:t.textTertiary,fontSize:"0.8rem",marginLeft:"0.3rem"}}>/ {macros.cal} kcal</span></div>
          <div style={{background:totals.cal>=macros.cal*0.9?t.greenBg:t.accentMuted,borderRadius:"8px",padding:"0.2rem 0.6rem"}}><span style={{color:totals.cal>=macros.cal*0.9?t.green:t.textSecondary,fontSize:"0.7rem",fontWeight:600}}>{totals.cal>=macros.cal?"✓ Objetivo":`Faltan ${macros.cal-Math.round(totals.cal)}`}</span></div>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:"0.5rem"}}>
          {[{l:"Proteína",v:Math.round(totals.p),max:macros.protein,unit:"g",c:t.blue},{l:"Carbos",v:Math.round(totals.c),max:macros.carbs,unit:"g",c:t.gold},{l:"Grasas",v:Math.round(totals.f),max:macros.fat,unit:"g",c:t.red}].map(m=>(
            <div key={m.l} style={{display:"flex",alignItems:"center",gap:"0.6rem"}}>
              <span style={{color:t.textSecondary,fontSize:"0.72rem",minWidth:"58px"}}>{m.l}</span>
              <Bar val={m.v} max={m.max} color={m.c}/>
              <span style={{color:t.textSecondary,fontSize:"0.7rem",minWidth:"56px",textAlign:"right"}}>{m.v}/{m.max}{m.unit}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* ── Mis platos guardados ── */}
      <Card t={t}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"0.55rem"}}>
          <Lbl t={t}>🍽️ Mis platos</Lbl>
          <span style={{color:t.textTertiary,fontSize:"0.6rem"}}>→ {MEAL_SLOTS.find(s=>s.id===selSlot)?.label}</span>
        </div>
        {(savedMeals||[]).length===0?(
          <div style={{color:t.textTertiary,fontSize:"0.74rem",textAlign:"center",padding:"0.6rem 0",lineHeight:1.5}}>
            Sin platos guardados.<br/>
            <span style={{fontSize:"0.65rem"}}>Añade alimentos a un slot y pulsa 💾 Guardar como plato.</span>
          </div>
        ):(
          <div style={{display:"flex",flexDirection:"column",gap:"0.4rem"}}>
            {(savedMeals||[]).map(meal=>{
              const mCal=meal.items.reduce((s,i)=>s+i.cal,0);
              const mP=meal.items.reduce((s,i)=>s+i.p,0);
              const isExp=expandedMeal===meal.id;
              return(
                <div key={meal.id} style={{border:`1px solid ${t.border}`,borderRadius:"10px",overflow:"hidden"}}>
                  <div style={{display:"flex",alignItems:"center",gap:"0.5rem",padding:"0.42rem 0.6rem",background:t.surfaceHover}}>
                    <span style={{fontSize:"1.1rem",flexShrink:0}}>{meal.icon}</span>
                    <div style={{flex:1,minWidth:0,cursor:"pointer"}} onClick={()=>setExpandedMeal(isExp?null:meal.id)}>
                      <div style={{color:t.text,fontSize:"0.8rem",fontWeight:600}}>{meal.name}</div>
                      <div style={{color:t.textTertiary,fontSize:"0.57rem"}}>{meal.items.length} ingredientes · {Math.round(mCal)} kcal · P:{Math.round(mP)}g</div>
                    </div>
                    <button onClick={()=>addMealToSlot(meal)} style={{background:t.accent,border:"none",borderRadius:"7px",padding:"0.25rem 0.55rem",color:t.bg,fontSize:"0.7rem",fontWeight:700,cursor:"pointer",flexShrink:0}}>+ Añadir</button>
                    <button onClick={()=>deleteMeal(meal.id)} style={{background:"transparent",border:"none",color:t.textTertiary,cursor:"pointer",fontSize:"0.75rem",padding:"0.1rem",flexShrink:0}}>✕</button>
                  </div>
                  {isExp&&(
                    <div style={{padding:"0.4rem 0.65rem",background:t.surface}}>
                      {meal.items.map((item,idx)=>(
                        <div key={idx} style={{display:"flex",alignItems:"center",gap:"0.4rem",padding:"0.2rem 0",borderBottom:`1px solid ${t.borderLight}`}}>
                          <span style={{fontSize:"0.78rem"}}>{item.icon}</span>
                          <span style={{flex:1,color:t.textSecondary,fontSize:"0.72rem"}}>{item.name}</span>
                          <span style={{color:t.textTertiary,fontSize:"0.62rem"}}>{item.qty}{item.unit} · {Math.round(item.cal)} kcal</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* ── Alimentos habituales (favoritos) ── */}
      {favIds.length>0&&(
        <Card t={t}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"0.65rem"}}>
            <Lbl t={t}>{tr("my_foods")}</Lbl>
            <span style={{color:t.textTertiary,fontSize:"0.6rem"}}>Añadir a: {MEAL_SLOTS.find(s=>s.id===selSlot)?.label}</span>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:"0.28rem"}}>
            {favIds.map(id=>{
              const food=FOODS.find(f=>f.id===id);
              if(!food)return null;
              const isU=food.unit==="ud"||food.unit==="scoop";
              return(
                <div key={id} style={{display:"flex",alignItems:"center",gap:"0.4rem",padding:"0.28rem 0",borderBottom:`1px solid ${t.borderLight}`}}>
                  <span style={{fontSize:"0.88rem"}}>{food.icon}</span>
                  <div style={{flex:1}}>
                    <div style={{color:t.text,fontSize:"0.75rem"}}>{food.name}</div>
                    <div style={{color:t.textTertiary,fontSize:"0.56rem"}}>{food.unit==="g"||food.unit==="ml"?`${(food.cal*100).toFixed(0)} kcal/100${food.unit}`:`${food.cal} kcal/${food.unit}`}</div>
                  </div>
                  <Inp value={amounts[id]||""} onChange={e=>setAmounts(p=>({...p,[id]:e.target.value}))} placeholder={isU?"1":"100"} type="number" t={t} style={{width:"46px",textAlign:"center"}}/>
                  <span style={{color:t.textTertiary,fontSize:"0.6rem",minWidth:"14px"}}>{food.unit}</span>
                  <button
                    onPointerDown={()=>{_lpTimerRef.current=setTimeout(()=>setQuickEditId(id),500);}}
                    onPointerUp={()=>{clearTimeout(_lpTimerRef.current);}}
                    onPointerLeave={()=>{clearTimeout(_lpTimerRef.current);}}
                    onClick={()=>addFood(food)}
                    style={{background:t.accent,color:t.bg,border:"none",borderRadius:"7px",padding:"0.2rem 0.45rem",fontSize:"0.7rem",fontWeight:700,cursor:"pointer",userSelect:"none"}}
                  >+</button>
                  {quickEditId===id&&(
                    <div style={{position:"fixed",inset:0,zIndex:100,display:"flex",alignItems:"flex-end",background:"rgba(0,0,0,0.5)"}} onClick={()=>setQuickEditId(null)}>
                      <div onClick={e=>e.stopPropagation()} style={{width:"100%",background:t.surface,borderRadius:"20px 20px 0 0",padding:"1.25rem",boxShadow:"0 -8px 30px rgba(0,0,0,0.4)"}}>
                        <div style={{color:t.text,fontWeight:700,marginBottom:"0.75rem",fontSize:"0.9rem"}}>{food.icon} {food.name}</div>
                        <div style={{display:"flex",gap:"0.5rem",alignItems:"center"}}>
                          <input autoFocus type="number" value={amounts[id]||""} onChange={e=>setAmounts(p=>({...p,[id]:e.target.value}))}
                            placeholder={isU?"1":"100"}
                            style={{flex:1,background:t.surfaceHover,border:`2px solid ${t.accent}`,borderRadius:"12px",color:t.text,padding:"0.7rem 0.9rem",fontSize:"1.2rem",fontWeight:700,outline:"none",textAlign:"center",fontFamily:"inherit"}}/>
                          <span style={{color:t.textTertiary,fontSize:"0.8rem",minWidth:"24px"}}>{food.unit}</span>
                          <button onClick={()=>{addFood(food);setQuickEditId(null);}} style={{background:t.accent,color:t.bg,border:"none",borderRadius:"12px",padding:"0.7rem 1.2rem",fontSize:"0.9rem",fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>+ Añadir</button>
                        </div>
                        <button onClick={()=>setQuickEditId(null)} style={{width:"100%",marginTop:"0.65rem",background:t.surfaceHover,border:"none",borderRadius:"10px",padding:"0.55rem",color:t.textSecondary,fontSize:"0.8rem",cursor:"pointer"}}>Cancelar</button>
                      </div>
                    </div>
                  )}
                  <button onClick={()=>toggleFav(id)} title="Quitar de habituales" style={{background:"transparent",border:"none",color:t.gold,cursor:"pointer",fontSize:"0.85rem",padding:"0.1rem",lineHeight:1}}>★</button>
                </div>
              );
            })}
          </div>
          <div style={{color:t.textTertiary,fontSize:"0.58rem",marginTop:"0.5rem",lineHeight:1.4}}>Usa ★ para quitar · Añade más desde "catálogo completo" con ☆</div>
        </Card>
      )}

      {/* Selector de slot */}
      <div style={{display:"flex",gap:"0.25rem",overflowX:"auto"}}>
        {MEAL_SLOTS.map(slot=>{const slotCal=allItems.filter(i=>i.slot===slot.id).reduce((a,i)=>a+i.cal,0);return <div key={slot.id} onClick={()=>setSelSlot(slot.id)} style={{minWidth:"76px",padding:"0.45rem 0.35rem",borderRadius:"10px",textAlign:"center",cursor:"pointer",flexShrink:0,background:selSlot===slot.id?t.accent:t.surface,border:`1px solid ${selSlot===slot.id?t.accent:t.border}`}}><div style={{fontSize:"0.62rem",fontWeight:600,color:selSlot===slot.id?t.bg:t.textSecondary}}>{slot.label}</div>{slotCal>0&&<div style={{color:selSlot===slot.id?t.bg:t.textTertiary,fontSize:"0.58rem",marginTop:"0.1rem"}}>{Math.round(slotCal)} kcal</div>}</div>;})}
      </div>

      {/* Lista de alimentos del slot actual */}
      <Card t={t}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"0.7rem"}}>
          <Lbl t={t}>{MEAL_SLOTS.find(s=>s.id===selSlot)?.label}</Lbl>
          <div style={{display:"flex",gap:"0.3rem"}}>
            <button onClick={voiceActive?()=>voiceRef.current?.stop():startVoice} style={{background:voiceActive?"#EF4444":t.surfaceHover,border:`1px solid ${voiceActive?"#EF4444":t.border}`,borderRadius:"8px",padding:"0.22rem 0.52rem",color:voiceActive?"#fff":t.textSecondary,fontSize:"0.85rem",cursor:"pointer",transition:"all 0.2s"}}>{voiceActive?"⏹":"🎤"}</button>
            <button onClick={()=>{setShowAdd(!showAdd);setShowPicker(false);}} style={{background:t.surfaceHover,border:`1px solid ${t.border}`,borderRadius:"8px",padding:"0.22rem 0.52rem",color:t.textSecondary,fontSize:"0.65rem",fontWeight:600,cursor:"pointer"}}>+ Custom</button>
            <button onClick={()=>{setShowPicker(!showPicker);setShowAdd(false);}} style={{background:t.accent,border:"none",borderRadius:"8px",padding:"0.22rem 0.55rem",color:t.bg,fontSize:"0.65rem",fontWeight:700,cursor:"pointer"}}>+ Alimento</button>
          </div>
        </div>

        {slotItems.map(item=>(<div key={item.id} style={{borderBottom:`1px solid ${t.border}`}}>
          <div style={{display:"flex",alignItems:"center",gap:"0.48rem",padding:"0.4rem 0"}}>
            <span style={{fontSize:"0.9rem"}}>{item.icon}</span>
            <div style={{flex:1}}>
              <div style={{color:t.text,fontSize:"0.8rem",fontWeight:500}}>{item.name}</div>
              <div style={{color:t.textTertiary,fontSize:"0.6rem"}}>{item.qty}{item.unit} · {Math.round(item.cal)} kcal · P:{Math.round(item.p)}g C:{Math.round(item.c)}g G:{Math.round(item.f)}g</div>
            </div>
            <span onClick={()=>{setEditItemId(editItemId===item.id?null:item.id);setEditItemQty(String(item.qty));}} style={{color:t.accent,cursor:"pointer",fontSize:"0.72rem",padding:"0.2rem 0.35rem",border:`1px solid ${t.accent}`,borderRadius:"4px",lineHeight:1}}>✏️</span>
            <span onClick={()=>removeItem(item.id)} style={{color:t.textTertiary,cursor:"pointer",fontSize:"0.8rem"}}>✕</span>
          </div>
          {editItemId===item.id&&(
            <div style={{display:"flex",alignItems:"center",gap:"0.4rem",padding:"0.3rem 0 0.5rem 1.4rem"}}>
              <input type="number" value={editItemQty} onChange={e=>setEditItemQty(e.target.value)} style={{width:"70px",background:t.surface,border:`1px solid ${t.accent}`,color:t.text,borderRadius:"6px",padding:"0.25rem 0.4rem",fontSize:"0.8rem"}} autoFocus/>
              <span style={{color:t.textSecondary,fontSize:"0.72rem"}}>{item.unit}</span>
              <button onClick={()=>updateItemQty(item,editItemQty)} style={{background:t.accent,color:"#000",border:"none",borderRadius:"6px",padding:"0.25rem 0.6rem",fontSize:"0.72rem",fontWeight:700,cursor:"pointer"}}>OK</button>
              <button onClick={()=>setEditItemId(null)} style={{background:"none",border:`1px solid ${t.border}`,color:t.textTertiary,borderRadius:"6px",padding:"0.25rem 0.5rem",fontSize:"0.72rem",cursor:"pointer"}}>✕</button>
            </div>
          )}
        </div>))}
        {slotItems.length===0&&!showPicker&&!showAdd&&<div style={{color:t.textTertiary,fontSize:"0.76rem",textAlign:"center",padding:"0.65rem 0"}}>Sin alimentos — pulsa + Alimento</div>}

        {/* Guardar slot como plato */}
        {slotItems.length>0&&(
          <div style={{marginTop:"0.6rem"}}>
            {showMealSaver?(
              <div style={{display:"flex",gap:"0.3rem",alignItems:"center",flexWrap:"wrap",padding:"0.45rem",background:t.surfaceHover,borderRadius:"8px"}}>
                <input value={mealSaveIcon} onChange={e=>setMealSaveIcon(e.target.value)} style={{width:"34px",background:t.bg,border:`1px solid ${t.border}`,borderRadius:"7px",padding:"0.28rem",color:t.text,fontSize:"1rem",textAlign:"center",outline:"none"}}/>
                <input value={mealSaveName} onChange={e=>setMealSaveName(e.target.value)} onKeyDown={e=>e.key==="Enter"&&saveSlotAsMeal()} placeholder="Nombre del plato…" style={{flex:1,minWidth:"120px",background:t.bg,border:`1px solid ${t.border}`,borderRadius:"7px",padding:"0.3rem 0.5rem",color:t.text,fontSize:"0.78rem",outline:"none"}}/>
                <button onClick={saveSlotAsMeal} style={{background:t.green,border:"none",borderRadius:"7px",padding:"0.3rem 0.6rem",color:"#fff",fontSize:"0.72rem",fontWeight:700,cursor:"pointer"}}>💾 Guardar</button>
                <button onClick={()=>setShowMealSaver(false)} style={{background:"transparent",border:"none",color:t.textTertiary,cursor:"pointer",fontSize:"0.75rem"}}>✕</button>
              </div>
            ):(
              <button onClick={()=>setShowMealSaver(true)} style={{background:t.accentMuted,border:`1px solid ${t.border}`,borderRadius:"7px",padding:"0.25rem 0.6rem",color:t.textSecondary,fontSize:"0.65rem",fontWeight:600,cursor:"pointer",width:"100%",textAlign:"center"}}>💾 Guardar slot como plato</button>
            )}
          </div>
        )}

        {showAdd&&(<div style={{marginTop:"0.65rem",background:t.surfaceHover,borderRadius:"10px",padding:"0.7rem",display:"flex",flexDirection:"column",gap:"0.35rem"}}><Inp value={nf.name} onChange={e=>setNf(p=>({...p,name:e.target.value}))} placeholder="Nombre" t={t} style={{width:"100%"}}/><select value={nf.unit} onChange={e=>setNf(p=>({...p,unit:e.target.value}))} style={{background:t.surface,border:`1px solid ${t.border}`,borderRadius:"8px",color:t.text,padding:"0.5rem 0.7rem",fontSize:"0.8rem",outline:"none"}}><option value="g">por 100g</option><option value="ml">por 100ml</option><option value="ud">por unidad</option><option value="scoop">por scoop</option></select><div style={{display:"flex",gap:"0.3rem"}}>{["cal","p","c","f"].map(k=><Inp key={k} value={nf[k]} onChange={e=>setNf(p=>({...p,[k]:e.target.value}))} placeholder={k==="cal"?"kcal":k==="p"?"prot":k==="c"?"carbs":"gras"} type="number" t={t} style={{flex:1}}/>)}</div><button onClick={saveCustom} style={{background:t.accent,color:t.bg,border:"none",borderRadius:"8px",padding:"0.42rem",fontSize:"0.72rem",fontWeight:700,cursor:"pointer"}}>Guardar</button></div>)}
        {showPicker&&(<div style={{marginTop:"0.65rem",background:t.surfaceHover,borderRadius:"10px",padding:"0.7rem"}}><div style={{display:"flex",gap:"0.2rem",overflowX:"auto",marginBottom:"0.58rem"}}>{FOOD_CATS.map(cat=><button key={cat} onClick={()=>setSelCat(cat)} style={{padding:"0.2rem 0.5rem",borderRadius:"20px",border:"none",cursor:"pointer",fontSize:"0.65rem",fontWeight:600,whiteSpace:"nowrap",background:selCat===cat?t.accent:t.surface,color:selCat===cat?t.bg:t.textSecondary}}>{cat}</button>)}</div>{allFoods.filter(f=>f.cat===selCat).map(food=>(<div key={food.id} style={{display:"flex",alignItems:"center",gap:"0.4rem",padding:"0.33rem 0",borderBottom:`1px solid ${t.borderLight}`}}><span style={{fontSize:"0.88rem"}}>{food.icon||"🍽️"}</span><div style={{flex:1}}><div style={{color:t.text,fontSize:"0.76rem"}}>{food.name}</div><div style={{color:t.textTertiary,fontSize:"0.56rem"}}>{food.unit==="g"||food.unit==="ml"?`${(food.cal*100).toFixed(0)} kcal/100${food.unit}`:`${food.cal} kcal/${food.unit}`}{food.note?` · pesar en ${food.note}`:""}</div></div><Inp value={amounts[food.id]||""} onChange={e=>setAmounts(p=>({...p,[food.id]:e.target.value}))} placeholder={food.unit==="g"||food.unit==="ml"?"100":"1"} type="number" t={t} style={{width:"48px",textAlign:"center"}}/><span style={{color:t.textTertiary,fontSize:"0.6rem",minWidth:"15px"}}>{food.unit}</span><button onClick={()=>addFood(food)} style={{background:t.accent,color:t.bg,border:"none",borderRadius:"7px",padding:"0.2rem 0.45rem",fontSize:"0.7rem",fontWeight:700,cursor:"pointer"}}>+</button>{food.cat!=="Custom"&&<button onClick={()=>toggleFav(food.id)} title={favIds.includes(food.id)?"Quitar de habituales":"Añadir a habituales"} style={{background:"transparent",border:"none",color:favIds.includes(food.id)?t.gold:t.textTertiary,cursor:"pointer",fontSize:"0.85rem",padding:"0.1rem",lineHeight:1}}>{favIds.includes(food.id)?"★":"☆"}</button>}</div>))}</div>)}
      </Card>

      {/* ── Voice: listening indicator ── */}
      {voiceActive&&(
        <div style={{position:"fixed",bottom:"80px",left:"50%",transform:"translateX(-50%)",zIndex:300,background:"#EF4444",borderRadius:"24px",padding:"0.55rem 1.2rem",boxShadow:"0 4px 20px rgba(239,68,68,0.5)",display:"flex",alignItems:"center",gap:"0.5rem",maxWidth:"85vw"}}>
          <span style={{fontSize:"0.9rem",animation:"pulse 1s infinite"}}>🎤</span>
          <span style={{color:"#fff",fontSize:"0.78rem",fontWeight:600}}>{voiceTranscript||"Escuchando…"}</span>
        </div>
      )}

      {/* ── Voice: preview / confirm ── */}
      {voicePreview&&(
        <div style={{position:"fixed",inset:0,zIndex:200,display:"flex",alignItems:"flex-end",background:"rgba(0,0,0,0.55)"}} onClick={()=>{setVoicePreview(null);setVoiceTranscript("");}}>
          <div onClick={e=>e.stopPropagation()} style={{width:"100%",background:t.surface,borderRadius:"20px 20px 0 0",padding:"1.4rem 1.25rem 2rem",boxShadow:"0 -8px 30px rgba(0,0,0,0.45)"}}>
            <div style={{color:t.textTertiary,fontSize:"0.6rem",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:"0.4rem"}}>🎤 Escuchado</div>
            <div style={{color:t.textSecondary,fontSize:"0.82rem",fontStyle:"italic",marginBottom:"1rem"}}>"{voicePreview.raw}"</div>
            {voicePreview.food?(
              <>
                <div style={{background:t.surfaceHover,borderRadius:"12px",padding:"0.85rem 1rem",marginBottom:"1rem",display:"flex",alignItems:"center",gap:"0.65rem"}}>
                  <span style={{fontSize:"1.5rem"}}>{voicePreview.food.icon}</span>
                  <div>
                    <div style={{color:t.text,fontSize:"0.95rem",fontWeight:700}}>{voicePreview.food.name}</div>
                    <div style={{color:t.gold,fontSize:"0.72rem",fontWeight:600,marginTop:"0.1rem"}}>{voicePreview.qty}{voicePreview.food.unit} · {Math.round(voicePreview.food.cal*voicePreview.qty)} kcal · P:{Math.round(voicePreview.food.p*voicePreview.qty)}g C:{Math.round(voicePreview.food.c*voicePreview.qty)}g G:{Math.round(voicePreview.food.f*voicePreview.qty)}g</div>
                  </div>
                </div>
                <div style={{display:"flex",gap:"0.5rem"}}>
                  <button onClick={confirmVoice} style={{flex:1,background:t.accent,border:"none",borderRadius:"12px",padding:"0.82rem",color:t.bg,fontSize:"0.92rem",fontWeight:700,cursor:"pointer"}}>+ Añadir</button>
                  <button onClick={()=>{setVoicePreview(null);setVoiceTranscript("");}} style={{background:t.surfaceHover,border:"none",borderRadius:"12px",padding:"0.82rem 1.1rem",color:t.textSecondary,fontSize:"0.92rem",cursor:"pointer"}}>✕</button>
                </div>
              </>
            ):(
              <>
                <div style={{color:t.red,fontSize:"0.8rem",marginBottom:"0.9rem"}}>No encontrado. Búscalo en el catálogo.</div>
                <button onClick={()=>{setShowPicker(true);setVoicePreview(null);setVoiceTranscript("");}} style={{width:"100%",background:t.accent,border:"none",borderRadius:"12px",padding:"0.78rem",color:t.bg,fontSize:"0.88rem",fontWeight:700,cursor:"pointer",marginBottom:"0.4rem"}}>Abrir catálogo</button>
                <button onClick={()=>{setVoicePreview(null);setVoiceTranscript("");}} style={{width:"100%",background:"none",border:"none",color:t.textTertiary,cursor:"pointer",fontSize:"0.78rem",padding:"0.4rem"}}>Cancelar</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── STATS TAB ────────────────────────────────────────────────────────────────
// Analítica de progreso: hábitos, calorías, peso, gym y artes marciales.
// Incluye gráficas de progresión de fuerza, heatmap de hábitos y análisis
// de composición corporal (músculo vs grasa) basado en surplus calórico.
// ─── AREA LINE CHART ─────────────────────────────────────────────────────────
function AreaLineChart({data,color,t,height=90,xLabels,id=""}) {
  if(!data||data.length<2)return(
    <div style={{color:t.textTertiary,fontSize:"0.72rem",textAlign:"center",padding:"1.5rem",background:t.surfaceHover,borderRadius:"10px"}}>Sin suficientes datos</div>
  );
  const W=500,H=height;
  const vals=data.map(d=>d.value||0);
  const activeVals=vals.filter(v=>v>0);
  const minV=activeVals.length?Math.min(...activeVals):0;
  const maxV=activeVals.length?Math.max(...activeVals):100;
  const range=maxV-minV||1;
  const lo=Math.max(0,minV-range*0.2);
  const hi=maxV+range*0.2;
  const totalRange=hi-lo||1;
  const coords=data.map((d,i)=>({x:(i/(data.length-1))*W,y:H-((vals[i]-lo)/totalRange)*H*0.82-H*0.08,value:vals[i],label:d.label}));
  let path=`M ${coords[0].x},${coords[0].y}`;
  for(let i=1;i<coords.length;i++){const p=coords[i-1],c=coords[i];const cpx=(p.x+c.x)/2;path+=` C ${cpx},${p.y} ${cpx},${c.y} ${c.x},${c.y}`;}
  const fillPath=path+` L ${coords[coords.length-1].x},${H+5} L ${coords[0].x},${H+5} Z`;
  const avgVal=activeVals.length?activeVals.reduce((a,b)=>a+b,0)/activeVals.length:0;
  const avgY=H-((avgVal-lo)/totalRange)*H*0.82-H*0.08;
  const gradId=`alc${id}${color.replace(/[^a-f0-9]/gi,"")}`;
  const peakVal=activeVals.length?Math.max(...activeVals):0;
  return(
    <svg viewBox={`0 -14 ${W} ${H+36}`} style={{width:"100%",overflow:"hidden"}} preserveAspectRatio="none">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3"/>
          <stop offset="100%" stopColor={color} stopOpacity="0.02"/>
        </linearGradient>
      </defs>
      {activeVals.length>0&&<line x1={0} y1={avgY} x2={W} y2={avgY} stroke={color} strokeWidth="1" strokeDasharray="7,5" opacity="0.35"/>}
      <path d={fillPath} fill={`url(#${gradId})`}/>
      <path d={path} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
      {coords.map((c,i)=>{
        const isLast=i===coords.length-1;
        const showLbl=(isLast||c.value===peakVal)&&c.value>0;
        return(
          <g key={i}>
            {isLast?<circle cx={c.x} cy={c.y} r={5} fill={t.surface} stroke={color} strokeWidth="2.5"/>
              :<circle cx={c.x} cy={c.y} r={c.value>0?3.5:2} fill={c.value>0?color:t.border}/>}
            {showLbl&&<text x={c.x} y={c.y-9} fill={color} fontSize="9.5" textAnchor="middle" fontWeight="700" fontFamily="inherit">{c.label||(c.value>0?c.value:"")}</text>}
          </g>
        );
      })}
      {xLabels&&coords.map((c,i)=>xLabels[i]?(
        <text key={i} x={c.x} y={H+19} fill={t.textTertiary} fontSize="8.5" textAnchor="middle" fontFamily="inherit">{xLabels[i]}</text>
      ):null)}
    </svg>
  );
}

function StatsTab({habits,workouts,nutrition,bodyStats,sleep,goal,habitConfig,t}) {
  const[compPeriod,setCompPeriod]=useState("week");
  const[period,setPeriod]=useState("30");
  const macros=calcMacros(goal);
  const MONTH_ABBR=["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
  const now=new Date();

  // ── Progresión de fuerza: max weight per month (last 7 months) ──
  const monthlyStrength=Array.from({length:7},(_,i)=>{
    const d=new Date(now.getFullYear(),now.getMonth()-6+i,1);
    const prefix=`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
    let maxW=0;
    Object.entries(workouts).filter(([k])=>k.startsWith(prefix)).forEach(([,wd])=>{
      if(wd.sessionType!=="Gym")return;
      (wd.exercises||[]).forEach(ex=>(ex.sets||[]).forEach(s=>{const w=parseFloat(s.weight)||0;if(w>maxW)maxW=w;}));
    });
    return{value:maxW,label:maxW>0?`${maxW}kg`:""};
  });
  // Media móvil de 3 puntos para suavizar saltos bruscos
  const monthlyStrengthSmoothed=monthlyStrength.map((d,i,arr)=>{
    if(!d.value)return d;
    const pts=[d.value];
    if(i>0&&arr[i-1].value)pts.push(arr[i-1].value);
    if(i<arr.length-1&&arr[i+1].value)pts.push(arr[i+1].value);
    return{...d,value:Math.round(pts.reduce((a,b)=>a+b,0)/pts.length*10)/10};
  });
  const strXLabels=Array.from({length:7},(_,i)=>MONTH_ABBR[new Date(now.getFullYear(),now.getMonth()-6+i,1).getMonth()]);
  const curStrength=monthlyStrength[6].value;
  const prevStrength=monthlyStrength[5].value;
  const strengthDelta=curStrength&&prevStrength?curStrength-prevStrength:null;

  // ── Índice de intensidad: sessions/week as % of target (last 12 weeks) ──
  const TARGET_SESS=3;
  const weeklyIntensity=Array.from({length:12},(_,i)=>{
    const wkStart=new Date(now);
    wkStart.setDate(now.getDate()-now.getDay()+1-(11-i)*7);
    wkStart.setHours(0,0,0,0);
    const wkEnd=new Date(wkStart);wkEnd.setDate(wkStart.getDate()+6);
    let sess=0;
    Object.entries(workouts).forEach(([k,wd])=>{const d=new Date(k);if(d>=wkStart&&d<=wkEnd&&wd.sessionType)sess++;});
    const pct=Math.min(150,Math.round(sess/TARGET_SESS*100));
    return{value:pct,label:pct>0?`${pct}%`:""};
  });
  const intXLabels=weeklyIntensity.map((_,i)=>`S${i+1}`);
  const curIntensity=weeklyIntensity[11].value;

  // ── Comparativa de periodo ──
  const dayOfWeek=now.getDay()||7;
  const monday=new Date(now);monday.setDate(now.getDate()-dayOfWeek+1);monday.setHours(0,0,0,0);
  const mkDays=(start,n)=>Array.from({length:n},(_,i)=>{const d=new Date(start);d.setDate(start.getDate()+i);return d.toISOString().split("T")[0];});
  const thisWeekDays=mkDays(monday,7);
  const lastMonday=new Date(monday);lastMonday.setDate(monday.getDate()-7);
  const lastWeekDays=mkDays(lastMonday,7);
  const daysInCurMonth=now.getDate();
  const thisMonthDays=mkDays(new Date(now.getFullYear(),now.getMonth(),1),daysInCurMonth);
  const lastMonthDays=mkDays(new Date(now.getFullYear(),now.getMonth()-1,1),daysInCurMonth);
  const curDays=compPeriod==="week"?thisWeekDays:thisMonthDays;
  const prevDays=compPeriod==="week"?lastWeekDays:lastMonthDays;
  const calcVol=ds=>ds.reduce((tot,k)=>{
    const wd=workouts[k];if(!wd||wd.sessionType!=="Gym")return tot;
    return tot+(wd.exercises||[]).reduce((a,ex)=>(ex.sets||[]).reduce((b,s)=>b+(parseFloat(s.weight)||0)*(parseInt(s.reps)||0),a),0);
  },0);
  const curVol=calcVol(curDays),prevVol=calcVol(prevDays);
  const volDeltaPct=prevVol>0?Math.round((curVol-prevVol)/prevVol*100):null;
  const curSess=curDays.filter(k=>workouts[k]?.sessionType).length;
  const prevSess=prevDays.filter(k=>workouts[k]?.sessionType).length;
  const sessDeltaPct=prevSess>0?Math.round((curSess-prevSess)/prevSess*100):null;
  const activeOblHabits=DEFAULT_HABITS.filter(h=>{const cfg=habitConfig[h.id];return(cfg==="obl"||(cfg===undefined&&h.obl))&&cfg!=="hidden";});
  const calcHabPct=ds=>{const vs=ds.map(k=>{const h=habits[k]||{};return activeOblHabits.length>0?activeOblHabits.filter(oh=>h[oh.id]).length/activeOblHabits.length*100:0;}).filter(v=>v>0);return vs.length?Math.round(vs.reduce((a,b)=>a+b,0)/vs.length):0;};
  const curHab=calcHabPct(curDays),prevHab=calcHabPct(prevDays);
  const habDeltaPct=prevHab>0?Math.round((curHab-prevHab)/prevHab*100):null;
  const fmtVol=v=>v>=1000?`${(v/1000).toFixed(1)}k kg`:`${Math.round(v)} kg`;
  const fmtDelta=d=>d===null?"":d>0?`+${d}%`:d<0?`${d}%`:"=";
  const deltaColor=d=>d===null?t.textTertiary:d>0?t.green:d<0?t.red:t.textSecondary;

  // ── Composición corporal (30d) ──
  const COMP_DAYS=30;
  const periodDays=Array.from({length:COMP_DAYS},(_,i)=>{const d=new Date();d.setDate(d.getDate()-(COMP_DAYS-1-i));return d.toISOString().split("T")[0];});
  const calDays=periodDays.map(k=>Object.values((nutrition[k]||{}).items||{}).reduce((a,it)=>a+it.cal,0)).filter(v=>v>0);
  const avgCal=calDays.length?Math.round(calDays.reduce((a,b)=>a+b,0)/calDays.length):0;
  const protDays=periodDays.map(k=>Object.values((nutrition[k]||{}).items||{}).reduce((a,it)=>a+it.p,0)).filter(v=>v>0);
  const avgProt=protDays.length?Math.round(protDays.reduce((a,b)=>a+b,0)/protDays.length):0;
  const weightEntries=periodDays.map(k=>({k,w:parseFloat(bodyStats[k]?.weight)})).filter(e=>!isNaN(e.w)&&e.w>0);
  const firstW=weightEntries[0]?.w,lastW=weightEntries[weightEntries.length-1]?.w;
  const actualDeltaKg=(firstW&&lastW&&weightEntries.length>=2)?+(lastW-firstW).toFixed(1):null;
  const calSurplus=avgCal>0?avgCal-macros.tdee:null;
  const expectedKgPeriod=calSurplus!==null?+((calSurplus*COMP_DAYS/7)/7700).toFixed(1):null;
  const protQuality=avgProt>0?Math.min(1,avgProt/macros.protein):0;
  const muscleRatio=calSurplus>0?(protQuality>=0.8?0.75:protQuality>=0.6?0.55:0.35):(protQuality>=0.8?0.88:protQuality>=0.6?0.70:0.50);
  const expectedMuscle=expectedKgPeriod!==null?+(expectedKgPeriod*muscleRatio).toFixed(1):null;
  const expectedFat=expectedKgPeriod!==null?+(expectedKgPeriod*(1-muscleRatio)).toFixed(1):null;
  const kgPerWeekExpected=calSurplus!==null?+((calSurplus*7/7)/7700).toFixed(2):null;

  // ── Stats del período seleccionado (selector 7d/30d/3m/1año) ──
  const days=parseInt(period);
  const selDays=Array.from({length:days},(_,i)=>{const d=new Date();d.setDate(d.getDate()-(days-1-i));return d.toISOString().split("T")[0];});
  const gymSessions=Object.entries(workouts).filter(([k,w])=>selDays.includes(k)&&w.sessionType==="Gym").map(([,w])=>w);
  const maSessions=Object.entries(workouts).filter(([k,w])=>selDays.includes(k)&&w.sessionType&&w.sessionType!=="Gym").map(([,w])=>w);
  const totalGymMin=gymSessions.reduce((a,w)=>a+(parseFloat(w.duration)||0),0);
  const totalMAMin=maSessions.reduce((a,w)=>a+(parseFloat(w.duration)||0),0);
  const totalRead=selDays.reduce((a,k)=>a+(parseFloat(habits[k]?.leer_min)||0),0);
  const totalMedit=selDays.reduce((a,k)=>a+(parseFloat(habits[k]?.medit_min)||0),0);
  const sleepGoalH=8; // horas objetivo por noche
  const sleepEntries=selDays.map(k=>{const stats=calcSleepStats((sleep[k]||{}).segments);return stats?parseFloat(stats.totalH):0;}).filter(v=>v>0);
  const avgSleepH=sleepEntries.length>0?(sleepEntries.reduce((a,b)=>a+b,0)/sleepEntries.length).toFixed(1):null;
  const sleepPct=avgSleepH?Math.min(1,parseFloat(avgSleepH)/sleepGoalH):0;
  const avgHabits=Math.round(selDays.map(k=>{const h=habits[k]||{};return activeOblHabits.length>0?activeOblHabits.filter(oh=>h[oh.id]).length/activeOblHabits.length*100:0;}).reduce((a,b)=>a+b,0)/selDays.length);
  const pCalDays=selDays.map(k=>Object.values((nutrition[k]||{}).items||{}).reduce((a,it)=>a+it.cal,0)).filter(v=>v>0);
  const pAvgCal=pCalDays.length?Math.round(pCalDays.reduce((a,b)=>a+b,0)/pCalDays.length):0;
  const pProtDays=selDays.map(k=>Object.values((nutrition[k]||{}).items||{}).reduce((a,it)=>a+it.p,0)).filter(v=>v>0);
  const pAvgProt=pProtDays.length?Math.round(pProtDays.reduce((a,b)=>a+b,0)/pProtDays.length):0;

  const[mi]=useState(()=>Math.floor(Math.random()*MOTIVATIONAL.length));

  return(
    <div style={{display:"flex",flexDirection:"column",gap:"1.25rem"}}>
      <div style={{background:t.accentMuted,border:`1px solid ${t.border}`,borderRadius:"14px",padding:"0.88rem 1rem"}}>
        <div style={{color:t.text,fontSize:"0.85rem",lineHeight:1.55,fontWeight:500}}>"{MOTIVATIONAL[mi].text}"</div>
        <div style={{color:t.textTertiary,fontSize:"0.7rem",marginTop:"0.28rem"}}>— {MOTIVATIONAL[mi].author}</div>
      </div>

      {/* Progresión de fuerza */}
      <Card t={t}>
        <div style={{color:t.textTertiary,fontSize:"0.58rem",fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:"0.55rem"}}>💪 Progresión de fuerza</div>
        <div style={{display:"flex",alignItems:"baseline",gap:"0.65rem",marginBottom:"0.5rem"}}>
          <span style={{color:t.gold,fontSize:"2.2rem",fontWeight:800,letterSpacing:"-0.03em",lineHeight:1}}>{curStrength>0?`${curStrength} kg`:"—"}</span>
          {strengthDelta!==null&&(
            <span style={{color:strengthDelta>0?t.green:strengthDelta<0?t.red:t.textSecondary,fontSize:"0.78rem",fontWeight:600}}>
              {strengthDelta>0?"+":""}{strengthDelta} vs mes anterior
            </span>
          )}
        </div>
        <AreaLineChart data={monthlyStrengthSmoothed} color={t.gold} t={t} height={90} xLabels={strXLabels} id="str"/>
      </Card>

      {/* Índice de intensidad */}
      <Card t={t}>
        <div style={{color:t.textTertiary,fontSize:"0.58rem",fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:"0.55rem"}}>⚡ Índice de intensidad</div>
        <div style={{display:"flex",alignItems:"baseline",gap:"0.65rem",marginBottom:"0.5rem"}}>
          <span style={{color:t.gold,fontSize:"2.2rem",fontWeight:800,letterSpacing:"-0.03em",lineHeight:1}}>{curIntensity}%</span>
          <span style={{color:t.textTertiary,fontSize:"0.72rem"}}>semana actual · meta {TARGET_SESS} sesiones</span>
        </div>
        <AreaLineChart data={weeklyIntensity} color={t.gold} t={t} height={90} xLabels={intXLabels} id="int"/>
      </Card>

      {/* Comparativa de periodo */}
      <Card t={t}>
        <div style={{color:t.textTertiary,fontSize:"0.58rem",fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:"0.7rem"}}>📊 Comparativa de periodo</div>
        <div style={{display:"flex",gap:"0.3rem",marginBottom:"1rem",background:t.surfaceHover,borderRadius:"10px",padding:"0.2rem"}}>
          {[["week","Esta semana"],["month","Este mes"]].map(([v,l])=>(
            <button key={v} onClick={()=>setCompPeriod(v)}
              style={{flex:1,padding:"0.45rem",borderRadius:"8px",border:"none",cursor:"pointer",fontSize:"0.75rem",fontWeight:700,
                background:compPeriod===v?t.gold:"transparent",
                color:compPeriod===v?"#000":t.textSecondary,transition:"all 0.15s"}}>
              {l}
            </button>
          ))}
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:"0.8rem"}}>
          {[
            {l:"Volumen",v:fmtVol(curVol),delta:volDeltaPct,sub:`vs ${compPeriod==="week"?"semana":"mes"} anterior`},
            {l:"Entrenos",v:curSess,delta:sessDeltaPct,sub:"sesiones"},
            {l:"Hábitos",v:`${curHab}%`,delta:habDeltaPct,sub:"cumplimiento"},
          ].map(s=>(
            <div key={s.l} style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <div>
                <div style={{color:t.textTertiary,fontSize:"0.6rem",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em"}}>{s.l}</div>
                <div style={{display:"flex",alignItems:"baseline",gap:"0.4rem"}}>
                  <span style={{color:t.text,fontSize:"1.3rem",fontWeight:800,letterSpacing:"-0.02em",lineHeight:1.2}}>{s.v}</span>
                  <span style={{color:t.textTertiary,fontSize:"0.6rem"}}>{s.sub}</span>
                </div>
              </div>
              {s.delta!==null&&(
                <div style={{background:s.delta>0?t.greenBg:s.delta<0?t.redBg:t.accentMuted,borderRadius:"8px",padding:"0.3rem 0.7rem",color:deltaColor(s.delta),fontSize:"0.82rem",fontWeight:700}}>
                  {fmtDelta(s.delta)}
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>

      {/* Selector de período */}
      <div style={{display:"flex",gap:"0.25rem"}}>
        {[["7","7d"],["30","30d"],["90","3m"],["365","1año"]].map(([v,l])=>(
          <button key={v} onClick={()=>setPeriod(v)} style={{flex:1,padding:"0.42rem",borderRadius:"10px",border:"none",cursor:"pointer",fontSize:"0.72rem",fontWeight:700,background:period===v?t.accent:t.surface,color:period===v?t.bg:t.textSecondary}}>{l}</button>
        ))}
      </div>

      {/* Tiempo activo */}
      {(()=>{
        const meditGoal=10*days; const readGoal=20*days;
        const meditPct=meditGoal>0?totalMedit/meditGoal:0;
        const readPct=readGoal>0?totalRead/readGoal:0;
        const goalColor=p=>p>=0.8?t.green:p>=0.4?t.gold:null;
        const goalBg=p=>p>=0.8?t.greenBg:p>=0.4?t.goldBg:null;
        return(
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0.5rem"}}>
          {[
            {l:"Gym",v:`${(totalGymMin/60).toFixed(1)}h`,sub:`${Math.round(totalGymMin)} min`,icon:"🏋️",pct:null},
            {l:"Artes marciales",v:`${(totalMAMin/60).toFixed(1)}h`,sub:`${Math.round(totalMAMin)} min`,icon:"🥊",pct:null},
            {l:"Meditación",v:`${Math.round(totalMedit)}min`,sub:`obj. ${meditGoal}min`,icon:"🧘",pct:meditPct},
            {l:"Lectura",v:`${Math.round(totalRead)}min`,sub:`obj. ${readGoal}min`,icon:"📖",pct:readPct},
          ].map(s=>{
            const gc=s.pct!==null?goalColor(s.pct):null;
            const gb=s.pct!==null?goalBg(s.pct):null;
            return(
            <Card key={s.l} t={t} style={{padding:"0.85rem",background:gb||t.surface,border:`1px solid ${gc?gc+"40":t.border}`}}>
              <div style={{fontSize:"1.1rem",marginBottom:"0.25rem"}}>{s.icon}</div>
              <div style={{color:gc||t.text,fontSize:"1.3rem",fontWeight:800,letterSpacing:"-0.02em"}}>{s.v}</div>
              <div style={{color:t.textTertiary,fontSize:"0.58rem",fontWeight:600,textTransform:"uppercase",marginTop:"0.05rem"}}>{s.l}</div>
              <div style={{color:t.textTertiary,fontSize:"0.6rem"}}>{s.sub}</div>
              {s.pct!==null&&<div style={{height:"2px",background:t.border,borderRadius:"1px",marginTop:"0.4rem",overflow:"hidden"}}><div style={{width:`${Math.min(100,Math.round(s.pct*100))}%`,height:"100%",background:gc||t.red,borderRadius:"1px"}}/></div>}
            </Card>
            );
          })}
        </div>
        );
      })()}

      {avgSleepH&&(
        <Card t={t} style={{padding:"0.85rem",background:sleepPct>=0.875?t.greenBg:sleepPct>=0.75?t.goldBg:t.redBg,border:`1px solid ${sleepPct>=0.875?t.green+"40":sleepPct>=0.75?t.gold+"40":t.red+"40"}`}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <div style={{display:"flex",alignItems:"center",gap:"0.5rem"}}>
              <span style={{fontSize:"1.3rem"}}>🌙</span>
              <div>
                <div style={{color:sleepPct>=0.875?t.green:sleepPct>=0.75?t.gold:t.red,fontSize:"1.3rem",fontWeight:800,letterSpacing:"-0.02em"}}>{avgSleepH}h <span style={{fontSize:"0.65rem",fontWeight:500,color:t.textTertiary}}>media/noche</span></div>
                <div style={{color:t.textTertiary,fontSize:"0.58rem",fontWeight:600,textTransform:"uppercase"}}>Sueño · obj. {sleepGoalH}h</div>
              </div>
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{color:t.textTertiary,fontSize:"0.6rem"}}>{sleepEntries.length} noches registradas</div>
              <div style={{height:"3px",width:"80px",background:t.border,borderRadius:"2px",marginTop:"0.3rem",overflow:"hidden"}}><div style={{width:`${Math.min(100,Math.round(sleepPct*100))}%`,height:"100%",background:sleepPct>=0.875?t.green:sleepPct>=0.75?t.gold:t.red,borderRadius:"2px"}}/></div>
            </div>
          </div>
        </Card>
      )}

      {/* Medias del período */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0.5rem"}}>
        {[
          {l:"Hábitos %",v:`${avgHabits}%`,c:avgHabits>=80?t.green:avgHabits>=60?t.gold:t.red},
          {l:"Sesiones total",v:gymSessions.length+maSessions.length,c:t.blue},
          {l:"Kcal media",v:pAvgCal||"—",c:pAvgCal>=macros.cal*0.9?t.green:t.gold},
          {l:"Proteína media",v:pAvgProt?`${pAvgProt}g`:"—",c:pAvgProt>=macros.protein*0.9?t.green:t.blue},
        ].map(s=>(
          <Card key={s.l} t={t} style={{padding:"0.85rem"}}>
            <div style={{color:t.textTertiary,fontSize:"0.58rem",fontWeight:700,letterSpacing:"0.07em",textTransform:"uppercase"}}>{s.l}</div>
            <div style={{color:s.c,fontSize:"1.4rem",fontWeight:800,letterSpacing:"-0.02em",marginTop:"0.15rem"}}>{s.v}</div>
          </Card>
        ))}
      </div>

      {/* Composición corporal */}
      <Card t={t}>
        <div style={{color:t.textTertiary,fontSize:"0.58rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:"0.7rem"}}>⚖️ Composición · {COMP_DAYS}d</div>
        <div style={{marginBottom:"0.75rem"}}>
          <div style={{color:t.textTertiary,fontSize:"0.6rem",fontWeight:600,marginBottom:"0.3rem"}}>Peso registrado</div>
          {actualDeltaKg!==null?(
            <div style={{display:"flex",alignItems:"baseline",gap:"0.4rem"}}>
              <span style={{color:actualDeltaKg>0?t.green:actualDeltaKg<0?t.red:t.textSecondary,fontSize:"1.5rem",fontWeight:800,letterSpacing:"-0.02em"}}>{actualDeltaKg>0?"+":""}{actualDeltaKg} kg</span>
              <span style={{color:t.textTertiary,fontSize:"0.65rem"}}>{firstW} → {lastW} kg · {weightEntries.length} registros</span>
            </div>
          ):(
            <div style={{color:t.textTertiary,fontSize:"0.75rem"}}>Sin suficientes registros en este período</div>
          )}
        </div>
        {calSurplus!==null&&(
          <div style={{background:t.surfaceHover,borderRadius:"10px",padding:"0.6rem 0.75rem",marginBottom:"0.65rem"}}>
            <div style={{color:t.textTertiary,fontSize:"0.6rem",fontWeight:600,marginBottom:"0.35rem"}}>Estimación por calorías (media {avgCal} kcal/día)</div>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:"0.18rem"}}>
              <span style={{color:t.textSecondary,fontSize:"0.72rem"}}>Superávit/déficit</span>
              <span style={{color:calSurplus>0?t.green:t.red,fontWeight:700,fontSize:"0.75rem"}}>{calSurplus>0?"+":""}{calSurplus} kcal/día</span>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:"0.18rem"}}>
              <span style={{color:t.textSecondary,fontSize:"0.72rem"}}>Ritmo esperado</span>
              <span style={{color:t.text,fontWeight:700,fontSize:"0.75rem"}}>{kgPerWeekExpected>0?"+":""}{kgPerWeekExpected} kg/semana</span>
            </div>
            <div style={{display:"flex",justifyContent:"space-between"}}>
              <span style={{color:t.textSecondary,fontSize:"0.72rem"}}>En {COMP_DAYS} días</span>
              <span style={{color:t.text,fontWeight:700,fontSize:"0.75rem"}}>{expectedKgPeriod>0?"+":""}{expectedKgPeriod} kg</span>
            </div>
          </div>
        )}
        {expectedKgPeriod!==null&&expectedKgPeriod!==0&&(
          <div>
            <div style={{color:t.textTertiary,fontSize:"0.6rem",fontWeight:600,marginBottom:"0.35rem"}}>
              De esos {Math.abs(expectedKgPeriod)} kg — split estimado <span style={{color:t.textTertiary,fontWeight:400}}>(proteína {Math.round(protQuality*100)}% objetivo)</span>
            </div>
            {calSurplus>0?(
              <>
                <div style={{display:"flex",gap:"0.4rem",marginBottom:"0.35rem"}}>
                  <div style={{flex:muscleRatio,background:t.green,borderRadius:"6px",height:"8px"}}/>
                  <div style={{flex:1-muscleRatio,background:t.gold,borderRadius:"6px",height:"8px"}}/>
                </div>
                <div style={{display:"flex",justifyContent:"space-between"}}>
                  <span style={{color:t.green,fontSize:"0.68rem",fontWeight:700}}>💪 Músculo: +{Math.abs(expectedMuscle)} kg ({Math.round(muscleRatio*100)}%)</span>
                  <span style={{color:t.gold,fontSize:"0.68rem",fontWeight:700}}>🟡 Grasa: +{Math.abs(expectedFat)} kg</span>
                </div>
              </>
            ):(
              <>
                <div style={{display:"flex",gap:"0.4rem",marginBottom:"0.35rem"}}>
                  <div style={{flex:muscleRatio,background:t.green,borderRadius:"6px",height:"8px"}}/>
                  <div style={{flex:1-muscleRatio,background:t.red,borderRadius:"6px",height:"8px"}}/>
                </div>
                <div style={{display:"flex",justifyContent:"space-between"}}>
                  <span style={{color:t.green,fontSize:"0.68rem",fontWeight:700}}>🔥 Grasa perdida: ~{Math.abs(expectedMuscle)} kg ({Math.round(muscleRatio*100)}%)</span>
                  <span style={{color:t.red,fontSize:"0.68rem",fontWeight:700}}>💪 Músculo perdido: ~{Math.abs(expectedFat)} kg</span>
                </div>
                <div style={{color:t.textTertiary,fontSize:"0.62rem",marginTop:"0.3rem"}}>
                  {protQuality>=0.8?"Con buena proteína pierdes casi todo en grasa y preservas músculo ✓":"Más proteína = menos músculo perdido"}
                </div>
              </>
            )}
            {protQuality<0.8&&<div style={{color:t.gold,fontSize:"0.62rem",marginTop:"0.4rem",background:t.goldBg,borderRadius:"8px",padding:"0.35rem 0.5rem"}}>⚠️ Con más proteína ({macros.protein}g objetivo) el ratio músculo/grasa mejoraría</div>}
          </div>
        )}
        <div style={{color:t.textTertiary,fontSize:"0.53rem",marginTop:"0.65rem",lineHeight:1.5,borderTop:`1px solid ${t.border}`,paddingTop:"0.5rem"}}>
          ⚠️ Estimaciones algorítmicas basadas en calorías y proteína registradas. No son valores médicos ni sustituyen seguimiento profesional.
        </div>
      </Card>
    </div>
  );
}

// ─── SETTINGS ─────────────────────────────────────────────────────────────────
// Panel de configuración: modo oscuro, idioma, hábitos, objetivo y usuarios.
// Se muestra como modal bottom-sheet sobre la app principal.
function Settings({goal,setGoal,habitConfig,setHabitConfig,darkMode,setDarkMode,onClose,t,userId,user,users,saveUsers,onLogout,lang,setLang}) {
  const[local,setLocal]=useState(goal);
  const[section,setSection]=useState("general");
  const[showAddUser,setShowAddUser]=useState(false);
  const[newName,setNewName]=useState("");
  const[newPin,setNewPin]=useState("");
  const[newPin2,setNewPin2]=useState("");
  const[addErr,setAddErr]=useState("");
  const preview=calcMacros(local);

  const resetAddForm=()=>{setNewName("");setNewPin("");setNewPin2("");setAddErr("");setAddErr("");setShowAddUser(false);};

  const handleAddUser=()=>{
    if(!newName.trim()){setAddErr("Introduce un nombre");return;}
    if(newPin.length<4){setAddErr("El PIN debe tener 4 dígitos");return;}
    if(newPin!==newPin2){setAddErr("Los PINs no coinciden");setNewPin2("");return;}
    const uid="user_"+Date.now();
    const nu={...users,[uid]:{id:uid,name:newName.trim(),pin:newPin,isAdmin:false,onboardingDone:false,country:null,createdAt:new Date().toISOString()}};
    if(saveUsers)saveUsers(nu);
    resetAddForm();
  };

  const getHabitStatus = h => {
    const cfg = habitConfig[h.id];
    if (cfg === "hidden") return "hidden";
    if (cfg === "obl" || (cfg === undefined && h.obl)) return "obl";
    return "opt";
  };

  const cycleHabit = h => {
    const current = getHabitStatus(h);
    const next = current === "obl" ? "opt" : current === "opt" ? "hidden" : "obl";
    setHabitConfig(p => ({...p, [h.id]: next}));
  };

  const statusLabel = s => s === "obl" ? "Obligatorio" : s === "opt" ? "Opcional" : "Oculto";
  const statusColor = (s,t) => s === "obl" ? t.green : s === "opt" ? t.textSecondary : t.border;

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:100,display:"flex",alignItems:"flex-end",backdropFilter:"blur(6px)"}} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{width:"100%",maxWidth:"480px",margin:"0 auto",background:t.surface,borderRadius:"20px 20px 0 0",maxHeight:"90vh",display:"flex",flexDirection:"column",overflow:"hidden"}}>
        {/* Cabecera fija — siempre visible aunque el contenido scrollee */}
        <div style={{padding:"1.25rem 1.25rem 0.85rem",flexShrink:0,borderBottom:`1px solid ${t.border}`}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"1rem"}}>
            <span style={{color:t.text,fontSize:"1rem",fontWeight:700}}>{tr("settings_title")}</span>
            <button onClick={onClose} style={{background:t.surfaceHover,border:"none",borderRadius:"8px",padding:"0.28rem 0.7rem",color:t.textSecondary,cursor:"pointer",fontSize:"1rem"}}>✕</button>
          </div>
          <div style={{display:"flex",gap:"0.3rem"}}>
            {[["general",tr("lbl_general")],["habitos",tr("lbl_habits")],["objetivo",tr("lbl_goal")],...(user?.isAdmin?[["usuarios",tr("lbl_users")]]:[])]
              .map(([s,l])=>(
              <button key={s} onClick={()=>setSection(s)} style={{flex:1,padding:"0.42rem",borderRadius:"10px",border:"none",cursor:"pointer",fontSize:"0.72rem",fontWeight:700,background:section===s?t.accent:t.surfaceHover,color:section===s?t.bg:t.textSecondary}}>{l}</button>
            ))}
          </div>
        </div>
        {/* Contenido scrollable */}
        <div style={{overflowY:"auto",flex:1,padding:"1rem 1.25rem 2rem"}}>


        {section==="general"&&(
          <div style={{display:"flex",flexDirection:"column",gap:"0.65rem"}}>
            <Card t={t} style={{padding:"0.85rem"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{color:t.text,fontSize:"0.85rem"}}>{tr("lbl_dark_mode")}</span>
                <div onClick={()=>setDarkMode(!darkMode)} style={{width:44,height:26,borderRadius:13,background:darkMode?t.green:t.border,position:"relative",cursor:"pointer",transition:"background 0.2s"}}>
                  <div style={{position:"absolute",top:3,left:darkMode?19:3,width:20,height:20,borderRadius:10,background:"#fff",transition:"left 0.2s",boxShadow:"0 1px 3px rgba(0,0,0,0.3)"}}/>
                </div>
              </div>
            </Card>
            {/* Selector de idioma */}
            <Card t={t} style={{padding:"0.85rem"}}>
              <div style={{color:t.textTertiary,fontSize:"0.6rem",fontWeight:700,letterSpacing:"0.07em",textTransform:"uppercase",marginBottom:"0.55rem"}}>{tr("lbl_language")}</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0.35rem"}}>
                {[["es","🇪🇸 Español"],["en","🇬🇧 English"],["ar","🇦🇪 العربية"],["de","🇩🇪 Deutsch"]].map(([code,label])=>(
                  <button key={code} onClick={()=>setLang&&setLang(code)}
                    style={{padding:"0.4rem 0.5rem",borderRadius:"10px",border:`1px solid ${lang===code?t.accent:t.border}`,background:lang===code?t.accentMuted:t.surface,color:lang===code?t.text:t.textSecondary,fontSize:"0.75rem",fontWeight:lang===code?700:500,cursor:"pointer",textAlign:"center"}}>
                    {label}
                  </button>
                ))}
              </div>
            </Card>
            {/* Perfil activo */}
            <Card t={t} style={{padding:"0.85rem"}}>
              <div style={{display:"flex",alignItems:"center",gap:"0.75rem"}}>
                <div style={{width:40,height:40,borderRadius:"50%",background:user?.isAdmin?t.green:t.blue,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  <span style={{color:"#fff",fontWeight:700,fontSize:"1rem"}}>{user?.name?.[0]?.toUpperCase()||"?"}</span>
                </div>
                <div style={{flex:1}}>
                  <div style={{color:t.text,fontSize:"0.85rem",fontWeight:600}}>{user?.name}</div>
                  <div style={{color:t.textTertiary,fontSize:"0.65rem",marginTop:"0.05rem"}}>
                    {user?.isAdmin?tr("login_admin"):tr("login_user")}{user?.country&&COUNTRY_PRESETS[user.country]?` · ${COUNTRY_PRESETS[user.country].label}`:""}
                  </div>
                </div>
                <button onClick={()=>{onClose();onLogout&&onLogout();}}
                  style={{background:t.redBg,color:t.red,border:`1px solid ${t.red}30`,borderRadius:"10px",padding:"0.35rem 0.75rem",fontSize:"0.72rem",fontWeight:700,cursor:"pointer"}}>
                  {tr("btn_logout")}
                </button>
              </div>
            </Card>
          </div>
        )}

        {section==="habitos"&&(
          <div style={{display:"flex",flexDirection:"column",gap:"0.4rem"}}>
            <div style={{color:t.textTertiary,fontSize:"0.7rem",marginBottom:"0.3rem",lineHeight:1.5}}>Pulsa en cada hábito para cambiar entre Obligatorio → Opcional → Oculto</div>
            {DEFAULT_HABITS.map(h=>{
              const status=getHabitStatus(h);
              return (
                <div key={h.id} onClick={()=>cycleHabit(h)} style={{display:"flex",alignItems:"center",gap:"0.65rem",padding:"0.65rem 0.9rem",borderRadius:"12px",cursor:"pointer",background:t.surfaceHover,border:`1px solid ${status==="obl"?t.green+"40":t.border}`}}>
                  <span style={{fontSize:"0.95rem"}}>{h.icon}</span>
                  <div style={{flex:1}}>
                    <div style={{color:t.text,fontSize:"0.8rem",fontWeight:500}}>{h.label}</div>
                    <div style={{color:t.textTertiary,fontSize:"0.62rem",marginTop:"0.05rem",lineHeight:1.4}}>{h.why.slice(0,60)}...</div>
                  </div>
                  <div style={{background:status==="obl"?t.greenBg:status==="opt"?t.accentMuted:t.redBg,borderRadius:"8px",padding:"0.2rem 0.55rem",flexShrink:0}}>
                    <span style={{color:statusColor(status,t),fontSize:"0.65rem",fontWeight:700}}>{statusLabel(status)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {section==="objetivo"&&(
          <div style={{display:"flex",flexDirection:"column",gap:"0.6rem"}}>
            {[{k:"weight",l:"Peso actual (kg)",ph:"69"},{k:"goalWeight",l:"Peso objetivo (kg)",ph:"75"},{k:"height",l:"Altura (cm)",ph:"175"},{k:"age",l:"Edad",ph:"22"}].map(({k,l,ph})=>(
              <div key={k}>
                <div style={{color:t.textTertiary,fontSize:"0.63rem",fontWeight:600,letterSpacing:"0.06em",textTransform:"uppercase",marginBottom:"0.28rem"}}>{l}</div>
                <Inp value={local[k]||""} onChange={e=>setLocal(p=>({...p,[k]:e.target.value}))} placeholder={ph} type="number" t={t} style={{width:"100%"}}/>
              </div>
            ))}
            <div>
              <div style={{color:t.textTertiary,fontSize:"0.63rem",fontWeight:600,letterSpacing:"0.06em",textTransform:"uppercase",marginBottom:"0.28rem"}}>Nivel de actividad</div>
              <div style={{display:"flex",flexDirection:"column",gap:"0.3rem"}}>
                {ACTIVITY_LEVELS.map(({v,l,desc})=>{
                  const sel=(local.activityFactor||"1.9")===v;
                  return (
                    <div key={v} onClick={()=>setLocal(p=>({...p,activityFactor:v}))} style={{display:"flex",alignItems:"center",gap:"0.65rem",padding:"0.55rem 0.75rem",borderRadius:"10px",cursor:"pointer",border:`1px solid ${sel?t.accent:t.border}`,background:sel?t.accentMuted:t.surfaceHover}}>
                      <div style={{width:14,height:14,borderRadius:"50%",border:`2px solid ${sel?t.accent:t.border}`,background:sel?t.accent:"transparent",flexShrink:0}}/>
                      <div style={{flex:1}}>
                        <div style={{color:t.text,fontSize:"0.78rem",fontWeight:sel?700:500}}>{l} <span style={{color:t.textTertiary,fontWeight:400,fontSize:"0.68rem"}}>×{v}</span></div>
                        <div style={{color:t.textTertiary,fontSize:"0.62rem",marginTop:"0.06rem"}}>{desc}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div>
              {(()=>{
                const pkg=parseFloat(local.proteinPerKg)||2.5;
                const af=local.activityFactor||"1.9";
                const rating=proteinRating(pkg,af);
                const r=PROTEIN_RANGES[af]||PROTEIN_RANGES["1.9"];
                const zones=[
                  {from:0,    to:r.min,  color:"#ef4444"},
                  {from:r.min,to:r.good, color:"#f59e0b"},
                  {from:r.good,to:r.opt, color:"#3b82f6"},
                  {from:r.opt,to:PROT_MAX_SLIDER, color:"#22c55e"},
                ];
                const markerPct=Math.min(100,(pkg/PROT_MAX_SLIDER)*100);
                return (
                  <div>
                    <div style={{color:t.textTertiary,fontSize:"0.63rem",fontWeight:600,letterSpacing:"0.06em",textTransform:"uppercase",marginBottom:"0.28rem"}}>Proteína por kg de peso</div>
                    <div style={{display:"flex",alignItems:"center",gap:"0.6rem",marginBottom:"0.5rem"}}>
                      <input type="range" min="0.8" max="3.5" step="0.1"
                        value={pkg}
                        onChange={e=>setLocal(p=>({...p,proteinPerKg:e.target.value}))}
                        style={{flex:1,accentColor:rating.color}}/>
                      <span style={{color:rating.color,fontWeight:800,fontSize:"1rem",minWidth:"36px",textAlign:"right"}}>{pkg.toFixed(1)}</span>
                      <span style={{color:t.textTertiary,fontSize:"0.7rem"}}>g/kg</span>
                    </div>
                    {/* Barra de zonas */}
                    <div style={{position:"relative",height:"10px",borderRadius:"6px",overflow:"hidden",display:"flex",marginBottom:"0.35rem"}}>
                      {zones.map((z,i)=>(
                        <div key={i} style={{flex:(z.to-z.from)/PROT_MAX_SLIDER,background:z.color,opacity:0.35}}/>
                      ))}
                      {/* Marcador */}
                      <div style={{position:"absolute",left:`${markerPct}%`,top:0,bottom:0,width:"3px",background:rating.color,borderRadius:"2px",transform:"translateX(-50%)",boxShadow:`0 0 4px ${rating.color}`}}/>
                    </div>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:"0.4rem"}}>
                      <span style={{color:t.textTertiary,fontSize:"0.55rem"}}>{r.min}g mín</span>
                      <span style={{color:t.textTertiary,fontSize:"0.55rem"}}>{r.good}g bueno</span>
                      <span style={{color:t.textTertiary,fontSize:"0.55rem"}}>{r.opt}g óptimo</span>
                    </div>
                    <div style={{background:rating.color+"18",border:`1px solid ${rating.color}40`,borderRadius:"8px",padding:"0.3rem 0.6rem",display:"inline-flex",alignItems:"center",gap:"0.4rem"}}>
                      <span style={{color:rating.color,fontWeight:700,fontSize:"0.75rem"}}>{rating.label}</span>
                      <span style={{color:t.textTertiary,fontSize:"0.65rem"}}>= {Math.round((parseFloat(local.weight)||69)*pkg)}g proteína/día</span>
                    </div>
                  </div>
                );
              })()}
            </div>
            <div>
              <div style={{color:t.textTertiary,fontSize:"0.63rem",fontWeight:600,letterSpacing:"0.06em",textTransform:"uppercase",marginBottom:"0.28rem"}}>Velocidad</div>
              <div style={{display:"flex",gap:"0.4rem"}}>
                {[["normal","Sostenido"],["fast","Rápido"]].map(([v,l])=>(
                  <button key={v} onClick={()=>setLocal(p=>({...p,pace:v}))} style={{flex:1,padding:"0.5rem",borderRadius:"10px",border:`1px solid ${t.border}`,cursor:"pointer",fontSize:"0.78rem",fontWeight:600,background:local.pace===v?t.accent:t.surfaceHover,color:local.pace===v?t.bg:t.textSecondary}}>{l}</button>
                ))}
              </div>
            </div>
            <div style={{background:t.surfaceHover,borderRadius:"12px",padding:"0.85rem",marginTop:"0.25rem"}}>
              <div style={{color:t.textTertiary,fontSize:"0.6rem",fontWeight:600,letterSpacing:"0.06em",textTransform:"uppercase",marginBottom:"0.5rem"}}>Preview de macros</div>
              {[{l:"Calorías",v:`${preview.cal} kcal`,c:t.text},{l:"Proteína",v:`${preview.protein}g`,c:t.blue},{l:"Carbos",v:`${preview.carbs}g`,c:t.gold},{l:"Grasas",v:`${preview.fat}g`,c:t.red}].map(s=>(
                <div key={s.l} style={{display:"flex",justifyContent:"space-between",marginBottom:"0.22rem"}}>
                  <span style={{color:t.textTertiary,fontSize:"0.72rem"}}>{s.l}</span>
                  <span style={{color:s.c,fontWeight:700,fontSize:"0.75rem"}}>{s.v}</span>
                </div>
              ))}
            </div>
            <button onClick={()=>{setGoal(local);onClose();}} style={{background:t.accent,color:t.bg,border:"none",borderRadius:"12px",padding:"0.82rem",fontSize:"0.88rem",fontWeight:700,cursor:"pointer",marginTop:"0.25rem"}}>{tr("settings_save_goal")}</button>
          </div>
        )}

        {section==="usuarios"&&user?.isAdmin&&(
          <div style={{display:"flex",flexDirection:"column",gap:"0.65rem"}}>
            <div style={{color:t.textTertiary,fontSize:"0.7rem",lineHeight:1.5}}>
              Crea el perfil de tu amigo desde aquí. Él solo tendrá que entrar con su tile y completar país + datos físicos la primera vez.
            </div>

            {/* Lista de usuarios existentes */}
            {Object.values(users||{}).map(u=>(
              <div key={u.id} style={{display:"flex",alignItems:"center",gap:"0.65rem",padding:"0.7rem 0.9rem",borderRadius:"12px",background:t.surfaceHover,border:`1px solid ${u.id===userId?t.green+"60":t.border}`}}>
                <div style={{width:36,height:36,borderRadius:"50%",background:u.isAdmin?t.green:t.blue,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  <span style={{color:"#fff",fontWeight:700,fontSize:"0.95rem"}}>{u.name[0].toUpperCase()}</span>
                </div>
                <div style={{flex:1}}>
                  <div style={{color:t.text,fontSize:"0.82rem",fontWeight:600}}>
                    {u.name} {u.id===userId&&<span style={{color:t.green,fontSize:"0.65rem"}}>(tú)</span>}
                  </div>
                  <div style={{color:t.textTertiary,fontSize:"0.63rem",marginTop:"0.05rem"}}>
                    {u.isAdmin?"Admin":"Usuario"} · {u.country&&COUNTRY_PRESETS[u.country]?COUNTRY_PRESETS[u.country].label:"Sin país aún"} · PIN {"•".repeat(u.pin?.length||4)}
                  </div>
                  {!u.onboardingDone&&<div style={{color:t.gold,fontSize:"0.6rem",marginTop:"0.08rem"}}>⏳ Completará su perfil al entrar</div>}
                </div>
                {!u.isAdmin&&(
                  <button onClick={()=>{const nu={...users};delete nu[u.id];if(saveUsers)saveUsers(nu);}}
                    style={{background:t.redBg,border:`1px solid ${t.red}40`,borderRadius:"8px",padding:"0.28rem 0.6rem",color:t.red,fontSize:"0.65rem",fontWeight:700,cursor:"pointer"}}>
                    Eliminar
                  </button>
                )}
              </div>
            ))}

            {/* Formulario añadir usuario */}
            {!showAddUser?(
              <button onClick={()=>setShowAddUser(true)}
                style={{background:t.greenBg,color:t.green,border:`1px solid ${t.green}40`,borderRadius:"12px",padding:"0.75rem",fontSize:"0.82rem",fontWeight:700,cursor:"pointer"}}>
                + Añadir usuario
              </button>
            ):(
              <div style={{background:t.surfaceHover,border:`1px solid ${t.border}`,borderRadius:"14px",padding:"1rem",display:"flex",flexDirection:"column",gap:"0.6rem"}}>
                <div style={{color:t.text,fontSize:"0.82rem",fontWeight:700,marginBottom:"0.1rem"}}>Nuevo perfil</div>

                {/* Nombre */}
                <div>
                  <div style={{color:t.textTertiary,fontSize:"0.62rem",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:"0.25rem"}}>Nombre</div>
                  <Inp value={newName} onChange={e=>setNewName(e.target.value)} placeholder="Nombre del amigo" t={t} style={{width:"100%"}}/>
                </div>

                {/* PIN */}
                <div>
                  <div style={{color:t.textTertiary,fontSize:"0.62rem",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:"0.25rem"}}>PIN de 4 dígitos</div>
                  <Inp value={newPin} onChange={e=>{const v=e.target.value.replace(/\D/g,"").slice(0,4);setNewPin(v);}} placeholder="• • • •" type="password" t={t} style={{width:"100%",letterSpacing:"0.25em",fontSize:"1.1rem"}}/>
                </div>

                {/* Confirmar PIN */}
                <div>
                  <div style={{color:t.textTertiary,fontSize:"0.62rem",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:"0.25rem"}}>Confirmar PIN</div>
                  <Inp value={newPin2} onChange={e=>{const v=e.target.value.replace(/\D/g,"").slice(0,4);setNewPin2(v);}} placeholder="• • • •" type="password" t={t} style={{width:"100%",letterSpacing:"0.25em",fontSize:"1.1rem"}}/>
                </div>

                {addErr&&<div style={{color:t.red,fontSize:"0.7rem",fontWeight:600}}>{addErr}</div>}

                <div style={{color:t.textTertiary,fontSize:"0.63rem",lineHeight:1.5,background:t.accentMuted,borderRadius:"8px",padding:"0.45rem 0.6rem"}}>
                  La primera vez que entre, elegirá su cocina base y sus datos físicos. Tú no tienes que hacer nada más.
                </div>

                <div style={{display:"flex",gap:"0.4rem",marginTop:"0.1rem"}}>
                  <button onClick={resetAddForm} style={{flex:1,background:t.surfaceHover,color:t.textSecondary,border:`1px solid ${t.border}`,borderRadius:"10px",padding:"0.6rem",fontSize:"0.78rem",fontWeight:600,cursor:"pointer"}}>Cancelar</button>
                  <button onClick={handleAddUser} style={{flex:2,background:t.green,color:"#000",border:"none",borderRadius:"10px",padding:"0.6rem",fontSize:"0.82rem",fontWeight:700,cursor:"pointer"}}>Crear perfil</button>
                </div>
              </div>
            )}
          </div>
        )}
        </div>{/* fin contenido scrollable */}
      </div>
    </div>
  );
}

// ─── STRENGTH CHART ──────────────────────────────────────────────────────────
function StrengthChart({workouts, t}) {
  // Build history: { exName: [{day, maxWeight, totalSets}] }
  const history = {};
  Object.entries(workouts).sort(([a],[b])=>a.localeCompare(b)).forEach(([day,wd])=>{
    (wd.exercises||[]).forEach(ex=>{
      const weights=(ex.sets||[]).map(s=>parseFloat(s.weight)||0).filter(w=>w>0);
      if(!weights.length)return;
      const maxW=Math.max(...weights);
      const sets=(ex.sets||[]).length;
      if(!history[ex.name])history[ex.name]=[];
      const existing=history[ex.name].find(e=>e.day===day);
      if(existing){if(maxW>existing.maxWeight){existing.maxWeight=maxW;existing.totalSets=sets;}}
      else history[ex.name].push({day,maxWeight:maxW,totalSets:sets});
    });
  });

  const exercises=Object.keys(history).filter(n=>history[n].length>=1).sort();
  const [selEx,setSelEx]=useState(null);
  const [selEx2,setSelEx2]=useState(null); // comparison

  // Auto-select KEY exercises if available, else first
  const KEY_EX_ORDER=["Press banca","Press inclinado","Jalón al pecho","Sentadilla","Peso muerto","Curl araña","Press francés","Hip thrust","Dominadas"];
  useEffect(()=>{
    if(exercises.length>0&&!selEx){
      const def=KEY_EX_ORDER.find(n=>exercises.includes(n))||exercises[0];
      setSelEx(def);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[exercises.length]);

  const drawCurve=(data,color,W,H,minV,maxV)=>{
    if(data.length<2)return null;
    const range=maxV-minV||1;
    const coords=data.map((d,i)=>({
      x:(i/(data.length-1))*W,
      y:H-((d.maxWeight-minV)/range)*H*0.85-H*0.07,
      ...d
    }));
    let path=`M ${coords[0].x},${coords[0].y}`;
    for(let i=1;i<coords.length;i++){
      const p=coords[i-1],c=coords[i];
      const cpx=(p.x+c.x)/2;
      path+=` C ${cpx},${p.y} ${cpx},${c.y} ${c.x},${c.y}`;
    }
    return {path,coords};
  };

  const data1=selEx?history[selEx]||[]:[];
  const data2=selEx2?history[selEx2]||[]:[];
  const allVals=[...data1.map(d=>d.maxWeight),...data2.map(d=>d.maxWeight)].filter(Boolean);
  const minV=allVals.length?Math.floor(Math.min(...allVals)*0.95):0;
  const maxV=allVals.length?Math.ceil(Math.max(...allVals)*1.05):100;
  const W=500,H=110;
  const curve1=drawCurve(data1,t.blue,W,H,minV,maxV);
  const curve2=selEx2&&data2.length>=2?drawCurve(data2,t.gold,W,H,minV,maxV):null;

  const pr1=data1.length?Math.max(...data1.map(d=>d.maxWeight)):0;
  const firstW=data1.length?data1[0].maxWeight:0;
  const gain=pr1&&firstW?(pr1-firstW).toFixed(1):null;

  if(!exercises.length)return(
    <div style={{color:t.textTertiary,fontSize:"0.78rem",textAlign:"center",padding:"2.5rem",lineHeight:1.6}}>
      Registra sesiones de Gym con pesos para ver la evolución de fuerza
    </div>
  );

  return(
    <div style={{display:"flex",flexDirection:"column",gap:"0.85rem"}}>
      {/* Controls */}
      <div style={{display:"flex",gap:"0.65rem",alignItems:"center",flexWrap:"wrap"}}>
        <div style={{display:"flex",alignItems:"center",gap:"0.4rem"}}>
          <div style={{width:10,height:10,borderRadius:"50%",background:t.blue,flexShrink:0}}/>
          <select value={selEx||""} onChange={e=>setSelEx(e.target.value)}
            style={{background:t.surfaceHover,border:`1px solid ${t.border}`,borderRadius:"8px",color:t.text,padding:"0.38rem 0.6rem",fontSize:"0.78rem",outline:"none",fontFamily:"inherit",minWidth:160}}>
            {exercises.map(n=><option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:"0.4rem"}}>
          <div style={{width:10,height:10,borderRadius:"50%",background:t.gold,flexShrink:0}}/>
          <select value={selEx2||""} onChange={e=>setSelEx2(e.target.value||null)}
            style={{background:t.surfaceHover,border:`1px solid ${t.border}`,borderRadius:"8px",color:t.text,padding:"0.38rem 0.6rem",fontSize:"0.78rem",outline:"none",fontFamily:"inherit",minWidth:160}}>
            <option value="">— Comparar con —</option>
            {exercises.filter(n=>n!==selEx).map(n=><option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        {gain&&<div style={{marginLeft:"auto",background:parseFloat(gain)>0?t.greenBg:t.redBg,borderRadius:"8px",padding:"0.3rem 0.8rem"}}>
          <span style={{color:parseFloat(gain)>0?t.green:t.red,fontSize:"0.75rem",fontWeight:700}}>{parseFloat(gain)>0?"+":""}{gain}kg ganados</span>
        </div>}
      </div>

      {/* Summaries */}
      {(selEx||selEx2)&&(
        <div style={{display:"flex",gap:"1rem",flexWrap:"wrap"}}>
          {selEx&&data1.length>0&&[
            {l:"PR",v:`${pr1}kg`,c:t.gold},
            {l:"Sesiones",v:data1.length,c:t.blue},
            {l:"Último",v:`${data1[data1.length-1].maxWeight}kg`,c:t.text},
          ].map(s=>(
            <div key={s.l} style={{background:t.surfaceHover,borderRadius:8,padding:"0.45rem 0.85rem"}}>
              <div style={{color:t.textTertiary,fontSize:"0.55rem",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.07em"}}>{s.l}</div>
              <div style={{color:s.c,fontSize:"1.05rem",fontWeight:800,lineHeight:1.2,marginTop:"0.1rem"}}>{s.v}</div>
            </div>
          ))}
          {selEx2&&data2.length>0&&(
            <div style={{background:t.goldBg,borderRadius:8,padding:"0.45rem 0.85rem"}}>
              <div style={{color:t.textTertiary,fontSize:"0.55rem",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.07em"}}>{selEx2} PR</div>
              <div style={{color:t.gold,fontSize:"1.05rem",fontWeight:800,lineHeight:1.2,marginTop:"0.1rem"}}>{Math.max(...data2.map(d=>d.maxWeight))}kg</div>
            </div>
          )}
        </div>
      )}

      {/* SVG Chart */}
      {data1.length>=2?(
        <div style={{position:"relative"}}>
          <svg viewBox={`0 0 ${W} ${H+20}`} style={{width:"100%",height:H+20}} preserveAspectRatio="none">
            <defs>
              <linearGradient id="strGrad1" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={t.blue} stopOpacity="0.25"/>
                <stop offset="100%" stopColor={t.blue} stopOpacity="0.02"/>
              </linearGradient>
              {selEx2&&<linearGradient id="strGrad2" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={t.gold} stopOpacity="0.2"/>
                <stop offset="100%" stopColor={t.gold} stopOpacity="0.02"/>
              </linearGradient>}
            </defs>
            {/* Gridlines */}
            {[0.25,0.5,0.75,1].map(f=>{
              const y=H-(f*H*0.85)-H*0.07;
              const val=Math.round(minV+(maxV-minV)*f);
              return <g key={f}>
                <line x1={0} y1={y} x2={W} y2={y} stroke={t.border} strokeWidth="0.5" opacity="0.5"/>
                <text x={4} y={y-2} fill={t.textTertiary} fontSize="9" fontFamily="inherit">{val}kg</text>
              </g>;
            })}
            {/* Curve 1 fill */}
            {curve1&&<path d={curve1.path+` L ${curve1.coords[curve1.coords.length-1].x},${H+5} L ${curve1.coords[0].x},${H+5} Z`} fill="url(#strGrad1)"/>}
            {/* Curve 2 fill */}
            {curve2&&<path d={curve2.path+` L ${curve2.coords[curve2.coords.length-1].x},${H+5} L ${curve2.coords[0].x},${H+5} Z`} fill="url(#strGrad2)"/>}
            {/* Curve 1 line */}
            {curve1&&<path d={curve1.path} fill="none" stroke={t.blue} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>}
            {/* Curve 2 line */}
            {curve2&&<path d={curve2.path} fill="none" stroke={t.gold} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="5,3"/>}
            {/* Dots + PR highlight */}
            {curve1&&curve1.coords.map((c,i)=>{
              const isPR=c.maxWeight===pr1;
              return <g key={i}>
                <circle cx={c.x} cy={c.y} r={isPR?5:3} fill={isPR?t.gold:t.blue} stroke={isPR?t.gold+"88":"none"} strokeWidth={isPR?3:0}/>
                {isPR&&<text x={c.x} y={c.y-8} fill={t.gold} fontSize="9" textAnchor="middle" fontWeight="700" fontFamily="inherit">PR</text>}
              </g>;
            })}
            {curve2&&curve2.coords.map((c,i)=>(
              <circle key={i} cx={c.x} cy={c.y} r={3} fill={t.gold}/>
            ))}
            {/* X axis date labels — show only first/last/some */}
            {curve1&&curve1.coords.filter((_,i,arr)=>i===0||i===arr.length-1||i===Math.floor(arr.length/2)).map((c,i)=>(
              <text key={i} x={c.x} y={H+17} fill={t.textTertiary} fontSize="8.5" textAnchor="middle" fontFamily="inherit">
                {c.day.slice(5).replace("-","/")}
              </text>
            ))}
          </svg>
        </div>
      ):(
        <div style={{color:t.textTertiary,fontSize:"0.72rem",textAlign:"center",padding:"1.5rem",background:t.surfaceHover,borderRadius:10}}>
          {data1.length===1?`1 sesión registrada — necesitas 2+ para ver la gráfica`:"Selecciona un ejercicio"}
        </div>
      )}
    </div>
  );
}

// ─── DESKTOP DASHBOARD ────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
function BarChart({data, color, t, height=80, maxVal}) {
  const max = maxVal || Math.max(...data.map(d=>d.value), 1);
  return (
    <div style={{display:"flex",alignItems:"flex-end",gap:"3px",height:height}}>
      {data.map((d,i)=>(
        <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:"2px"}}>
          <div style={{width:"100%",background:color,borderRadius:"4px 4px 0 0",height:`${Math.round((d.value/max)*height*0.85)}px`,minHeight:d.value>0?2:0,transition:"height 0.3s"}}/>
          <div style={{color:t.textTertiary,fontSize:"0.5rem",fontWeight:600,textAlign:"center",lineHeight:1}}>{d.label}</div>
        </div>
      ))}
    </div>
  );
}

function LineChart({data, color, t, height=80, showLabels=false}) {
  const filtered = data.filter(d=>d.value>0);
  if (filtered.length === 0) return <div style={{height,display:"flex",alignItems:"center",justifyContent:"center",color:t.textTertiary,fontSize:"0.7rem"}}>Sin datos</div>;
  if (filtered.length === 1) return <div style={{height,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:"0.2rem"}}><div style={{color,fontSize:"1.5rem",fontWeight:800}}>{filtered[0].value}</div><div style={{color:t.textTertiary,fontSize:"0.6rem"}}>{filtered[0].label}</div></div>;
  const useData = showLabels ? data : filtered;
  const vals = useData.map(d=>d.value);
  const min = 0, max = Math.max(...vals, 1);
  const range = max - min;
  const W = 400, labelH = showLabels ? 14 : 0, H = height - labelH;
  const gradId = "lcg_" + color.replace(/[^a-z0-9]/gi,"");
  const coords = useData.map((d,i)=>({
    x: useData.length>1 ? (i/(useData.length-1))*W : W/2,
    y: H - ((d.value-min)/range)*H*0.88 + H*0.05,
    label: d.label, value: d.value
  }));
  let path = `M ${coords[0].x},${coords[0].y}`;
  for(let i=1;i<coords.length;i++){
    const p=coords[i-1],c=coords[i],cpx=(p.x+c.x)/2;
    path+=` C ${cpx},${p.y} ${cpx},${c.y} ${c.x},${c.y}`;
  }
  const fillPath = path+` L ${coords[coords.length-1].x},${H} L ${coords[0].x},${H} Z`;
  return (
    <svg viewBox={`0 0 ${W} ${height}`} style={{width:"100%",height}} preserveAspectRatio="none">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25"/>
          <stop offset="100%" stopColor={color} stopOpacity="0.02"/>
        </linearGradient>
      </defs>
      <path d={fillPath} fill={`url(#${gradId})`}/>
      <path d={path} fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
      {coords.map((c,i)=>(
        <g key={i}>
          {c.value>0&&<circle cx={c.x} cy={c.y} r="3" fill={color}/>}
          {showLabels&&<text x={c.x} y={height-2} textAnchor="middle" fontSize="7" fill={t.textTertiary} fontFamily="sans-serif">{c.label}</text>}
        </g>
      ))}
    </svg>
  );
}

function HeatMap({data, t}) {
  // data: object { "2025-01-01": 0..1 }
  const today_d = new Date();
  const days = Array.from({length:56},(_,i)=>{ const d=new Date(today_d); d.setDate(d.getDate()-55+i); return d.toISOString().split("T")[0]; });
  const weeks = [];
  for(let i=0;i<days.length;i+=7) weeks.push(days.slice(i,i+7));
  return (
    <div style={{display:"flex",gap:"3px"}}>
      {weeks.map((week,wi)=>(
        <div key={wi} style={{display:"flex",flexDirection:"column",gap:"3px"}}>
          {week.map(day=>{
            const v = data[day]||0;
            const bg = v===0?t.border:v<0.4?"rgba(34,197,94,0.2)":v<0.7?"rgba(34,197,94,0.5)":"rgba(34,197,94,0.9)";
            return <div key={day} title={day} style={{width:11,height:11,borderRadius:2,background:bg}}/>;
          })}
        </div>
      ))}
    </div>
  );
}

function PeriodPicker({value, onChange, t, opts=["1d","7d","15d","30d","3m","6m","12m"]}) {
  return (
    <div style={{display:"flex",gap:"0.18rem"}}>
      {opts.map(p=>(
        <button key={p} onClick={()=>onChange(p)} style={{padding:"0.15rem 0.38rem",borderRadius:"5px",border:"none",cursor:"pointer",fontSize:"0.58rem",fontWeight:700,background:value===p?t.accent:t.surfaceHover,color:value===p?t.bg:t.textTertiary}}>{p}</button>
      ))}
    </div>
  );
}
function pDays(p){return{"1d":1,"7d":7,"15d":15,"30d":30,"3m":90,"6m":180,"12m":365}[p]||30;}

function DesktopDashboard({habits, setHabits, workouts, setWorkouts, nutrition, bodyStats, setBodyStats, goal, habitConfig, sleep, t}) {
  const macros = calcMacros(goal);
  const tk = today();
  const[expandedSection,setExpandedSection]=useState(null); // "prs" | "ma" | "dayEdit"
  const[viewDay,setViewDay]=useState(tk);
  const toggleSection=s=>setExpandedSection(p=>p===s?null:s);
  const[editField,setEditField]=useState(null); // "weight"|"session"|"habits"
  const[editWeightVal,setEditWeightVal]=useState("");
  const[editSessionVal,setEditSessionVal]=useState({type:"",duration:""});
  const openEdit=(field)=>{
    if(field==="weight") setEditWeightVal(bodyStats[tk]?.weight||"");
    if(field==="session") setEditSessionVal({type:workouts[tk]?.sessionType||"",duration:workouts[tk]?.duration||""});
    setEditField(p=>p===field?null:field);
  };
  const saveWeight=()=>{setBodyStats(p=>({...p,[tk]:{...(p[tk]||{}),weight:editWeightVal}}));setEditField(null);};
  const saveSession=()=>{
    if(!editSessionVal.type)return;
    setWorkouts(p=>({...p,[tk]:{...(p[tk]||{}),sessionType:editSessionVal.type,duration:editSessionVal.duration}}));
    setEditField(null);
  };
  const toggleHabit=(id)=>setHabits(p=>({...p,[tk]:{...(p[tk]||{}),[id]:!p[tk]?.[id]}}));

  // Last 8 weeks of data (habits/gym — fixed 8 weeks)
  const weeks = Array.from({length:8},(_,i)=>{
    const days = Array.from({length:7},(_,j)=>{ const d=new Date(); d.setDate(d.getDate()-(7*(7-i))+(j)); return d.toISOString().split("T")[0]; });
    const label = `S${i+1}`;
    const oblH = DEFAULT_HABITS.filter(h=>{ const c=habitConfig[h.id]; return c==="obl"||(c===undefined&&h.obl); });
    const habPct = days.reduce((acc,day)=>{ const h=habits[day]||{}; const done=oblH.filter(x=>h[x.id]).length; return acc+(oblH.length?done/oblH.length:0); },0)/7;
    const daysWithCal2=days.filter(day=>Object.values((nutrition[day]||{}).items||{}).length>0);
    const avgCal = daysWithCal2.length>0?daysWithCal2.reduce((acc,day)=>{ const items=Object.values((nutrition[day]||{}).items||{}); return acc+items.reduce((a,i)=>a+i.cal,0); },0)/daysWithCal2.length:0;
    const gymDays = days.filter(day=>workouts[day]?.sessionType).length;
    const totalTrainingMins = days.reduce((acc,day)=>acc+(parseFloat(workouts[day]?.duration)||0),0);
    return { label, habPct: Math.round(habPct*100), avgCal: Math.round(avgCal), gymDays, totalTrainingMins: Math.round(totalTrainingMins) };
  });

  // Period states for each chart
  const[weightP,setWeightP]=useState("30d");
  const[sleepP,setSleepP]=useState("30d");
  const[calP,setCalP]=useState("30d");
  const[volP,setVolP]=useState("7d");
  const[habP,setHabP]=useState("7d");
  const[trainP,setTrainP]=useState("7d");

  // Hábitos dynamic — grouped by week
  const habDays=pDays(habP);
  const habNWeeks=Math.ceil(habDays/7);
  const oblH_chart=DEFAULT_HABITS.filter(h=>{const c=habitConfig[h.id];return c==="obl"||(c===undefined&&h.obl);});
  const habWeeks=Array.from({length:habNWeeks},(_,i)=>{
    const days=Array.from({length:7},(_,j)=>{const d=new Date();d.setDate(d.getDate()-(habDays-1)+(i*7)+j);return d.toISOString().split("T")[0];});
    const pct=days.reduce((acc,day)=>{const h=habits[day]||{};const done=oblH_chart.filter(x=>h[x.id]).length;return acc+(oblH_chart.length?done/oblH_chart.length:0);},0)/7;
    return{label:`S${i+1}`,value:Math.round(pct*100)};
  });

  // Training minutes dynamic — grouped by week
  const trainDays=pDays(trainP);
  const trainNWeeks=Math.ceil(trainDays/7);
  const trainWeeks=Array.from({length:trainNWeeks},(_,i)=>{
    const days=Array.from({length:7},(_,j)=>{const d=new Date();d.setDate(d.getDate()-(trainDays-1)+(i*7)+j);return d.toISOString().split("T")[0];});
    const mins=days.reduce((acc,day)=>acc+(parseFloat(workouts[day]?.duration)||0),0);
    return{label:`S${i+1}`,value:Math.round(mins)};
  });

  // Calories dynamic — per-day bars grouped by week
  const calDays=pDays(calP);
  const calNWeeks=Math.ceil(calDays/7);
  const calWeeks=Array.from({length:calNWeeks},(_,i)=>{
    const days=Array.from({length:7},(_,j)=>{const d=new Date();d.setDate(d.getDate()-(calDays-1)+(i*7)+j);return d.toISOString().split("T")[0];});
    const daysWithCal=days.filter(day=>Object.values((nutrition[day]||{}).items||{}).length>0);
    const avgCal=daysWithCal.length>0?daysWithCal.reduce((acc,day)=>{const items=Object.values((nutrition[day]||{}).items||{});return acc+items.reduce((a,it)=>a+it.cal,0);},0)/daysWithCal.length:0;
    return{label:`S${i+1}`,value:Math.round(avgCal)};
  });

  // Weight trend
  const weightDays=pDays(weightP);
  const weightData = Array.from({length:weightDays},(_,i)=>{
    const d=new Date(); d.setDate(d.getDate()-(weightDays-1)+i);
    const key=d.toISOString().split("T")[0];
    const step=weightDays<=30?7:weightDays<=90?14:30;
    return { label: i%step===0?`${d.getDate()}/${d.getMonth()+1}`:"", value: parseFloat(bodyStats[key]?.weight)||0 };
  }).filter(d=>d.value>0);

  // Sleep data
  const sleepDays=pDays(sleepP);
  const sleepData30 = Array.from({length:sleepDays},(_,i)=>{
    const d=new Date(); d.setDate(d.getDate()-(sleepDays-1)+i);
    const key=d.toISOString().split("T")[0];
    const stats=calcSleepStats((sleep[key]||{}).segments);
    return {label:`${d.getDate()}/${d.getMonth()+1}`, value: stats?parseFloat(stats.totalH):0, cycles: stats?.cycles||0, score: stats?.score||0, key};
  });
  const sleepWithData=sleepData30.filter(d=>d.value>0);
  const avgSleepH=sleepWithData.length>0?(sleepWithData.reduce((a,d)=>a+d.value,0)/sleepWithData.length).toFixed(1):null;
  const avgCycles=sleepWithData.length>0?Math.round(sleepWithData.reduce((a,d)=>a+d.cycles,0)/sleepWithData.length):null;

  // Today
  const todayItems = Object.values((nutrition[tk]||{}).items||{});
  const todayCal = Math.round(todayItems.reduce((a,i)=>a+i.cal,0));
  const todayP = Math.round(todayItems.reduce((a,i)=>a+i.p,0));
  const oblH = DEFAULT_HABITS.filter(h=>{ const c=habitConfig[h.id]; return c==="obl"||(c===undefined&&h.obl); });
  const todayH = habits[tk]||{};
  const oblDone = oblH.filter(h=>todayH[h.id]).length;
  const todayW = workouts[tk]||{};

  // Habit heatmap
  const heatData = {};
  Object.keys(habits).forEach(day => {
    const h = habits[day]||{};
    const done = oblH.filter(x=>h[x.id]).length;
    heatData[day] = oblH.length ? done/oblH.length : 0;
  });

  // Muscle volume — dynamic period
  const volDays=pDays(volP);
  const weekVol = {};
  Array.from({length:volDays},(_,i)=>{const d=new Date();d.setDate(d.getDate()-(volDays-1)+i);return d.toISOString().split("T")[0];}).forEach(day=>{
    const w=workouts[day];
    if(!w?.exercises) return;
    w.exercises.forEach(ex=>{ weekVol[ex.muscle]=(weekVol[ex.muscle]||0)+(ex.sets||[]).length; });
  });

  // Martial arts hours (last 30 days from session durations)
  const maLast30 = Array.from({length:30},(_,i)=>{const d=new Date();d.setDate(d.getDate()-29+i);return d.toISOString().split("T")[0];});
  const maTypes = ["Muay Thai","BJJ","Lucha","MMA"];
  let totalMAMins=0;
  const maByType={};
  maLast30.forEach(day=>{
    const w=workouts[day];
    if(w&&maTypes.includes(w.sessionType)&&w.duration){
      const mins=parseFloat(w.duration)||0;
      totalMAMins+=mins;
      maByType[w.sessionType]=(maByType[w.sessionType]||0)+mins;
    }
  });
  const maHoursTotal=(totalMAMins/60).toFixed(1);

  // PRs for specific exercises
  const KEY_EX=["Jalón al pecho","Press banca","Press inclinado","Sentadilla","Curl araña","Press francés","Curl martillo","Peso muerto","Hip thrust","Dominadas"];
  const allPRs={};
  Object.values(workouts).forEach(wd=>(wd.exercises||[]).forEach(ex=>(ex.sets||[]).forEach(set=>{
    const w2=parseFloat(set.weight)||0,r=parseInt(set.reps)||0;
    if(w2===0&&r===0)return;
    const key=ex.name;
    if(!allPRs[key]||w2>allPRs[key].weight||(w2===allPRs[key].weight&&r>allPRs[key].reps))allPRs[key]={weight:w2,reps:r};
  })));

  // Strength Δ% — last 5 gym sessions vs previous 5
  const gymSessions=Object.entries(workouts).filter(([,w])=>w?.sessionType==="Gym"&&(w?.exercises||[]).length>0).sort(([a],[b])=>a>b?-1:1);
  const calcVol=sess=>sess.reduce((acc,[,w])=>acc+(w.exercises||[]).reduce((a,ex)=>a+(ex.sets||[]).reduce((s,set)=>s+(parseFloat(set.weight)||0)*(parseInt(set.reps)||0),0),0),0);
  const last5Vol=calcVol(gymSessions.slice(0,5));
  const prev5Vol=calcVol(gymSessions.slice(5,10));
  const strengthDelta=prev5Vol>0?Math.round((last5Vol-prev5Vol)/prev5Vol*100):null;

  // Dynamic averages — period picker
  const[avgP,setAvgP]=useState("30d");
  const avgPDays=pDays(avgP);
  const avgPeriodDates=Array.from({length:avgPDays},(_,i)=>{const d=new Date();d.setDate(d.getDate()-(avgPDays-1)+i);return d.toISOString().split("T")[0];});
  const datesWithCal=avgPeriodDates.filter(d=>Object.values((nutrition[d]||{}).items||{}).length>0);
  const avgCalIngested=datesWithCal.length>0?Math.round(datesWithCal.reduce((acc,d)=>acc+Object.values((nutrition[d]||{}).items||{}).reduce((a,i)=>a+i.cal,0),0)/datesWithCal.length):null;
  const gymSetWeights=[];
  avgPeriodDates.forEach(d=>{const w=workouts[d];if(w?.sessionType==="Gym")(w.exercises||[]).forEach(ex=>(ex.sets||[]).forEach(s=>{if(parseFloat(s.weight)>0)gymSetWeights.push(parseFloat(s.weight));}));});
  // eslint-disable-next-line no-unused-vars
  const avgWeightLifted=gymSetWeights.length>0?Math.round(gymSetWeights.reduce((a,b)=>a+b,0)/gymSetWeights.length):null;
  const bwDates=avgPeriodDates.filter(d=>parseFloat(bodyStats[d]?.weight)>0);
  const avgBodyWeight=bwDates.length>0?(bwDates.reduce((acc,d)=>acc+parseFloat(bodyStats[d].weight),0)/bwDates.length).toFixed(1):null;

  // ── Composición corporal (mismo cálculo que StatsTab) ───────────────────────
  const avgProtIngested=datesWithCal.length>0?Math.round(datesWithCal.reduce((acc,d)=>acc+Object.values((nutrition[d]||{}).items||{}).reduce((a,i)=>a+i.p,0),0)/datesWithCal.length):null;
  const bwEntriesPeriod=avgPeriodDates.map(d=>({w:parseFloat(bodyStats[d]?.weight)})).filter(e=>!isNaN(e.w)&&e.w>0);
  const firstBWP=bwEntriesPeriod[0]?.w;
  const lastBWP=bwEntriesPeriod[bwEntriesPeriod.length-1]?.w;
  const actualDeltaKgD=(firstBWP&&lastBWP&&bwEntriesPeriod.length>=2)?+(lastBWP-firstBWP).toFixed(1):null;
  const calSurplusD=avgCalIngested?avgCalIngested-macros.tdee:null;
  const kgPerWeekD=calSurplusD!==null?+((calSurplusD*7/7)/7700).toFixed(2):null;
  const expectedKgD=calSurplusD!==null?+((calSurplusD*avgPDays/7)/7700).toFixed(1):null;
  const protQualityD=avgProtIngested?Math.min(1,avgProtIngested/macros.protein):0;
  const muscleRatioD=calSurplusD>0?(protQualityD>=0.8?0.75:protQualityD>=0.6?0.55:0.35):(protQualityD>=0.8?0.88:protQualityD>=0.6?0.70:0.50);
  const expectedMuscleD=expectedKgD!==null?+(expectedKgD*muscleRatioD).toFixed(1):null;
  const expectedFatD=expectedKgD!==null?+(expectedKgD*(1-muscleRatioD)).toFixed(1):null;

  // Viewed day workout
  const viewedW=workouts[viewDay];
  const last14days=Array.from({length:14},(_,i)=>{const d=new Date();d.setDate(d.getDate()-i);return d.toISOString().split("T")[0];});

  const SESSION_TYPES=["Gym","Muay Thai","BJJ","Lucha","MMA"];

  return (
    <div style={{padding:"2rem",display:"flex",flexDirection:"column",gap:"1.5rem",maxWidth:1200,margin:"0 auto"}}>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",flexWrap:"wrap",gap:"0.5rem"}}>
        <div>
          <div style={{color:t.text,fontSize:"1.6rem",fontWeight:800,letterSpacing:"-0.03em"}}>Dashboard completo</div>
          <div style={{color:t.textSecondary,fontSize:"0.8rem",marginTop:"0.2rem"}}>{new Date().toLocaleDateString("es-ES",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}</div>
        </div>
        <div style={{color:t.textTertiary,fontSize:"0.65rem"}}>Pulsa ✏️ en cualquier stat para editar</div>
      </div>

      {/* Top stats — editable */}
      <div style={{display:"flex",gap:"1rem",flexWrap:"wrap"}}>

        {/* Hábitos */}
        <div style={{background:t.surfaceHover,borderRadius:14,padding:"1rem",flex:1,minWidth:130}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"0.3rem"}}>
            <div style={{color:t.textTertiary,fontSize:"0.58rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase"}}>Hábitos hoy</div>
            <button onClick={()=>openEdit("habits")} style={{background:"none",border:"none",cursor:"pointer",color:editField==="habits"?t.accent:t.textTertiary,fontSize:"0.7rem",padding:"0 0.1rem"}}>✏️</button>
          </div>
          <div style={{color:oblDone===oblH.length?t.green:t.gold,fontSize:"1.6rem",fontWeight:800,lineHeight:1,letterSpacing:"-0.03em"}}>{oblDone}/{oblH.length}</div>
          <div style={{color:t.textTertiary,fontSize:"0.65rem",marginTop:"0.2rem"}}>{oblH.length>0?Math.round(oblDone/oblH.length*100):0}% completado</div>
        </div>

        {/* Calorías — read only */}
        <div style={{background:t.surfaceHover,borderRadius:14,padding:"1rem",flex:1,minWidth:130}}>
          <div style={{color:t.textTertiary,fontSize:"0.58rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:"0.3rem"}}>Calorías hoy</div>
          <div style={{color:todayCal>=macros.cal*0.9?t.green:t.gold,fontSize:"1.6rem",fontWeight:800,lineHeight:1,letterSpacing:"-0.03em"}}>{todayCal}</div>
          <div style={{color:t.textTertiary,fontSize:"0.65rem",marginTop:"0.2rem"}}>/ {macros.cal} kcal objetivo</div>
        </div>

        {/* Proteína — read only */}
        <div style={{background:t.surfaceHover,borderRadius:14,padding:"1rem",flex:1,minWidth:130}}>
          <div style={{color:t.textTertiary,fontSize:"0.58rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:"0.3rem"}}>Proteína hoy</div>
          <div style={{color:todayP>=macros.protein*0.9?t.green:t.blue,fontSize:"1.6rem",fontWeight:800,lineHeight:1,letterSpacing:"-0.03em"}}>{todayP}g</div>
          <div style={{color:t.textTertiary,fontSize:"0.65rem",marginTop:"0.2rem"}}>/ {macros.protein}g objetivo</div>
        </div>

        {/* Entreno hoy — editable */}
        <div style={{background:t.surfaceHover,borderRadius:14,padding:"1rem",flex:1,minWidth:130}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"0.3rem"}}>
            <div style={{color:t.textTertiary,fontSize:"0.58rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase"}}>Entreno hoy</div>
            <button onClick={()=>openEdit("session")} style={{background:"none",border:"none",cursor:"pointer",color:editField==="session"?t.accent:t.textTertiary,fontSize:"0.7rem",padding:"0 0.1rem"}}>✏️</button>
          </div>
          <div style={{color:todayW.sessionType?t.green:t.textTertiary,fontSize:"1.6rem",fontWeight:800,lineHeight:1,letterSpacing:"-0.03em"}}>{todayW.sessionType||"—"}</div>
          <div style={{color:t.textTertiary,fontSize:"0.65rem",marginTop:"0.2rem"}}>{todayW.duration?`${todayW.duration} min`:"Sin registro"}</div>
        </div>

        {/* Peso — editable */}
        <div style={{background:t.surfaceHover,borderRadius:14,padding:"1rem",flex:1,minWidth:130}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"0.3rem"}}>
            <div style={{color:t.textTertiary,fontSize:"0.58rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase"}}>Peso actual</div>
            <button onClick={()=>openEdit("weight")} style={{background:"none",border:"none",cursor:"pointer",color:editField==="weight"?t.accent:t.textTertiary,fontSize:"0.7rem",padding:"0 0.1rem"}}>✏️</button>
          </div>
          <div style={{color:t.blue,fontSize:"1.6rem",fontWeight:800,lineHeight:1,letterSpacing:"-0.03em"}}>{bodyStats[tk]?.weight?`${bodyStats[tk].weight}kg`:"—"}</div>
          <div style={{color:t.textTertiary,fontSize:"0.65rem",marginTop:"0.2rem"}}>Objetivo: {goal.goalWeight||75}kg</div>
        </div>
      </div>

      {/* Inline edit panels */}
      {editField==="weight"&&(
        <Card t={t} style={{padding:"1rem",border:`1px solid ${t.blue}40`}}>
          <div style={{color:t.textTertiary,fontSize:"0.6rem",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:"0.6rem"}}>✏️ Editar peso de hoy</div>
          <div style={{display:"flex",gap:"0.65rem",alignItems:"center"}}>
            <Inp value={editWeightVal} onChange={e=>setEditWeightVal(e.target.value)} placeholder="69.5" type="number" t={t} style={{width:100,fontSize:"1.1rem",fontWeight:700,textAlign:"center"}}/>
            <span style={{color:t.textSecondary,fontSize:"0.85rem"}}>kg</span>
            <button onClick={saveWeight} style={{background:t.green,color:"#fff",border:"none",borderRadius:"8px",padding:"0.42rem 1rem",fontSize:"0.78rem",fontWeight:700,cursor:"pointer"}}>Confirmar</button>
            <button onClick={()=>setEditField(null)} style={{background:t.surfaceHover,border:`1px solid ${t.border}`,borderRadius:"8px",padding:"0.42rem 0.7rem",color:t.textSecondary,fontSize:"0.72rem",cursor:"pointer"}}>Cancelar</button>
          </div>
        </Card>
      )}
      {editField==="session"&&(
        <Card t={t} style={{padding:"1rem",border:`1px solid ${t.green}40`}}>
          <div style={{color:t.textTertiary,fontSize:"0.6rem",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:"0.6rem"}}>✏️ Editar entreno de hoy</div>
          <div style={{display:"flex",gap:"0.65rem",alignItems:"center",flexWrap:"wrap"}}>
            <select value={editSessionVal.type} onChange={e=>setEditSessionVal(p=>({...p,type:e.target.value}))}
              style={{background:t.surfaceHover,border:`1px solid ${t.border}`,borderRadius:"8px",color:t.text,padding:"0.42rem 0.7rem",fontSize:"0.82rem",outline:"none",fontFamily:"inherit"}}>
              <option value="">— Tipo —</option>
              {SESSION_TYPES.map(s=><option key={s}>{s}</option>)}
            </select>
            <Inp value={editSessionVal.duration} onChange={e=>setEditSessionVal(p=>({...p,duration:e.target.value}))} placeholder="Minutos" type="number" t={t} style={{width:90,textAlign:"center"}}/>
            <span style={{color:t.textSecondary,fontSize:"0.8rem"}}>min</span>
            <button onClick={saveSession} style={{background:t.green,color:"#fff",border:"none",borderRadius:"8px",padding:"0.42rem 1rem",fontSize:"0.78rem",fontWeight:700,cursor:"pointer"}}>Confirmar</button>
            <button onClick={()=>setEditField(null)} style={{background:t.surfaceHover,border:`1px solid ${t.border}`,borderRadius:"8px",padding:"0.42rem 0.7rem",color:t.textSecondary,fontSize:"0.72rem",cursor:"pointer"}}>Cancelar</button>
          </div>
        </Card>
      )}
      {editField==="habits"&&(
        <Card t={t} style={{padding:"1rem",border:`1px solid ${t.green}40`}}>
          <div style={{color:t.textTertiary,fontSize:"0.6rem",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:"0.7rem"}}>✏️ Hábitos de hoy — confirmar</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:"0.4rem"}}>
            {oblH.map(h=>{
              const done=!!habits[tk]?.[h.id];
              return(
                <button key={h.id} onClick={()=>toggleHabit(h.id)}
                  style={{display:"flex",alignItems:"center",gap:"0.35rem",padding:"0.35rem 0.75rem",borderRadius:"20px",border:`1px solid ${done?t.green+"60":t.border}`,background:done?t.greenBg:t.surfaceHover,cursor:"pointer",color:done?t.green:t.textSecondary,fontSize:"0.72rem",fontWeight:600,transition:"all 0.15s"}}>
                  <span>{h.icon}</span><span>{h.label}</span>{done&&<span style={{fontSize:"0.65rem"}}>✓</span>}
                </button>
              );
            })}
          </div>
          <button onClick={()=>setEditField(null)} style={{marginTop:"0.65rem",background:t.green,color:"#fff",border:"none",borderRadius:"8px",padding:"0.42rem 1.2rem",fontSize:"0.78rem",fontWeight:700,cursor:"pointer"}}>Listo</button>
        </Card>
      )}

      {/* Dynamic averages + strength delta */}
      <Card t={t} style={{padding:"1.25rem"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"0.8rem"}}>
          <Lbl t={t}>Medias del período</Lbl>
          <PeriodPicker value={avgP} onChange={setAvgP} t={t}/>
        </div>
        <div style={{display:"flex",gap:"1rem",flexWrap:"wrap"}}>
          <div style={{flex:1,minWidth:110,textAlign:"center",background:t.surfaceHover,borderRadius:12,padding:"0.75rem 0.5rem"}}>
            <div style={{color:t.gold,fontSize:"1.5rem",fontWeight:800,lineHeight:1,letterSpacing:"-0.02em"}}>{avgCalIngested||"—"}</div>
            <div style={{color:t.textTertiary,fontSize:"0.58rem",marginTop:"0.25rem"}}>kcal ingeridas/día</div>
          </div>
          <div style={{flex:1,minWidth:110,textAlign:"center",background:t.surfaceHover,borderRadius:12,padding:"0.75rem 0.5rem"}}>
            <div style={{color:t.purple,fontSize:"1.5rem",fontWeight:800,lineHeight:1,letterSpacing:"-0.02em"}}>{avgBodyWeight?`${avgBodyWeight}kg`:"—"}</div>
            <div style={{color:t.textTertiary,fontSize:"0.58rem",marginTop:"0.25rem"}}>peso corporal medio</div>
          </div>
          {strengthDelta!==null&&(
            <div style={{flex:1,minWidth:110,textAlign:"center",background:strengthDelta>=0?t.greenBg:t.redBg,borderRadius:12,padding:"0.75rem 0.5rem",border:`1px solid ${strengthDelta>=0?t.green+"40":t.red+"40"}`}}>
              <div style={{color:strengthDelta>=0?t.green:t.red,fontSize:"1.5rem",fontWeight:800,lineHeight:1,letterSpacing:"-0.02em"}}>{strengthDelta>=0?"+":""}{strengthDelta}%</div>
              <div style={{color:t.textTertiary,fontSize:"0.58rem",marginTop:"0.25rem"}}>Δ fuerza (5 vs 5 sesiones)</div>
            </div>
          )}
        </div>
      </Card>

      {/* Main grid */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"1.5rem"}}>

        {/* Weekly habit completion */}
        <Card t={t} style={{padding:"1.25rem"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"0.5rem"}}>
            <Lbl t={t}>Hábitos por semana (%)</Lbl>
            <PeriodPicker value={habP} onChange={setHabP} t={t}/>
          </div>
          <LineChart data={habWeeks} color={t.green} t={t} height={110} showLabels={true}/>
        </Card>

        {/* Weekly training minutes */}
        <Card t={t} style={{padding:"1.25rem"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"0.5rem"}}>
            <Lbl t={t}>Minutos de entrenamiento por semana</Lbl>
            <PeriodPicker value={trainP} onChange={setTrainP} t={t}/>
          </div>
          <LineChart data={trainWeeks} color={t.blue} t={t} height={110} showLabels={true}/>
        </Card>

        {/* Avg calories per week */}
        <Card t={t} style={{padding:"1.25rem"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"0.5rem"}}>
            <Lbl t={t}>Calorías promedio</Lbl>
            <PeriodPicker value={calP} onChange={setCalP} t={t}/>
          </div>
          <LineChart data={calWeeks} color={t.gold} t={t} height={110} showLabels={true}/>
          <div style={{color:t.textTertiary,fontSize:"0.62rem",marginTop:"0.5rem"}}>Objetivo: {macros.cal} kcal/día</div>
        </Card>

        {/* Weight trend */}
        <Card t={t} style={{padding:"1.25rem"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"0.5rem"}}>
            <Lbl t={t}>Evolución de peso</Lbl>
            <PeriodPicker value={weightP} onChange={setWeightP} t={t}/>
          </div>
          {weightData.length>=2 ? (
            <>
              <LineChart data={weightData} color={t.purple} t={t} height={100}/>
              <div style={{display:"flex",justifyContent:"space-between",marginTop:"0.5rem"}}>
                <span style={{color:t.textTertiary,fontSize:"0.62rem"}}>Inicio: {weightData[0].value}kg</span>
                <span style={{color:t.textTertiary,fontSize:"0.62rem"}}>Ahora: {weightData[weightData.length-1].value}kg</span>
                <span style={{color:weightData[weightData.length-1].value>weightData[0].value?t.green:t.red,fontSize:"0.62rem",fontWeight:700}}>
                  {(weightData[weightData.length-1].value-weightData[0].value>0?"+":"")+((weightData[weightData.length-1].value-weightData[0].value).toFixed(1))}kg
                </span>
              </div>
            </>
          ) : (
            <div style={{color:t.textTertiary,fontSize:"0.7rem",height:100,display:"flex",alignItems:"center",justifyContent:"center"}}>Registra tu peso diariamente para ver la tendencia</div>
          )}
        </Card>
      </div>

      {/* Sleep chart — full width curved line */}
      <Card t={t} style={{padding:"1.25rem"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:"0.6rem",flexWrap:"wrap",gap:"0.5rem"}}>
          <div>
            <Lbl t={t}>Sueño</Lbl>
            <div style={{display:"flex",gap:"0.75rem",alignItems:"center",marginTop:"0.2rem"}}>
              {avgSleepH&&<span style={{color:t.purple,fontSize:"0.72rem",fontWeight:700}}>{avgSleepH}h promedio</span>}
              {avgCycles&&<span style={{color:t.textTertiary,fontSize:"0.68rem"}}>{avgCycles} ciclos/noche</span>}
            </div>
          </div>
          <PeriodPicker value={sleepP} onChange={setSleepP} t={t}/>
        </div>
        {sleepWithData.length>=2 ? (
          <div style={{position:"relative"}}>
            {/* Reference lines */}
            <svg viewBox="0 0 400 120" style={{width:"100%",height:120,overflow:"visible"}} preserveAspectRatio="none">
              {/* 8h reference */}
              <line x1="0" y1={120-((8-3)/(10-3))*100} x2="400" y2={120-((8-3)/(10-3))*100} stroke={t.green} strokeWidth="0.5" strokeDasharray="4,3" opacity="0.5"/>
              {/* 6h reference */}
              <line x1="0" y1={120-((6-3)/(10-3))*100} x2="400" y2={120-((6-3)/(10-3))*100} stroke={t.gold} strokeWidth="0.5" strokeDasharray="4,3" opacity="0.5"/>
              {/* Smooth curved path using bezier */}
              {(()=>{
                const pts=sleepData30.filter(d=>d.value>0);
                if(pts.length<2) return null;
                const fullIdx=sleepData30.map((d,i)=>({...d,i})).filter(d=>d.value>0);
                const W=400,H=100,minV=3,maxV=10,range=maxV-minV;
                const coords=fullIdx.map(d=>({
                  x:(d.i/(sleepData30.length-1))*W,
                  y:H-((Math.min(maxV,Math.max(minV,d.value))-minV)/range)*H+10
                }));
                // Build smooth bezier path
                let path=`M ${coords[0].x},${coords[0].y}`;
                for(let i=1;i<coords.length;i++){
                  const prev=coords[i-1],curr=coords[i];
                  const cpx=(prev.x+curr.x)/2;
                  path+=` C ${cpx},${prev.y} ${cpx},${curr.y} ${curr.x},${curr.y}`;
                }
                // Fill area
                const fillPath=path+` L ${coords[coords.length-1].x},${H+10} L ${coords[0].x},${H+10} Z`;
                return (
                  <>
                    <defs>
                      <linearGradient id="sleepGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={t.purple} stopOpacity="0.3"/>
                        <stop offset="100%" stopColor={t.purple} stopOpacity="0.02"/>
                      </linearGradient>
                    </defs>
                    <path d={fillPath} fill="url(#sleepGrad)"/>
                    <path d={path} fill="none" stroke={t.purple} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    {coords.map((c,i)=>(
                      <circle key={i} cx={c.x} cy={c.y} r="3" fill={fullIdx[i].score>=85?t.green:fullIdx[i].score>=60?t.blue:fullIdx[i].score>=40?t.gold:t.red}/>
                    ))}
                  </>
                );
              })()}
            </svg>
            {/* Y labels */}
            <div style={{position:"absolute",left:0,top:0,height:120,display:"flex",flexDirection:"column",justifyContent:"space-between",pointerEvents:"none"}}>
              {[10,8,6,4,3].map(h=>(
                <span key={h} style={{color:t.textTertiary,fontSize:"0.5rem",lineHeight:1}}>{h}h</span>
              ))}
            </div>
            {/* Reference labels */}
            <div style={{display:"flex",gap:"1rem",marginTop:"0.4rem",alignItems:"center",flexWrap:"wrap"}}>
              {[{c:t.green,l:"≥85 Óptimo"},{c:t.blue,l:"60–84 Bueno"},{c:t.gold,l:"40–59 Regular"},{c:t.red,l:"<40 Mal"},{c:t.green,l:"— 8h ref.",d:true},{c:t.gold,l:"— 6h ref.",d:true}].map((x,i)=>(
                <div key={i} style={{display:"flex",alignItems:"center",gap:"0.3rem"}}>
                  {x.d?<div style={{width:12,height:1,borderTop:`1.5px dashed ${x.c}`}}/>:<div style={{width:8,height:8,borderRadius:"50%",background:x.c}}/>}
                  <span style={{color:t.textTertiary,fontSize:"0.58rem"}}>{x.l}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div style={{color:t.textTertiary,fontSize:"0.75rem",textAlign:"center",padding:"2rem",lineHeight:1.6}}>
            Registra tu sueño desde el móvil (tab Hoy → abajo del todo)<br/>
            <span style={{fontSize:"0.65rem"}}>Con 2+ días de datos aparece la gráfica</span>
          </div>
        )}
      </Card>

      {/* Full-width: Habit heatmap */}
      <Card t={t} style={{padding:"1.25rem"}}>
        <Lbl t={t}>Consistencia de hábitos — últimas 8 semanas</Lbl>
        <div style={{marginTop:"0.5rem",overflowX:"auto"}}>
          <HeatMap data={heatData} t={t}/>
        </div>
        <div style={{display:"flex",gap:"0.8rem",marginTop:"0.75rem",alignItems:"center",flexWrap:"wrap"}}>
          {[["Sin datos",t.border],["< 40%","rgba(34,197,94,0.2)"],["40-70%","rgba(34,197,94,0.5)"],["≥ 70%","rgba(34,197,94,0.9)"]].map(([l,c])=>(
            <div key={l} style={{display:"flex",alignItems:"center",gap:"0.35rem"}}>
              <div style={{width:10,height:10,borderRadius:2,background:c}}/>
              <span style={{color:t.textTertiary,fontSize:"0.6rem"}}>{l}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* Muscle volume */}
      <Card t={t} style={{padding:"1.25rem"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"0.5rem"}}>
          <Lbl t={t}>Volumen muscular general (series)</Lbl>
          <PeriodPicker value={volP} onChange={setVolP} t={t} opts={["1d","7d","15d","30d","3m","6m","12m"]}/>
        </div>
        <div style={{display:"flex",gap:"0.75rem",flexWrap:"wrap",marginTop:"0.5rem"}}>
          {(()=>{const volWeeks=Math.ceil(volDays/7);return Object.entries(OPT_TARGETS).map(([muscle, target]) => {
            const vol = weekVol[muscle]||0;
            const scaledTarget = target * volWeeks;
            const pct = Math.min(1, vol/scaledTarget);
            const color = pct===0?t.border:pct<0.4?t.red:pct<0.7?t.gold:pct<1?t.blue:t.green;
            return (
              <div key={muscle} style={{background:t.surfaceHover,borderRadius:12,padding:"0.75rem 1rem",minWidth:110,textAlign:"center"}}>
                <div style={{color:color,fontSize:"1.2rem",fontWeight:800}}>{vol}</div>
                <div style={{color:t.textSecondary,fontSize:"0.63rem",fontWeight:600}}>{muscle}</div>
                <div style={{color:t.textTertiary,fontSize:"0.55rem"}}>/ {scaledTarget} series</div>
                <div style={{marginTop:"0.4rem",height:4,borderRadius:2,background:t.border}}>
                  <div style={{height:"100%",borderRadius:2,background:color,width:`${pct*100}%`,transition:"width 0.3s"}}/>
                </div>
              </div>
            );
          });})()}
        </div>
      </Card>

      {/* Weekly comparisons */}
      <Card t={t} style={{padding:"1.25rem"}}>
        <Lbl t={t}>Comparativa semanal</Lbl>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:"0.75rem"}}>
            <thead>
              <tr>
                {["Semana","Hábitos","Min entreno","Kcal ing.","vs anterior"].map(h=>(
                  <th key={h} style={{color:t.textTertiary,fontWeight:700,padding:"0.4rem 0.7rem",textAlign:"left",borderBottom:`1px solid ${t.border}`,fontSize:"0.62rem",letterSpacing:"0.06em",textTransform:"uppercase"}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeks.map((w,i)=>{
                const prev = weeks[i-1];
                const diff = prev ? w.habPct - prev.habPct : null;
                return (
                  <tr key={i} style={{borderBottom:`1px solid ${t.border}22`}}>
                    <td style={{color:t.textSecondary,padding:"0.55rem 0.7rem",fontWeight:600}}>{w.label}</td>
                    <td style={{color:w.habPct>=70?t.green:w.habPct>=40?t.gold:t.red,padding:"0.55rem 0.7rem",fontWeight:700}}>{w.habPct}%</td>
                    <td style={{color:t.text,padding:"0.55rem 0.7rem"}}>{w.totalTrainingMins}m</td>
                    <td style={{color:t.text,padding:"0.55rem 0.7rem"}}>{w.avgCal} kcal</td>
                    <td style={{padding:"0.55rem 0.7rem",color:diff===null?t.textTertiary:diff>=0?t.green:t.red,fontWeight:600}}>
                      {diff===null?"—":diff>=0?`+${diff}%`:`${diff}%`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Strength progress — full width curved chart */}
      <Card t={t} style={{padding:"1.25rem"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"0.85rem",flexWrap:"wrap",gap:"0.5rem"}}>
          <Lbl t={t}>Evolución de fuerza — progreso de pesos en el gym</Lbl>
          <span style={{color:t.textTertiary,fontSize:"0.6rem"}}>Selecciona ejercicio para ver la curva</span>
        </div>
        <StrengthChart workouts={workouts} t={t}/>
      </Card>

      {/* ── Composición corporal ─────────────────────────────────────────────── */}
      <Card t={t} style={{padding:"1.25rem"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"1rem",flexWrap:"wrap",gap:"0.5rem"}}>
          <Lbl t={t}>⚖️ Composición corporal</Lbl>
          <PeriodPicker value={avgP} onChange={setAvgP} t={t}/>
        </div>
        <div style={{display:"flex",gap:"1.5rem",flexWrap:"wrap"}}>
          {/* Peso real */}
          <div style={{flex:1,minWidth:160}}>
            <div style={{color:t.textTertiary,fontSize:"0.6rem",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:"0.4rem"}}>Cambio real de peso</div>
            {actualDeltaKgD!==null?(
              <div>
                <span style={{color:actualDeltaKgD>0?t.green:actualDeltaKgD<0?t.red:t.textSecondary,fontSize:"2rem",fontWeight:800,letterSpacing:"-0.03em"}}>{actualDeltaKgD>0?"+":""}{actualDeltaKgD} kg</span>
                <div style={{color:t.textTertiary,fontSize:"0.65rem",marginTop:"0.2rem"}}>{firstBWP} → {lastBWP} kg · {bwEntriesPeriod.length} registros</div>
              </div>
            ):(
              <div style={{color:t.textTertiary,fontSize:"0.78rem"}}>Sin suficientes registros</div>
            )}
          </div>
          {/* Estimación por calorías */}
          {calSurplusD!==null&&(
            <div style={{flex:1,minWidth:180}}>
              <div style={{color:t.textTertiary,fontSize:"0.6rem",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:"0.4rem"}}>Estimación calórica ({avgCalIngested} kcal/día media)</div>
              <div style={{display:"flex",gap:"1.5rem",flexWrap:"wrap"}}>
                {[
                  {l:"Superávit/día", v:`${calSurplusD>0?"+":""}${calSurplusD} kcal`, c:calSurplusD>0?t.green:t.red},
                  {l:"Ritmo/semana",  v:`${kgPerWeekD>0?"+":""}${kgPerWeekD} kg/sem`, c:t.text},
                  {l:`En ${avgPDays} días`,  v:`${expectedKgD>0?"+":""}${expectedKgD} kg`, c:t.text},
                ].map(s=>(
                  <div key={s.l}>
                    <div style={{color:t.textTertiary,fontSize:"0.6rem"}}>{s.l}</div>
                    <div style={{color:s.c,fontSize:"1rem",fontWeight:800,marginTop:"0.1rem"}}>{s.v}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Split músculo/grasa */}
          {expectedKgD!==null&&expectedKgD!==0&&(
            <div style={{flex:1,minWidth:200}}>
              <div style={{color:t.textTertiary,fontSize:"0.6rem",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:"0.4rem"}}>
                Split estimado <span style={{fontWeight:400,textTransform:"none"}}>(proteína {Math.round(protQualityD*100)}% objetivo)</span>
              </div>
              <div style={{display:"flex",gap:"0.4rem",marginBottom:"0.4rem"}}>
                <div style={{flex:muscleRatioD,background:t.green,borderRadius:"6px",height:"10px"}}/>
                <div style={{flex:1-muscleRatioD,background:t.gold,borderRadius:"6px",height:"10px"}}/>
              </div>
              <div style={{display:"flex",gap:"1.5rem"}}>
                <div><span style={{color:t.green,fontWeight:700,fontSize:"0.85rem"}}>💪 {calSurplusD>0?"Músculo":"Preservado"}: ~{Math.abs(expectedMuscleD)} kg</span><span style={{color:t.textTertiary,fontSize:"0.65rem",marginLeft:"0.3rem"}}>({Math.round(muscleRatioD*100)}%)</span></div>
                <div><span style={{color:t.gold,fontWeight:700,fontSize:"0.85rem"}}>Grasa: ~{Math.abs(expectedFatD)} kg</span></div>
              </div>
              {protQualityD<0.8&&<div style={{color:t.gold,fontSize:"0.62rem",marginTop:"0.5rem",background:t.goldBg,borderRadius:"8px",padding:"0.35rem 0.6rem"}}>⚠️ Más proteína mejoraría el ratio músculo/grasa</div>}
            </div>
          )}
        </div>
      </Card>

      {/* ── DROPDOWN PANELS ─────────────────────────────────────────────────── */}
      <div style={{display:"flex",gap:"0.65rem",flexWrap:"wrap"}}>
        {[
          {key:"ma",icon:"🥊",label:"Artes marciales — horas"},
          {key:"prs",icon:"🏆",label:"Récords personales (PRs)"},
          {key:"dayEdit",icon:"📅",label:"Ver / editar día"},
        ].map(({key,icon,label})=>(
          <button key={key} onClick={()=>toggleSection(key)} style={{
            display:"flex",alignItems:"center",gap:"0.5rem",
            padding:"0.55rem 1rem",borderRadius:"12px",border:`1px solid ${expandedSection===key?t.accent:t.border}`,
            background:expandedSection===key?t.accentMuted:t.surface,cursor:"pointer",
            color:expandedSection===key?t.text:t.textSecondary,fontSize:"0.78rem",fontWeight:600,
          }}>
            <span>{icon}</span>{label}
            <span style={{color:t.textTertiary,fontSize:"0.65rem",marginLeft:"0.2rem"}}>{expandedSection===key?"▲":"▼"}</span>
          </button>
        ))}
      </div>

      {/* MA hours panel */}
      {expandedSection==="ma"&&(
        <Card t={t} style={{padding:"1.5rem"}}>
          <div style={{display:"flex",gap:"2rem",alignItems:"flex-start",flexWrap:"wrap"}}>
            <div>
              <div style={{color:t.textTertiary,fontSize:"0.6rem",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:"0.3rem"}}>Total últimos 30 días</div>
              <div style={{color:t.accent,fontSize:"2.8rem",fontWeight:800,letterSpacing:"-0.04em",lineHeight:1}}>{maHoursTotal}<span style={{fontSize:"1rem",fontWeight:400,color:t.textSecondary,marginLeft:"0.3rem"}}>h</span></div>
            </div>
            <div style={{display:"flex",gap:"1.2rem",flexWrap:"wrap"}}>
              {Object.entries(maByType).map(([type,mins])=>(
                <div key={type}>
                  <div style={{color:t.textTertiary,fontSize:"0.58rem",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:"0.2rem"}}>{type}</div>
                  <div style={{color:t.text,fontSize:"1.3rem",fontWeight:800}}>{(mins/60).toFixed(1)}<span style={{fontSize:"0.7rem",color:t.textSecondary,marginLeft:"0.2rem"}}>h</span></div>
                  <div style={{color:t.textTertiary,fontSize:"0.6rem"}}>{Math.round(mins/30)} sesiones ~</div>
                </div>
              ))}
              {Object.keys(maByType).length===0&&<div style={{color:t.textTertiary,fontSize:"0.78rem"}}>Sin sesiones de artes marciales en 30 días.</div>}
            </div>
          </div>
          {/* Timeline */}
          {totalMAMins>0&&(
            <div style={{marginTop:"1.2rem"}}>
              <div style={{color:t.textTertiary,fontSize:"0.6rem",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:"0.5rem"}}>Sesiones últimos 30 días</div>
              <div style={{display:"flex",gap:"3px",alignItems:"flex-end",height:40}}>
                {maLast30.map(day=>{
                  const w=workouts[day];
                  const isMA=w&&maTypes.includes(w.sessionType);
                  const mins=isMA?parseFloat(w.duration)||0:0;
                  return (
                    <div key={day} title={isMA?`${day}: ${w.sessionType} ${mins}min`:""}
                      style={{flex:1,height:isMA?`${Math.max(10,Math.round(mins/120*100))}%`:"3px",borderRadius:2,
                        background:isMA?({...{"Muay Thai":t.red,BJJ:t.blue,Lucha:t.gold,MMA:t.purple}}[w.sessionType]||t.green):t.border,
                        transition:"height 0.2s"}}/>
                  );
                })}
              </div>
            </div>
          )}
        </Card>
      )}

      {/* PRs panel */}
      {expandedSection==="prs"&&(
        <Card t={t} style={{padding:"1.5rem"}}>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:"1rem"}}>
            {KEY_EX.map(name=>{
              const pr=allPRs[name];
              return (
                <div key={name} style={{background:t.surfaceHover,borderRadius:12,padding:"0.85rem 1rem"}}>
                  <div style={{color:t.textSecondary,fontSize:"0.65rem",fontWeight:600,marginBottom:"0.3rem"}}>{name}</div>
                  {pr?(
                    <>
                      <div style={{color:t.gold,fontSize:"1.4rem",fontWeight:800,letterSpacing:"-0.03em",lineHeight:1}}>{pr.weight}<span style={{fontSize:"0.8rem",fontWeight:400,color:t.textSecondary,marginLeft:"0.2rem"}}>kg</span></div>
                      <div style={{color:t.textTertiary,fontSize:"0.65rem",marginTop:"0.15rem"}}>× {pr.reps} reps</div>
                    </>
                  ):(
                    <div style={{color:t.textTertiary,fontSize:"0.72rem",fontStyle:"italic"}}>Sin datos aún</div>
                  )}
                </div>
              );
            })}
          </div>
          {Object.keys(allPRs).length>KEY_EX.length&&(
            <div style={{marginTop:"1rem",borderTop:`1px solid ${t.border}`,paddingTop:"0.75rem"}}>
              <div style={{color:t.textTertiary,fontSize:"0.6rem",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:"0.5rem"}}>Todos los ejercicios</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:"0.5rem"}}>
                {Object.entries(allPRs).filter(([n])=>!KEY_EX.includes(n)).sort(([,a],[,b])=>b.weight-a.weight).map(([name,pr])=>(
                  <div key={name} style={{background:t.surfaceHover,borderRadius:8,padding:"0.35rem 0.65rem",fontSize:"0.7rem"}}>
                    <span style={{color:t.textSecondary}}>{name}: </span>
                    <span style={{color:t.text,fontWeight:700}}>{pr.weight}kg×{pr.reps}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Day view/edit panel */}
      {expandedSection==="dayEdit"&&(
        <Card t={t} style={{padding:"1.5rem"}}>
          {/* Day picker */}
          <div style={{display:"flex",gap:"3px",overflowX:"auto",marginBottom:"1rem",paddingBottom:"4px"}}>
            {last14days.map(d=>{
              const hasW=!!workouts[d]?.sessionType;
              const isSel=d===viewDay;
              const day=new Date(d+"T12:00").getDate();
              const dow=new Date(d+"T12:00").toLocaleDateString("es-ES",{weekday:"short"}).slice(0,2).toUpperCase();
              return (
                <div key={d} onClick={()=>setViewDay(d)} style={{minWidth:36,padding:"0.3rem 0.1rem",borderRadius:8,textAlign:"center",cursor:"pointer",flexShrink:0,
                  background:isSel?t.accent:hasW?t.surfaceHover:"transparent",
                  border:`1px solid ${isSel?t.accent:hasW?t.borderLight:t.border+"44"}`}}>
                  <div style={{fontSize:"0.42rem",color:isSel?t.bg:t.textTertiary,fontWeight:700}}>{dow}</div>
                  <div style={{fontSize:"0.8rem",fontWeight:700,color:isSel?t.bg:hasW?t.text:t.textTertiary,lineHeight:1.2}}>{day}</div>
                  {hasW&&<div style={{width:3,height:3,borderRadius:"50%",background:isSel?t.bg:t.green,margin:"1px auto 0"}}/>}
                </div>
              );
            })}
          </div>

          {viewedW?.sessionType?(
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:"1rem"}}>
                <div>
                  <div style={{color:t.text,fontSize:"1rem",fontWeight:700}}>{viewedW.sessionType}</div>
                  <div style={{color:t.textSecondary,fontSize:"0.72rem",marginTop:"0.15rem"}}>
                    {viewedW.duration&&`${viewedW.duration} min`}
                    {(()=>{const dc=Math.round(Object.values((nutrition[viewDay]||{}).items||{}).reduce((a,i)=>a+i.cal,0));return dc>0?` · ${dc} kcal ingeridas`:"";})()}
                    {viewedW.savedAt&&` · Guardado ${new Date(viewedW.savedAt).toLocaleTimeString("es-ES",{hour:"2-digit",minute:"2-digit"})}`}
                  </div>
                </div>
                <button onClick={()=>{if(window.confirm(`¿Eliminar entrenamiento del ${viewDay}?`)){setWorkouts(p=>{const n={...p};delete n[viewDay];return n;});}}}
                  style={{background:t.redBg,border:"none",borderRadius:"8px",padding:"0.3rem 0.75rem",color:t.red,fontSize:"0.7rem",cursor:"pointer",fontWeight:600}}>
                  Eliminar
                </button>
              </div>
              {(viewedW.exercises||[]).length>0&&(
                <div style={{display:"flex",flexDirection:"column",gap:"0.6rem"}}>
                  {viewedW.exercises.map((ex,i)=>(
                    <div key={i} style={{background:t.surfaceHover,borderRadius:10,padding:"0.65rem 0.85rem"}}>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:"0.3rem"}}>
                        <span style={{color:t.text,fontSize:"0.8rem",fontWeight:600}}>{ex.name}</span>
                        <span style={{color:t.textTertiary,fontSize:"0.63rem"}}>{ex.muscle} · {(ex.sets||[]).length} sets</span>
                      </div>
                      {(ex.sets||[]).map((s,si)=>(
                        <span key={si} style={{display:"inline-block",background:t.surface,borderRadius:6,padding:"0.12rem 0.4rem",fontSize:"0.65rem",color:t.textSecondary,marginRight:"0.3rem",marginBottom:"0.2rem"}}>
                          {s.weight}kg×{s.reps}
                        </span>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ):(
            <div style={{color:t.textTertiary,textAlign:"center",padding:"1.5rem",fontSize:"0.8rem"}}>Sin entrenamiento registrado este día.</div>
          )}
        </Card>
      )}
    </div>
  );
}

// ─── MULTI-USER AUTH COMPONENTS ──────────────────────────────────────────────

// Teclado numérico PIN genérico
function PinKeypad({value, maxLen, onPress, onDel, error, shake}) {
  const dots = Array.from({length: maxLen}, (_, i) => value.length > i);
  return (
    <>
      <div style={{display:"flex",gap:"1rem",animation:shake?"shake 0.5s":"none"}}>
        <style>{`@keyframes shake{0%,100%{transform:translateX(0)}20%,60%{transform:translateX(-8px)}40%,80%{transform:translateX(8px)}}`}</style>
        {dots.map((filled, i) => (
          <div key={i} style={{width:14,height:14,borderRadius:"50%",background:filled?(error?"#EF4444":"#22C55E"):"#333",border:"2px solid",borderColor:filled?(error?"#EF4444":"#22C55E"):"#555",transition:"all 0.15s"}}/>
        ))}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:"0.75rem",width:220}}>
        {[1,2,3,4,5,6,7,8,9,"",0,"⌫"].map((k, i) => (
          k===""?<div key={i}/>:
          <button key={i} onClick={()=>k==="⌫"?onDel():onPress(String(k))}
            style={{padding:"1rem",borderRadius:"50%",border:"1px solid #333",background:k==="⌫"?"transparent":"#1a1a1a",color:"#fff",fontSize:k==="⌫"?"1.2rem":"1.3rem",fontWeight:700,cursor:"pointer",textAlign:"center",lineHeight:1}}>
            {k}
          </button>
        ))}
      </div>
    </>
  );
}

// Entrada de PIN para un usuario específico
function PinEntry({user, onSuccess, onBack}) {
  const [input, setInput] = useState("");
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);

  const press = d => {
    if(input.length >= 6) return;
    const next = input + d;
    setInput(next);
    setError(false);
    if(next.length === user.pin.length) {
      if(next === user.pin) { onSuccess(); }
      else { setShake(true); setError(true); setTimeout(()=>{setInput("");setShake(false);}, 600); }
    }
  };
  const del = () => { setInput(p => p.slice(0,-1)); setError(false); };

  return (
    <div style={{minHeight:"100vh",background:"#0a0a0a",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:"2rem",fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Display',sans-serif",position:"relative"}}>
      <button onClick={onBack} style={{position:"absolute",top:"1.5rem",left:"1.5rem",background:"transparent",border:"none",color:"#666",fontSize:"0.85rem",cursor:"pointer"}}>← Volver</button>
      <div style={{textAlign:"center"}}>
        <div style={{width:52,height:52,borderRadius:"50%",background:"#22C55E",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 0.75rem"}}>
          <span style={{color:"#fff",fontSize:"1.4rem",fontWeight:700}}>{user.name[0].toUpperCase()}</span>
        </div>
        <div style={{color:"#fff",fontSize:"1.1rem",fontWeight:700}}>{user.name}</div>
        <div style={{color:"#666",fontSize:"0.78rem",marginTop:"0.25rem"}}>Introduce tu PIN</div>
      </div>
      <PinKeypad value={input} maxLen={user.pin.length} onPress={press} onDel={del} error={error} shake={shake}/>
      {error && <div style={{color:"#EF4444",fontSize:"0.75rem",fontWeight:600}}>PIN incorrecto</div>}
    </div>
  );
}

// Pantalla de selección de usuario / login
// ─── LOGIN SCREEN ─────────────────────────────────────────────────────────────
// Pantalla de selección de perfil. Muestra todos los usuarios registrados
// y permite refrescar la lista desde Supabase.
function LoginScreen({users, onLogin, onRefresh}) {
  const [selected, setSelected] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState("");

  const handleRefresh = async () => {
    setRefreshing(true);
    setRefreshMsg("");
    try {
      await onRefresh();
      setRefreshMsg("Perfiles actualizados");
    } catch {
      setRefreshMsg("Error al conectar");
    } finally {
      setRefreshing(false);
      setTimeout(() => setRefreshMsg(""), 3000);
    }
  };

  if(selected) return <PinEntry user={selected} onSuccess={()=>onLogin(selected.id)} onBack={()=>setSelected(null)}/>;

  const list = Object.values(users);
  return (
    <div style={{minHeight:"100vh",background:"#0a0a0a",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:"2rem",fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Display',sans-serif",padding:"1.5rem"}}>
      <div style={{textAlign:"center"}}>
        <div style={{fontSize:"2.5rem",marginBottom:"0.5rem"}}>💪</div>
        <div style={{color:"#fff",fontSize:"1.3rem",fontWeight:800,letterSpacing:"-0.02em"}}>mi-tracker</div>
        <div style={{color:"#666",fontSize:"0.78rem",marginTop:"0.25rem"}}>{tr("select_profile")}</div>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:"0.6rem",width:"100%",maxWidth:"320px"}}>
        {list.map(u => (
          <button key={u.id} onClick={()=>setSelected(u)}
            style={{background:"#141414",border:"1px solid #242424",borderRadius:"14px",padding:"0.9rem 1.1rem",display:"flex",alignItems:"center",gap:"0.85rem",cursor:"pointer",textAlign:"left",width:"100%"}}>
            <div style={{width:40,height:40,borderRadius:"50%",background:u.isAdmin?"#22C55E":"#3B82F6",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              <span style={{color:"#fff",fontSize:"1.1rem",fontWeight:700}}>{u.name[0].toUpperCase()}</span>
            </div>
            <div style={{flex:1}}>
              <div style={{color:"#fff",fontSize:"0.9rem",fontWeight:600}}>{u.name}</div>
              <div style={{color:"#666",fontSize:"0.7rem",marginTop:"0.05rem"}}>{u.isAdmin?tr("login_admin"):tr("login_user")}{u.country&&COUNTRY_PRESETS[u.country]?` · ${COUNTRY_PRESETS[u.country].label}`:""}{!u.onboardingDone?` · Primera vez`:""}</div>
            </div>
            <div style={{color:"#444",fontSize:"1rem"}}>›</div>
          </button>
        ))}
      </div>
      {/* Botón de actualizar perfiles — para cuando el admin haya añadido un nuevo usuario */}
      <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:"0.4rem"}}>
        <button onClick={handleRefresh} disabled={refreshing}
          style={{background:"transparent",border:"1px solid #2a2a2a",borderRadius:"10px",padding:"0.5rem 1.2rem",color:refreshing?"#444":"#666",cursor:refreshing?"default":"pointer",fontSize:"0.75rem",fontWeight:600,display:"flex",alignItems:"center",gap:"0.4rem"}}>
          {refreshing ? "Actualizando…" : tr("login_refresh")}
        </button>
        {refreshMsg && <div style={{color:refreshMsg.startsWith("Error")?"#EF4444":"#22C55E",fontSize:"0.7rem"}}>{refreshMsg}</div>}
      </div>
    </div>
  );
}

// ─── ONBOARDING WIZARD ────────────────────────────────────────────────────────
// Wizard de onboarding para nuevos usuarios: paso 0 (idioma), paso 1 (país),
// paso 2 (perfil físico). Solo se muestra la primera vez que entra un usuario.
function OnboardingWizard({user, onComplete, onLangChange}) {
  const [step, setStep] = useState(0);
  const [obLang, setObLang] = useState("es");
  const [country, setCountry] = useState(null);
  const [profile, setProfile] = useState({weight:"",goalWeight:"",height:"",age:"",sex:"M",pace:"normal",activityFactor:"1.55",proteinPerKg:"2.0"});
  const [err, setErr] = useState("");

  // Sincroniza el idioma seleccionado en onboarding con el módulo
  const pickLang = (code) => {
    setObLang(code);
    _LANG = code;
    if(onLangChange) onLangChange(code);
  };

  const goStep1 = () => { setStep(1); };

  const goStep2 = () => {
    if(!country){ setErr(tr("onboard_step1_title")); return; }
    setErr(""); setStep(2);
  };

  const goFinish = () => {
    if(!profile.weight||!profile.height||!profile.age){ setErr("Completa los campos obligatorios (peso, altura, edad)"); return; }
    const pfx = user.id === "cris" ? "" : user.id + "_";
    try{ localStorage.setItem(pfx+"ufc_goal_v7", JSON.stringify(profile)); }catch{}
    try{ localStorage.setItem(pfx+"ufc_lang_v1", JSON.stringify(obLang)); }catch{}
    if(country && COUNTRY_PRESETS[country]){
      try{ localStorage.setItem(pfx+"ufc_fav_v1", JSON.stringify(COUNTRY_PRESETS[country].foodIds)); }catch{}
    }
    onComplete({country, onboardingDone:true});
  };

  const preview = profile.weight&&profile.height&&profile.age ? calcMacros(profile) : null;
  const totalSteps = 3;

  return (
    <div style={{minHeight:"100vh",background:"#0a0a0a",color:"#fff",fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Display',sans-serif",display:"flex",flexDirection:"column",alignItems:"center",padding:"2rem 1.5rem",overflowY:"auto"}}>
      {/* Barra de progreso */}
      <div style={{width:"100%",maxWidth:"400px",marginBottom:"2rem"}}>
        <div style={{display:"flex",justifyContent:"space-between",marginBottom:"0.5rem"}}>
          <span style={{color:"#666",fontSize:"0.72rem",fontWeight:600}}>Hola, {user.name} 👋 — Paso {step+1} de {totalSteps}</span>
          <span style={{color:"#22C55E",fontSize:"0.72rem",fontWeight:600}}>{step===0?tr("onboard_lang_title"):step===1?tr("onboard_step1_title"):tr("onboard_step2_title")}</span>
        </div>
        <div style={{background:"#222",borderRadius:"4px",height:"4px"}}>
          <div style={{background:"#22C55E",height:"100%",borderRadius:"4px",width:`${((step+1)/totalSteps)*100}%`,transition:"width 0.4s"}}/>
        </div>
      </div>

      {/* PASO 0: Selección de idioma */}
      {step===0&&(
        <div style={{width:"100%",maxWidth:"400px",display:"flex",flexDirection:"column",gap:"1rem"}}>
          <div style={{textAlign:"center",marginBottom:"0.5rem"}}>
            <div style={{fontSize:"2rem",marginBottom:"0.5rem"}}>🌐</div>
            <div style={{fontSize:"1.2rem",fontWeight:800,letterSpacing:"-0.02em"}}>{tr("onboard_lang_title")}</div>
          </div>
          {[["es","🇪🇸 Español"],["en","🇬🇧 English"],["ar","🇦🇪 العربية"],["de","🇩🇪 Deutsch"]].map(([code,label])=>(
            <button key={code} onClick={()=>pickLang(code)}
              style={{background:obLang===code?"#0f1a12":"#0f0f0f",border:`2px solid ${obLang===code?"#22C55E":"#242424"}`,borderRadius:"14px",padding:"1rem 1.25rem",display:"flex",alignItems:"center",gap:"0.9rem",cursor:"pointer",textAlign:"left",transition:"all 0.2s",width:"100%"}}>
              <div style={{fontSize:"1.8rem",flexShrink:0}}>{label.split(" ")[0]}</div>
              <div style={{flex:1}}>
                <div style={{color:obLang===code?"#22C55E":"#fff",fontSize:"0.9rem",fontWeight:700}}>{label.split(" ").slice(1).join(" ")}</div>
              </div>
              {obLang===code&&<div style={{color:"#22C55E",fontSize:"1.2rem"}}>✓</div>}
            </button>
          ))}
          <button onClick={goStep1}
            style={{background:"#22C55E",color:"#000",border:"none",borderRadius:"14px",padding:"0.95rem",fontSize:"0.95rem",fontWeight:800,cursor:"pointer",marginTop:"0.5rem"}}>
            {tr("onboard_next")}
          </button>
        </div>
      )}

      {/* PASO 1: Cocina base */}
      {step===1&&(
        <div style={{width:"100%",maxWidth:"400px",display:"flex",flexDirection:"column",gap:"1rem"}}>
          <div style={{textAlign:"center",marginBottom:"0.5rem"}}>
            <div style={{fontSize:"2rem",marginBottom:"0.5rem"}}>🌍</div>
            <div style={{fontSize:"1.2rem",fontWeight:800,letterSpacing:"-0.02em"}}>{tr("onboard_step1_title")}</div>
            <div style={{color:"#666",fontSize:"0.78rem",marginTop:"0.35rem",lineHeight:1.5}}>
              {tr("onboard_step1_subtitle")}
            </div>
          </div>
          {Object.entries(COUNTRY_PRESETS).map(([key, {label, foodIds}]) => {
            const sel = country === key;
            return (
              <button key={key} onClick={()=>setCountry(key)}
                style={{background:sel?"#0f1a12":"#0f0f0f",border:`2px solid ${sel?"#22C55E":"#242424"}`,borderRadius:"14px",padding:"1rem 1.25rem",display:"flex",alignItems:"center",gap:"0.9rem",cursor:"pointer",textAlign:"left",transition:"all 0.2s",width:"100%"}}>
                <div style={{fontSize:"1.8rem",flexShrink:0}}>{label.split(" ")[0]}</div>
                <div style={{flex:1}}>
                  <div style={{color:sel?"#22C55E":"#fff",fontSize:"0.9rem",fontWeight:700}}>{label.split(" ").slice(1).join(" ")}</div>
                  <div style={{color:"#555",fontSize:"0.65rem",marginTop:"0.12rem"}}>{foodIds.length} alimentos preseleccionados</div>
                </div>
                {sel&&<div style={{color:"#22C55E",fontSize:"1.2rem"}}>✓</div>}
              </button>
            );
          })}
          {country&&(
            <div style={{background:"#0f1a12",border:"1px solid rgba(34,197,94,0.2)",borderRadius:"12px",padding:"0.85rem"}}>
              <div style={{color:"#22C55E",fontSize:"0.65rem",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:"0.5rem"}}>Alimentos incluidos</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:"0.35rem"}}>
                {COUNTRY_PRESETS[country].foodIds.map(id=>{const f=FOODS.find(x=>x.id===id);return f?<span key={id} style={{background:"#141414",border:"1px solid #242424",borderRadius:"8px",padding:"0.2rem 0.45rem",fontSize:"0.62rem",color:"#aaa"}}>{f.icon} {f.name.split(" ").slice(0,2).join(" ")}</span>:null;})}
              </div>
            </div>
          )}
          {err&&<div style={{color:"#EF4444",fontSize:"0.72rem",textAlign:"center"}}>{err}</div>}
          <button onClick={goStep2}
            style={{background:"#22C55E",color:"#000",border:"none",borderRadius:"14px",padding:"0.95rem",fontSize:"0.95rem",fontWeight:800,cursor:"pointer",marginTop:"0.5rem"}}>
            {tr("onboard_next")}
          </button>
          <button onClick={()=>setStep(0)} style={{background:"transparent",border:"none",color:"#666",fontSize:"0.8rem",cursor:"pointer",padding:"0.25rem"}}>← {tr("btn_cancel")}</button>
        </div>
      )}

      {/* PASO 2: Perfil físico */}
      {step===2&&(
        <div style={{width:"100%",maxWidth:"400px",display:"flex",flexDirection:"column",gap:"0.85rem"}}>
          <div style={{textAlign:"center",marginBottom:"0.25rem"}}>
            <div style={{fontSize:"2rem",marginBottom:"0.5rem"}}>⚙️</div>
            <div style={{fontSize:"1.2rem",fontWeight:800,letterSpacing:"-0.02em"}}>{tr("onboard_step2_title")}</div>
            <div style={{color:"#666",fontSize:"0.78rem",marginTop:"0.35rem"}}>Para calcular tus macros personalizados</div>
          </div>
          {[{k:"weight",l:"Peso actual (kg)",ph:"70"},{k:"goalWeight",l:"Peso objetivo (kg)",ph:"75"},{k:"height",l:"Altura (cm)",ph:"175"},{k:"age",l:"Edad",ph:"25"}].map(({k,l,ph})=>(
            <div key={k}>
              <div style={{color:"#666",fontSize:"0.63rem",fontWeight:600,letterSpacing:"0.06em",textTransform:"uppercase",marginBottom:"0.28rem"}}>{l}</div>
              <input value={profile[k]||""} onChange={e=>setProfile(p=>({...p,[k]:e.target.value}))} placeholder={ph} type="number"
                style={{background:"#141414",border:"1px solid #242424",borderRadius:"10px",color:"#fff",padding:"0.7rem 0.9rem",fontSize:"0.88rem",outline:"none",fontFamily:"inherit",width:"100%",boxSizing:"border-box"}}/>
            </div>
          ))}
          <div>
            <div style={{color:"#666",fontSize:"0.63rem",fontWeight:600,letterSpacing:"0.06em",textTransform:"uppercase",marginBottom:"0.45rem"}}>Nivel de actividad</div>
            <div style={{display:"flex",flexDirection:"column",gap:"0.28rem"}}>
              {ACTIVITY_LEVELS.map(({v,l,desc})=>{
                const sel=(profile.activityFactor||"1.55")===v;
                return(
                  <div key={v} onClick={()=>setProfile(p=>({...p,activityFactor:v}))}
                    style={{display:"flex",alignItems:"center",gap:"0.65rem",padding:"0.5rem 0.75rem",borderRadius:"10px",cursor:"pointer",border:`1px solid ${sel?"#22C55E":"#242424"}`,background:sel?"#0f1a12":"#0f0f0f"}}>
                    <div style={{width:14,height:14,borderRadius:"50%",border:`2px solid ${sel?"#22C55E":"#333"}`,background:sel?"#22C55E":"transparent",flexShrink:0}}/>
                    <div style={{flex:1}}>
                      <div style={{color:sel?"#22C55E":"#fff",fontSize:"0.78rem",fontWeight:sel?700:500}}>{l}</div>
                      <div style={{color:"#555",fontSize:"0.6rem",marginTop:"0.05rem"}}>{desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {preview&&(
            <div style={{background:"#141414",border:"1px solid #242424",borderRadius:"12px",padding:"0.85rem"}}>
              <div style={{color:"#666",fontSize:"0.6rem",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:"0.45rem"}}>Preview de macros diarios</div>
              {[{l:tr("lbl_calories"),v:`${preview.cal} ${tr("lbl_kcal")}`,c:"#fff"},{l:tr("lbl_protein"),v:`${preview.protein}g`,c:"#3b82f6"},{l:tr("lbl_carbs"),v:`${preview.carbs}g`,c:"#f59e0b"},{l:tr("lbl_fat"),v:`${preview.fat}g`,c:"#ef4444"}].map(s=>(
                <div key={s.l} style={{display:"flex",justifyContent:"space-between",marginBottom:"0.22rem"}}>
                  <span style={{color:"#666",fontSize:"0.72rem"}}>{s.l}</span>
                  <span style={{color:s.c,fontWeight:700,fontSize:"0.75rem"}}>{s.v}</span>
                </div>
              ))}
            </div>
          )}
          {err&&<div style={{color:"#EF4444",fontSize:"0.72rem",textAlign:"center"}}>{err}</div>}
          <div style={{display:"flex",gap:"0.5rem",marginTop:"0.25rem"}}>
            <button onClick={()=>{setStep(1);setErr("");}} style={{flex:1,background:"#141414",color:"#888",border:"1px solid #242424",borderRadius:"14px",padding:"0.85rem",fontSize:"0.85rem",fontWeight:600,cursor:"pointer"}}>← Volver</button>
            <button onClick={goFinish} style={{flex:2,background:"#22C55E",color:"#000",border:"none",borderRadius:"14px",padding:"0.85rem",fontSize:"0.95rem",fontWeight:800,cursor:"pointer"}}>{tr("onboard_finish")}</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── BOOKS TAB ────────────────────────────────────────────────────────────────
function BooksTab({books,setBooks,t}) {
  const[showAdd,setShowAdd]=useState(false);
  const[title,setTitle]=useState("");
  const[author,setAuthor]=useState("");
  const[totalPages,setTotalPages]=useState("");
  const[filter,setFilter]=useState("all");
  const[editId,setEditId]=useState(null);
  const[editPage,setEditPage]=useState("");

  const addBook=()=>{
    if(!title.trim())return;
    const book={id:Date.now(),title:title.trim(),author:author.trim(),totalPages:parseInt(totalPages)||0,currentPage:0,status:"pending",addedDate:today()};
    setBooks(p=>[book,...p]);
    setTitle("");setAuthor("");setTotalPages("");setShowAdd(false);
  };

  const updatePage=(id,page)=>{
    const p=Math.max(0,parseInt(page)||0);
    setBooks(prev=>prev.map(b=>{
      if(b.id!==id)return b;
      const tp=b.totalPages||0;
      const done=tp>0&&p>=tp;
      return{...b,currentPage:p,status:done?"done":p>0?"reading":"pending",finishDate:done?today():b.finishDate};
    }));
    setEditId(null);setEditPage("");
  };

  const deleteBook=(id)=>{if(window.confirm("¿Eliminar este libro?"))setBooks(p=>p.filter(b=>b.id!==id));};

  const STATUS_LABEL={reading:"Leyendo",pending:"Pendiente",done:"Terminado"};
  const STATUS_COLOR={reading:t.blue,pending:t.gold,done:t.green};

  const filtered=books.filter(b=>filter==="all"||b.status===filter);
  const counts={all:books.length,reading:books.filter(b=>b.status==="reading").length,pending:books.filter(b=>b.status==="pending").length,done:books.filter(b=>b.status==="done").length};

  return(
    <div style={{display:"flex",flexDirection:"column",gap:"1rem"}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:"0.5rem"}}>
        {[{k:"reading",label:"Leyendo",icon:"📖"},{k:"done",label:"Terminados",icon:"✅"},{k:"pending",label:"Pendientes",icon:"📚"}].map(({k,label,icon})=>(
          <div key={k} style={{background:t.surface,border:`1px solid ${t.border}`,borderRadius:"10px",padding:"0.6rem",textAlign:"center"}}>
            <div style={{fontSize:"1.2rem"}}>{icon}</div>
            <div style={{color:t.text,fontWeight:700,fontSize:"1rem"}}>{counts[k]}</div>
            <div style={{color:t.textTertiary,fontSize:"0.6rem"}}>{label}</div>
          </div>
        ))}
      </div>

      <button onClick={()=>setShowAdd(p=>!p)} style={{background:t.accentMuted,border:`1px solid ${t.border}`,borderRadius:"10px",padding:"0.55rem",color:t.text,fontSize:"0.8rem",fontWeight:600,cursor:"pointer",textAlign:"center"}}>
        {showAdd?"Cancelar":"+ Añadir libro"}
      </button>

      {showAdd&&(
        <div style={{background:t.surface,border:`1px solid ${t.border}`,borderRadius:"12px",padding:"1rem",display:"flex",flexDirection:"column",gap:"0.6rem"}}>
          <input value={title} onChange={e=>setTitle(e.target.value)} placeholder="Título *" style={{background:t.bg,border:`1px solid ${t.border}`,borderRadius:"8px",padding:"0.5rem 0.7rem",color:t.text,fontSize:"0.82rem",outline:"none"}}/>
          <input value={author} onChange={e=>setAuthor(e.target.value)} placeholder="Autor" style={{background:t.bg,border:`1px solid ${t.border}`,borderRadius:"8px",padding:"0.5rem 0.7rem",color:t.text,fontSize:"0.82rem",outline:"none"}}/>
          <input value={totalPages} onChange={e=>setTotalPages(e.target.value)} placeholder="Páginas totales" type="number" style={{background:t.bg,border:`1px solid ${t.border}`,borderRadius:"8px",padding:"0.5rem 0.7rem",color:t.text,fontSize:"0.82rem",outline:"none"}}/>
          <button onClick={addBook} style={{background:t.accent,border:"none",borderRadius:"8px",padding:"0.55rem",color:t.bg,fontWeight:700,fontSize:"0.82rem",cursor:"pointer"}}>Guardar</button>
        </div>
      )}

      <div style={{display:"flex",gap:"0.35rem",flexWrap:"wrap"}}>
        {[{k:"all",label:`Todos (${counts.all})`},{k:"reading",label:`Leyendo (${counts.reading})`},{k:"pending",label:`Pendiente (${counts.pending})`},{k:"done",label:`Terminado (${counts.done})`}].map(({k,label})=>(
          <button key={k} onClick={()=>setFilter(k)} style={{background:filter===k?t.accent:t.accentMuted,border:`1px solid ${filter===k?t.accent:t.border}`,borderRadius:"8px",padding:"0.25rem 0.6rem",color:filter===k?t.bg:t.textSecondary,fontSize:"0.7rem",fontWeight:filter===k?700:500,cursor:"pointer"}}>{label}</button>
        ))}
      </div>

      {filtered.length===0&&(
        <div style={{textAlign:"center",color:t.textTertiary,fontSize:"0.82rem",padding:"2rem 0"}}>
          {filter==="all"?"Añade tu primer libro 📚":"Sin libros en esta categoría"}
        </div>
      )}

      {filtered.map(book=>{
        const pct=book.totalPages>0?Math.min(100,Math.round(book.currentPage/book.totalPages*100)):0;
        const isEditing=editId===book.id;
        return(
          <div key={book.id} style={{background:t.surface,border:`1px solid ${t.border}`,borderRadius:"12px",padding:"0.9rem",display:"flex",flexDirection:"column",gap:"0.55rem"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:"0.5rem"}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{color:t.text,fontWeight:700,fontSize:"0.88rem",wordBreak:"break-word"}}>{book.title}</div>
                {book.author&&<div style={{color:t.textSecondary,fontSize:"0.72rem",marginTop:"0.1rem"}}>{book.author}</div>}
              </div>
              <div style={{display:"flex",gap:"0.3rem",alignItems:"center",flexShrink:0}}>
                <span style={{background:STATUS_COLOR[book.status]+"22",color:STATUS_COLOR[book.status],fontSize:"0.62rem",fontWeight:700,padding:"0.15rem 0.4rem",borderRadius:"6px"}}>{STATUS_LABEL[book.status]}</span>
                <button onClick={()=>deleteBook(book.id)} style={{background:"transparent",border:"none",color:t.textTertiary,cursor:"pointer",fontSize:"0.75rem",padding:"0.1rem 0.25rem"}}>✕</button>
              </div>
            </div>

            {book.totalPages>0&&(
              <div>
                <div style={{background:t.border,borderRadius:"4px",height:"6px",overflow:"hidden"}}>
                  <div style={{width:`${pct}%`,height:"100%",background:book.status==="done"?t.green:t.blue,borderRadius:"4px",transition:"width 0.3s"}}/>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",marginTop:"0.2rem"}}>
                  <span style={{color:t.textTertiary,fontSize:"0.62rem"}}>Pág. {book.currentPage} / {book.totalPages}</span>
                  <span style={{color:t.textSecondary,fontSize:"0.62rem",fontWeight:700}}>{pct}%</span>
                </div>
              </div>
            )}

            {book.status!=="done"&&(
              isEditing?(
                <div style={{display:"flex",gap:"0.4rem",alignItems:"center"}}>
                  <input autoFocus value={editPage} onChange={e=>setEditPage(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")updatePage(book.id,editPage);if(e.key==="Escape"){setEditId(null);setEditPage("");}}} type="number" placeholder="Página actual" style={{flex:1,background:t.bg,border:`1px solid ${t.blue}`,borderRadius:"8px",padding:"0.4rem 0.6rem",color:t.text,fontSize:"0.82rem",outline:"none"}}/>
                  <button onClick={()=>updatePage(book.id,editPage)} style={{background:t.blue,border:"none",borderRadius:"8px",padding:"0.4rem 0.7rem",color:"#fff",fontWeight:700,fontSize:"0.75rem",cursor:"pointer"}}>OK</button>
                  <button onClick={()=>{setEditId(null);setEditPage("");}} style={{background:t.accentMuted,border:"none",borderRadius:"8px",padding:"0.4rem 0.6rem",color:t.textSecondary,fontSize:"0.75rem",cursor:"pointer"}}>✕</button>
                </div>
              ):(
                <button onClick={()=>{setEditId(book.id);setEditPage(String(book.currentPage));}} style={{background:t.accentMuted,border:`1px solid ${t.border}`,borderRadius:"8px",padding:"0.35rem",color:t.textSecondary,fontSize:"0.72rem",cursor:"pointer",textAlign:"center"}}>
                  📖 Actualizar página
                </button>
              )
            )}
            {book.status==="done"&&book.finishDate&&(
              <div style={{color:t.green,fontSize:"0.65rem",fontWeight:600}}>✅ Terminado el {new Date(book.finishDate+"T12:00:00").toLocaleDateString("es-ES",{day:"numeric",month:"short",year:"numeric"})}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── OBJETIVOS TAB ────────────────────────────────────────────────────────────
// Sección unificada: Objetivos (Macro/Meso/Micro) + Biblioteca de libros.
// Objetivos siguen la estructura: Macro (fin) → Mesos (hitos) → Micros (acciones).
const GOAL_COLORS=["blue","green","gold","purple"];
const GOAL_COLOR_MAP={blue:"#3B82F6",green:"#22C55E",gold:"#F59E0B",purple:"#A78BFA"};

// GOAL_TEMPLATES: plantillas para objetivos comunes
const GOAL_TEMPLATES=[
  {label:"🚗 Carnet de conducir",title:"Sacar el carnet de conducir",macro:"Conducir solo con total seguridad",color:"blue",goalType:"error_tracker",
   mesos:["Dominar la teoría","Aprobar el examen teórico","Completar las prácticas","Aprobar el examen práctico"],
   micros:[]},
  {label:"🥋 Muay Thai / Box",title:"500 horas de Muay Thai",macro:"Llegar a 500 horas de práctica acumulada",color:"gold",goalType:"hours_goal",targetHours:500,
   mesos:["Primeras 50h — bases","100h — técnica sólida","250h — nivel intermedio","500h — avanzado"],
   micros:[]},
  {label:"🤼 Lucha / Grappling",title:"300 horas de Lucha",macro:"Llegar a 300 horas de práctica acumulada",color:"purple",goalType:"hours_goal",targetHours:300,
   mesos:["Primeras 30h — bases","75h — patrones básicos","150h — nivel intermedio","300h — avanzado"],
   micros:[]},
  {label:"💪 Físico/deporte",title:"Mejorar el físico",macro:"Bajar grasa y ganar músculo",color:"green",goalType:"default",
   mesos:["Establecer rutina","Primer mes constante","Cambio visible","Objetivo de peso/fuerza"],
   micros:[{label:"Entrenar",type:"bool",target:1,unit:""},{label:"Calorías objetivo",type:"bool",target:1,unit:""}]},
  {label:"📚 Aprender algo nuevo",title:"",macro:"",color:"purple",goalType:"default",
   mesos:["Bases cubiertas","Nivel intermedio","Nivel avanzado","Aplicarlo en la práctica"],
   micros:[{label:"Estudiar",type:"min",target:30,unit:"min"},{label:"Práctica",type:"min",target:20,unit:"min"}]},
];

// Calcula racha de días consecutivos completados, soporta los tres tipos de objetivo
function calcStreak(goal) {
  const log=goal.microLog||{};
  const gtype=goal.goalType||"default";
  let streak=0;
  const d=new Date();
  while(streak<365){
    const k=d.toISOString().split("T")[0];
    const day=log[k]||{};
    let done=false;
    if(gtype==="error_tracker") done=!!day.done;
    else if(gtype==="hours_goal") done=(parseFloat(day.hours)||0)>0;
    else{
      const micros=goal.micros||[];
      if(!micros.length) break;
      done=micros.every(m=>{const v=day[m.id];return m.type==="bool"?!!v:(parseFloat(v)||0)>=(m.target||1);});
    }
    if(!done) break;
    streak++;
    d.setDate(d.getDate()-1);
  }
  return streak;
}

// Nivel XP para objetivos default y error_tracker (días completados como XP)
function calcGoalLevel(xp) {
  const LEVELS=[
    {min:0,  label:"Recluta",      icon:"🌱", next:3},
    {min:3,  label:"Aprendiz",     icon:"⚡", next:7},
    {min:7,  label:"Constante",    icon:"🔵", next:21},
    {min:21, label:"Disciplinado", icon:"💪", next:60},
    {min:60, label:"Élite",        icon:"🔥", next:90},
    {min:90, label:"Leyenda",      icon:"🏆", next:null},
  ];
  const lvl=LEVELS.slice().reverse().find(l=>xp>=l.min)||LEVELS[0];
  const idx=LEVELS.indexOf(lvl);
  const nextLvl=LEVELS[idx+1]||null;
  const pct=nextLvl?Math.round((xp-lvl.min)/(nextLvl.min-lvl.min)*100):100;
  return{...lvl,pct,idx};
}

// Nivel por porcentaje de horas completadas (cinturón estilo artes marciales)
function calcHoursLevel(pct) {
  const LEVELS=[
    {pct:0,  label:"Principiante",icon:"🌱",nextPct:10},
    {pct:10, label:"Blanco",      icon:"⚪",nextPct:25},
    {pct:25, label:"Amarillo",    icon:"🟡",nextPct:40},
    {pct:40, label:"Naranja",     icon:"🟠",nextPct:55},
    {pct:55, label:"Verde",       icon:"🟢",nextPct:70},
    {pct:70, label:"Azul",        icon:"🔵",nextPct:85},
    {pct:85, label:"Marrón",      icon:"🟤",nextPct:100},
    {pct:100,label:"Negro",       icon:"⚫",nextPct:null},
  ];
  const lvl=LEVELS.slice().reverse().find(l=>pct>=l.pct)||LEVELS[0];
  const idx=LEVELS.indexOf(lvl);
  const nextLvl=LEVELS[idx+1]||null;
  const levelPct=nextLvl?Math.min(100,Math.round((pct-lvl.pct)/(nextLvl.pct-lvl.pct)*100)):100;
  return{...lvl,levelPct,nextLabel:nextLvl?.label||null};
}

function GoalForm({initial,onSave,onCancel,t}) {
  const isEdit=!!initial;
  const def={title:"",macro:"",color:"blue",mesoInputs:["","",""],microInputs:[{label:"",type:"bool",target:"",unit:""}]};
  const[title,setTitle]=useState(initial?.title||def.title);
  const[macro,setMacro]=useState(initial?.macro||def.macro);
  const[color,setColor]=useState(initial?.color||def.color);
  const[goalType,setGoalType]=useState(initial?.goalType||"default");
  const[targetHours,setTargetHours]=useState(String(initial?.targetHours||""));
  const[mesoInputs,setMesoInputs]=useState(
    initial?.mesos?.length ? initial.mesos.map(m=>m.label) : def.mesoInputs
  );
  const[microInputs,setMicroInputs]=useState(
    initial?.micros?.length
      ? initial.micros.map(m=>({label:m.label,type:m.type||"bool",target:String(m.target||""),unit:m.unit||""}))
      : def.microInputs
  );
  const[showTemplates,setShowTemplates]=useState(false);

  const applyTemplate=tpl=>{
    setTitle(tpl.title);setMacro(tpl.macro);setColor(tpl.color);
    setGoalType(tpl.goalType||"default");
    if(tpl.targetHours) setTargetHours(String(tpl.targetHours));
    setMesoInputs([...tpl.mesos,"",""]);
    setMicroInputs((tpl.micros||[]).length>0?tpl.micros.map(m=>({...m,target:String(m.target)})):[{label:"",type:"bool",target:"",unit:""}]);
    setShowTemplates(false);
  };

  const setMicro=(i,field,val)=>setMicroInputs(p=>{const n=[...p];n[i]={...n[i],[field]:val};return n;});

  const save=()=>{
    if(!title.trim())return;
    const mesos=mesoInputs.filter(m=>m.trim()).map((m,i)=>({id:(initial?.mesos?.[i]?.id||i+1),label:m.trim(),done:initial?.mesos?.[i]?.done||false}));
    const micros=(goalType==="error_tracker"||goalType==="hours_goal")?[]:microInputs.filter(m=>m.label.trim()).map((m,i)=>({
      id:(initial?.micros?.[i]?.id||Date.now()+i),
      label:m.label.trim(),
      type:m.type||"bool",
      target:m.type==="bool"?1:parseFloat(m.target)||1,
      unit:m.unit.trim()
    }));
    const base=initial||{id:Date.now(),logs:[],microLog:{},status:"active",startDate:today()};
    onSave({...base,title:title.trim(),macro:macro.trim(),mesos,micros,color,goalType,
      ...(goalType==="hours_goal"&&{targetHours:parseFloat(targetHours)||100})
    });
  };

  const TYPE_LABELS={bool:"✓ Hecho",min:"⏱ Minutos",count:"🔢 Cantidad"};
  const GTYPE_OPTS=[{k:"default",icon:"🎯",label:"Estándar"},{k:"error_tracker",icon:"✏️",label:"Con errores"},{k:"hours_goal",icon:"⏱",label:"Meta horas"}];

  return(
    <div style={{background:t.surface,border:`1px solid ${t.border}`,borderRadius:"14px",padding:"1rem",display:"flex",flexDirection:"column",gap:"0.7rem"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div style={{color:t.text,fontWeight:700,fontSize:"0.88rem"}}>{isEdit?"Editar objetivo":"Nuevo objetivo"}</div>
        {!isEdit&&<button onClick={()=>setShowTemplates(p=>!p)} style={{background:t.accentMuted,border:`1px solid ${t.border}`,borderRadius:"7px",padding:"0.2rem 0.55rem",color:t.textSecondary,fontSize:"0.68rem",cursor:"pointer"}}>📋 Plantillas</button>}
      </div>

      {showTemplates&&(
        <div style={{display:"flex",flexDirection:"column",gap:"0.3rem"}}>
          {GOAL_TEMPLATES.map((tpl,i)=>(
            <button key={i} onClick={()=>applyTemplate(tpl)} style={{background:t.surfaceHover,border:`1px solid ${t.border}`,borderRadius:"8px",padding:"0.45rem 0.7rem",color:t.text,fontSize:"0.78rem",textAlign:"left",cursor:"pointer"}}>{tpl.label}</button>
          ))}
        </div>
      )}

      <input value={title} onChange={e=>setTitle(e.target.value)} placeholder="¿Qué quieres conseguir? *" style={{background:t.bg,border:`1px solid ${t.border}`,borderRadius:"8px",padding:"0.5rem 0.7rem",color:t.text,fontSize:"0.82rem",outline:"none"}}/>
      <input value={macro} onChange={e=>setMacro(e.target.value)} placeholder="El fin último (ej. Tener el carnet)" style={{background:t.bg,border:`1px solid ${t.border}`,borderRadius:"8px",padding:"0.5rem 0.7rem",color:t.text,fontSize:"0.82rem",outline:"none"}}/>

      <div>
        <div style={{color:t.textTertiary,fontSize:"0.65rem",marginBottom:"0.35rem",fontWeight:600}}>TIPO DE OBJETIVO</div>
        <div style={{display:"flex",gap:"0.3rem"}}>
          {GTYPE_OPTS.map(({k,icon,label})=>(
            <button key={k} onClick={()=>setGoalType(k)} style={{flex:1,background:goalType===k?t.accent:t.accentMuted,border:`1px solid ${goalType===k?t.accent:t.border}`,borderRadius:"8px",padding:"0.38rem 0.2rem",color:goalType===k?t.bg:t.textSecondary,fontSize:"0.7rem",fontWeight:goalType===k?700:500,cursor:"pointer"}}>
              {icon} {label}
            </button>
          ))}
        </div>
        {goalType==="error_tracker"&&<div style={{color:t.textTertiary,fontSize:"0.62rem",marginTop:"0.3rem"}}>Marca si practicaste cada día y apunta los errores. Se calcula la media automáticamente.</div>}
        {goalType==="hours_goal"&&<div style={{color:t.textTertiary,fontSize:"0.62rem",marginTop:"0.3rem"}}>Acumula horas hacia una meta y sube de nivel conforme progresas.</div>}
      </div>

      {goalType==="hours_goal"&&(
        <div style={{display:"flex",gap:"0.5rem",alignItems:"center"}}>
          <span style={{color:t.textTertiary,fontSize:"0.72rem",flexShrink:0}}>Meta de horas:</span>
          <input type="number" value={targetHours} onChange={e=>setTargetHours(e.target.value)} placeholder="ej. 500" style={{flex:1,background:t.bg,border:`1px solid ${t.border}`,borderRadius:"8px",padding:"0.4rem 0.6rem",color:t.text,fontSize:"0.82rem",outline:"none"}}/>
          <span style={{color:t.textTertiary,fontSize:"0.72rem"}}>horas</span>
        </div>
      )}

      <div>
        <div style={{color:t.textTertiary,fontSize:"0.65rem",marginBottom:"0.35rem",fontWeight:600}}>HITOS (el camino dividido en bloques)</div>
        {mesoInputs.map((v,i)=>(
          <input key={i} value={v} onChange={e=>setMesoInputs(p=>{const n=[...p];n[i]=e.target.value;return n;})} placeholder={`Hito ${i+1}`} style={{display:"block",width:"100%",boxSizing:"border-box",background:t.bg,border:`1px solid ${t.border}`,borderRadius:"8px",padding:"0.4rem 0.6rem",color:t.text,fontSize:"0.78rem",outline:"none",marginBottom:"0.3rem"}}/>
        ))}
        <button onClick={()=>setMesoInputs(p=>[...p,""])} style={{background:t.accentMuted,border:"none",borderRadius:"6px",padding:"0.2rem 0.5rem",color:t.textSecondary,fontSize:"0.68rem",cursor:"pointer"}}>+ Hito</button>
      </div>

      {goalType==="default"&&(
        <div>
          <div style={{color:t.textTertiary,fontSize:"0.65rem",marginBottom:"0.35rem",fontWeight:600}}>ACCIONES DIARIAS (qué haces cada día)</div>
          {microInputs.map((m,i)=>(
            <div key={i} style={{display:"flex",gap:"0.3rem",marginBottom:"0.4rem",alignItems:"center",flexWrap:"wrap"}}>
              <input value={m.label} onChange={e=>setMicro(i,"label",e.target.value)} placeholder="Acción…" style={{flex:2,minWidth:"80px",background:t.bg,border:`1px solid ${t.border}`,borderRadius:"8px",padding:"0.38rem 0.55rem",color:t.text,fontSize:"0.76rem",outline:"none"}}/>
              <select value={m.type} onChange={e=>setMicro(i,"type",e.target.value)} style={{background:t.bg,border:`1px solid ${t.border}`,borderRadius:"8px",padding:"0.38rem 0.3rem",color:t.text,fontSize:"0.68rem",outline:"none"}}>
                {Object.entries(TYPE_LABELS).map(([k,v])=><option key={k} value={k}>{v}</option>)}
              </select>
              {m.type!=="bool"&&<>
                <input value={m.target} onChange={e=>setMicro(i,"target",e.target.value)} placeholder="Meta" type="number" style={{width:"48px",background:t.bg,border:`1px solid ${t.border}`,borderRadius:"8px",padding:"0.38rem 0.3rem",color:t.text,fontSize:"0.76rem",outline:"none",textAlign:"center"}}/>
                <input value={m.unit} onChange={e=>setMicro(i,"unit",e.target.value)} placeholder="unidad" style={{width:"52px",background:t.bg,border:`1px solid ${t.border}`,borderRadius:"8px",padding:"0.38rem 0.3rem",color:t.text,fontSize:"0.68rem",outline:"none"}}/>
              </>}
              {microInputs.length>1&&<button onClick={()=>setMicroInputs(p=>p.filter((_,j)=>j!==i))} style={{background:"transparent",border:"none",color:t.red,cursor:"pointer",fontSize:"0.75rem",padding:"0.1rem"}}>✕</button>}
            </div>
          ))}
          <button onClick={()=>setMicroInputs(p=>[...p,{label:"",type:"bool",target:"",unit:""}])} style={{background:t.accentMuted,border:"none",borderRadius:"6px",padding:"0.2rem 0.5rem",color:t.textSecondary,fontSize:"0.68rem",cursor:"pointer"}}>+ Acción</button>
        </div>
      )}

      <div style={{display:"flex",gap:"0.4rem",alignItems:"center"}}>
        <span style={{color:t.textTertiary,fontSize:"0.68rem"}}>Color:</span>
        {GOAL_COLORS.map(c=>(
          <div key={c} onClick={()=>setColor(c)} style={{width:18,height:18,borderRadius:"50%",background:GOAL_COLOR_MAP[c],border:`2px solid ${color===c?"#fff":GOAL_COLOR_MAP[c]}`,cursor:"pointer",boxShadow:color===c?`0 0 0 2px ${GOAL_COLOR_MAP[c]}`:"none"}}/>
        ))}
      </div>
      <div style={{display:"flex",gap:"0.5rem"}}>
        <button onClick={save} style={{flex:1,background:t.accent,border:"none",borderRadius:"8px",padding:"0.55rem",color:t.bg,fontWeight:700,fontSize:"0.82rem",cursor:"pointer"}}>Guardar</button>
        <button onClick={onCancel} style={{flex:1,background:t.accentMuted,border:`1px solid ${t.border}`,borderRadius:"8px",padding:"0.55rem",color:t.textSecondary,fontWeight:600,fontSize:"0.82rem",cursor:"pointer"}}>Cancelar</button>
      </div>
    </div>
  );
}

// Mensaje motivacional según estado del objetivo — también usado por GoalCard
function goalMotivation(streak,pct,allDoneToday,daysActive) {
  if(allDoneToday&&streak>0) return {msg:"✅ Acciones de hoy completadas. Eso es todo lo que necesitas hacer.",color:"green"};
  if(streak>=30) return {msg:`🏆 ¡${streak} días de racha! Esto ya es disciplina de élite. El hábito es automático.`,color:"gold"};
  if(streak>=14) return {msg:`🔥 ¡${streak} días seguidos! El hábito ya forma parte de tu identidad.`,color:"gold"};
  if(streak>=7)  return {msg:`💪 Una semana de racha. El cerebro ya tiene un circuito nuevo formándose.`,color:"blue"};
  if(streak>=3)  return {msg:`⚡ ${streak} días seguidos. El hábito empieza a solidificarse.`,color:"blue"};
  if(pct>=80)    return {msg:"¡Casi lo tienes! Los últimos pasos son los más importantes. No pares ahora.",color:"gold"};
  if(pct>=50)    return {msg:"A mitad del camino. La parte más difícil ya está hecha.",color:"blue"};
  if(daysActive<=2) return {msg:"El primer paso ya está dado. Cada día que cumples es un ladrillo.",color:"blue"};
  return {msg:"Los fallos forman parte del proceso. Lo que cuenta es retomar hoy.",color:"red"};
}

function GoalCard({goal,onUpdate,onDelete,t}) {
  const[expanded,setExpanded]=useState(false);
  const[editing,setEditing]=useState(false);
  const[logNote,setLogNote]=useState("");
  const[hoursInput,setHoursInput]=useState("");

  const gtype=goal.goalType||"default";
  const color=GOAL_COLOR_MAP[goal.color]||GOAL_COLOR_MAP.blue;
  const tk=today();
  const microLog=goal.microLog||{};
  const todayLog=microLog[tk]||{};
  const logDates=Object.keys(microLog);

  // ── Default type ──────────────────────────────────────────────────────────
  const mesoDone=goal.mesos.filter(m=>m.done).length;
  const mesoPct=goal.mesos.length>0?Math.round(mesoDone/goal.mesos.length*100):0;
  const microsDoneToday=gtype==="default"?goal.micros.filter(m=>{
    const v=todayLog[m.id];return m.type==="bool"?!!v:(parseFloat(v)||0)>=(m.target||1);
  }).length:0;
  const daysDone=gtype==="default"?logDates.filter(d=>{
    const day=microLog[d];return goal.micros.every(m=>{const v=day[m.id];return m.type==="bool"?!!v:(parseFloat(v)||0)>=(m.target||1);});
  }).length:0;
  const successRate=logDates.length>0?Math.round(daysDone/logDates.length*100):0;
  const totals={};
  if(gtype==="default") goal.micros.filter(m=>m.type!=="bool").forEach(m=>{
    totals[m.id]=logDates.reduce((s,d)=>s+(parseFloat((microLog[d]||{})[m.id])||0),0);
  });

  // ── Error tracker type ────────────────────────────────────────────────────
  const errDays=gtype==="error_tracker"?logDates.filter(d=>(microLog[d]||{}).done):[];
  const errValues=errDays.map(d=>parseFloat((microLog[d]||{}).errors)||0);
  const errAvg=errDays.length>0?(errValues.reduce((a,b)=>a+b,0)/errDays.length).toFixed(1):null;
  const errMin=errDays.length>0?Math.min(...errValues):null;
  const errZeroDays=errValues.filter(e=>e===0).length;
  const todayDoneET=gtype==="error_tracker"&&!!todayLog.done;
  const errChartDays=errDays.slice().sort().slice(-14);
  const errChartMax=errChartDays.length>0?Math.max(...errChartDays.map(d=>(microLog[d]||{}).errors||0),1):1;

  // ── Hours goal type ───────────────────────────────────────────────────────
  const totalHoursAccum=gtype==="hours_goal"?logDates.reduce((s,d)=>s+(parseFloat((microLog[d]||{}).hours)||0),0):0;
  const targetH=parseFloat(goal.targetHours)||1;
  const hoursPct=gtype==="hours_goal"?Math.min(100,Math.round(totalHoursAccum/targetH*100)):0;
  const sessionCount=gtype==="hours_goal"?logDates.filter(d=>(parseFloat((microLog[d]||{}).hours)||0)>0).length:0;
  const avgPerSession=sessionCount>0?(totalHoursAccum/sessionCount).toFixed(1):0;
  const todayHours=gtype==="hours_goal"?(parseFloat(todayLog.hours)||0):0;

  // ── Streak + levels ───────────────────────────────────────────────────────
  const streak=calcStreak(goal);
  const xpForLevel=gtype==="default"?daysDone:errDays.length;
  const level=gtype!=="hours_goal"?calcGoalLevel(xpForLevel):null;
  const hoursLevel=gtype==="hours_goal"?calcHoursLevel(hoursPct):null;

  // ── Unified today / motivation ────────────────────────────────────────────
  const todayDone=gtype==="error_tracker"?todayDoneET:gtype==="hours_goal"?todayHours>0:(goal.micros.length>0&&microsDoneToday===goal.micros.length);
  const activeDays=gtype==="default"?logDates.length:gtype==="error_tracker"?errDays.length:sessionCount;
  const motivation=goalMotivation(streak,mesoPct,todayDone,activeDays);
  const motivColor={green:t.green,blue:t.blue,gold:t.gold,red:t.red}[motivation.color]||t.blue;

  // ── Handlers ──────────────────────────────────────────────────────────────
  const toggleMeso=(id)=>onUpdate({...goal,mesos:goal.mesos.map(m=>m.id===id?{...m,done:!m.done}:m)});
  const setMicroVal=(id,val)=>{const ml={...microLog};ml[tk]={...(ml[tk]||{}),[id]:val};onUpdate({...goal,microLog:ml});};
  const setETField=(field,val)=>{const ml={...microLog};ml[tk]={...(ml[tk]||{}),[field]:val};onUpdate({...goal,microLog:ml});};
  const addHours=(h)=>{const ml={...microLog};ml[tk]={...(ml[tk]||{}),hours:Math.round((todayHours+h)*100)/100};onUpdate({...goal,microLog:ml});};
  const saveHoursInput=()=>{const h=parseFloat(hoursInput)||0;if(h>0){addHours(h);setHoursInput("");}};
  const addLog=()=>{
    if(!logNote.trim())return;
    onUpdate({...goal,logs:[{id:Date.now(),date:tk,note:logNote.trim()},...(goal.logs||[])]});
    setLogNote("");
  };

  if(editing) return(<GoalForm initial={goal} onSave={g=>{onUpdate(g);setEditing(false);}} onCancel={()=>setEditing(false)} t={t}/>);

  const lvlBadge=level||hoursLevel;
  const todayIndicator=gtype==="error_tracker"
    ?<span style={{fontSize:"0.62rem",color:todayDoneET?t.green:t.red,fontWeight:700}}>{todayDoneET?"Hoy ✓":"⚠️ Sin practicar"}</span>
    :gtype==="hours_goal"&&todayHours>0?<span style={{fontSize:"0.62rem",color:t.green,fontWeight:600}}>+{todayHours}h hoy</span>
    :gtype==="default"&&goal.micros.length>0?<span style={{fontSize:"0.62rem",color:microsDoneToday===goal.micros.length?t.green:t.textTertiary,fontWeight:600}}>Hoy: {microsDoneToday}/{goal.micros.length}</span>
    :null;

  return(
    <div style={{background:t.surface,border:`1px solid ${t.border}`,borderRadius:"14px",overflow:"hidden",borderLeft:`3px solid ${color}`}}>

      {/* ── Collapsed header ── */}
      <div onClick={()=>setExpanded(p=>!p)} style={{padding:"0.85rem 1rem",cursor:"pointer",display:"flex",gap:"0.6rem",alignItems:"flex-start"}}>
        {gtype==="hours_goal"&&(
          <div style={{flexShrink:0,position:"relative",width:44,height:44}}>
            <svg width={44} height={44} style={{transform:"rotate(-90deg)"}}>
              <circle cx={22} cy={22} r={18} fill="none" stroke={t.border} strokeWidth={4}/>
              <circle cx={22} cy={22} r={18} fill="none" stroke={color} strokeWidth={4}
                strokeDasharray={String(2*Math.PI*18)}
                strokeDashoffset={String(2*Math.PI*18*(1-hoursPct/100))}
                strokeLinecap="round"/>
            </svg>
            <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:"0.5rem",fontWeight:800,color:t.text}}>{hoursPct}%</div>
          </div>
        )}
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:"flex",alignItems:"center",gap:"0.45rem",flexWrap:"wrap"}}>
            <span style={{color:t.text,fontWeight:700,fontSize:"0.88rem"}}>{goal.title}</span>
            {goal.status==="done"&&<span style={{fontSize:"0.6rem",color:t.green,background:t.greenBg,padding:"0.1rem 0.4rem",borderRadius:"5px",fontWeight:700}}>✓ Completado</span>}
            {lvlBadge&&<span style={{fontSize:"0.6rem",padding:"0.12rem 0.45rem",borderRadius:"5px",fontWeight:700,background:color+"22",color}}>{lvlBadge.icon} {lvlBadge.label}</span>}
          </div>
          {goal.macro&&<div style={{color:t.textSecondary,fontSize:"0.7rem",marginTop:"0.1rem"}}>🎯 {goal.macro}</div>}
          <div style={{display:"flex",gap:"0.55rem",marginTop:"0.3rem",flexWrap:"wrap",alignItems:"center"}}>
            {streak>0&&<span style={{fontSize:"0.65rem",color:t.gold,fontWeight:700}}>🔥 {streak}d racha</span>}
            {todayIndicator}
            {gtype==="hours_goal"&&<span style={{fontSize:"0.62rem",color:t.textTertiary}}>{totalHoursAccum.toFixed(1)}h / {targetH}h</span>}
          </div>
          {gtype!=="hours_goal"&&goal.mesos.length>0&&(
            <div style={{marginTop:"0.4rem"}}>
              <div style={{background:t.border,borderRadius:"3px",height:"4px",overflow:"hidden"}}>
                <div style={{width:`${mesoPct}%`,height:"100%",background:color,borderRadius:"3px",transition:"width 0.3s"}}/>
              </div>
              <div style={{color:t.textTertiary,fontSize:"0.58rem",marginTop:"0.12rem"}}>{mesoDone}/{goal.mesos.length} hitos</div>
            </div>
          )}
        </div>
        <div style={{display:"flex",gap:"0.3rem",flexShrink:0,alignItems:"center"}}>
          <button onClick={e=>{e.stopPropagation();setEditing(true);setExpanded(false);}} style={{background:"transparent",border:"none",color:t.textTertiary,cursor:"pointer",fontSize:"0.68rem",padding:"0.1rem 0.2rem"}}>✏️</button>
          <button onClick={e=>{e.stopPropagation();onDelete();}} style={{background:"transparent",border:"none",color:t.textTertiary,cursor:"pointer",fontSize:"0.75rem"}}>✕</button>
          <span style={{color:t.textTertiary,fontSize:"0.72rem"}}>{expanded?"▲":"▼"}</span>
        </div>
      </div>

      {expanded&&(
        <div style={{borderTop:`1px solid ${t.border}`,padding:"0.8rem 1rem",display:"flex",flexDirection:"column",gap:"0.85rem"}}>

          {/* Motivación */}
          <div style={{background:motivColor+"18",border:`1px solid ${motivColor}44`,borderRadius:"10px",padding:"0.55rem 0.8rem"}}>
            <span style={{color:motivColor,fontSize:"0.75rem",fontWeight:600,lineHeight:1.4}}>{motivation.msg}</span>
          </div>

          {/* ══ DEFAULT ══ */}
          {gtype==="default"&&<>
            {logDates.length>0&&(
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:"0.4rem"}}>
                {[{label:"Días activo",v:logDates.length,icon:"📅"},{label:"Tasa éxito",v:`${successRate}%`,icon:"🎯"},{label:"Racha",v:streak>0?`${streak}d`:"—",icon:"🔥"}].map(({label,v,icon})=>(
                  <div key={label} style={{background:t.surfaceHover,border:`1px solid ${t.border}`,borderRadius:"8px",padding:"0.4rem",textAlign:"center"}}>
                    <div style={{fontSize:"0.9rem"}}>{icon}</div>
                    <div style={{color:t.text,fontWeight:700,fontSize:"0.82rem"}}>{v}</div>
                    <div style={{color:t.textTertiary,fontSize:"0.55rem"}}>{label}</div>
                  </div>
                ))}
              </div>
            )}
            {Object.keys(totals).length>0&&(
              <div style={{display:"flex",gap:"0.4rem",flexWrap:"wrap"}}>
                {goal.micros.filter(m=>m.type!=="bool").map(m=>{
                  const total=totals[m.id]||0;
                  const display=m.type==="min"?`${Math.floor(total/60)}h ${Math.round(total%60)}m`:`${total} ${m.unit||""}`;
                  return(<div key={m.id} style={{background:color+"18",border:`1px solid ${color}44`,borderRadius:"8px",padding:"0.3rem 0.6rem"}}>
                    <span style={{color:t.textSecondary,fontSize:"0.65rem"}}>{m.label}: </span>
                    <span style={{color,fontWeight:700,fontSize:"0.75rem"}}>{display} acumulados</span>
                  </div>);
                })}
              </div>
            )}
            {level&&(
              <div style={{background:t.surfaceHover,border:`1px solid ${t.border}`,borderRadius:"10px",padding:"0.55rem 0.8rem"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"0.3rem"}}>
                  <span style={{color:t.text,fontSize:"0.75rem",fontWeight:700}}>{level.icon} {level.label}</span>
                  <span style={{color:t.textTertiary,fontSize:"0.65rem"}}>{xpForLevel} días completados</span>
                </div>
                {level.next&&<>
                  <div style={{background:t.border,borderRadius:"3px",height:"4px",overflow:"hidden"}}>
                    <div style={{width:`${level.pct}%`,height:"100%",background:color,borderRadius:"3px",transition:"width 0.5s"}}/>
                  </div>
                  <div style={{color:t.textTertiary,fontSize:"0.58rem",marginTop:"0.2rem"}}>Próximo nivel: {level.next} días</div>
                </>}
              </div>
            )}
            {goal.micros.length>0&&(
              <div>
                <div style={{color:t.textTertiary,fontSize:"0.62rem",fontWeight:700,marginBottom:"0.4rem",textTransform:"uppercase",letterSpacing:"0.06em"}}>Hoy — Acciones diarias</div>
                <div style={{display:"flex",flexDirection:"column",gap:"0.45rem"}}>
                  {goal.micros.map(m=>{
                    const val=todayLog[m.id];
                    const done=m.type==="bool"?!!val:(parseFloat(val)||0)>=(m.target||1);
                    return(
                      <div key={m.id} style={{display:"flex",alignItems:"center",gap:"0.55rem",padding:"0.45rem 0.65rem",borderRadius:"10px",background:done?t.greenBg:t.surfaceHover,border:`1px solid ${done?t.green+"40":t.border}`}}>
                        <div style={{flex:1,minWidth:0}}>
                          <span style={{color:done?t.green:t.text,fontSize:"0.8rem",fontWeight:done?600:500}}>{m.label}</span>
                          {m.type!=="bool"&&<span style={{color:t.textTertiary,fontSize:"0.65rem",marginLeft:"0.35rem"}}>meta: {m.target} {m.unit}</span>}
                        </div>
                        {m.type==="bool"
                          ?<button onClick={()=>setMicroVal(m.id,val?0:1)} style={{background:done?t.green:t.accentMuted,border:`1px solid ${done?t.green:t.border}`,borderRadius:"7px",padding:"0.3rem 0.6rem",color:done?"#fff":t.textSecondary,fontSize:"0.72rem",fontWeight:700,cursor:"pointer"}}>{done?"✓ Hecho":"Marcar"}</button>
                          :<div style={{display:"flex",alignItems:"center",gap:"0.3rem"}}>
                            <input type="number" value={val||""} onChange={e=>setMicroVal(m.id,e.target.value)} placeholder="0" style={{width:"48px",background:t.bg,border:`1px solid ${done?t.green:t.border}`,borderRadius:"7px",padding:"0.3rem",color:done?t.green:t.text,fontSize:"0.78rem",outline:"none",textAlign:"center",fontWeight:done?700:400}}/>
                            <span style={{color:t.textTertiary,fontSize:"0.65rem"}}>{m.unit||"min"}</span>
                          </div>
                        }
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>}

          {/* ══ ERROR TRACKER ══ */}
          {gtype==="error_tracker"&&<>
            <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:"0.4rem"}}>
              {[
                {label:"Días practicados",v:errDays.length,icon:"📅",c:t.blue},
                {label:"Media errores",v:errAvg!=null?errAvg:"—",icon:"📊",c:errAvg!=null&&parseFloat(errAvg)===0?t.green:t.gold},
                {label:"Mejor sesión",v:errMin!=null?`${errMin} err`:"—",icon:"🏆",c:t.green},
                {label:"Sesiones 0 errores",v:errZeroDays,icon:"✨",c:t.green},
              ].map(({label,v,icon,c})=>(
                <div key={label} style={{background:t.surfaceHover,border:`1px solid ${t.border}`,borderRadius:"8px",padding:"0.4rem",textAlign:"center"}}>
                  <div style={{fontSize:"0.9rem"}}>{icon}</div>
                  <div style={{color:c,fontWeight:700,fontSize:"0.88rem"}}>{v}</div>
                  <div style={{color:t.textTertiary,fontSize:"0.55rem"}}>{label}</div>
                </div>
              ))}
            </div>
            {level&&errDays.length>0&&(
              <div style={{background:t.surfaceHover,border:`1px solid ${t.border}`,borderRadius:"10px",padding:"0.55rem 0.8rem"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"0.3rem"}}>
                  <span style={{color:t.text,fontSize:"0.75rem",fontWeight:700}}>{level.icon} {level.label}</span>
                  <span style={{color:t.textTertiary,fontSize:"0.65rem"}}>{errDays.length} días de práctica</span>
                </div>
                {level.next&&<>
                  <div style={{background:t.border,borderRadius:"3px",height:"4px",overflow:"hidden"}}>
                    <div style={{width:`${level.pct}%`,height:"100%",background:color,borderRadius:"3px",transition:"width 0.5s"}}/>
                  </div>
                  <div style={{color:t.textTertiary,fontSize:"0.58rem",marginTop:"0.2rem"}}>Próximo nivel: {level.next} días</div>
                </>}
              </div>
            )}
            {errChartDays.length>=3&&(
              <div>
                <div style={{color:t.textTertiary,fontSize:"0.62rem",fontWeight:700,marginBottom:"0.4rem",textTransform:"uppercase",letterSpacing:"0.06em"}}>Historial de errores — últimas {errChartDays.length} sesiones</div>
                <div style={{display:"flex",alignItems:"flex-end",gap:"3px",height:"40px"}}>
                  {errChartDays.map(d=>{
                    const e=(microLog[d]||{}).errors||0;
                    const h=Math.round((e/errChartMax)*32)+4;
                    const c=e===0?t.green:e<=2?t.gold:t.red;
                    return <div key={d} title={`${d}: ${e} errores`} style={{flex:1,height:`${h}px`,background:c,borderRadius:"2px 2px 0 0",cursor:"help"}}/>;
                  })}
                </div>
                <div style={{display:"flex",justifyContent:"space-between",color:t.textTertiary,fontSize:"0.5rem",marginTop:"0.1rem",borderTop:`1px solid ${t.border}`,paddingTop:"0.1rem"}}>
                  <span>más antiguo</span><span style={{color:t.green}}>■ 0 err</span><span style={{color:t.gold}}>■ 1-2</span><span style={{color:t.red}}>■ 3+</span><span>hoy →</span>
                </div>
              </div>
            )}
            <div>
              <div style={{color:t.textTertiary,fontSize:"0.62rem",fontWeight:700,marginBottom:"0.4rem",textTransform:"uppercase",letterSpacing:"0.06em"}}>Registro de hoy</div>
              <div style={{display:"flex",flexDirection:"column",gap:"0.45rem"}}>
                <div style={{display:"flex",alignItems:"center",gap:"0.55rem",padding:"0.45rem 0.65rem",borderRadius:"10px",background:todayDoneET?t.greenBg:t.surfaceHover,border:`1px solid ${todayDoneET?t.green+"40":t.border}`}}>
                  <span style={{flex:1,color:todayDoneET?t.green:t.text,fontSize:"0.8rem",fontWeight:todayDoneET?600:500}}>¿Practicaste hoy?</span>
                  <button onClick={()=>setETField("done",todayDoneET?0:1)} style={{background:todayDoneET?t.green:t.accentMuted,border:`1px solid ${todayDoneET?t.green:t.border}`,borderRadius:"7px",padding:"0.3rem 0.65rem",color:todayDoneET?"#fff":t.textSecondary,fontSize:"0.72rem",fontWeight:700,cursor:"pointer"}}>{todayDoneET?"✓ Sí":"Marcar"}</button>
                </div>
                {todayDoneET&&(
                  <div style={{display:"flex",alignItems:"center",gap:"0.55rem",padding:"0.45rem 0.65rem",borderRadius:"10px",background:t.surfaceHover,border:`1px solid ${t.border}`}}>
                    <span style={{flex:1,color:t.text,fontSize:"0.8rem"}}>Errores cometidos</span>
                    <input type="number" min="0" value={todayLog.errors||""} onChange={e=>setETField("errors",e.target.value)} placeholder="0" style={{width:"52px",background:t.bg,border:`1px solid ${t.border}`,borderRadius:"7px",padding:"0.3rem",color:t.text,fontSize:"0.82rem",outline:"none",textAlign:"center",fontWeight:700}}/>
                    <span style={{color:t.textTertiary,fontSize:"0.65rem"}}>errores</span>
                  </div>
                )}
                {todayDoneET&&(
                  <input value={todayLog.note||""} onChange={e=>setETField("note",e.target.value)} placeholder="Nota rápida del día (opcional)…" style={{background:t.bg,border:`1px solid ${t.border}`,borderRadius:"8px",padding:"0.4rem 0.6rem",color:t.text,fontSize:"0.76rem",outline:"none"}}/>
                )}
              </div>
            </div>
          </>}

          {/* ══ HOURS GOAL ══ */}
          {gtype==="hours_goal"&&<>
            <div style={{display:"flex",gap:"1rem",alignItems:"center",background:t.surfaceHover,border:`1px solid ${t.border}`,borderRadius:"12px",padding:"0.85rem"}}>
              <div style={{position:"relative",width:76,height:76,flexShrink:0}}>
                <svg width={76} height={76} style={{transform:"rotate(-90deg)"}}>
                  <circle cx={38} cy={38} r={32} fill="none" stroke={t.border} strokeWidth={6}/>
                  <circle cx={38} cy={38} r={32} fill="none" stroke={color} strokeWidth={6}
                    strokeDasharray={String(2*Math.PI*32)}
                    strokeDashoffset={String(2*Math.PI*32*(1-hoursPct/100))}
                    strokeLinecap="round"/>
                </svg>
                <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:"1px"}}>
                  <span style={{color:t.text,fontWeight:800,fontSize:"1rem",lineHeight:1}}>{hoursPct}%</span>
                  <span style={{color:t.textTertiary,fontSize:"0.45rem"}}>completado</span>
                </div>
              </div>
              <div style={{flex:1}}>
                <div style={{color:t.text,fontWeight:700,fontSize:"0.9rem"}}>{totalHoursAccum.toFixed(1)}h <span style={{color:t.textTertiary,fontWeight:400,fontSize:"0.78rem"}}>/ {targetH}h</span></div>
                {hoursLevel&&<div style={{marginTop:"0.3rem"}}><span style={{fontSize:"0.75rem",fontWeight:700,color,background:color+"22",padding:"0.15rem 0.55rem",borderRadius:"5px"}}>{hoursLevel.icon} {hoursLevel.label}</span></div>}
                {hoursLevel&&hoursLevel.nextPct&&<>
                  <div style={{background:t.border,borderRadius:"3px",height:"4px",overflow:"hidden",marginTop:"0.45rem"}}>
                    <div style={{width:`${hoursLevel.levelPct}%`,height:"100%",background:color,borderRadius:"3px",transition:"width 0.5s"}}/>
                  </div>
                  <div style={{color:t.textTertiary,fontSize:"0.58rem",marginTop:"0.15rem"}}>Próximo: {hoursLevel.nextLabel} — {Math.round(targetH*(hoursLevel.nextPct/100))}h</div>
                </>}
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:"0.4rem"}}>
              {[{label:"Total horas",v:`${totalHoursAccum.toFixed(1)}h`,icon:"⏱",c:color},{label:"Sesiones",v:sessionCount,icon:"📅",c:t.blue},{label:"Media/sesión",v:`${avgPerSession}h`,icon:"📊",c:t.textSecondary}].map(({label,v,icon,c})=>(
                <div key={label} style={{background:t.surfaceHover,border:`1px solid ${t.border}`,borderRadius:"8px",padding:"0.4rem",textAlign:"center"}}>
                  <div style={{fontSize:"0.9rem"}}>{icon}</div>
                  <div style={{color:c,fontWeight:700,fontSize:"0.82rem"}}>{v}</div>
                  <div style={{color:t.textTertiary,fontSize:"0.55rem"}}>{label}</div>
                </div>
              ))}
            </div>
            <div>
              <div style={{color:t.textTertiary,fontSize:"0.62rem",fontWeight:700,marginBottom:"0.4rem",textTransform:"uppercase",letterSpacing:"0.06em"}}>Añadir sesión de hoy</div>
              {todayHours>0&&<div style={{color:t.green,fontSize:"0.72rem",fontWeight:600,marginBottom:"0.35rem"}}>✓ {todayHours}h registradas hoy</div>}
              <div style={{display:"flex",gap:"0.4rem",alignItems:"center",marginBottom:"0.35rem"}}>
                <input type="number" min="0" step="0.25" value={hoursInput} onChange={e=>setHoursInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&saveHoursInput()} placeholder="ej. 1.5" style={{flex:1,background:t.bg,border:`1px solid ${t.border}`,borderRadius:"8px",padding:"0.4rem 0.6rem",color:t.text,fontSize:"0.82rem",outline:"none"}}/>
                <span style={{color:t.textTertiary,fontSize:"0.72rem"}}>h</span>
                <button onClick={saveHoursInput} style={{background:color,border:"none",borderRadius:"8px",padding:"0.4rem 0.7rem",color:"#fff",fontWeight:700,fontSize:"0.75rem",cursor:"pointer"}}>+ Añadir</button>
              </div>
              <div style={{display:"flex",gap:"0.3rem",flexWrap:"wrap"}}>
                {[0.5,1,1.5,2].map(h=>(
                  <button key={h} onClick={()=>addHours(h)} style={{background:t.accentMuted,border:`1px solid ${t.border}`,borderRadius:"6px",padding:"0.2rem 0.5rem",color:t.textSecondary,fontSize:"0.68rem",cursor:"pointer"}}>+{h}h</button>
                ))}
              </div>
            </div>
          </>}

          {/* Hitos (todos los tipos) */}
          {goal.mesos.length>0&&(
            <div>
              <div style={{color:t.textTertiary,fontSize:"0.62rem",fontWeight:700,marginBottom:"0.35rem",textTransform:"uppercase",letterSpacing:"0.06em"}}>Hitos del camino</div>
              <div style={{display:"flex",flexDirection:"column",gap:"0.3rem"}}>
                {goal.mesos.map(m=>(
                  <div key={m.id} onClick={()=>toggleMeso(m.id)} style={{display:"flex",alignItems:"center",gap:"0.5rem",cursor:"pointer",padding:"0.3rem 0"}}>
                    <div style={{width:16,height:16,borderRadius:"50%",border:`2px solid ${m.done?color:t.border}`,background:m.done?color:"transparent",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
                      {m.done&&<span style={{color:"#fff",fontSize:"0.5rem",fontWeight:800}}>✓</span>}
                    </div>
                    <span style={{color:m.done?t.textSecondary:t.text,fontSize:"0.8rem",textDecoration:m.done?"line-through":"none"}}>{m.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Log de notas (todos los tipos) */}
          <div>
            <div style={{color:t.textTertiary,fontSize:"0.62rem",fontWeight:700,marginBottom:"0.35rem",textTransform:"uppercase",letterSpacing:"0.06em"}}>Notas de progreso</div>
            <div style={{display:"flex",gap:"0.4rem",marginBottom:"0.4rem"}}>
              <input value={logNote} onChange={e=>setLogNote(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addLog()} placeholder="Anota cómo va hoy…" style={{flex:1,background:t.bg,border:`1px solid ${t.border}`,borderRadius:"8px",padding:"0.4rem 0.6rem",color:t.text,fontSize:"0.76rem",outline:"none"}}/>
              <button onClick={addLog} style={{background:color,border:"none",borderRadius:"8px",padding:"0.4rem 0.6rem",color:"#fff",fontWeight:700,fontSize:"0.72rem",cursor:"pointer"}}>+</button>
            </div>
            {(goal.logs||[]).slice(0,5).map(l=>(
              <div key={l.id} style={{display:"flex",gap:"0.5rem",padding:"0.25rem 0",borderTop:`1px solid ${t.borderLight}`}}>
                <span style={{color:t.textTertiary,fontSize:"0.62rem",flexShrink:0}}>{l.date}</span>
                <span style={{color:t.textSecondary,fontSize:"0.72rem"}}>{l.note}</span>
              </div>
            ))}
          </div>

          {goal.status!=="done"&&(
            <button onClick={()=>onUpdate({...goal,status:"done",finishDate:today()})} style={{background:t.greenBg,border:`1px solid ${t.green}`,borderRadius:"8px",padding:"0.4rem",color:t.green,fontSize:"0.75rem",fontWeight:700,cursor:"pointer"}}>
              ✓ Marcar objetivo como completado
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function ObjetivosTab({goals,setGoals,books,setBooks,t}) {
  const[subTab,setSubTab]=useState("goals");
  const[showAdd,setShowAdd]=useState(false);

  const activeGoals=goals.filter(g=>g.status==="active");
  const doneGoals=goals.filter(g=>g.status==="done");

  const addGoal=g=>{setGoals(p=>[g,...p]);setShowAdd(false);};
  const updateGoal=g=>setGoals(p=>p.map(x=>x.id===g.id?g:x));
  const deleteGoal=id=>{if(window.confirm("¿Eliminar este objetivo?"))setGoals(p=>p.filter(x=>x.id!==id));};

  return(
    <div style={{display:"flex",flexDirection:"column",gap:"1rem"}}>
      {/* Sub-nav */}
      <div style={{display:"flex",gap:"0",background:t.surfaceHover,borderRadius:"10px",padding:"3px",border:`1px solid ${t.border}`}}>
        {[{k:"goals",label:"🎯 Objetivos"},{k:"books",label:"📚 Libros"}].map(({k,label})=>(
          <button key={k} onClick={()=>setSubTab(k)} style={{flex:1,padding:"0.42rem",background:subTab===k?t.surface:"transparent",border:`1px solid ${subTab===k?t.border:"transparent"}`,borderRadius:"8px",color:subTab===k?t.text:t.textSecondary,fontWeight:subTab===k?700:500,fontSize:"0.78rem",cursor:"pointer"}}>
            {label}
          </button>
        ))}
      </div>

      {subTab==="books"&&<BooksTab books={books} setBooks={setBooks} t={t}/>}

      {subTab==="goals"&&(
        <>
          {/* Stats */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:"0.5rem"}}>
            {[{label:"Activos",v:activeGoals.length,icon:"🎯",c:t.blue},{label:"Completados",v:doneGoals.length,icon:"✅",c:t.green},{label:"Total",v:goals.length,icon:"📊",c:t.textSecondary}].map(({label,v,icon,c})=>(
              <div key={label} style={{background:t.surface,border:`1px solid ${t.border}`,borderRadius:"10px",padding:"0.6rem",textAlign:"center"}}>
                <div style={{fontSize:"1.2rem"}}>{icon}</div>
                <div style={{color:c,fontWeight:700,fontSize:"1rem"}}>{v}</div>
                <div style={{color:t.textTertiary,fontSize:"0.6rem"}}>{label}</div>
              </div>
            ))}
          </div>

          {showAdd
            ? <GoalForm onSave={g=>{addGoal(g);}} onCancel={()=>setShowAdd(false)} t={t}/>
            : <button onClick={()=>setShowAdd(true)} style={{background:t.accentMuted,border:`1px solid ${t.border}`,borderRadius:"10px",padding:"0.55rem",color:t.text,fontSize:"0.8rem",fontWeight:600,cursor:"pointer",textAlign:"center"}}>+ Nuevo objetivo</button>
          }

          {activeGoals.length===0&&!showAdd&&(
            <div style={{textAlign:"center",color:t.textTertiary,fontSize:"0.82rem",padding:"1.5rem 0"}}>Sin objetivos activos. ¡Añade uno! 🎯</div>
          )}
          {activeGoals.map(g=><GoalCard key={g.id} goal={g} onUpdate={updateGoal} onDelete={()=>deleteGoal(g.id)} t={t}/>)}

          {doneGoals.length>0&&(
            <details style={{marginTop:"0.25rem"}}>
              <summary style={{color:t.textTertiary,fontSize:"0.75rem",cursor:"pointer",padding:"0.3rem 0"}}>✅ Completados ({doneGoals.length})</summary>
              <div style={{display:"flex",flexDirection:"column",gap:"0.5rem",marginTop:"0.5rem"}}>
                {doneGoals.map(g=><GoalCard key={g.id} goal={g} onUpdate={updateGoal} onDelete={()=>deleteGoal(g.id)} t={t}/>)}
              </div>
            </details>
          )}
        </>
      )}
    </div>
  );
}

// ─── ROOT ─────────────────────────────────────────────────────────────────────
// TABS se computa en render para reflejar el idioma activo (ver MainApp)
const TABS_IDS=["hoy","gym","dieta","stats","metas"];

// Supabase helpers (no package needed — raw fetch)
const SB_URL = process.env.REACT_APP_SUPABASE_URL || "";
const SB_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || "";

async function cloudPush(key, value) {
  if(!SB_URL||!SB_KEY) return;
  await fetch(`${SB_URL}/rest/v1/yz_data`, {
    method:"POST",
    headers:{"Content-Type":"application/json","apikey":SB_KEY,"Authorization":`Bearer ${SB_KEY}`,"Prefer":"resolution=merge-duplicates"},
    body:JSON.stringify({id:key, value:JSON.stringify(value), updated_at:new Date().toISOString()})
  });
}

async function cloudPull(key) {
  if(!SB_URL||!SB_KEY) return null;
  const r = await fetch(`${SB_URL}/rest/v1/yz_data?id=eq.${key}&select=value`, {
    headers:{"apikey":SB_KEY,"Authorization":`Bearer ${SB_KEY}`}
  });
  const d = await r.json();
  const raw=d?.[0]?.value;
  if(!raw) return null;
  try{return JSON.parse(raw);}catch{return null;}
}

// ─── XP / GAMIFICATION ───────────────────────────────────────────────────────
const XP_RANKS=[
  {min:0,    name:"Novato",      color:"#94a3b8",icon:"🥉",next:500},
  {min:500,  name:"Consistente", color:"#22c55e",icon:"🥈",next:1500},
  {min:1500, name:"Élite",       color:"#3b82f6",icon:"🥇",next:3000},
  {min:3000, name:"Animal",      color:"#f59e0b",icon:"🔥",next:null},
];
function getUserRank(xp){
  for(let i=XP_RANKS.length-1;i>=0;i--){if(xp>=XP_RANKS[i].min)return XP_RANKS[i];}
  return XP_RANKS[0];
}
function computeXP(habits,workouts,nutrition,habitConfig){
  let xp=0;
  const oblIds=DEFAULT_HABITS.filter(h=>{const c=habitConfig?.[h.id];return c==="obl"||(c===undefined&&h.obl);}).map(h=>h.id);
  Object.entries(habits||{}).forEach(([,dh])=>{
    let done=0;
    oblIds.forEach(id=>{if(dh[id]){xp+=3;done++;}});
    if(done>0&&done>=oblIds.length)xp+=15;
  });
  Object.values(workouts||{}).forEach(w=>{if(w.sessionType)xp+=20;});
  Object.values(nutrition||{}).forEach(day=>{
    const items=Object.values((day.items)||{});
    const tp=items.reduce((a,i)=>a+(i.p||0),0);
    if(tp>=100)xp+=8;
  });
  return xp;
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
// Componente raíz para el usuario autenticado. Gestiona toda la sincronización
// de datos, el estado global de la app y el enrutamiento por tabs.
function MainApp({userId, user, users, saveUsers, onLogout}) {
  // Prefijo por usuario: admin "cris" usa claves legadas sin prefijo
  const pfx = userId === "cris" ? "" : userId + "_";
  const ck = base => pfx + base; // cloud key con prefijo de usuario
  const[darkMode,setDarkMode]=useLS(pfx+"ufc_dark_v3",true);
  const[lang,setLang]=useLS(pfx+"ufc_lang_v1","es");
  _LANG = lang; // sincronizar variable de módulo antes del render
  const[tab,setTab]=useState("hoy");
  const[showSettings,setShowSettings]=useState(false);
  const[habits,setHabits]=useLS(pfx+"ufc_hab_v7",{});
  const[workouts,setWorkouts]=useLS(pfx+"ufc_wk_v7",{});
  const[nutrition,setNutrition]=useLS(pfx+"ufc_nut_v7",{});
  const[bodyStats,setBodyStats]=useLS(pfx+"ufc_bs_v7",{});
  const[customFoods,setCustomFoods]=useLS(pfx+"ufc_cf_v7",[]);
  const[customExercises,setCustomExercises]=useLS(pfx+"ufc_cex_v1",[]);
  const[goal,setGoal]=useLS(pfx+"ufc_goal_v7",{weight:"69",goalWeight:"75",height:"175",age:"22",sex:"M",pace:"normal",activityFactor:"1.9",proteinPerKg:"2.5"});
  const[habitConfig,setHabitConfig]=useLS(pfx+"ufc_hcfg_v1",{});
  const[sleep,setSleep]=useLS(pfx+"ufc_sleep_v1",{});
  const[favoriteFoods,setFavoriteFoods]=useLS(pfx+"ufc_fav_v1", user?.country&&COUNTRY_PRESETS[user.country]?COUNTRY_PRESETS[user.country].foodIds:[]);
  const[books,setBooks]=useLS(pfx+"ufc_books_v1",[]);
  const[goals,setGoals]=useLS(pfx+"ufc_goals_v1",[]);
  const[savedMeals,setSavedMeals]=useLS(pfx+"ufc_meals_v1",[]);
  const[syncStatus,setSyncStatus]=useState("idle");
  const syncTimer=useRef(null);
  const syncKeysRef=useRef(null); // ref siempre actualizado con SYNC_KEYS para el listener de pagehide

  const winWidth = useWindowWidth();
  const isDesktop = winWidth >= 960;
  const { clock: clockStr, day: currentDay } = useClock();

  const t=darkMode?DARK:LIGHT;
  const tk=today();
  const todayH=habits[tk]||{};
  const activeOblH=DEFAULT_HABITS.filter(h=>{const cfg=habitConfig[h.id];return(cfg==="obl"||(cfg===undefined&&h.obl))&&cfg!=="hidden";});
  const oblDone=activeOblH.filter(h=>todayH[h.id]).length;
  const items=Object.values((nutrition[tk]||{}).items||{});
  const totalCal=Math.round(items.reduce((a,i)=>a+i.cal,0));




const SYNC_KEYS = [
  { key: ck("ufc_hab_v7"),  data: habits },
  { key: ck("ufc_wk_v7"),   data: workouts },
  { key: ck("ufc_nut_v7"),  data: nutrition },
  { key: ck("ufc_bs_v7"),   data: bodyStats },
  { key: ck("ufc_cf_v7"),   data: customFoods },
  { key: ck("ufc_cex_v1"),  data: customExercises },
  { key: ck("ufc_goal_v7"), data: goal },
  { key: ck("ufc_hcfg_v1"), data: habitConfig },
  { key: ck("ufc_sleep_v1"),data: sleep },
  { key: ck("ufc_fav_v1"),  data: favoriteFoods },
  { key: ck("ufc_lang_v1"), data: lang },
  { key: ck("ufc_books_v1"), data: books },
  { key: ck("ufc_goals_v1"), data: goals },
  { key: ck("ufc_meals_v1"), data: savedMeals },
];
syncKeysRef.current=SYNC_KEYS; // siempre apunta a la versión más reciente


// Claves que son objetos con fecha como clave (se mergean en lugar de reemplazar)
  const DATE_KEYED=new Set([ck("ufc_hab_v7"),ck("ufc_wk_v7"),ck("ufc_nut_v7"),ck("ufc_bs_v7"),ck("ufc_sleep_v1")]);

// On mount: pull from cloud, merge date-keyed objects, replace the rest; poll every 30s
  useEffect(()=>{
    const KEY_SETTERS={
      [ck("ufc_hab_v7")]:setHabits,
      [ck("ufc_wk_v7")]:setWorkouts,
      [ck("ufc_nut_v7")]:setNutrition,
      [ck("ufc_bs_v7")]:setBodyStats,
      [ck("ufc_cf_v7")]:setCustomFoods,
      [ck("ufc_cex_v1")]:setCustomExercises,
      [ck("ufc_goal_v7")]:setGoal,
      [ck("ufc_hcfg_v1")]:setHabitConfig,
      [ck("ufc_sleep_v1")]:setSleep,
      [ck("ufc_fav_v1")]:setFavoriteFoods,
      [ck("ufc_lang_v1")]:setLang,
      [ck("ufc_books_v1")]:setBooks,
      [ck("ufc_goals_v1")]:setGoals,
      [ck("ufc_meals_v1")]:setSavedMeals,
    };
    async function pullAndApply(){
      setSyncStatus("syncing");
      try{
        for(const{key}of SYNC_KEYS){
          const cloud=await cloudPull(key);
          if(cloud==null) continue;
          if(DATE_KEYED.has(key)){
            // Merge: cloud aporta fechas lejanas (otro dispositivo), local aporta datos recientes
            // Resultado: unión de todas las fechas; si hay conflicto en la misma fecha, gana local
            const localRaw=localStorage.getItem(key);
            const local=localRaw?JSON.parse(localRaw):null;
            if(local&&typeof local==="object"&&!Array.isArray(local)){
              const merged={...cloud,...local}; // local wins: más reciente
              KEY_SETTERS[key]?.(merged);
            } else {
              KEY_SETTERS[key]?.(cloud);
            }
          } else {
            KEY_SETTERS[key]?.(cloud);
          }
        }
        setSyncStatus("ok");
      }catch{setSyncStatus("error");}
    }
    pullAndApply();
    const interval=setInterval(pullAndApply,30000);

    // Push inmediato al salir de la app (ocultar pestaña, cerrar, etc.)
    // Usa el ref para tener siempre los datos más recientes del render actual
    const pushAll=()=>{
      if(!syncKeysRef.current) return;
      for(const{key,data}of syncKeysRef.current){ cloudPush(key,data); }
    };
    document.addEventListener("visibilitychange",()=>{if(document.visibilityState==="hidden")pushAll();});
    window.addEventListener("pagehide",pushAll);
    return()=>{
      clearInterval(interval);
      document.removeEventListener("visibilitychange",pushAll);
      window.removeEventListener("pagehide",pushAll);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  // On data change: debounced push (1 segundo — más rápido que antes)
  useEffect(()=>{
    clearTimeout(syncTimer.current);
    syncTimer.current=setTimeout(async()=>{
      setSyncStatus("syncing");
      try{
        for(const{key,data}of SYNC_KEYS)await cloudPush(key,data);
        setSyncStatus("ok");
      }catch{setSyncStatus("error");}
    },1000);
    return()=>clearTimeout(syncTimer.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[habits,workouts,nutrition,bodyStats,customFoods,customExercises,goal,habitConfig,sleep,favoriteFoods,lang,books,goals,savedMeals]);

  const syncBadge=<span style={{fontSize:"0.65rem",color:syncStatus==="ok"?t.green:syncStatus==="syncing"?t.gold:t.red,fontWeight:700}}>
    {syncStatus==="ok"?"☁️✓":syncStatus==="syncing"?"☁️…":"☁️✕"}
  </span>;

  // TABS con labels traducidos al idioma activo
  const TABS = TABS_IDS.map(id=>({id, label:tr(id==="dieta"?"tab_diet":"tab_"+id)}));

  // Saludo dinámico según hora del día y tab activa
  const _hr=new Date().getHours();
  const _greetKey=_hr<13?"greeting_hoy":_hr<20?"greeting_afternoon":"greeting_evening";
  const greeting = tab==="hoy" ? tr(_greetKey,{name:user?.name||""})
    : tab==="gym" ? tr("greeting_gym")
    : tab==="dieta" ? tr("greeting_diet")
    : tab==="metas" ? tr("greeting_metas")
    : tr("greeting_stats");

  // XP y rango del usuario
  const userXP=computeXP(habits,workouts,nutrition,habitConfig);
  const userRank=getUserRank(userXP);
  const xpToNext=userRank.next?userRank.next-userRank.min:null;
  const xpInLevel=userRank.next?userXP-userRank.min:null;
  const xpPct=xpToNext?Math.min(100,Math.round(xpInLevel/xpToNext*100)):100;

  const dirAttr = lang==="ar" ? "rtl" : "ltr";

  if(isDesktop) return (
    <div dir={dirAttr} style={{minHeight:"100vh",background:t.bg,color:t.text,fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Display',sans-serif"}}>
      <div style={{background:t.surface,borderBottom:`1px solid ${t.border}`,padding:"0.7rem 2rem",display:"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:0,zIndex:20}}>
        <div style={{color:t.text,fontSize:"0.95rem",fontWeight:800,letterSpacing:"-0.02em"}}>mi-tracker <span style={{color:t.textTertiary,fontWeight:400,fontSize:"0.75rem"}}>dashboard</span></div>
        <div style={{display:"flex",gap:"0.65rem",alignItems:"center"}}>
          <span style={{color:t.textSecondary,fontSize:"0.9rem",fontWeight:700,fontVariantNumeric:"tabular-nums"}}>{clockStr}</span>
          {syncBadge}
          <div style={{display:"flex",alignItems:"center",gap:"0.4rem",background:t.accentMuted,borderRadius:"8px",padding:"0.25rem 0.6rem"}}>
            <div style={{width:22,height:22,borderRadius:"50%",background:user?.isAdmin?t.green:t.blue,display:"flex",alignItems:"center",justifyContent:"center",fontSize:"0.65rem",fontWeight:700,color:"#fff"}}>{user?.name?.[0]?.toUpperCase()||"?"}</div>
            <span style={{color:t.textSecondary,fontSize:"0.8rem",fontWeight:600}}>{user?.name}</span>
          </div>
          <button onClick={()=>setDarkMode(!darkMode)} style={{background:t.accentMuted,border:"none",borderRadius:"8px",padding:"0.3rem 0.6rem",color:t.textSecondary,fontSize:"0.88rem",cursor:"pointer"}}>{darkMode?"☀️":"🌙"}</button>
          <button onClick={()=>setShowSettings(true)} style={{background:t.accentMuted,border:"none",borderRadius:"8px",padding:"0.3rem 0.6rem",color:t.textSecondary,fontSize:"0.88rem",cursor:"pointer"}}>⚙️ {tr("settings_title")}</button>
        </div>
      </div>
      <DesktopDashboard habits={habits} setHabits={setHabits} workouts={workouts} setWorkouts={setWorkouts} nutrition={nutrition} bodyStats={bodyStats} setBodyStats={setBodyStats} goal={goal} habitConfig={habitConfig} sleep={sleep} t={t}/>
      {showSettings&&<Settings goal={goal} setGoal={setGoal} habitConfig={habitConfig} setHabitConfig={setHabitConfig} darkMode={darkMode} setDarkMode={setDarkMode} onClose={()=>setShowSettings(false)} t={t} userId={userId} user={user} users={users} saveUsers={saveUsers} onLogout={onLogout} lang={lang} setLang={setLang}/>}
    </div>
  );


    return (
    <div dir={dirAttr} style={{minHeight:"100vh",background:t.bg,color:t.text,fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Display',sans-serif",maxWidth:"480px",margin:"0 auto"}}>
      <div style={{padding:"0.85rem 1.1rem 0",background:t.bg,borderBottom:`1px solid ${t.border}`,position:"sticky",top:0,zIndex:20}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"0.7rem"}}>
          <div>
            <div style={{color:t.text,fontSize:"1rem",fontWeight:700,letterSpacing:"-0.02em"}}>
              {greeting}
            </div>
            <div style={{color:t.textTertiary,fontSize:"0.67rem",marginTop:"0.04rem"}}>{tr("header_kcal_habits",{cal:totalCal,done:oblDone,total:activeOblH.length})}</div>
            <div style={{display:"flex",alignItems:"center",gap:"0.4rem",marginTop:"0.25rem",flexWrap:"wrap"}}>
              <span style={{fontSize:"0.63rem",fontWeight:700,color:userRank.color}}>{userRank.icon} {userRank.name}</span>
              <span style={{fontSize:"0.58rem",color:t.textTertiary,fontVariantNumeric:"tabular-nums"}}>{userXP} XP</span>
              {xpToNext&&<>
                <div style={{width:"50px",height:"3px",background:t.border,borderRadius:"2px",overflow:"hidden"}}><div style={{width:`${xpPct}%`,height:"100%",background:userRank.color,borderRadius:"2px"}}/></div>
                <span style={{fontSize:"0.56rem",color:t.textTertiary}}>faltan {userRank.next-userXP} → {XP_RANKS[XP_RANKS.indexOf(userRank)+1]?.name}</span>
              </>}
              {!xpToNext&&<span style={{fontSize:"0.58rem",color:userRank.color,fontWeight:700}}>MAX 🔥</span>}
            </div>
          </div>
          <div style={{display:"flex",gap:"0.45rem",alignItems:"center"}}>
            {tab!=="stats"&&tab!=="metas"&&<span style={{color:t.textSecondary,fontSize:"0.82rem",fontWeight:700,fontVariantNumeric:"tabular-nums",letterSpacing:"0.02em"}}>{clockStr}</span>}
            {syncBadge}
            <button onClick={()=>setDarkMode(!darkMode)} style={{background:t.accentMuted,border:"none",borderRadius:"8px",padding:"0.3rem 0.48rem",color:t.textSecondary,fontSize:"0.88rem",cursor:"pointer"}}>{darkMode?"☀️":"🌙"}</button>
            <button onClick={()=>setShowSettings(true)} style={{background:t.accentMuted,border:"none",borderRadius:"8px",padding:"0.3rem 0.48rem",color:t.textSecondary,fontSize:"0.88rem",cursor:"pointer"}}>⚙️</button>
          </div>
        </div>
        <div style={{display:"flex",gap:"0",marginBottom:"-1px"}}>
          {TABS.map(tb=>(
            <button key={tb.id} onClick={()=>setTab(tb.id)} style={{flex:1,padding:"0.58rem 0",background:"transparent",border:"none",cursor:"pointer",borderBottom:`2px solid ${tab===tb.id?t.accent:"transparent"}`,transition:"all 0.2s"}}>
              <div style={{fontSize:"0.75rem",fontWeight:tab===tb.id?700:500,color:tab===tb.id?t.text:t.textTertiary}}>{tb.label}</div>
            </button>
          ))}
        </div>
      </div>
      <div style={{padding:"1.1rem"}}>
        {tab==="hoy"&&<HoyTab habits={habits} setHabits={setHabits} habitConfig={habitConfig} setHabitConfig={setHabitConfig} bodyStats={bodyStats} setBodyStats={setBodyStats} nutrition={nutrition} workouts={workouts} goal={goal} sleep={sleep} setSleep={setSleep} currentDay={currentDay} t={t}/>}
        {tab==="gym"&&<GymTab workouts={workouts} setWorkouts={setWorkouts} habits={habits} setHabits={setHabits} goal={goal} customExercises={customExercises} setCustomExercises={setCustomExercises} currentDay={currentDay} t={t}/>}
        {tab==="dieta"&&<DietaTab nutrition={nutrition} setNutrition={setNutrition} customFoods={customFoods} setCustomFoods={setCustomFoods} goal={goal} favoriteFoods={favoriteFoods} setFavoriteFoods={setFavoriteFoods} savedMeals={savedMeals} setSavedMeals={setSavedMeals} currentDay={currentDay} t={t}/>}
        {tab==="stats"&&<StatsTab habits={habits} workouts={workouts} nutrition={nutrition} bodyStats={bodyStats} sleep={sleep} goal={goal} habitConfig={habitConfig} t={t}/>}
        {tab==="metas"&&<ObjetivosTab goals={goals} setGoals={setGoals} books={books} setBooks={setBooks} t={t}/>}
      </div>
      {showSettings&&<Settings goal={goal} setGoal={setGoal} habitConfig={habitConfig} setHabitConfig={setHabitConfig} darkMode={darkMode} setDarkMode={setDarkMode} onClose={()=>setShowSettings(false)} t={t} userId={userId} user={user} users={users} saveUsers={saveUsers} onLogout={onLogout} lang={lang} setLang={setLang}/>}
      <div style={{height:"2rem"}}/>

      </div>
  );
}
// ─── APP ROOT ─────────────────────────────────────────────────────────────────
// Componente raíz de la aplicación. Gestiona el registro de usuarios,
// la sesión activa y el flujo: carga → login → onboarding → app principal.
export default function App() {
  const USERS_KEY    = "mi_tracker_users_v2";
  const SESSION_KEY  = "mi_tracker_session_v2";
  const USERS_CLOUD  = "users_registry_v2"; // clave en Supabase compartida por todos los dispositivos

  // Inicializar usuarios desde localStorage (rápido, offline)
  const [users, setUsers] = useState(() => {
    try {
      const s = localStorage.getItem(USERS_KEY);
      if(s){ const p=JSON.parse(s); if(p && typeof p==="object" && Object.keys(p).length){
        p["cris"] = {...p["cris"], id:"cris", name:"Cris", pin:"5129", isAdmin:true, onboardingDone:true};
        return p;
      }}
    } catch {}
    const init = {"cris":{id:"cris",name:"Cris",pin:"5129",isAdmin:true,onboardingDone:true,country:null,createdAt:new Date().toISOString()}};
    localStorage.setItem(USERS_KEY, JSON.stringify(init));
    return init;
  });

  const [session, setSession] = useState(() => localStorage.getItem(SESSION_KEY) || null);
  const [loadingUsers, setLoadingUsers] = useState(true);

  // Al arrancar: sincronizar registro de usuarios desde Supabase
  // Así todos los dispositivos ven los perfiles que el admin haya creado
  useEffect(() => {
    cloudPull(USERS_CLOUD).then(cloud => {
      if(cloud && typeof cloud === "object" && Object.keys(cloud).length) {
        // Garantía: cris siempre es admin, nunca pierde sus datos aunque el cloud diga otra cosa
        cloud["cris"] = {
          ...cloud["cris"],
          id:"cris", name:"Cris", pin:"5129", isAdmin:true, onboardingDone:true
        };
        localStorage.setItem(USERS_KEY, JSON.stringify(cloud));
        setUsers(cloud);
      }
    }).catch(()=>{}).finally(()=>setLoadingUsers(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // saveUsers: guarda local + sube a Supabase para que el resto de dispositivos lo vean
  const saveUsers = u => {
    localStorage.setItem(USERS_KEY, JSON.stringify(u));
    setUsers(u);
    cloudPush(USERS_CLOUD, u);
  };

  // refreshUsers: re-descarga el registro de usuarios desde Supabase (para el botón de la login)
  const refreshUsers = async () => {
    const cloud = await cloudPull(USERS_CLOUD);
    if(cloud && typeof cloud === "object" && Object.keys(cloud).length) {
      cloud["cris"] = {...cloud["cris"], id:"cris", name:"Cris", pin:"5129", isAdmin:true, onboardingDone:true};
      localStorage.setItem(USERS_KEY, JSON.stringify(cloud));
      setUsers(cloud);
    }
  };

  const handleLogin = id => { localStorage.setItem(SESSION_KEY, id); setSession(id); };
  const handleLogout = () => { localStorage.removeItem(SESSION_KEY); setSession(null); };
  const handleOnboardingDone = (id, upd) => {
    const nu = {...users, [id]: {...users[id], ...upd, onboardingDone:true}};
    saveUsers(nu);
    setSession(id); // forzar re-render tras onboarding
  };

  // Pantalla de carga mientras se descarga el registro de usuarios
  if(loadingUsers && !session) {
    return (
      <div style={{minHeight:"100vh",background:"#0a0a0a",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:"1rem",fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Display',sans-serif"}}>
        <div style={{fontSize:"2rem"}}>💪</div>
        <div style={{color:"#fff",fontSize:"1.1rem",fontWeight:700}}>mi-tracker</div>
        <div style={{color:"#444",fontSize:"0.78rem"}}>Cargando perfiles…</div>
        <div style={{width:40,height:4,background:"#1a1a1a",borderRadius:2,overflow:"hidden",marginTop:"0.5rem"}}>
          <div style={{height:"100%",background:"#22C55E",borderRadius:2,animation:"loadbar 1.2s ease-in-out infinite"}}/>
        </div>
        <style>{`@keyframes loadbar{0%{width:0%;margin-left:0}50%{width:100%;margin-left:0}100%{width:0%;margin-left:100%}}`}</style>
      </div>
    );
  }

  if(!session) return <LoginScreen users={users} onLogin={handleLogin} onRefresh={refreshUsers}/>;

  const currentUser = users[session];
  if(!currentUser) { handleLogout(); return null; }
  if(!currentUser.onboardingDone) {
    return <OnboardingWizard user={currentUser} onComplete={upd=>handleOnboardingDone(session, upd)} onLangChange={code=>{_LANG=code;}}/>;
  }
  return <MainApp userId={session} user={currentUser} users={users} saveUsers={saveUsers} onLogout={handleLogout}/>;
}
